/***************************************************************************
 * COPYRIGHT (C) 2013, Rapid7 LLC, Boston, MA, USA.
 * All rights reserved. This material contains unpublished, copyrighted
 * work including confidential and proprietary information of Rapid7.
 **************************************************************************/
import EventStatus from 'modules/automation/resources/EventStatus';
import EventType from 'modules/automation/resources/EventType';
import NotificationType from 'modules/automation/resources/NotificationType';
import EventTriggerStatus from 'modules/automation/resources/TriggerStatus';
import EventTriggerTypes from 'modules/automation/resources/TriggerType';
import {WorkflowTemplateIds} from 'modules/automation/resources/WorkflowTemplateEnums';
import {RegistryScope, RegistryVendor} from 'modules/container/lib/enum';
import {ProvisionStatus} from 'modules/infrastructure/resources/ProvisionStatus';
import EDIT_PANEL_MODES from 'modules/management/resources/EDIT_PANEL_MODES';
import INTEGRATION_TYPES from 'modules/management/resources/INTEGRATION_TYPES';
import {
  AssetStatuses,
  Expiration,
  OverrideScope,
  OverrideState,
  OverrideType,
} from 'modules/policy/types';
import TicketServiceType from 'modules/remediation/resources/RemediationTicketServerType';
import INFRASTRUCTURE_TYPES from 'resources/INFRASTRUCTURE_TYPES';
import PreviewFeature from 'resources/PreviewFeature';
import ReportTemplate from 'resources/ReportTemplate';
import ReportType from 'resources/ReportType';

const enums = {
  DashboardTemplateCategory: {
    ALL: 'All',
    MODERN_INFRASTRUCTURE: 'Modern Infrastructure',
    PRIORITIZATION: 'Prioritization',
    REMEDIATION: 'Remediation',
  },
};

const components = {
  AttributeBar: {
    showLess: 'Show Less',
    showMore: 'Show More',
  },
  CreateRemediationButton: {
    confirmButton: 'Exit',
    confirmNavigationButton: 'Take me to query builder',
    confirmNavigationModalBody:
      'You will be leaving the project wizard and redirected to the query builder.',
    confirmNavigationModalTitle: 'Are you sure you want to exit?',
  },
  DeleteAssetModal: {
    body:
      'Deleting assets removes them from all sites and asset groups to which they belong. Historical data will be kept. Are you sure you want to delete this asset?',
    cantReachConsole:
      "Your security console didn't respond.  Make sure that you are on the same network as your Security Console host and verify that it is online.",
    noSSO:
      'To delete an asset, you need to <a style="text-decoration: underline" href="https://docs.rapid7.com/insightvm/enable-insightvm-platform-login/" target="_blank" rel="noopener noreferrer">enable InsightVM Platform Login</a>. This new experience streamlines how you access Security Console and cloud-based features in InsightVM. <a style="text-decoration: underline" href="https://docs.rapid7.com/insightvm/enable-insightvm-platform-login/" target="_blank" rel="noopener noreferrer">Learn more</a>',
    notification: {
      error: 'Failed to delete your asset',
      success:
        'Your asset has been successfully deleted.  It can take a few minutes for the changes to reflect on this page.',
    },
  },
  FormWizard: {
    continue: 'Continue',
    submit: 'Submit',
  },
  RemediationProjectWizard: {
    StepBasicInformation: {
      accessDescription:
        'Permit users who do not have access to asset(s) associated with this project to see',
      assignTo: 'Assign To',
      assigneeRequired: 'At least one user or email is required',
      emailAssigneesToggle: 'Permit users who do not have access to console',
      emailAssigneesToggleNote:
        '* Unchecking this option will remove the non-console users from this list automatically',
      emailAssigneesWillBeEmailed:
        'Email assignees will be notified of this project',
      minimumDate: 'The due date must be on or after today.',
      nameInUse: 'This name is already in use',
      projectQuery: '__name__ Project Query',
      projectQueryOnly: 'Project Query',
      queryCopy: '__name__ Copy',
      queryName: 'Query Name',
      submitError:
        'There was an error saving your remediation project, try again later',
      usersLoadError:
        'There was an issue loading users, please try again later.',
    },
    StepBasicInformationPreview: {
      assignees: 'Assignees',
      description: 'Description',
      dueOnTime: 'Due On',
      queryName: 'Query Name',
      title: 'Name',
      visibleFields: 'Visible Fields',
    },
    StepTicketing: {
      automatedTicketing: 'Automated ticketing',
      chooseConnection: 'Choose a project connection',
      configured: 'Configured for __fieldMappingName__',
      configuredWarning:
        '__type__ project connection can not be changed. If you wish to use a different ticketing configuration, please create a new project.',
      noConnection: 'No project connection',
      ticketingConfigurationWarning:
        'The selection you make here can not be changed later.',
      typeJira: 'JIRA',
      typeServiceNow: 'ServiceNow',
    },
    StepTicketingPreview: {
      ticketing: 'Ticketing',
    },
    StepType: {
      dynamicDetail:
        '<0>Dynamic projects</0> automatically add solutions as conditions change in your network.',
      projectTypeSubtext:
        '__assetCount__ assets | __vulnerabilityCount__ vulnerabilities | __solutionCount__ solutions',
      staticDetail:
        '<0>Static projects</0> lock asset and solution membership after you initially create them.',
    },
    StepTypePreview: {
      dynamic: 'Dynamic',
      scope: 'Scope',
      static: 'Static',
      type: 'Type',
    },
    createProject: 'Create Remediation Project',
    steps: {
      basicInformation: {
        description: `
        <list>
          <item>Name and describe your project so it can be easily identified.</item>
          <item>
            Set a due date for this project. You will be provided with alerts as the project approaches this date.
            Expired projects will no longer update and cannot be accessed via any emailed links sent previously.
          </item>
        </list>
        `,
      },
      review: {
        description: 'Confirm the details for your project.',
      },
      scope: {
        description:
          'Select any of the queries that your organization has made before to dedicate a scope for your report. You can build a new query and save from the query builder.',
        title: 'Select Scope',
      },
    },
    submitSuccess:
      'Remediation project <0/> was created successfully. <1>Click here to view your new project</1>',
    ticketingRulesLoadError:
      'There was an issue loading data required for project creation, please try again later.',
    updateProject: 'Update Remediation Project',
  },
  ReportWizard: {
    DashboardPodField: {
      ownedByMe: 'Owned by me',
      ownedByOther: 'Owned by __dashboardOwner__',
    },
    StepConfigure: {
      invalidFrequency: 'Value must be between __from__ and __to__',
      nameInUse: 'This name is already in use',
      notRecurring: 'I do not want to run a recurring report',
      recurring: 'I want to schedule and run a recurring report',
      schedule: 'Schedule',
      scheduleLabel: {
        days: 'days',
        hours: 'hours',
        monthly: 'months',
        weeks: 'weeks',
      },
      sharedWith: 'Shared With',
      startOn: 'Start On',
      toggleLabel: 'Permit users who do not have access to console',
      toggleNote:
        '* Unchecking this option will remove the non-console users from this list automatically',
      toggleNotification:
        'Users without an InsightVM account will receive a link to download a copy of the report via e-mail when an e-mail address is entered into this field',
    },
    StepReview: {
      entity: {
        asset: 'Asset',
        exploit: 'Exploit',
        malwareKit: 'MalwareKit',
        newVulnerability: 'NewVulnerability',
        policies: 'Policies',
        project: 'Project',
        remediatedVulnerability: 'RemediatedVulnerability',
        service: 'Service',
        software: 'Software',
        solution: 'Solution',
        vulnerability: 'Vulnerability',
        vulnerabilityFinding: 'VulnerabilityFinding',
      },
      item: {
        description: 'Description',
        name: 'Name',
        schedule: 'Schedule',
        type: 'Report Type',
      },
      loadError:
        'There was an error loading the dashboards, please try again later.',
      onlyOnce: 'Run only once',
      schedule:
        'Report will be generated every __value__ times a __unit__, at __time__, starting on __startOn__',
      scheduleLabel: {
        days: 'day(s)',
        months: 'months(s)',
        weeks: 'week(s)',
      },
      sharedWith: 'Shared With',
      type: {
        customDashboard: 'Custom Dashboard - __name__',
        entityCsv: 'CSV File Report - __entity__',
        reportTemplate: 'Report Template - __name__',
      },
    },
    StepReviewContainer: {
      submitError: 'There was an error in saving your report.',
    },
    StepTemplateCustomDashboard: {
      loadError:
        'There was an error loading the dashboards, please try again later.',
      noDashboards: 'There are no dashboards matching your criteria.',
      ownedByMe: 'Owned by me',
      usersLoadError:
        'There was an issue loading users, please try again later.',
    },
    StepTemplateEntityCsv: {
      entity: {
        asset: {
          description: 'All assets found within your scope.',
          name: 'Assets',
        },
        exploit: {
          description:
            'Exploit data across vulnerabilities specific to your scope.',
          name: 'Exploits',
        },
        malwareKit: {
          description:
            'Malware kit data across vulnerabilities specific to your scope.',
          name: 'Malware Kits',
        },
        newVulnerability: {
          description: 'New vulnerabilities found within your scope.',
          name: 'New Vulnerabilities',
        },
        policies: {
          description:
            'Export the assessment results of your agent-based policies. Proof and remediation data is included.',
          moreInfo: 'Bulk Export API is also available for Policies.',
          name: 'Policies',
        },
        remediatedVulnerability: {
          description:
            'Vulnerabilities found to be remediated within your scope.',
          name: 'Remediated Vulnerabilities',
        },
        service: {
          description: 'Services specific to your scope.',
          name: 'Services',
        },
        software: {
          description: 'Software specific to your scope.',
          name: 'Software',
        },
        solution: {
          description: 'Solutions specific to your scope.',
          name: 'Solutions',
        },
        vulnerability: {
          description: 'All vulnerabilities found within your scope.',
          name: 'Vulnerabilities',
        },
      },
    },
    StepTemplatePreview: {
      title: 'Template',
    },
    StepTemplateReport: {
      asset: {
        templateDescription:
          'See a monthly snapshot of your vulnerability management program, which includes vulnerability, asset, and remediation data. The report also shows the number of new to closed remediation projects and breakdowns of location, owner, and criticality tags to help you prioritize your remediation efforts.',
        templateName: 'Asset Summary',
      },
      executiveSummary: {
        templateDescription:
          'See a monthly snapshot of your vulnerability management program, which includes vulnerability, asset, and remediation data. The report also shows the number of new to closed remediation projects and breakdowns of location, owner, and criticality tags to help you prioritize your remediation efforts.',
        templateName: 'Executive Summary',
      },
      vulnerability: {
        templateDescription:
          'See a monthly snapshot of your vulnerability management program, which includes vulnerability, asset, and remediation data. The report also shows the number of new to closed remediation projects and breakdowns of location, owner, and criticality tags to help you prioritize your remediation efforts.',
        templateName: 'Vulnerability Summary',
      },
    },
    StepType: {
      type: {
        customDashboard: {
          description:
            'Create a report based on a dashboard you created and customized.',
          name: 'Your Dashboards',
        },
        entityCsv: {
          description:
            'Create a report that compiles large amounts of data in a CSV format. For example, export query results or the assessment results of agent-based policies, including proof and remediation information.',
          name: 'Export as CSV',
        },
        reportTemplate: {
          description:
            'Present your vulnerability data and actionable insights in one of our ready-to-print formats.',
          name: 'Easy Reports',
        },
      },
    },
    StepTypePreview: {
      scope: 'Scope',
      title: 'Type',
    },
    createReport: 'Create Report',
    createSuccess:
      'Your report was created successfully. View it in the <0>reports management</0> page',
    step: {
      configureDetail: {
        description:
          'Specify the details of the report, enter users to share it with, and create a schedule if needed.',
        heading: 'Add details',
        title: 'Details',
      },
      review: {
        description: "Confirm your report's details.",
        title: 'Review',
      },
      scope: {
        description:
          'Select any of the queries that your organization has made before to dedicate a scope for your report. You can build a new query and save from the query builder.',
        heading: 'Set the scope',
        title: 'Scope',
      },
      template: {
        descriptionCustomDashboard:
          'Select one of your customized dashboards to create a report based off of it.',
        descriptionEntityCsv: 'Select a CSV file to create a report',
        descriptionReportTemplate:
          'Select a report template to create a report from data off of our Dashboard Cards. If you customized a Dashboard Card and want to create a report, go back to the previous step and select <bold>Customized Dashboard Report</bold>.',
        heading: 'Select the data',
        title: 'Template',
      },
      type: {
        description:
          'If you want to produce reports in our signature style, choose an <bold>Easy Report</bold>. For a more customized look and tailored output, choose the <bold>Your Dashboards</bold> option. The <bold>Export as CSV</bold> option compiles your data in a CSV format.',
        heading: 'Select a type',
        title: 'Type',
      },
    },
    title: 'Create Report',
    updateReport: 'Update Report',
    updateSuccess: 'Your report was updated successfully.',
  },
  ReportWizardButton: {
    confirmNavigationButton: 'Take me to reports',
    confirmNavigationModalBody:
      'If you leave the Report wizard before saving, your changes will be lost.',
    confirmNavigationModalTitle: 'Exit the Report wizard?',
  },
  VulnerabilityExceptionModal: {
    cancel: 'Cancel',
    cantReachConsole:
      "Your security console didn't respond.  Make sure that you are on the same network as your Security Console host and verify that it is online.",
    comment: 'Comment',
    expiration: {
      options: {
        endOfMonth: 'End Of Month',
        endOfQuarter: 'End Of Quarter',
        endOfYear: 'End Of Year',
        never: 'Never',
        oneMonth: 'One Month',
        oneWeek: 'One Week',
        oneYear: 'One Year',
        placeholder: 'Select an expiration',
      },
      title: 'Expires',
    },
    impact: {
      subtitle: 'Based on latest assessment data from __date__',
      title: 'Exception Impact',
    },
    kpi: {
      assets: 'Assets',
      cntAssets: 'Asset Count',
      cvssScore: 'CVSS Score',
      instances: 'Instances',
      totalRisk: 'Total Risk',
    },
    noSSO:
      'To exclude a vulnerability, you need to <a style="text-decoration: underline" href="https://docs.rapid7.com/insightvm/enable-insightvm-platform-login/" target="_blank" rel="noopener noreferrer">enable InsightVM Platform Login</a>. This new experience streamlines how you access Security Console and cloud-based features in InsightVM. <a style="text-decoration: underline" href="https://docs.rapid7.com/insightvm/enable-insightvm-platform-login/" target="_blank" rel="noopener noreferrer">Learn more</a>',
    notification: {
      error: 'Failed to exclude your vulnerability',
      success:
        'Your vulnerability has been successfully excluded.  It can take a few minutes for the changes to reflect on this page.',
    },
    reason: {
      options: {
        acceptableRisk: 'Acceptable Risk',
        acceptableUse: 'Acceptable Use',
        compensatingControl: 'Compensating Control',
        falsePositive: 'False Positive',
        other: 'Other',
        placeholder: 'Select a reason',
      },
      title: 'Reason',
    },
    scope: {
      excludeAll: 'Exclude all instances (all assets)',
    },
    submit: 'Submit',
    submitting: 'Submitting',
    title: 'Vulnerability Exception',
    vulnerability: 'Vulnerability',
  },
  chart: {
    buildAssessedAssetsVsRiskScore: {
      assessedAssets: 'Assessed Assets',
      dualComparisonTooltip:
        'Week of __dayOfWeek__, __date__ <br/> __label__ __value__',
      riskScoreLabel: 'Risk Score',
      title: 'Assessed Assets vs Risk Score',
    },
    buildAssetsByOperatingSystem: {
      otherValue:
        '__percent__ (__value__) of my assets are running other Operating Systems',
      title: 'Assets by Operating System',
      unknownValue:
        '__percent__ (__value__) of my assets are running an unknown Operating System',
      value:
        '__percent__ (__value__) of my assets are running the __os__ Operating System',
    },
    buildExploitableAssetsBySkillLevel: {
      summary: {
        cntMinSkillLevelRankDoesNotExist:
          'of exploitable assets have no known exploit',
        cntMinSkillLevelRankExpert:
          'of exploitable assets can be exploited by an expert',
        cntMinSkillLevelRankIntermediate:
          'of exploitable assets can be exploited with intermediate skill',
        cntMinSkillLevelRankNovice:
          'of exploitable assets can be exploited by a novice',
      },
      title: 'Exploitable Assets By Skill Level',
    },
    buildExploitableVulnerabilitiesBySkillLevel: {
      summary: {
        cntMinSkillLevelRankDoesNotExist:
          'of vulnerabilities have no known exploit',
        cntMinSkillLevelRankExpert:
          'of vulnerabilities can be exploited by an expert',
        cntMinSkillLevelRankIntermediate:
          'of vulnerabilities can be exploited with intermediate skill',
        cntMinSkillLevelRankNovice:
          'of vulnerabilities can be exploited by a novice',
      },
      title: 'Exploitable Vulnerabilities By Skill Level',
    },
    buildVulnerabilitiesByCvssScore: {
      summary:
        'of vulnerabilities have a CVSS score between __from__ and __to__',
      title: 'Vulnerabilities by CVSS Score',
    },
    buildVulnerabilitiesByCvssV3Score: {
      summary:
        'of vulnerabilities have a CVSS V3 score between __from__ and __to__',
      title: 'Vulnerabilities by CVSS Score (Grouped by V3 Category)',
    },
  },
  tables: {
    AssetTable: {
      columnLabels: {
        address: 'Address',
        exploits: 'Exploits',
        instances: 'Instances',
        malware: 'Malware',
        name: 'Name',
        operatingSystem: 'Operating System',
        operatingSystemArchitecture: 'OS Product',
        operatingSystemVendor: 'OS Vendor',
        operatingSystemVersion: 'OS Version',
        risk: 'Risk',
        vulnerabilities: 'Vulnerabilities',
      },
    },
    ComplianceAssetsTable: {
      columnLabels: {
        address: 'Address',
        assessedPolicies: 'Assessed Policies',
        assetName: 'Asset Name',
        lastAssessed: 'Last Assessed',
        operatingSystemName: 'Operating System Name',
        ruleCompliance: 'Rule Compliance',
        rulesPassed: 'Rules Passed',
      },
    },
    CompliancePoliciesTable: {
      columnLabels: {
        assetCompliance: 'Asset Compliance',
        assetCount: 'Asset Count',
        assetsFailed: 'Assets Failed',
        assetsPassed: 'Assets Passed',
        policyName: 'Policy Name',
        publisher: 'Publisher',
        ruleCompliance: 'Rule Compliance',
        rulesPassed: 'Rules Passed',
        version: 'Version',
      },
    },
    CompliancePolicyRulesTable: {
      columnLabels: {
        assetCompliance: 'Asset Compliance',
        assetsPassed: 'Assets Passed',
        ruleName: 'Rule Name',
      },
    },
    ServiceTable: {
      columnLabels: {
        assetCount: 'Asset Count',
        port: 'Port',
        product: 'Product',
        protocol: 'Protocol',
        serviceName: 'Service Name',
        vendor: 'Vendor',
        version: 'Version',
      },
    },
    SoftwareTable: {
      columnLabels: {
        assetCount: 'Asset Count',
        product: 'Product',
        vendor: 'Vendor',
        version: 'Version',
      },
    },
    VulnerabilityTable: {
      columnLabels: {
        address: 'Address',
        assetCount: 'Asset Count',
        assetInstancesCount: 'Instances',
        cvss: 'CVSS',
        dateAdded: 'Added On',
        dateModified: 'Modified',
        datePublished: 'Published',
        exploits: 'Exploits',
        hasExploits: 'Is Exploitable',
        hasMalware: 'Has Malware',
        instances: 'Instances',
        malware: 'Malware',
        minDateFound: 'Discovery Date',
        name: 'Name',
        operatingSystem: 'Operating System',
        operatingSystemArchitecture: 'OS Product',
        operatingSystemVendor: 'OS Vendor',
        operatingSystemVersion: 'OS Version',
        risk: 'Risk',
        severity: 'Severity',
        title: 'Title',
        totalRisk: 'Total Risk',
        vulnId: 'ID',
        vulnerabilities: 'Vulnerabilities',
      },
    },
  },
};

const helpfulQueries = {
  AssetsNewlyDiscoveredInTheLast7Days: {
    description:
      'Filter for asset in your environment discovered within the Last 7 days.',
    name: 'Assets Newly Discovered in the Last 7 Days',
  },
  AssetsNewlyDiscoveredInTheLast30Days: {
    description:
      'Filter for asset in your environment discovered within the last 30 days.',
    name: 'Assets Newly Discovered in the last 30 Days',
  },
  AssetsWithActivelyExploitedVulnerabilities: {
    description:
      'Filter for asset in your environment with vulnerabilities for which there are exploits.',
    name: 'Assets With Actively Exploited Vulnerabilities',
  },
  AssetsWithContainers: {
    description: 'Filter for asset in your environment running containers.',
    name: 'Assets with Containers',
  },
  AssetsWithCriticalVulnerabilities: {
    description:
      'Filter for asset in your environment with critical vulnerabilities.',
    name: 'Assets with Critical Vulnerabilities',
  },
  AssetsWithDefaultAccountVulns: {
    description:
      'Filter for asset in your environment with vulnerabilities in the default account.',
    name: 'Assets With Default Account Vulns',
  },
  AssetsWithModerateVulnerabilities: {
    description:
      'Filter for asset in your environment with moderate vulnerabilities.',
    name: 'Assets with Moderate Vulnerabilities',
  },
  AssetsWithSevereVulnerabilities: {
    description:
      'Filter for asset in your environment with critical vulnerabilities.',
    name: 'Assets with Severe Vulnerabilities',
  },
  AssetsWithoutContainers: {
    description: 'Filter for asset in your environment without containers.',
    name: 'Assets Without Containers',
  },
  CISA: {
    description:
      'Filter for the CISA Known Exploitable Vulnerabilities in your environment.',
    name: 'CISA Known Exploited Vulnerabilities',
  },
  ContainerHosts: {
    description:
      'Filter for asset in your environment that are container hosts.',
    name: 'Container Hosts',
  },
  ContiLeak: {
    description: 'Filter for the ContiLeak vulnerabilities in your environment',
    name: 'ContiLeak Vulnerabilities',
  },
  ExpiredSSLCertificatesCard: {
    description:
      'Filter for asset in your environment with expired SSL certificates.',
    name: 'Expired SSL Certificates Card',
  },
  ExpiringSSLCertificatesCard: {
    description:
      'Filter for asset in your environment in your environment  with SSL certificates that will be expiring soon.',
    name: 'Expiring SSL Certificates Card',
  },
  ExploitableCriticalVulnerabilities: {
    description:
      'Filter for vulnerabilities  in your environment with exploits and a severity of critical.',
    name: 'Exploitable Critical Vulnerabilities',
  },
  ExploitableModerateVulnerabilities: {
    description:
      'Filter for vulnerabilities in your environment  with exploits and critical moderate.',
    name: 'Exploitable Moderate Vulnerabilities',
  },
  ExploitableSevereVulnerabilities: {
    description:
      'Filter for vulnerabilities in your environment  with exploits and a severity of severe.',
    name: 'Exploitable Severe Vulnerabilities',
  },
  ExploitableVulnerabilities: {
    description:
      'Filter for vulnerabilities in your environment with an exploit.',
    name: 'Exploitable Vulnerabilities',
  },
  ExternalIPAddressAssets: {
    description:
      'This shows all the assets with an IP address that do not belong in the RFC-1918 IP ranges.',
    name: 'External IP Address Assets',
  },
  Log4j: {
    description: 'Filter for the log4j vulnerability in your environment.',
    name: 'log4j vulnerability by CVE ID',
  },
  MostCommonExploitedVulnerabilities: {
    description:
      'Filter vulnerabilities in your environment with the most commonly exploited vulnerability components.',
    name: 'Most Common Exploited Vulnerabilities',
  },
  NumberOfConnectedAgents: {
    description:
      'Filter for asset in your environment with an agent installed.',
    name: 'Number Of Connected Agents',
  },
  RemoteNonServerAssets: {
    description:
      'This shows all the remote non-server assets in your environment.',
    name: 'Remote Non-server Assets',
  },
  Spring4Shell: {
    description:
      'Filter for the Spring4Shell vulnerability in your environment.',
    name: 'Spring4Shell by CVE ID',
  },
  VulnerabilityPublishAgeBySeverity: {
    description:
      'Filter for vulnerabilities by date published and according to severity.',
    name: 'Vulnerability Publish Age By Severity',
  },
};

const vulnerability = {
  views: {
    vulnerabilityListing: {
      pageTitle: 'Vulnerabilities',
    },
  },
};

const asset = {
  assetGroups: 'Asset Groups',
  assets: 'Assets',
  tags: 'Tags',
  views: {
    assetListing: {
      pageTitle: 'Assets',
    },
  },
};

const infrastructure = {
  clear: 'Clear',
  clearAll: 'Clear All',
  exclude: 'Exclude',
  failed: 'Failed',
  include: 'Include',
  infrastructure: {
    arn: {
      _label: 'ARN',
      account: 'Account',
      region: 'Region',
      service: 'Service',
      type: 'Type',
    },
    cloudConfiguration: 'Cloud Configuration',
    columnLabels: {
      cidr: 'CIDR',
      description: 'Description',
      ipv4: 'IPv 4',
      ipv6: 'IPv 6',
      prefixListId: 'ID',
      prefixListIds: 'Prefix List IDs',
      public: 'Public',
    },
    dashboardCards: {
      LeastCompliantRules: {
        yAxisLabel: 'Total findings',
      },
      MostExceptedRules: {
        tooltip:
          '__excepted__ (__percent__) of __total__ findings have been excepted',
        yAxisLabel: 'Excepted findings',
      },
      menuLabel: 'Cloud Configuration',
      ruleCompliance: {
        label: 'of findings failed for rule',
      },
      ruleSelector: {
        instructions: 'Select a rule',
      },
      totalFindings: {
        _label: 'Manage Findings',
      },
      viewExceptedRules: 'View Excepted Rules',
      viewFailedRules: 'View Failed Rules',
      viewFindings: 'View All Failed Findings',
      viewRuleDetails: 'View Rule Details',
    },
    divvyEntityColumns: {
      common: {
        account: 'Account',
        accountId: 'Account Id',
        cloud: 'Cloud',
        id: 'Resource ID',
        namespaceId: 'Namespace ID',
        region: 'Region',
        resourceName: 'Resource Name',
        resourceType: 'Resource Type',
        tags: 'Tags',
      },
      name: 'Name',
    },
    entity: {
      accesslistflowlog: 'Access List Flow Log',
      airflowenvironment: 'Air Flow Environment',
      apiaccountingconfig: 'API Accounting Config',
      appserver: 'App Server',
      autoscalinggroup: 'Auto Scaling Group',
      autoscalinglaunchconfiguration: 'Auto Scaling Launch Configuration',
      'aws-account': 'Account',
      'aws-account-region': 'Account Region',
      'aws-acm-certificate': 'ACM Certificate',
      'aws-alb': 'Application Load Balancer',
      'aws-alb-ssl-policy': 'ALB SSL Policy',
      'aws-alb-target-group': 'ALB Target Group',
      'aws-auto-scaling-group': 'Auto Scaling Group',
      'aws-cloud-trail-trail': 'CloudTrail Trail',
      'aws-cloud-watch-alarm': 'CloudWatch Alarm',
      'aws-cloud-watch-log-group': 'CloudWatch Log Group',
      'aws-config-recorder': 'Config Recorder',
      'aws-ec2-ebs-volume': 'EBS Volume',
      'aws-ec2-egress-only-gateway': 'Egress Only Gateway',
      'aws-ec2-elastic-ip-address': 'Elastic IP',
      'aws-ec2-flow-log': 'VPC Flow Log',
      'aws-ec2-image': 'EC2 Image',
      'aws-ec2-instance': 'EC2 Instance',
      'aws-ec2-internet-gateway': 'Internet Gateway',
      'aws-ec2-nat-gateway': 'NAT Gateway',
      'aws-ec2-network-acl': 'Network ACL',
      'aws-ec2-route-table': 'Route Table',
      'aws-ec2-security-group': 'Security Group',
      'aws-ec2-vpc': 'VPC',
      'aws-ecr-repository': 'ECR Repository',
      'aws-elasti-cache-cluster': 'Elasti Cache Cluster',
      'aws-elasti-cache-replication-group': 'Elasti Cache Replication Group',
      'aws-elb': 'Elastic Load Balancer',
      'aws-iam-account-summary': 'IAM Account Summary',
      'aws-iam-credential-report': 'IAM Credential Report',
      'aws-iam-group': 'IAM Group',
      'aws-iam-instance-profile': 'Instance Profile',
      'aws-iam-managed-policy': 'IAM Policy',
      'aws-iam-password-policy': 'Password Policy',
      'aws-iam-role': 'IAM Role',
      'aws-iam-saml-provider': 'SAML Provider',
      'aws-iam-server-certificate': 'IAM Certificate',
      'aws-iam-user': 'IAM User',
      'aws-kms-key': 'KMS Key',
      'aws-lambda-function': 'Lambda Function',
      'aws-lambda-layer': 'Lambda Layer',
      'aws-rds-cluster': 'RDS Cluster',
      'aws-rds-cluster-snapshot': 'RDS Cluster Snapshot',
      'aws-rds-event-subscription': 'Event Subscription',
      'aws-rds-instance': 'RDS Instance',
      'aws-rds-instance-snapshot': 'RDS Instance Snapshot',
      'aws-redshift-cluster': 'Redshift Cluster',
      'aws-redshift-cluster-security-group': 'Redshift Cluster Security Group',
      'aws-redshift-subnet-group': 'Redshift Subnet Group',
      'aws-route53-hosted-zone': 'Route 53 Hosted Zone',
      'aws-s3-bucket': 'S3 Bucket',
      'aws-sns-topic': 'SNS Topic',
      'aws-sqs-queue': 'SQS Queue',
      'aws-step-functions-state-machine': 'Step Functions State Machine',
      bigdatainstance: 'Big Data Instance',
      containerregistry: 'Container Registry',
      dbcluster: 'DB Cluster',
      dbinstance: 'DB Instance',
      dbsnapshot: 'DB Snapshot',
      divvyorganizationservice: 'Cloud Account',
      dnszone: 'DNS Zone',
      instance: 'Instance',
      mapreducecluster: 'Map Reduce Cluster',
      mcinstance: 'Cache Instance',
      messagequeue: 'Message Queue',
      natgateway: 'Nat Gateway',
      networkflowlog: 'Network Flow Log',
      notificationsubscription: 'Notification Subscription',
      notificationtopic: 'Notification Topic',
      privateimage: 'Private Image',
      privatenetwork: 'Network',
      privatesubnet: 'Private Subnet',
      publicip: 'Public IP',
      resourceaccesslist: 'Access List',
      serverlessfunction: 'Lambda',
      servicealarm: 'Service Alarm',
      servicecertificate: 'Service Certificate',
      serviceencryptionkey: 'Encryption Key',
      servicegroup: 'Service Group',
      servicepolicy: 'Service Policy',
      serviceregion: 'Service Region',
      servicerole: 'Service Role',
      serviceuser: 'Service User',
      snapshot: 'Snapshot',
      storagecontainer: 'Storage Container',
      volume: 'Volume',
    },
    entityAttributeColumns: {
      'aws-acm-certificate': {
        inUseBy: {
          resourceId: '$t(infrastructure.entity.aws-acm-certificate) $t(name)',
        },
        keyUsage: {
          keyUsage: 'Key Usage',
        },
        subjectAlternateNames: {
          subjectAlternateNames: 'Subject Alternate Names',
        },
      },
      'aws-alb': {
        availabilityZones: {
          availabilityZones: 'Availability Zones',
        },
        listeners: {
          certificates: 'Certificates',
          id: '$t(infrastructure.entity.aws-alb) $t(name)',
          port: 'Port',
          protocol: 'Protocol',
          sslPolicy: 'SSL Policy',
        },
        securityGroups: {
          resourceId: '$t(infrastructure.entity.aws-alb) $t(name)',
        },
        subnets: {
          resourceId: '$t(infrastructure.entity.aws-alb) $t(name)',
        },
      },
      'aws-alb-ssl-policy': {
        ciphers: {
          ciphers: 'Ciphers',
        },
        protocols: {
          protocols: 'Protocols',
        },
      },
      'aws-alb-target-group': {
        loadBalancers: {
          resourceId: '$t(infrastructure.entity.aws-alb-target-group) $t(name)',
        },
      },
      'aws-auto-scaling-group': {
        availabilityZones: {
          availabilityZones: 'Availability Zones',
        },
        instances: {
          availabilityZone: 'Availability Zone',
          healthStatus: 'Health Status',
          id: '$t(infrastructure.entity.aws-auto-scaling-group) $t(name)',
          launchConfiguration: 'Launch Configuration',
          launchTemplate: 'Launch Template',
          lifecycleState: 'Lifecycle State',
          protectedFromScaleIn: 'Protected From Scale In',
        },
        loadBalancers: {
          id: '$t(infrastructure.entity.aws-auto-scaling-group) $t(name)',
          state: 'State',
        },
        metrics: {
          granularity: 'Granularity',
          metricCode: 'Metric Code',
        },
        subnetIds: {
          resourceId:
            '$t(infrastructure.entity.aws-auto-scaling-group) $t(name)',
        },
        suspendedProcesses: {
          name: 'Name',
          reason: 'Reason',
        },
        targetGroupArns: {
          resourceId:
            '$t(infrastructure.entity.aws-auto-scaling-group) $t(name)',
        },
        terminationPolicies: {
          terminationPolicies: 'Termination Policies',
        },
      },
      'aws-cloud-trail-trail': {
        snsTopic: {
          resourceId:
            '$t(infrastructure.entity.aws-cloud-trail-trail) $t(name)',
        },
      },
      'aws-cloud-watch-alarm': {
        alarmActions: {
          alarmActions: 'Alarm Actions',
        },
        dimensions: {
          name: 'Name',
          value: 'Value',
        },
      },
      'aws-cloud-watch-log-group': {
        filters: {
          creationTime: 'Creation Time',
          filterName: 'Filter Name',
          filterPattern: 'Filter Pattern',
          metrics: 'Metrics',
        },
      },
      'aws-ec2-ebs-volume': {
        attachments: {
          attachTime: 'Attach Time',
          deleteOnTermination: 'Delete On Termination',
          device: 'Device',
          instanceId: 'Instance ID',
          state: 'State',
        },
      },
      'aws-ec2-instance': {
        ebsMappings: {
          id: 'ID',
          volume: 'Volume',
        },
        networkInterfaces: {
          id: '$t(infrastructure.entity.aws-ec2-instance) $t(name)',
          mac: 'MAC',
          privateDns: 'Private DNS',
          privateIp: 'Private IP',
          securityGroups: 'Security Groups',
          status: 'Status',
          subnet: 'Subnet',
          vpc: 'VPC',
        },
        securityGroups: {
          resourceId: '$t(infrastructure.entity.aws-ec2-instance) $t(name)',
        },
      },
      'aws-ec2-internet-gateway': {
        attachments: {
          state: 'State',
          vpcId: 'VPC ID',
        },
      },
      'aws-ec2-nat-gateway': {
        addresses: {
          elasticIpAllocation: 'Elastic IP Allocation',
          networkInterfaceId: 'Network Interface ID',
          privateIp: 'Private IP',
          publicIp: 'Public IP',
        },
      },
      'aws-ec2-network-acl': {
        associations: {
          associationId: 'Association ID',
          networkId: 'Network ID',
          subnetId: 'Subnet ID',
        },
        entries: {
          ipv4CidrBlock: 'IPV 4 CIDR Block',
          ipv6CidrBlock: 'IPV 6 CIDR Block',
          isEgress: 'IS Egress',
          portRange: 'Port Range',
          protocol: 'Protocol',
          ruleAction: 'Rule Action',
          ruleNumber: 'Rule Number',
          typeCode: 'Type Code',
        },
      },
      'aws-ec2-route-table': {
        routes: {
          cidrBlock: 'CIDR Block',
          egressOnlyGateway: 'Egress Only Gateway',
          gatewayId: 'Gateway ID',
          ipv6CidrBlock: 'IPV 6 CIDR Block',
          natGatewayId: 'NAT Gateway ID',
          natInstanceId: 'NAT Instance ID',
          natOwnerAccount: 'NAT Owner Account',
          networkInterfaceId: 'Network Interface ID',
          origin: 'Origin',
          peeringConnection: 'Peering Connection',
          servicePrefix: 'Service Prefix',
          state: 'State',
        },
        subnets: {
          resourceId: '$t(infrastructure.entity.aws-ec2-route-table) $t(name)',
        },
      },
      'aws-ec2-security-group': {
        egressRules: {
          allProtocols: 'All Protocols',
          description: 'Description',
          from: 'From',
          protocol: 'Protocol',
          securityGroup: 'Destination',
          to: 'To',
        },
        ingressRules: {
          allProtocols: 'All Protocols',
          description: 'Description',
          from: 'From',
          protocol: 'Protocol',
          securityGroup: 'Source',
          to: 'To',
        },
      },
      'aws-ec2-vpc': {
        ipv4: {
          associationId: 'Association ID',
          blockState: 'Block State',
          cidrBlock: 'CIDR Block',
        },
      },
      'aws-elb': {
        availabilityZones: {
          availabilityZones: 'Availability Zones',
        },
        instances: {
          resourceId: '$t(infrastructure.entity.aws-elb) $t(name)',
        },
        listenerPolicies: {
          certificate: 'Certificate',
          instancePort: 'Instance Port',
          instanceProtocol: 'Instance Protocol',
          loadBalancerPort: 'Load Balancer Port',
          loadBalancerProtocol: 'Load Balancer Protocol',
          policies: 'Policies',
        },
        policies: {
          attributes: 'Attributes',
          id: 'ID',
          type: 'Type',
        },
        securityGroups: {
          resourceId: '$t(infrastructure.entity.aws-elb) $t(name)',
        },
        subnets: {
          resourceId: '$t(infrastructure.entity.aws-elb) $t(name)',
        },
      },
      'aws-iam-credential-report': {
        users: {
          accessKey1: 'Access KEY 1',
          accessKey2: 'Access KEY 2',
          cert1: 'Cert 1',
          cert2: 'Cert 2',
          created: 'Created',
          id: '$t(infrastructure.entity.aws-iam-credential-report) $t(name)',
          mfaActive: 'MFA Active',
          name: 'Name',
          passwordEnabled: 'Password Enabled',
          passwordLastChanged: 'Password Last Changed',
          passwordLastUsed: 'Password Last Used',
          passwordNextRotation: 'Password Next Rotation',
        },
      },
      'aws-iam-group': {
        inlinePolicies: {
          name: 'Name',
          statements: 'Statements',
          version: 'Version',
        },
      },
      'aws-iam-instance-profile': {
        roles: {
          resourceId:
            '$t(infrastructure.entity.aws-iam-instance-profile) $t(name)',
        },
      },
      'aws-iam-managed-policy': {
        statements: {
          actions: 'Actions',
          conditions: 'Conditions',
          effect: 'Effect',
          id: 'ID',
          principals: 'Principals',
          resources: 'Resources',
        },
      },
      'aws-iam-user': {
        accessKeys: {
          created: 'Created',
          id: '$t(infrastructure.entity.aws-iam-user) $t(name)',
          lastUsed: 'Last Used',
          lastUsedRegion: 'Last Used Region',
          lastUsedService: 'Last Used Service',
          status: 'Status',
        },
      },
      'aws-kms-key': {
        policies: {
          name: 'Name',
          statements: 'Statements',
          version: 'Version',
        },
      },
      'aws-rds-cluster': {
        availabilityZones: {
          availabilityZones: 'Availability Zones',
        },
        members: {
          canWrite: 'Can Write',
          id: '$t(infrastructure.entity.aws-rds-cluster) $t(name)',
          promotionTier: 'Promotion Tier',
        },
        vpcSecurityGroups: {
          id: '$t(infrastructure.entity.aws-rds-cluster) $t(name)',
          status: 'Status',
        },
      },
      'aws-rds-cluster-snapshot': {
        attributes: {
          name: 'Name',
          values: 'Values',
        },
        availabilityZones: {
          availabilityZones: 'Availability Zones',
        },
      },
      'aws-rds-instance': {
        vpcSecurityGroups: {
          id: '$t(infrastructure.entity.aws-rds-instance) $t(name)',
          status: 'Status',
        },
      },
      'aws-rds-instance-snapshot': {
        attributes: {
          name: 'Name',
          values: 'Values',
        },
      },
      'aws-route53-hosted-zone': {
        recordSets: {
          aliasTarget: 'Alias Target',
          domainName: 'Domain Name',
          failover: 'Failover',
          geoLocation: 'Geo Location',
          healthCheckId: 'Health Check ID',
          identifier: 'Identifier',
          multivalueAnswer: 'Multivalue Answer',
          region: 'Region',
          resourceRecordList: 'Resource Record List',
          trafficPolicyId: 'Traffic Policy ID',
          ttl: 'TTL',
          type: 'Type',
          weight: 'Weight',
        },
        vpcs: {
          id: '$t(infrastructure.entity.aws-route53-hosted-zone) $t(name)',
          region: 'Region',
        },
      },
      'aws-s3-bucket': {
        acl: {
          grants: {
            id: 'ID',
            permission: 'Permission',
            type: 'Type',
          },
        },
        cors: {
          allowedHeaders: 'Allowed Headers',
          allowedMethods: 'Allowed Methods',
          allowedOrigins: 'Allowed Origins',
          exposedHeaders: 'Exposed Headers',
          id: 'ID',
          maxAge: 'Max Age',
        },
        sse: {
          algorithm: 'Algorithm',
          kmsKey: 'KMS Key',
        },
      },
      'aws-sns-topic': {
        subscriptions: {
          endpoint: 'Endpoint',
          id: '$t(infrastructure.entity.aws-sns-topic) $t(name)',
          owner: 'Owner',
          protocol: 'Protocol',
        },
      },
    },
    entityColumns: {
      'aws-account': {
        id: '$t(infrastructure.entity.aws-account) $t(id)',
      },
      'aws-account-region': {
        id: '$t(infrastructure.entity.aws-account-region) $t(id)',
      },
      'aws-acm-certificate': {
        authority: 'Authority',
        created: 'Created',
        domainName: 'Domain Name',
        id: '$t(infrastructure.entity.aws-acm-certificate) $t(name)',
        inUseBy: 'In Use By',
        issued: 'Issued',
        issuer: 'Issuer',
        keyAlgorithm: 'Key Algorithm',
        keyUsage: 'Key Usage',
        notValidAfter: 'Not Valid After',
        notValidBefore: 'Not Valid Before',
        renewable: 'Renewable',
        revoked: 'Revoked',
        serial: 'Serial',
        signatureAlgorithm: 'Signature Algorithm',
        status: 'Status',
        subject: 'Subject',
        subjectAlternateNames: 'Subject Alternate Names',
        tags: 'Tags',
        type: 'Type',
      },
      'aws-alb': {
        accessLogSettings: 'Access LOG Settings',
        addressType: 'Address Type',
        availabilityZones: 'Availability Zones',
        created: 'Created',
        crossZoneSettings: 'Cross Zone Settings',
        dns: 'DNS',
        hostedZoneId: 'Hosted Zone ID',
        id: '$t(infrastructure.entity.aws-alb) $t(name)',
        listeners: 'Listeners',
        scheme: 'Scheme',
        securityGroups: 'Security Groups',
        state: 'State',
        stateReason: 'State Reason',
        subnets: 'Subnets',
        tags: 'Tags',
        type: 'Type',
        vpc: 'VPC',
      },
      'aws-alb-ssl-policy': {
        ciphers: 'Ciphers',
        id: '$t(infrastructure.entity.aws-alb-ssl-policy) $t(name)',
        protocols: 'Protocols',
      },
      'aws-alb-target-group': {
        healthCheckInterval: 'Health Check Interval',
        healthCheckPath: 'Health Check Path',
        healthCheckPort: 'Health Check Port',
        healthCheckProtocol: 'Health Check Protocol',
        healthCheckTimeout: 'Health Check Timeout',
        id: '$t(infrastructure.entity.aws-alb-target-group) $t(name)',
        loadBalancers: 'Load Balancers',
        matcherCodes: 'Matcher Codes',
        name: 'Name',
        port: 'Port',
        protocol: 'Protocol',
        thresholdCount: 'Threshold Count',
        type: 'Type',
        unhealthyThresholdCount: 'Unhealthy Threshold Count',
        vpcId: 'VPC ID',
      },
      'aws-auto-scaling-group': {
        availabilityZones: 'Availability Zones',
        created: 'Created',
        defaultCooldown: 'Default Cooldown',
        desiredCapacity: 'Desired Capacity',
        healthCheckGracePeriod: 'Health Check Grace Period',
        healthCheckType: 'Health Check Type',
        id: '$t(infrastructure.entity.aws-auto-scaling-group) $t(name)',
        instances: 'Instances',
        launchConfiguration: 'Launch Configuration',
        launchTemplate: 'Launch Template',
        loadBalancers: 'Load Balancers',
        maxSize: 'Max Size',
        metrics: 'Metrics',
        minSize: 'Min Size',
        name: 'Name',
        placementGroup: 'Placement Group',
        protectedFromScaleIn: 'Protected From Scale In',
        serviceRole: 'Service Role',
        subnetIds: 'Subnet IDs',
        suspendedProcesses: 'Suspended Processes',
        tags: 'Tags',
        targetGroupArns: 'Target Group Arns',
        terminationPolicies: 'Termination Policies',
      },
      'aws-cloud-trail-trail': {
        bucketId: 'Bucket ID',
        customEventSelectors: 'Custom Event Selectors',
        group: 'Group',
        homeRegion: 'Home Region',
        id: '$t(infrastructure.entity.aws-cloud-trail-trail) $t(name)',
        includeGlobalServiceEvents: 'Include Global Service Events',
        kmsKey: 'KMS Key',
        logFileValidationEnabled: 'Log File Validation Enabled',
        loggingEnabled: 'Logging Enabled',
        multiRegional: 'Multi Regional',
        role: 'Role',
        s3KeyPrefix: 'S3 Key Prefix',
        selectors: 'Selectors',
        snsTopic: 'SNS Topic',
      },
      'aws-cloud-watch-alarm': {
        actionsEnabled: 'Actions Enabled',
        alarmActions: 'Alarm Actions',
        datapointsForAlarm: 'Datapoints For Alarm',
        description: 'Description',
        dimensions: 'Dimensions',
        id: '$t(infrastructure.entity.aws-cloud-watch-alarm) $t(name)',
        metricName: 'Metric Name',
        name: 'Name',
      },
      'aws-cloud-watch-log-group': {
        filters: 'Filters',
        id: '$t(infrastructure.entity.aws-cloud-watch-log-group) $t(name)',
        keyId: 'Key ID',
      },
      'aws-config-recorder': {
        id: '$t(name)',
        keyId: 'Key ID',
      },
      'aws-ec2-ami': {
        creationDate: 'Creation Date',
        description: 'Description',
        enaSupport: 'ENA Support',
        hypervisor: 'Hypervisor',
        id: '$t(infrastructure.entity.aws-ec2-image) Id',
        kernel: 'Kernel',
        location: 'Location',
        name: 'Name',
        platform: 'Platform',
        publicAccess: 'Public Access',
        ramDiskId: 'Ram Disk Id',
        rootDeviceName: 'Root Device Name',
        rootDeviceType: 'Root Device Type',
        sriovNetSupport: 'SRIOV Net Support',
        state: 'State',
        tags: 'Tags',
        type: 'Type',
        virtualizationType: 'Virtualization Type',
      },
      'aws-ec2-ebs-volume': {
        attachments: 'Attachments',
        created: 'Created',
        encrypted: 'Encrypted',
        id: '$t(infrastructure.entity.aws-ec2-ebs-volume) $t(name)',
        iops: 'IOPS',
        kmsKey: 'KMS Key',
        modification: 'Modification',
        size: 'Size',
        snapshot: 'Snapshot',
        state: 'State',
        statusItem: 'Status Item',
        tags: 'Tags',
        type: 'Type',
        zone: 'Zone',
      },
      'aws-ec2-egress-only-gateway': {},
      'aws-ec2-elastic-ip-address': {
        associationId: 'Association ID',
        domain: 'Domain',
        id: '$t(infrastructure.entity.aws-ec2-elastic-ip-address) $t(name)',
        instanceId: 'Instance ID',
        networkInterfaceId: 'Network Interface ID',
        privateAddress: 'Private Address',
        publicAddress: 'Public Address',
        tags: 'Tags',
      },
      'aws-ec2-flow-log': {
        created: 'Created',
        deliverErrorMessage: 'Deliver Error Message',
        deliverRole: 'Deliver Role',
        destination: 'Destination',
        destinationType: 'Destination Type',
        id: '$t(infrastructure.entity.aws-ec2-flow-log) $t(name)',
        logGroup: 'Log Group',
        resourceId: 'Resource ID',
        trafficType: 'Traffic Type',
      },
      'aws-ec2-instance': {
        architecture: 'Architecture',
        availabilityZone: 'Availability Zone',
        ebsMappings: 'EBS Mappings',
        id: '$t(infrastructure.entity.aws-ec2-instance) $t(name)',
        imageId: 'Image ID',
        kernelId: 'Kernel ID',
        launched: 'Launched',
        networkInterfaces: 'Network Interfaces',
        privateDns: 'Private DNS',
        privateIp: 'Private IP',
        profile: 'Profile',
        publicDns: 'Public DNS',
        publicIp: 'Public IP',
        rootDeviceName: 'Root Device Name',
        rootDeviceType: 'Root Device Type',
        securityGroups: 'Security Groups',
        state: 'State',
        stateReason: 'State Reason',
        stateReasonCode: 'State Reason Code',
        subnet: 'Subnet',
        tags: 'Tags',
        type: 'Type',
        vpc: 'VPC',
      },
      'aws-ec2-internet-gateway': {
        attachments: 'Attachments',
        id: '$t(infrastructure.entity.aws-ec2-internet-gateway) $t(name)',
        tags: 'Tags',
      },
      'aws-ec2-nat-gateway': {
        addresses: 'Addresses',
        bandwidth: 'Bandwidth',
        createTime: 'Create Time',
        deleteTime: 'Delete Time',
        failureCode: 'Failure Code',
        failureMessage: 'Failure Message',
        id: '$t(infrastructure.entity.aws-ec2-nat-gateway) $t(name)',
        state: 'State',
        subnet: 'Subnet',
        tags: 'Tags',
        vpc: 'VPC',
      },
      'aws-ec2-network-acl': {
        associations: 'Associations',
        default: 'Default',
        entries: 'Entries',
        id: '$t(infrastructure.entity.aws-ec2-network-acl) $t(name)',
        tags: 'Tags',
        vpcId: 'VPC ID',
      },
      'aws-ec2-route-table': {
        gatewayIds: 'Gateway IDS',
        id: '$t(infrastructure.entity.aws-ec2-route-table) $t(name)',
        main: 'Main',
        routes: 'Routes',
        subnets: 'Subnets',
        tags: 'Tags',
        vpcId: 'VPC ID',
      },
      'aws-ec2-security-group': {
        description: 'Description',
        egressRules: 'Egress Rules',
        id: '$t(infrastructure.entity.aws-ec2-security-group) $t(name)',
        ingressRules: 'Ingress Rules',
        name: 'Name',
        owner: 'Owner',
        tags: 'Tags',
        vpc: 'VPC',
      },
      'aws-ec2-vpc': {
        dcpOptions: 'DCP Options',
        default: 'Default',
        enableDnsHostnames: 'Enable DNS Hostnames',
        enableDnsSupport: 'Enable DNS Support',
        id: '$t(infrastructure.entity.aws-ec2-vpc) $t(name)',
        ipv4: 'IPV 4',
        ipv6: 'IPV 6',
        primaryCidrBlock: 'Primary Cidr Block',
        state: 'State',
        supportsClassicLink: 'Supports Classic Link',
        supportsClassicLinkDns: 'Supports Classic Link DNS',
        tags: 'Tags',
        tenancy: 'Tenancy',
      },
      'aws-ecr-repository': {
        createdAt: 'Created AT',
        id: '$t(infrastructure.entity.aws-ecr-repository) $t(name)',
        lifecyclePolicy: 'Lifecycle Policy',
        lifecyclePolicyLastEvaluated: 'Lifecycle Policy Last Evaluated',
        policy: 'Policy',
        registryId: 'Registry ID',
        repositoryPolicy: 'Repository Policy',
        repositoryUri: 'Repository URI',
      },
      'aws-elasti-cache-cluster': {
        authTokenEnabled: 'Auth Token Enabled',
        autoMinorVersionUpgrade: 'Auto Minor Version Upgrade',
        cacheSecurityGroups: 'Cache Security Groups',
        clientDownloadLandingPage: 'Client Download Landing Page',
        clusterStatus: 'Cluster Status',
        endpoint: 'Endpoint',
        engine: 'Engine',
        engineVersion: 'EngineVersion',
        id: '$t(name)',
        nodeCount: 'Node Count',
        nodeType: 'Node Type',
        nodes: 'Nodes',
        notificationStatus: 'Notification Status',
        notificationTopic: 'Notification Topic',
        parameterGroup: 'Parameter Group',
        preferredMaintenanceWindow: 'Preferred Maintenance Window',
        preferredZone: 'Preferred Zone',
        replicationGroup: 'Replication Group',
        restEncryptionEnabled: 'Rest Encryption Enabled',
        snapshotRetentionLimit: 'Snapshot Retention Limit',
        snapshotWindow: 'Snapshot Window',
        subnetGroupName: 'Subnet Group Name',
        transitEncryptionEnabled: 'Transit Encryption Enabled',
        vpcSecurityGroups: 'VPC Security Groups',
      },
      'aws-elasti-cache-replication-group': {
        atRestEncryptionEnabled: 'At Rest Encryption Enabled',
        authTokenEnabled: 'Auth Token Enabled',
        automaticFailover: 'Automatic Failover',
        cacheNodeType: 'Cache Node Type',
        clusterEnabled: 'Cluster Enabled',
        configurationEndpoint: 'Configuration Endpoint',
        description: 'Description',
        id: '$t(name)',
        memberClusters: 'Member Clusters',
        nodeGroups: 'Node Groups',
        preferredZone: 'Preferred Zone',
        retention: 'Retention',
        snapshotCluster: 'Snapshot Cluster',
        snapshotWindow: 'Snapshot Window',
        status: 'Status',
        transitEncryptionEnabled: 'Transit Encryption Enabled',
      },
      'aws-elb': {
        accessLogSettings: 'Access LOG Settings',
        availabilityZones: 'Availability Zones',
        created: 'Created',
        crossZoneSettings: 'Cross Zone Settings',
        dns: 'DNS',
        id: '$t(infrastructure.entity.aws-elb) $t(name)',
        instances: 'Instances',
        listenerPolicies: 'Listener Policies',
        policies: 'Policies',
        scheme: 'Scheme',
        securityGroups: 'Security Groups',
        sourceSecurityGroupAccountId: 'Source Security Group Account ID',
        sourceSecurityGroupName: 'Source Security Group Name',
        subnets: 'Subnets',
        tags: 'Tags',
        type: 'Type',
        vpc: 'VPC',
      },
      'aws-iam-account-summary': {
        accountAccessKeysPresent: 'Account Access Keys Present',
        accountSigningCertificatesPresent:
          'Account Signing Certificates Present',
        groups: 'Groups',
        id: '$t(name)',
        instanceProfiles: 'Instance Profiles',
        mfaDevices: 'MFA Devices',
        mfaDevicesInUse: 'MFA Devices In Use',
        mfaEnabled: 'MFA Enabled',
        policies: 'Policies',
        policyVersionsInUse: 'Policy Versions In Use',
        providers: 'Providers',
        quotas: 'Quotas',
        roles: 'Roles',
        serverCertificates: 'ServerCertificates',
        users: 'Users',
      },
      'aws-iam-credential-report': {
        generated: 'Generated',
        id: '$t(infrastructure.entity.aws-iam-credential-report) $t(name)',
        users: 'Users',
      },
      'aws-iam-group': {
        created: 'Created',
        id: '$t(infrastructure.entity.aws-iam-group) $t(name)',
        identifier: 'Identifier',
        inlinePolicies: 'Inline Policies',
        managedPolicies: 'Managed Policies',
        name: 'Name',
        path: 'Path',
        permissionsBoundary: 'Permissions Boundary',
      },
      'aws-iam-instance-profile': {
        created: 'Created',
        id: '$t(infrastructure.entity.aws-iam-instance-profile) $t(name)',
        identifier: 'Identifier',
        name: 'Name',
        path: 'Path',
        roles: 'Roles',
      },
      'aws-iam-managed-policy': {
        id: '$t(infrastructure.entity.aws-iam-managed-policy) $t(name)',
        statements: 'Statements',
        version: 'Version',
      },
      'aws-iam-password-policy': {
        allowUsersToChangePassword: 'Allow Users To Change Password',
        expirationRequiresAdministratorReset:
          'Expiration Requires Administrator Reset',
        expirePasswords: 'Expire Passwords',
        id: '$t(infrastructure.entity.aws-iam-password-policy) $t(name)',
        maximumPasswordAge: 'Maximum Password Age',
        minimumPasswordLength: 'Minimum Password Length',
        requireLowercaseCharacters: 'Require Lowercase Characters',
        requireNumbers: 'Require Numbers',
        requireSymbols: 'Require Symbols',
        requireUppercaseCharacters: 'Require Uppercase Characters',
        reusePrevention: 'Reuse Prevention',
      },
      'aws-iam-role': {
        assumeRolePolicy: 'Assume Role Policy',
        created: 'Created',
        description: 'Description',
        id: '$t(infrastructure.entity.aws-iam-role) $t(name)',
        identifier: 'Identifier',
        inlinePolicies: 'Inline Policies',
        managedPolicies: 'Managed Policies',
        name: 'Name',
        path: 'Path',
        permissionsBoundary: 'Permissions Boundary',
        policy: 'Policy',
        sessionDuration: 'Session Duration',
      },
      'aws-iam-saml-provider': {
        created: 'Created',
        expires: 'Expires',
        id: '$t(infrastructure.entity.aws-iam-saml-provider) $t(name)',
      },
      'aws-iam-server-certificate': {
        body: 'Body',
        certificateChain: 'Certificate Chain',
        certificateId: 'Certificate ID',
        expiration: 'Expiration',
        id: '$t(infrastructure.entity.aws-iam-server-certificate) $t(name)',
        path: 'Path',
        uploaded: 'Uploaded',
      },
      'aws-iam-user': {
        accessKeys: 'Access Keys',
        created: 'Created',
        groups: 'Groups',
        id: '$t(infrastructure.entity.aws-iam-user) $t(name)',
        identifier: 'Identifier',
        inlinePolicies: 'Inline Policies',
        loginProfileCreated: 'Login Profile Created',
        managedPolicies: 'Managed Policies',
        mfaDevices: 'MFA Devices',
        name: 'Name',
        passwordLastUsed: 'Password Last Used',
        passwordResetRequired: 'Password Reset Required',
        path: 'Path',
        permissionsBoundary: 'Permissions Boundary',
        signingCertificates: 'Signing Certificates',
        uniqueId: 'Unique ID',
      },
      'aws-kms-key': {
        created: 'Created',
        description: 'Description',
        enabled: 'Enabled',
        id: '$t(infrastructure.entity.aws-kms-key) $t(name)',
        manager: 'Manager',
        origin: 'Origin',
        policy: 'Policy',
        rotationEnabled: 'Rotation Enabled',
        state: 'State',
      },
      'aws-lambda-function': {
        deadLetterTarget: 'Dead Target Letter',
        description: 'Description',
        id: '$t(infrastructure.entity.aws-lambda-function) $t(name)',
        lastModified: 'Last Modified',
        memorySize: 'Memory Size',
        policy: 'Policy',
        role: 'Role',
        runtimeEnvironment: 'Runtime Env',
        tags: 'Tags',
      },
      'aws-lambda-layer': {
        id: '$t(infrastructure.entity.aws-lambda-function) $t(name)',
      },
      'aws-rds-cluster': {
        availabilityZones: 'Availability Zones',
        clusterPort: 'Cluster Port',
        clusterRoles: 'Cluster Roles',
        created: 'Created',
        encrypted: 'Encrypted',
        endpoint: 'Endpoint',
        engineName: 'Engine Name',
        engineVersion: 'Engine Version',
        hostedZoneId: 'Hosted Zone ID',
        iamAuthenticationEnabled: 'IAM Authentication Enabled',
        id: '$t(infrastructure.entity.aws-rds-cluster) $t(name)',
        kmsKey: 'KMS Key',
        members: 'Members',
        subnetGroup: 'Subnet Group',
        username: 'Username',
        vpcSecurityGroups: 'VPC Security Groups',
      },
      'aws-rds-cluster-snapshot': {
        allocatedStorage: 'Allocated Storage',
        attributes: 'Attributes',
        availabilityZones: 'Availability Zones',
        clusterCreated: 'Cluster Created',
        engine: 'Engine',
        engineVersion: 'Engine Version',
        iamDatabaseAuthEnabled: 'IAM Database Auth Enabled',
        id: '$t(infrastructure.entity.aws-rds-cluster-snapshot) $t(name)',
        kmsKey: 'KMS Key',
        licenseModel: 'License Model',
        originalCluster: 'Original Cluster',
        port: 'Port',
        snapshotCreated: 'Snapshot Created',
        snapshotType: 'Snapshot Type',
        sourceSnapshot: 'Source Snapshot',
        storageEncrypted: 'Storage Encrypted',
        username: 'Username',
        vpcId: 'VPC ID',
      },
      'aws-rds-event-subscription': {
        createTime: 'Create Time',
        customerId: 'Customer ID',
        enabled: 'Enabled',
        eventCategories: 'Event Categories',
        id: '$t(infrastructure.entity.aws-rds-event-subscription) $t(name)',
        snsTopic: 'SNS Topic',
        sourceIds: 'Source IDS',
        sourceType: 'Source Type',
        status: 'Status',
      },
      'aws-rds-instance': {
        autoMinorVersionUpgrade: 'Auto Minor Version Upgrade',
        availabilityZone: 'Availability Zone',
        backupRetentionPeriod: 'Backup Retention Period',
        certificateIdentifier: 'Certificate Identifier',
        created: 'Created',
        databaseName: 'Database Name',
        dbSecurityGroups: 'DB Security Groups',
        deletionProtection: 'Deletion Protection',
        endpoint: 'Endpoint',
        engineName: 'Engine Name',
        engineVersion: 'Engine Version',
        id: '$t(infrastructure.entity.aws-rds-instance) $t(name)',
        instancePort: 'Instance Port',
        kmsKey: 'KMS Key',
        masterUserName: 'Master User Name',
        multiAz: 'Multi AZ',
        publiclyAccessible: 'Publicly Accessible',
        resourceId: 'Resource ID',
        secondaryAvailabilityZone: 'Secondary Availability Zone',
        storageEncrypted: 'Storage Encrypted',
        subnetGroup: 'Subnet Group',
        tdeCredentialArn: 'TDE Credential ARN',
        vpcSecurityGroups: 'VPC Security Groups',
      },
      'aws-rds-instance-snapshot': {
        allocatedStorage: 'Allocated Storage',
        attributes: 'Attributes',
        availabilityZone: 'Availability Zone',
        encrypted: 'Encrypted',
        engine: 'Engine',
        engineVersion: 'Engine Version',
        iamDatabaseAuthEnabled: 'IAM Database Auth Enabled',
        id: '$t(infrastructure.entity.aws-rds-instance-snapshot) $t(name)',
        instanceCreated: 'Instance Created',
        iops: 'IOPS',
        kmsKey: 'KMS Key',
        licenseModel: 'License Model',
        optionGroup: 'Option Group',
        originalInstance: 'Original Instance',
        port: 'Port',
        snapshotCreated: 'Snapshot Created',
        snapshotType: 'Snapshot Type',
        sourceRegion: 'Source Region',
        sourceSnapshot: 'Source Snapshot',
        storageType: 'Storage Type',
        tdeCredential: 'TDE Credential',
        username: 'Username',
        vpcId: 'VPC ID',
      },
      'aws-redshift-cluster': {
        allowVersionUpgrade: 'Allow Version Upgrade',
        automatedSnapshotRetention: 'Automated Snapshot Retention',
        availabilityZone: 'Availability Zone',
        clusterSecurityGroups: 'Cluster Security Groups',
        clusterVersion: 'Cluster Version',
        createTime: 'Create Time',
        description: 'Description',
        elasticIp: 'Elastic IP',
        encrypted: 'Encrypted',
        endpoint: 'Endpoint',
        enhancedVpcRouting: 'Enhanced VPC Routing',
        hsmStatus: 'HSM Status',
        id: '$t(name)',
        keyId: 'Key ID',
        loggingBucket: 'Logging Bucket',
        loggingEnabled: 'Logging Enabled',
        maintenanceTrack: 'Maintenance Track',
        masterUsername: 'Master Username',
        nodeType: 'Node Type',
        nodes: 'Nodes',
        parameterGroups: 'Parameter Groups',
        preferredMaintenanceWindow: 'Preferred Maintenance Window',
        publicKey: 'Public Key',
        publiclyAccessible: 'Publicly Accessible',
        resizeOptions: 'Resize Options',
        revisionVersion: 'Revision Version',
        roles: 'Roles',
        status: 'Status',
        subnetGroupName: 'Subnet Group Name',
        tags: 'Tags',
        vpcId: 'VPC ID',
        vpcSecurityGroups: 'VPC Security Groups',
      },
      'aws-redshift-cluster-security-group': {
        description: 'Description',
        id: '$t(name)',
      },
      'aws-redshift-subnet-group': {
        description: 'Description',
        id: '$t(name)',
        status: 'Status',
        subnets: 'Subnets',
        tags: 'Tags',
        vpcId: 'VPC ID',
      },
      'aws-route53-hosted-zone': {
        callerReference: 'Caller Reference',
        config: 'Config',
        delegationSet: 'Delegation Set',
        domainName: 'Domain Name',
        id: '$t(infrastructure.entity.aws-route53-hosted-zone) $t(name)',
        linkedService: 'Linked Service',
        recordSetCount: 'Record Set Count',
        recordSets: 'Record Sets',
        tags: 'Tags',
        trafficPolicies: 'Traffic Policies',
        vpcs: 'VPCs',
      },
      'aws-s3-bucket': {
        acl: 'ACL',
        cors: 'Cors',
        created: 'Created',
        id: '$t(name)',
        owner: 'Owner',
        policy: 'Policy',
        requesterCharged: 'Requester Charged',
        sse: 'SSE',
        websiteConfiguration: 'Website Configuration',
      },
      'aws-sns-topic': {
        displayName: 'Display Name',
        id: '$t(infrastructure.entity.aws-sns-topic) $t(name)',
        owner: 'Owner',
        policy: 'Policy',
        subscriptions: 'Subscriptions',
        subscriptionsConfirmed: 'Subscriptions Confirmed',
        subscriptionsDeleted: 'Subscriptions Deleted',
        subscriptionsPending: 'Subscriptions Pending',
      },
      'aws-sqs-queue': {
        created: 'Created',
        deadLetterTarget: 'Dead Letter Target',
        id: '$t(infrastructure.entity.aws-sqs-queue) $t(name)',
        kmsKey: 'KMS Key',
        kmsReuse: 'KMS Reuse',
        maximumMessageSize: 'Maximum Message Size',
        maximumReceiveCount: 'Maximum Receive Count',
        modified: 'Modified',
        policy: 'Policy',
        receiveWaitTime: 'Receive Wait Time',
        retention: 'Retention',
        tags: 'Tags',
        url: 'URL',
        visibilityTimeout: 'Visibility Timeout',
      },
      'aws-step-functions-state-machine': {
        created: 'Created',
        definition: 'Definition',
        id:
          '$t(infrastructure.entity.aws-step-functions-state-machine) $t(name)',
        role: 'Role',
      },
      awsEntityFinding: {
        entity: {
          id: 'Resource',
          region: 'Region',
          service: 'Service',
        },
        finding: {
          actions: 'Actions',
          failedSince: 'Failing Since',
          firstEvaluated: 'First Evaluated',
          lastEvaluated: 'Last Confirmed',
          links: 'links',
          provider: 'Provider',
          results: 'Results',
          severity: {
            _label: 'Severity',
          },
          status: {
            _label: 'Status',
            excepted: 'Excepted',
            fail: 'Fail',
            'not-applicable': 'N/A',
            pass: 'Pass',
            passFormerFail: 'Pass (Former Fail)',
          },
        },
        rule: {
          _label: 'Rule',
          severity: 'Severity',
        },
      },
      awsServiceSummary: {
        accounts: 'Accounts',
        entities: 'Resources',
        name: 'Service',
        regionTotal: 'Regions',
        types: 'Types',
      },
      divvyServiceSummary: {
        categories: 'Categories',
        resources: 'Resources',
      },
    },
    entityDetails: {
      kpi: {
        failedRules: 'Failed Rules',
        passedRules: 'Passed Rules',
      },
    },
    exception: 'Exception',
    exceptionType: 'Exception Type',
    exceptions: {
      accounts: 'Accounts',
      comments: 'Comments',
      created: 'Created',
      details: 'Details',
      expiration: 'Expiration',
      findings: 'Findings',
      metrics: {
        titles: {
          custom: 'Custom',
          expiringThisTimePeriod: 'Expiring this __timePeriod__',
          global: 'Global',
          single: 'Single',
        },
      },
      modalDeleteWarning: 'Deleting this exception may take several minutes.',
      modalSaveWarning: 'Saving this exception may take several minutes.',
      regions: 'Regions',
      resourceId: 'Resource ID',
      rule: 'Rule',
      scope: 'Scope',
      title: 'Exceptions',
      type: 'Type',
      viewDetails: '__scope__ Exception',
    },
    exclusion: {
      acceptableRisk: 'Acceptable Risk',
      accounts: 'Accounts',
      cancel: 'Cancel',
      comment: 'Comment',
      compensatingControl: 'Compensating Control',
      confirmDelete: 'Are you sure you want to delete this exception?',
      createException: 'Create Exception',
      custom:
        'Custom Scope (Define scope for findings to be excluded in your AWS environment)',
      customKpiMessage: 'KPIs are unavailable until a custom scope is defined.',
      date: 'Date: __date__',
      deleteError: 'An error occurred while deleting the exception.',
      deleteModalTitle: '$t(deleteModal.title) and may take several minutes',
      endOfMonth: 'End Of Month',
      endOfQuarter: 'End Of Quarter',
      endOfYear: 'End Of Year',
      entities: 'Entities',
      entityId: 'Entity ID',
      exceptionImpact: 'Potential Exception Impact',
      excludedFindings: 'Excluded Findings',
      expiration: 'Expiration',
      expire: 'Expire',
      falsePositive: 'False Positive',
      global: 'Global',
      globalException: 'Exclude this Rule: __name__',
      key: 'Key',
      kpiData: {
        Account: 'Accounts',
        Findings: 'Excluded Findings',
        Region: 'Regions',
      },
      kpiMessage: 'KPIs are unavailable until a scope is defined.',
      never: 'Never',
      oneMonth: 'One Month',
      oneWeek: 'One Week',
      oneYear: 'One Year',
      other: 'Other',
      path: 'Path',
      reason: 'Reason',
      regions: 'Regions',
      removeAll: 'Remove All',
      resource: 'Resource ID',
      resourceHelp:
        'This field supports Regular Expression on the resource identifier. For more information please visit <a href="https://insightvm.help.rapid7.com/docs/using-regular-expressions" target="_blank" rel="noopener noreferrer">Regular Expression Help</a>',
      selectExpiration: 'Select An Expiration',
      selectReason: 'Select A Reason',
      selectedOptionsType: '__type__ (__count__ Selected)',
      singleFinding: 'Exclude resource: __entityId__',
      submit: 'Submit',
      submitting: 'Submitting',
      tags: 'Tags',
      value: 'Value',
    },
    exposureCommand: {
      description:
        'Rapid7 has started the <anchor>End-of-Life process</anchor> for Cloud Configuration Assessment (CCA). To continue securing your cloud environments check out exposure command on the Insight Platform.',
      exploreMore: 'Explore more',
      heading: 'Protect your cloud environment with Exposure Command',
      newPackage: 'New Package',
    },
    failedDetails: 'Failed',
    findingDetails: 'Finding Details',
    findings: 'Findings',
    findingsCount: '__count__ $t(infrastructure.findings)',
    findingsFilter: {
      accounts: 'Accounts',
      appliedCount: '__count__ filter applied',
      appliedCount_0: 'No filters applied',
      appliedCount_plural: '__count__ filters applied',
      regions: 'Regions',
      resource_types: 'Resource Types',
      rules: 'Rules',
      severities: 'Severities',
      statuses: 'Statuses',
      title: 'Filter Results',
    },
    firstAssessed: 'First Assessed',
    ftux: {
      addConnection: 'Add Connection',
      configurationUnavailable:
        'Cloud Configuration Assessment is currently unavailable.',
      createConnection: 'Create a Connection',
      details: {
        [ProvisionStatus.NOT_FOUND]: `Cloud Configuration Assessment in InsightVM is a CIS-benchmark based assessment feature that provides visibility on misconfigurations that impact the security of cloud infrastructure.<br/><br/>Please note that fully activating this feature will take up to two hours to complete, after which you will be able to add connections to Cloud Configuration Assessment. <br/><br/>After adding cloud connections, billable resources will count toward your organization’s total InsightVM license count after the trial window. Moreover, your organization must have a minimum of 1000 licenses in order to continue to use this feature. Please see our documentation for more details.`,
        [ProvisionStatus.PENDING]: `Thank you for activating Cloud Configuration. You will see the InsightVM CCA user interface once everything is ready. The set up process may take up to two hours. <br/>¡<br/>Also, you will not be able to create connections during setup.`,
        [ProvisionStatus.OK]: `Cloud Configuration Assessment in InsightVM is a policy-based assessment feature that provides visibility on the weaknesses that impact the security of cloud infrastructure. Please enable the feature in the management page when adding your first cloud connection to begin using the feature.`,
      },
      disabled: 'Welcome to Cloud Configuration Assessment',
      disabledDetails:
        'Cloud Configuration Assessment is currently unavailable.',
      enablementDetails: `Cloud Configuration Assessment in InsightVM is a CIS-benchmark based assessment feature that provides visibility on misconfigurations that impact the security of cloud infrastructure. Please note that fully activating this feature will take up to two hours to complete, after which you will be able to add connections to Cloud Configuration Assessment. After adding cloud connections, billable resources will count toward your organization’s total InsightVM license count after the trial window. Moreover, your organization must have a minimum of 1000 licenses in order to continue to use this feature. Please see our documentation for more details.`,
      header: "You haven't configured any infrastructure connections yet.",
      save: 'Continue',
      seeProductDoc: 'See our CCA product document',
      welcome: 'Welcome to Cloud Configuration Assessment',
    },
    lastAssessed: 'Last Assessed',
    notifications: {
      findingsRecoverableError:
        'The page cannot be loaded due to a server error. <a class="action reload">Reload</a> the page to try again',
      findingsUnrecoverableError:
        'The page cannot be loaded due to a server error. <a class="action reset">Reset</a> query data to try again.',
      nothingToDisplay:
        'No connections are currently configured. Please contact your administrator to add connections.',
    },
    poweredByInsightCloudSec: 'powered by InsightCloudSec',
    reassess: 'Reassess',
    refresh: 'Refresh',
    regionAggregate: {
      none: 'None',
    },
    resourceAggregate: {
      divvyorganizationservice: 'cloud account',
    },
    resources: 'Resources',
    results: 'Results',
    resultsTotal: 'Results __count__',
    riskLevel: {
      critical: 'HIGH',
      moderate: 'LOW',
      negligible: 'NONE',
      severe: 'MEDIUM',
    },
    ruleDetails: {
      description: {
        controls: 'CONTROLS __number__',
        createdBy: 'CREATED',
        headers: {
          links: 'LINKS',
          related: 'RELATED',
          standards: 'STANDARDS',
        },
        id: 'Rule ID',
        modifiedBy: 'MODIFIED',
      },
      exportOrReport: 'Export/Report',
      metrics: {
        titles: {
          excepted: '__count__ Excepted Rules',
          failed: '__count__ Failed Rules',
          findings: 'Findings',
          passed: '__count__ Passed Rules',
          severity: 'Severity',
        },
      },
      mute: 'Mute',
      muteSelected: 'Mute Selected',
      mutedUntil: 'muted until: __date__',
      notes: 'Information',
      pageTitle: 'Rule Details',
      ruleToggleLabel: 'Rule Status',
      service: 'Service',
      tableHeaderTitle: 'Findings',
      title: 'Rules Details',
    },
    rules: 'Rules',
    services: 'Services',
    settings: {
      accountDetails: 'Account Details',
      accountName: 'Account Nickname',
      accountNumber: 'Account Number',
      apiCredentials: {
        title: 'API Credentials',
      },
      apiKey: 'API key',
      [EDIT_PANEL_MODES.ADD]: {
        AWS: 'Add AWS Cloud Infrastructure',
        AZURE_ARM: 'Add Azure Cloud Infrastructure',
        GCE: 'Add Google Cloud Platform Infrastructure',
        generic: 'Add Cloud Infrastructure',
      },
      [EDIT_PANEL_MODES.EDIT]: {
        AWS: 'Edit AWS Cloud Infrastructure',
        AZURE_ARM: 'Edit Azure Cloud Infrastructure',
        GCE: 'Edit Google Cloud Platform Infrastructure',
        generic: 'Edit Cloud Infrastructure',
      },
      appId: 'App ID',
      authenticationType: {
        apiKey: 'API Key/Secret',
        clientCertificate: 'Client Certificate',
        instance: 'Instance Assume Role',
        sts: ' STS Assume Role',
        title: 'Authentication Type',
      },
      clientDetails: 'Client Details',
      connection: {
        cloudConnection: 'Cloud Connection',
        cloudtrail: 'Cloud Trail',
        date: 'Date',
        failedPermissions: 'Permission',
        failedResourceType: 'Resource Type',
        setup: 'Setup Connection',
        status: 'Status',
        tableLabel: 'Failed Collections',
      },
      directions: {
        accountId: 'Account ID',
        attachPolicy: {
          builtIn: 'Attach AWS SecurityAudit Policy to your IAM Role',
          custom: 'Attach the Custom Policy to your IAM Role',
        },
        content: `Connect to an AWS Account by defining a role which \
          <a href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_common-scenarios_third-party.html"  target="_blank" rel="noopener noreferrer"> \
          grants cross-account access \
          </a> using these steps. \
          This role requires the AWS Security Audit managed policy and a few other read-only permissions in a custom policy. \
          This may be accomplished using the AWS Console or CLI.
          <br/>
          <br/>
          For complete setup and feature documentation, see the <a href="https://insightvm.help.rapid7.com/docs/cloud-configuration-assessment-overview" target="_blank" rel="noopener noreferrer">Cloud Configuration Assessment Help pages</a>.
        `,
        inlinePolicy: {
          command: 'Create Custom Managed Policy',
          document: 'Managed Policy Document',
        },
        rolePolicy: 'Create IAM Role',
        titles: {
          cli: 'CLI',
          console: 'AWS Console',
          linux: 'Linux',
          windows: 'Windows',
        },
        type: 'sdsadsadsa',
        uniqueId: 'External ID',
      },
      externalId: 'External ID',
      form: {
        accountName: {
          tooltip: 'Enter the cloud account nick name.',
        },
        accountNumber: {
          tooltip: 'Enter the cloud account number.',
        },
        apiCredentials: {
          tooltip: 'Enter the account api key.',
          type: 'Enter API credentials type.',
        },
        apiKey: {
          tooltip: 'Enter the account api key.',
        },
        appId: {
          tooltip: 'Enter the app ID.',
        },
        authProviderX509CertUrl: {
          tooltip: 'Enter the authorization certificate Uri',
        },
        authorizationUri: {
          tooltip: 'Enter the authorization Uri.',
        },
        clientDetails: {
          email: {
            tooltip: 'Enter the client email address.',
          },
          id: {
            tooltip: 'Enter the client id',
          },
          x509CertUrl: {
            tooltip: 'Enter the client certificate Uri',
          },
        },
        externalId: {
          tooltip: 'Enter an external ID.',
        },
        privateKey: {
          tooltip: "Enter the project's private key.",
        },
        privateKeyId: {
          tooltip: "Enter the project's private key id.",
        },
        project: {
          tooltip: "Enter the project's name.",
        },
        projectId: {
          tooltip: "Enter the project's id.",
        },
        role: {
          tooltip: "If you don't wish to change the arn, don't input anything.",
        },
        secretKey: {
          tooltip: 'Enter the account secret key.',
        },
        sessionName: {
          tooltip: 'Enter a name to use to identify this session.',
        },
        subscriptionId: {
          tooltip: 'Enter the subscription ID.',
        },
        tenantId: {
          tooltip: 'Enter the account tenant Id.',
        },
        tokenUri: {
          tooltip: 'Enter the token uri.',
        },
        type: {
          tooltip: 'Enter the project type.',
        },
        username: {
          tooltip:
            'If no value is provided, the username will default to arn id.',
        },
      },
      label: 'Cloud Infrastructure',
      method:
        'Select one of the following setup methods to configure a connection to your AWS infrastructure.',
      notifications: {
        connection: {
          create: {
            error: 'There was a problem while creating the configuration.',
            success: 'The configuration was successfully created.',
            warning:
              'The connection failed, but we still created the configuration.',
          },
          delete: {
            error: 'There was a problem while deleting the configuration.',
            success: 'The configuration was successfully deleted.',
          },
          get: {
            error: 'There was a problem while loading the configuration.',
          },
          test: {
            error: {
              auth:
                'Unable to connect with those credentials. Access was denied.',
              role:
                "The arn you provided wasn't a valid one. Please check the arn and try again.",
            },
            success: 'The test was successful. The credentials are valid.',
          },
          update: {
            error: 'There was a problem while updating the configuration.',
            success: 'The configuration was successfully updated.',
            warning:
              'The connection failed, but we still updated the configuration.',
          },
        },
      },
      project: {
        name: 'Project Id',
        title: 'Project',
      },
      role: 'Role ARN',
      secretKey: 'Secret Key',
      sessionName: 'Session Name',
      shortTitle: {
        [INFRASTRUCTURE_TYPES.AWS]: 'AWS',
        [INFRASTRUCTURE_TYPES.AZURE]: 'AZURE_ARM',
        [INFRASTRUCTURE_TYPES.GCP]: 'GCE',
      },
      subscriptionId: 'Subscription ID',
      tenantId: 'Tenant ID',
      title: {
        [INFRASTRUCTURE_TYPES.AWS]: 'Amazon Web Services',
        [INFRASTRUCTURE_TYPES.AZURE]: 'Microsoft Azure',
        [INFRASTRUCTURE_TYPES.GCP]: 'Google Cloud Platform',
      },
    },
    setupWizard: {
      continue: 'Continue',
      steps: {
        attachPolicies: {
          description:
            'Your IAM Role must have the required permissions to allow InsightVM to assess your cloud resources. You can provide these permissions by attaching the necessary policies to the role using one of the following methods.',
          directionsDescription:
            'Create and attach the required policies using either a CLI or your AWS Console.',
          directionsTitle: 'Policy Attachment Methods',
          directionsTooltip: `Your IAM Role must have the necessary permissions assigned that allow InsightVM to assess your cloud resources. You assign these permissions to your role by attaching the default SecurityAudit AWS policy and a custom managed policy that you create manually. These policies are composed entirely of read-only permissions for a baseline AWS connection, but the custom managed policy generated here will have some additional write permissions for the CloudTrail-specific SQS queue if you chose to include a CloudTrail connection in step 1 of this wizard.<br /><br />The CLI policy attachment methods contain scripts for creating the custom managed policy and attaching both to the role. The AWS Console method contains the body of the custom managed policy for you to copy and paste in your policy creation interface.<br /><br />If you need more detailed guidance, read our <a  target="_blank" rel="noopener noreferrer" href="https://insightvm.help.rapid7.com/docs/aws-connect-to-cloud-configuration-assessment">AWS connection configuration documentation</a>.`,
          heading: 'Attach Policies',
          title: 'Attach Policies',
        },
        connectionDetails: {
          accountPrompt: 'Connection Name',
          accountPromptDescription:
            'Enter a name for this connection. Cloud resources sourced from this connection will display this name for reference and filtering purposes.',
          arnPrompt: 'IAM Role ARN',
          arnPromptDescription:
            'Enter the Amazon Resource Name (ARN) of the IAM Role that you intend to use for this connection. You can use any existing IAM Role that you may already have, but we recommend that you create a new one dedicated to InsightVM’s use. You can create a new IAM Role using any of the methods shown below.',
          cloudtrailTooltip:
            'Connecting to the CloudTrail service allows Cloud Configuration Assessment to respond immediately to any configuration changes that take place in your AWS environment. Check this box to configure a CloudTrail connection during this wizard.<br /><br />See the <a  target="_blank" rel="noopener noreferrer" href="https://insightvm.help.rapid7.com/docs/aws-connect-to-cloud-configuration-assessment">AWS connection configuration documentation</a> for instructions on how to configure a CloudTrail connection.',
          description: '',
          directionsDescription:
            'You can create a new IAM Role by running one of the provided scripts in a Command Line Interface (CLI) or through your AWS Console with the provided field values.',
          directionsTitle: 'IAM Role Creation Methods',
          directionsTooltip: `The IAM Role you create here allows InsightVM to assume a role with <a target="_blank" rel="noopener noreferrer" href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_common-scenarios_third-party.html">cross-account access</a> to your AWS resources. You will assign permissions to this role in a later step.<br /><br />If you need more detailed guidance, read our <a  target="_blank" rel="noopener noreferrer" href="https://insightvm.help.rapid7.com/docs/aws-connect-to-cloud-configuration-assessment">AWS connection configuration documentation</a>.`,
          optionalConnections: {
            cloudtrail: 'Include a CloudTrail connection',
            prompt: 'Additional Connection Options',
          },
          title: 'Configure a Connection',
        },
        optionalConnections: {
          cloudtrail: {
            sqsArn: 'SQS ARN',
            sqsArnPrompt:
              'Enter the ARN for the Simple Queue Service (SQS) message queue that you want your CloudTrail connection to monitor.',
            sqsBucketName: 'S3 Bucket Name',
            sqsBucketNamePrompt:
              'Enter the name of the S3 bucket you have configured to receive log files from CloudTrail. InsightVM reads these log files to determine if your resources have undergone any configuration changes.',
            title: 'Configure a CloudTrail Connection',
          },
          description: '',
          skip: 'None selected',
          title: 'Optional Connections',
        },
        review: {
          complete: 'Your connection was successfully created',
          description: '',
          details: {
            accountName: 'Connection Name',
            awsConnection: 'AWS Connection',
            cloudtrail: {
              bucketArn: 'SQS ARN',
              bucketName: 'SQS Bucket Name',
              title: 'CloudTrail Connection',
            },
            roleArn: 'IAM Role ARN',
          },
          header: 'Review Your Connection',
          title: 'Review',
        },
      },
      submit: 'Submit',
      submitting: 'Submitting',
      title: 'Add Cloud Connection',
    },
    severity: 'Severity',
    severityAggregate: {
      critical: 'Critical',
      info: 'Info',
      low: 'Low',
      moderate: 'Moderate',
      severe: 'Severe',
    },
    severityAggregateTotal: {
      critical: 'Critical (__total__)',
      info: 'Info (__total__)',
      low: 'Low (__total__)',
      moderate: 'Moderate (__total__)',
      severe: 'Severe (__total__)',
    },
    statusAggregate: {
      fail: 'Fail',
      pass: 'Pass',
      passFormerFail: 'Pass (Former Fail)',
    },
    tabs: {
      exceptions: {
        metrics: {
          title: '__numberOfExceptions__ Exceptions',
        },
      },
      rules: {
        created: 'Created',
        lastModifiedBy: 'Last Modified By',
        manageConnections: 'Add/Manage Connections',
        metrics: {
          headlines: {
            failed: 'failed',
            off: 'off',
          },
          title: '__numberOfRules__ rules',
        },
        modified: 'Last Modified',
        offUntilDate: 'off until __formattedDate__',
        severity: 'Severity',
        status: 'Status',
        tableHeaderTitle: 'Rules',
        title: 'Name',
        type: 'Type',
      },
    },
    validation: {
      iamRoleRequired: 'A valid IAM Role ARN is required',
      s3BucketRequired: 'A valid S3 Bucket name is required',
      sqsArnRequired: 'A valid SQS ARN is required',
    },
  },
  name: 'Name',
  passed: 'Passed',
  remove: 'Remove',
  riskLevel: {
    critical: 'HIGH',
    moderate: 'LOW',
    negligible: 'NONE',
    severe: 'MEDIUM',
  },
  ruleDetails: {
    description: {
      controls: 'CONTROLS __number__',
      createdBy: 'CREATED BY            __date__',
      headers: {
        links: 'LINKS',
        related: 'RELATED',
      },
      id: 'Rule ID',
      modifiedBy: 'MODIFIED BY __date__',
    },
    exportOrReport: 'Export/Report',
    metrics: {
      titles: {
        excepted: '__count__ Excepted Rules',
        exceptedResources: '__count__ Excepted Resources',
        exceptions: '__count__ Exceptions',
        failed: '__count__ Failed Rules',
        failedResources: '__count__ Failed Resources',
        findings: 'Findings',
        passed: '__count__ Passed Rules',
        passedResources: '__count__ Passed Resources',
        severity: 'Severity',
      },
    },
    mute: 'Mute',
    muteSelected: 'Mute Selected',
    mutedUntil: 'muted until: __date__',
    pageTitle: 'Rule Details',
    ruleToggleLabel: 'Rule Status',
    service: 'Service',
    tableHeaderTitle: 'Findings',
    title: 'Rules Details',
  },
  selectAll: 'Select All',
  status: 'Status',
  toggle: 'Toggle',
  totalCount: '(__count__ total)',
};

const settingsModule = {
  integration: {
    connection: 'Enable Integration',
    [EDIT_PANEL_MODES.ADD]: 'Add AWS Security Hub Integration',
    [EDIT_PANEL_MODES.EDIT]: 'Edit AWS Security Hub Integration',
    directions: {
      content1: `Integrate with AWS Security Hub to view asset data and vulnerabilities collected by Rapid7
                that impact the AWS environment. To learn more, refer to our
                <a href="https://insightvm.help.rapid7.com/docs/aws-security-hub"  target="_blank" rel="noopener noreferrer">
                AWS Security Hub help documentation.</a>`,
      content2:
        'Set the toggle to "Enabled" to connect to AWS Security Hub. To disconnect, set the toggle to "Disabled."',
      title: 'Connect to AWS Security Hub',
    },
    label: 'Asset Data',
    notifications: {
      connection: {
        create: {
          error:
            'There was a problem while creating the AWS Security Hub connection.',
          success: 'The AWS Security Hub connection was successfully created.',
        },
        delete: {
          error:
            'There was a problem while deleting the AWS Security Hub connection.',
          success: 'The AWS Security Hub connection was successfully deleted.',
        },
        update: {
          error:
            'There was a problem while updating the AWS Security Hub connection.',
          success: 'The AWS Security Hub connection was successfully updated.',
        },
      },
    },
    shortTitle: {
      [INTEGRATION_TYPES.AWS]: `$t(infrastructure.settings.shortTitle.${INFRASTRUCTURE_TYPES.AWS})`,
    },
    status: {
      disabled: 'Disabled',
      enabled: 'Enabled',
    },
    title: {
      [INTEGRATION_TYPES.AWS]: 'AWS Security Hub',
    },
  },
  label: 'Settings',
};

export const clipboard = {
  button: {
    copied: 'Copied',
    copyToClipboard: 'Copy',
  },
};

const vulnerabilitiesByCvssScoreLabeling = {
  label: {
    cntHigh: '__percent__ (__value__) 7.0 and 10.0',
    cntLow: '__percent__ (__value__) 0 and 3.9',
    cntMedium: '__percent__ (__value__) 4.0 and 6.9',
  },
  summary: {
    cntHigh:
      '__percent__ of vulnerabilities have a CVSS score between 7.0 and 10.0',
    cntLow:
      '__percent__ of vulnerabilities have a CVSS score between 0 and 3.9',
    cntMedium:
      '__percent__ of vulnerabilities have a CVSS score between 4.0 and 6.9',
  },
  value: {
    cntHigh:
      '__percent__ (__value__) of vulnerabilities have a CVSS score between 7.0 and 10.0',
    cntLow:
      '__percent__ (__value__) of vulnerabilities have a CVSS score between 0 and 3.9',
    cntMedium:
      '__percent__ (__value__) of vulnerabilities have a CVSS score between 4.0 and 6.9',
  },
};

const vulnerabilitiesByCvssV3ScoreLabeling = {
  label: {
    cntCritical: '__percent__ (__value__) 9.0 and 10.0',
    cntHigh: '__percent__ (__value__) 7.0 and 8.9',
    cntLow: '__percent__ (__value__) 0.1 and 3.9',
    cntMedium: '__percent__ (__value__) 4.0 and 6.9',
    cntNone: '__percent__ (__value__) 0',
  },
  summary: {
    cntCritical:
      '__percent__ of vulnerabilities have a CVSS score between 9.0 and 10.0',
    cntHigh:
      '__percent__ of vulnerabilities have a CVSS score between 7.0 and 8.9',
    cntLow:
      '__percent__ of vulnerabilities have a CVSS score between 0.1 and 3.9',
    cntMedium:
      '__percent__ of vulnerabilities have a CVSS score between 4.0 and 6.9',
    cntNone: '__percent__ of vulnerabilities have a CVSS score of 0',
  },
  value: {
    cntCritical:
      '__percent__ (__value__) of vulnerabilities have a CVSS score between 9.0 and 10.0',
    cntHigh:
      '__percent__ (__value__) of vulnerabilities have a CVSS score between 7.0 and 8.9',
    cntLow:
      '__percent__ (__value__) of vulnerabilities have a CVSS score between 0.1 and 3.9',
    cntMedium:
      '__percent__ (__value__) of vulnerabilities have a CVSS score between 4.0 and 6.9',
    cntNone:
      '__percent__ (__value__) of vulnerabilities have a CVSS score of 0',
  },
};

const vulnerabilityCountByCvssKeyValues = {
  label: {
    cntHigh: 'CVSS between 7.0 and 10.0',
    cntLow: 'CVSS between 0 and 3.9',
    cntMedium: 'CVSS between 4.0 and 6.9',
  },
  value: {
    cntHigh:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score between 7.0 and 10.0',
    cntLow:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score between 0 and 3.9',
    cntMedium:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score between 4.0 and 6.9',
  },
};

const vulnerabilityCountByCvssV3KeyValues = {
  label: {
    cntCritical: 'CVSS between 9.0 and 10.0',
    cntHigh: 'CVSS between 7.0 and 8.9',
    cntLow: 'CVSS between 0.1 and 3.9',
    cntMedium: 'CVSS between 4.0 and 6.9',
    cntNone: 'CVSS of 0',
  },
  value: {
    cntCritical:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score between 9.0 and 10.0',
    cntHigh:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score between 7.0 and 8.9',
    cntLow:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score between 0.1 and 3.9',
    cntMedium:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score between 4.0 and 6.9',
    cntNone:
      '$t(weekOf)<br/>__percent__ (__value__) of vulnerabilities have a CVSS score of 0',
  },
};

const vulnerabilitiesBySeverityLabeling = {
  label: {
    cntCritical: '__percent__ (__value__) Critical Vulnerabilities',
    cntModerate: '__percent__ (__value__) Moderate Vulnerabilities',
    cntSevere: '__percent__ (__value__) Severe Vulnerabilities',
    cntSeverityUnknown: '__percent__ (__value__) Unknown Vulnerabilities',
  },
  summary: {
    cntCritical: '__percent__ of your vulnerabilities are Critical',
    cntModerate: '__percent__ of your vulnerabilities are Moderate',
    cntSevere: '__percent__ of your vulnerabilities are Severe',
    cntSeverityUnknown: '__percent__ of your vulnerabilities are Unknown',
  },
  value: {
    cntCritical: '__percent__ (__value__) of your vulnerabilities are Critical',
    cntModerate: '__percent__ (__value__) of your vulnerabilities are Moderate',
    cntSevere: '__percent__ (__value__) of your vulnerabilities are Severe',
    cntSeverityUnknown:
      '__percent__ (__value__) of your vulnerabilities are Unknown',
  },
};

const dayRangeLabels = {
  0: '0-29 days',
  1: '30-59 days',
  2: '60-90 days',
  3: '>90 days',
};

const severityLabels = {
  0: '$t(moderate)',
  1: '$t(severe)',
  2: '$t(critical)',
};

const cvssScoreLabels = {
  0: '0.0-3.9',
  1: '4.0-6.9',
  2: '7.0-10.0',
};

const cvssV3ScoreLabels = {
  0: '0',
  1: '0.1-3.9',
  2: '4.0-6.9',
  3: '7.0-8.9',
  4: '9.0-10.0',
};

const riskScoreLabels = {
  0: 'Unassigned',
  1: 'Low',
  2: 'Moderate',
  3: 'High',
  4: 'Critical',
};

const reportModule = {
  manage: {
    banner: {
      link: 'Take me there!',
      text: 'Looking for your console reports? Visit our console reports page!',
    },
    createReportFile: 'Success! __name__ will have a new report ready soon.',
    criteria: 'Criteria: ',
    dateCreated: 'Date Generated',
    delete: '$t(delete)',
    deleteReport: 'Delete Report',
    deleteReportConfirmation:
      '__name__ report & its __numReportFiles__ historical files',
    deleteReportFileConfirmation: '__name__ report, generated at __date__',
    download: 'Download',
    downloadRecent: 'Download latest generated',
    editReport: 'Edit Report',
    empty: {
      description:
        "Try adjusting your filters or come back when you've generated some reports.",
      title: 'No reports found',
    },
    error:
      'An error occurred when loading the scope for __name__ created on __date__',
    execReportInfo:
      'The Executive Summary report is generated for you on a monthly basis. On-demand generation or removal of this report is not supported.',
    failed: '__date__ (Failed)',
    generatedAt: 'Generated at __date__',
    generating: 'Generating...',
    hideNumFiles: 'Hide __num__ files',
    moreUsers: '__num__ more',
    none: 'None',
    numReportsGenerated: 'Reports (__num__ records)',
    regenerateReport: 'Run Report',
    reportFilterTypes: {
      formats: 'Format',
      templates: 'Template',
    },
    reportFilters: {
      [ReportType.CSV]: 'CSV',
      [ReportType.PDF]: 'PDF',
      [ReportTemplate.ASSET]: 'Asset Detail',
      [ReportTemplate.ASSET_LIST]: 'Asset List',
      [ReportTemplate.BEST_SOLUTION_LIST]: 'Best Solution List',
      [ReportTemplate.DASHBOARD]: 'Dashboard',
      [ReportTemplate.EXECUTIVE_SUMMARY]: 'Executive Summary Report',
      [ReportTemplate.EXPLOIT_LIST]: 'Exploit List',
      [ReportTemplate.MALWARE_LIST]: 'Malware List',
      [ReportTemplate.NEW_VULNERABILITY_LIST]: 'New Vulnerability List',
      [ReportTemplate.REMEDIATED_VULNERABILITY_LIST]:
        'Remediated Vulnerability List',
      [ReportTemplate.SERVICE_LIST]: 'Service List',
      [ReportTemplate.SOFTWARE_LIST]: 'Software List',
      [ReportTemplate.VULNERABILITY]: 'Vulnerability Detail',
      [ReportTemplate.VULNERABILITY_LIST]: 'Vulnerability List',
      [ReportTemplate.BULK_REMEDIATION_LIST]: 'Policies',
    },
    reportFormat: 'Format',
    reportTemplate: 'Source',
    reportsArchive: 'Reports Archive',
    schedule:
      'A __format__ report will be generated every __interval__ $t(reportModule.manage.scheduleFrequency.__type__) at __time__ starting on __nextOccurrence__.',
    scheduleFrequency: {
      DAILY: 'day(s)',
      MONTHLY: 'month(s)',
      WEEKLY: 'week(s)',
      YEARLY: 'year(s)',
    },
    searchReportName: 'Search Report Name',
    showLess: 'Show less',
    showNumFiles: 'Show __num__ files',
  },
  title: 'Reports',
};

const policyModule = {
  policyModule: {
    actions: 'Actions',
    addToWatchlist: 'Add to Watchlist',
    affectedPolicyWarning:
      "Warning: Policy is affected. Some rules won't be assessing in that policy.",
    agentBasedPolicies: 'Agent Based Policies',
    agentBasedPolicy: 'Agent Based Policy',
    agentCompatible: 'Agent Compatible',
    agentEnabled: 'Agent Enabled',
    allApplicableAssets: 'All Applicable Assets',
    allAssets: 'All assets',
    allPolicies: 'All Policies',
    applicableAssets: 'Applicable Assets',
    approveOverride: {
      confirm: 'Yes, Approve it',
      text: 'Are you sure you want to approve this override?',
      title: 'Approve Override?',
    },
    assessment: 'Assessment',
    assessmentScope: 'Assessment Scope',
    assessmentStatus: 'Assessment Status',
    assetCompliance: 'Asset Compliance',
    assetCompliancePercent: 'Asset Compliance __assetPercent__',
    assetFilters: 'Asset Filters',
    assetResultsTitle: 'Asset Compliance: __hostname__',
    assetStatuses: {
      [AssetStatuses.PASS]: 'Passed',
      [AssetStatuses.FAIL]: 'Failed',
    },
    assets: 'Assets',
    assetsAndOverrides: 'Assets and Overrides',
    assetsFailed: 'Assets Failed',
    assetsPassed: 'Assets Passed',
    assetsTableSearchPlaceholder: 'Search Assets or IP',
    availablePolicies: 'Available Policies',
    benchmarkTitle: 'Benchmark',
    builtin: 'Built-in',
    changeAssessmentScopeBanner:
      'Assessment scope change pending. Changes will be reflected in the next collection cycle and may take up to 24 hours to appear.',
    compliance: 'Compliance',
    complianceTrends: 'Compliance Trends',
    complianceTrendsDropdown: {
      last90Days: 'Last 90 Days',
      last180Days: 'Last 180 Days',
      last365Days: 'Last 365 Days',
      ytd: 'YTD',
    },
    components: {
      PolicyStatusIndicator: {
        pendingMessage: 'Policy processing: TODO UPDATE THIS',
      },
    },
    confirmDisable: 'Yes, disable it',
    confirmEnabledPolicyCreation: {
      disclaimer:
        '* Changes made to policy assessment scope are not reflected on the policy details page until the next collection cycle and may take up to 24 hours to appear.',
      paragraph_1: 'Which assets should be assessed for this policy?',
      paragraph_2: 'Load or build a query to narrow down your assets.',
    },
    confirmEnabledPolicyDisable: {
      paragraph_1: {
        name:
          'This will disable __policyName__ from your list of enabled policies.',
      },
      paragraph_2: {
        name: 'Are you sure you want to disable __policyName__?',
      },
    },
    copyPolicy: 'Copy Policy',
    createOverride: 'Create Override',
    createOverrideModal: {
      labels: {
        [OverrideScope.GLOBAL]: 'All assets',
        [OverrideScope.CUSTOM]: 'Custom scope',
        comment: 'Comment',
        expiration: 'Expiration',
        [OverrideType.FAIL]: 'Failed',
        [OverrideType.FIXED]: 'Fixed',
        [Expiration.NEVER]: 'Never',
        [OverrideScope.IP_RANGE_HOSTNAME_NETMASK]: 'IP Range/Netmask/Host Name',
        [OverrideType.NOT_APPLICABLE]: 'N/A',
        [OverrideType.PASS]: 'Passed',
        neverExpire: 'Never expire',
        reason: 'Reason',
        resultStatus: 'Override type',
        select: 'Select',
        selectDate: 'Select a date',
        [OverrideScope.SINGLE]: 'This asset: __assetName__',
        selectRule: 'Select Rule',
        submitAndApprove: 'Submit and Approve',
      },
      notifications: {
        error: {
          create: 'There was an error creating your override.',
          update: 'There was an error updating your override.',
        },
        success: {
          create:
            'Your override has been created. <0>View override details</0>',
          update: 'Your override has been updated.',
        },
      },
    },
    createdBy: 'Created By',
    custom: 'Custom',
    customPolicies: 'Custom Policies',
    customPolicyBuilder: 'Custom Policy Builder',
    dailyCompliance: 'Daily Compliance',
    dateCreated: 'Date Created',
    deleteOverrideModal: {
      confirm: 'Yes, delete it',
      error: 'An error occurred while trying to delete this override.',
      paragraph_1: 'Deleting an override will permanently remove it.',
      paragraph_2: 'Are you sure you want to delete this override?',
      success: 'The override was deleted successfully.',
      title: 'Delete Override?',
    },
    description: 'Description',
    disableEnabledPolicy: 'Disable Enabled Policy?',
    editOverride: 'Edit Override',
    editPolicy: 'Edit Policy',
    enablePolicy: 'Enable Policy',
    enabledPolicies: 'Enabled Policies',
    enabledPoliciesEmptyState: {
      content: 'Policies you enable for assessment can be tracked here.',
      header: 'No Enabled Policies',
      primaryButton: 'View Policies',
      secondaryButton: 'See documentation',
    },
    enabledPoliciesNotifications: {
      alert: 'Max limit of 100 enabled policies reached.',
      warning: 'You can assess a maxiumum of 100 policies at a time.',
    },
    enabledPoliciesOverallCompliance: 'Enabled Policies Compliance',
    enabledPolicy: 'Enabled Policy',
    entityColumns: {
      assets: 'Assets',
      compliance: 'Compliance',
      created: 'Created',
      createdBy: 'Creator',
      expiration: 'Expiration',
      failed: 'Failed',
      hostname: 'Hostname',
      ipAddress: 'IP Address',
      lastAssessed: 'Last Assessed',
      location: 'Location',
      overrides: 'Overrides',
      passed: 'Passed',
      passedRules: 'Passed Rules',
      policyTitle: 'Policy Title',
      reason: 'Reason',
      resultStatus: 'Override Type',
      ruleName: 'Rule Name',
      site: 'Site',
      sites: 'Site(s)',
      status: 'Status',
      tag: 'Tag',
      tags: 'Tag(s)',
      type: 'Scope',
    },
    expired: 'Expired',
    failedAssets: 'Failed Assets',
    failedRules: 'Failed Rules',
    family: 'Family',
    filterResults: 'Filter Results',
    generalQueryErrorMsg:
      'An unknown error has occurred loading the query associated with this policy.',
    global: 'Global',
    hostName: 'Host Name',
    iawBannerLinkText: 'Manage your agents.',
    iawBannerText:
      'We are preparing your first policy compliance assessment. Your results will be available soon.',
    isOverridden: 'Is Overridden',
    lastAssessed: 'Last Assessed',
    lastAssessment: 'Last Assessment __date__',
    lastPolicyAssessment: 'Last Policy Assessment',
    locations: 'Locations',
    makeACopy: 'Make A Copy',
    manageAssessmentScope: 'Manage Assessment Scope',
    manageAssets: 'Manage Assets',
    manageCustomPolicies: 'Manage Custom Policies',
    missingAssociatedQueryErrMsg:
      'The query associated with this policy has been deleted, please disable and re-enable the policy.',
    noData: 'Not enough data',
    nullstate: {
      401: {
        description:
          'You do not have the correct permissions to view the Agent-Based Assessment page. This feature is currently limited to Global Administrators.',
      },
    },
    numPassed: '__passed__ of __total__',
    overallCompliance: 'Overall Compliance',
    overridden: 'Overridden',
    override: 'Override',
    overrideScope: 'Override Scope',
    overrideScopes: {
      [OverrideScope.GLOBAL]: 'Global',
      [OverrideScope.SINGLE]: 'Single',
      displayAsset: {
        [OverrideScope.SINGLE]: 'This asset: __assetName__',
      },
      [OverrideScope.CUSTOM]: 'Custom',
    },
    overrideStates: {
      [OverrideState.ACTIVE]: 'Active',
      [OverrideState.APPROVED]: 'Approved',
      [OverrideState.DELETED]: 'Deleted',
      [OverrideState.EXPIRED]: 'Expired',
      [OverrideState.REJECTED]: 'Rejected',
      [OverrideState.UNDER_REVIEW]: 'Pending Review',
    },
    overrideSummary: 'Override Summary',
    overrideTypes: {
      [OverrideType.PASS]: 'Passed',
      [OverrideType.FAIL]: 'Failed',
      [OverrideType.FIXED]: 'Fixed',
      [OverrideType.NOT_APPLICABLE]: 'N/A',
    },
    overrideWithTitle: 'Override: __title__',
    overrides: 'Overrides',
    overridesWithCount: '__count__ Overrides',
    passedAssets: 'Passed Assets',
    passedRules: 'Passed Rules',
    platform: 'Platform',
    platforms: 'Platforms',
    policies: 'Policies',
    policy: 'Policy',
    policyCompliance: 'Policy Compliance',
    policyControls: 'Policy Controls',
    policyControlsNoData: 'There are no policy controls for this rule.',
    policyDetails: 'Policy Details',
    policyId: 'Policy ID',
    policyResultsTitle: 'Policy Compliance: __policyTitle__',
    policyRule: 'Policy Rule',
    profileTitle: 'Profile',
    proof: 'Proof',
    proofDetail: 'Proof Detail',
    proofNavigation: 'Proof Navigation',
    proofPeekPanel: {
      emptyState: 'Proof information not available.',
    },
    publisher: 'Publisher',
    publishers: 'Publishers',
    rationale: 'Rationale',
    rejectOverride: {
      confirm: 'Yes, Reject it',
      paragraph_1:
        'Rejecting an override will decline its request for approval.',
      paragraph_2: 'Are you sure you want to reject this override?',
      title: 'Reject Override?',
    },
    remediation: 'Remediation',
    reviewCustomPoliciesWarning:
      'It is recommended that you review custom policies before enabling for agent-based assessment. <a target="_blank" rel="noopener noreferrer" href="https://docs.rapid7.com/insightvm/custom-policy-risks-and-recs"><strong><u>Learn More</u></strong></a>',
    ruleCompliance: 'Rule Compliance',
    ruleCompliancePercent: 'Rule Compliance __rulePercent__',
    ruleDetails: 'Rule Details',
    ruleInfo: 'Rule Info',
    ruleName: 'Rule Name',
    ruleResultsTitle: 'Rule Compliance: __ruleTitle__',
    rules: 'Rules',
    selectPolicy: 'Select a policy',
    selectPolicyText: 'Select a policy to populate this card with its data.',
    showEnabledPolicies: 'Show only enabled policies',
    shutdownView: {
      button: 'GO TO HOME',
      header:
        'As of November 1, 2021, the Agent Based Policy Compliance Early Access Program has ended. We want to thank everyone who has taken the time and energy to provide feedback to the team as we build the next generation of Policy Compliance for InsightVM.',
    },
    single: 'Single',
    state: 'State',
    status: 'Status',
    statuses: 'Statuses',
    successMessageOnNewWatchPolicyCreation:
      '<policyCount/> policy(s) added to your Watchlist. <watchlistLink>View Watchlist.</watchlistLink>',
    successMessageOnWatchPolicyRemoval:
      '<policyCount/> policy(s) removed from your Watchlist',
    // ignore highcharts string interpolation syntax; will read "(5) 17% of assets have OS Windows 8"
    tooltipSupportPlatforms:
      '({point.y}) {point.percentage:.1f}% of assets have platform {point.name}',
    topSupportedPlatforms: 'Top 5 Supported Platforms',
    topSupportedPlatformsZeroState:
      'System is gathering data. Visualization will be ready soon.',
    totalAssetsWithAgent: 'Total Assets with Agent',
    totalOverrides: 'Total Overrides',
    totalPolicies: 'Total Policies',
    totalRules: 'Total Rules',
    updateWatchList: 'Update Watch List',
    version: 'Version',
    versions: 'Versions',
    viewOverride: 'View Override',
    viewOverrideDetails: 'View Override Details',
    viewPolicies: 'View Policies',
    viewPolicyDetails: 'View Policy Details',
    viewProof: 'View Proof',
    viewRule: 'View Rule',
    watchPolicies: 'Watch Policies',
    watchedPolicies: 'Watched Policies',
    watchlist: 'Watchlist',
    watchlistTable: {
      emptyState: {
        content:
          'The Policy Watchlist is a conveniant place to track priority policies that you desire to review regularly',
        header: 'No Policies in Watchlist',
      },
    },
  },
};

const customPolicyModule = {
  customPolicy: {
    action: 'Action',
    addAccount: 'Add account',
    addBehavior: 'Add behavior',
    addField: 'Add Field',
    addRule: 'Add Rule',
    addRuleGroup: 'Add Rule Group',
    addRuleSuccess: 'Rule(s) are added!!',
    addRules: 'Add Rule(s)',
    addState: 'Add state',
    agentEnabledWarningModal: {
      body:
        'This policy is currently enabled for agent-based assessment. Making edits will force the assessment to stop. After your edits are saved and the policy is finished processing, you can re-enable the policy for agent-based assessment.',
      title: 'Editing stops your assessment',
    },
    auditLogSummaryModal: {
      content:
        'Your policy saved successfully.  For an easier review, add a description of your changes.',
      placeholderText: '(Optional) Add a helpful description of your changes',
      saveTitle: 'Add a log summary (optional)',
    },
    auditLogsNote:
      'You need Insight Platform administrator privileges to view audit logs.<br/><br/>View our <a target="_blank" rel="noopener noreferrer" href="https://docs.rapid7.com/insight/user-management/#platform-administrator"><strong>Insight Platform User Management docs</strong></a> to learn about user roles.',
    auditLogsNoteTitle: 'Limited Access to Audit Logs',
    cancelModal: {
      body: 'Any unsaved work will be lost. Are you sure you want to continue?',
      title: 'Unsaved Work Will Be Lost',
    },
    checkDetails: {
      accessTokenPlaceholder: 'Enter account name',
      accounts: 'Accounts',
      accountsPlaceholder: 'Enter SID (e.g., S-1-5-11)',
      addResultFields: 'Add Result Fields',
      addSectionFields: 'Add __term__ Fields',
      additionalConfigFields: 'Additional Configuration Fields',
      all: 'All',
      allExist: 'All Exist',
      anyExist: 'Any Exist',
      appSpecTooltip: `Specify an app by using either:
        <ul>
        <li>The <b>app_id</b> which is unique application identifier that specifies the application to use when looking up the preference key (e.g. com.apple.Safari).</li>
        <li>The <b>filepath</b>, which is the absolute path to a plist file (e.g. ~/Library/Preferences/com.apple.Safari.plist).</li>
        </ul>`,
      arch: 'Architecture',
      atLeastOne: 'At Least One',
      atLeastOneExists: 'At Least One Exists',
      auditCategory: 'Audit Category',
      auditLog: {
        action: {
          updatedCpeInfo: {
            description:
              'This action type includes updates to policy CPE string and CPE title',
          },
          updatedRuleInfo: {
            description:
              'This action type includes updates to policy rule fields such as title, description, weight, role, severity etc',
          },
        },
      },
      auditSubcategory: 'Audit Subcategory',
      auditType: 'Audit Type',
      check: 'Check',
      commonTestFields: {
        connectionString: 'Connection String',
      },
      compositeSection: '__term__',
      definitionId: 'Definition ID',
      definitionIdTooltip:
        'Definition ID can help troubleshoot policy scan failures to find relevant commands used during the policy scan and the output of those commands.',
      device: 'Device',
      entityCheck: 'Entity Check',
      epoch: 'Epoch',
      evr: 'EVR',
      existence: 'Existence',
      expectedResult: 'Expected Result',
      expectedSection: 'Expected __term__',
      expectedState: 'Expected State',
      expectedValues: 'Expected Values',
      fail: 'Fail',
      family: 'Family',
      fieldPlaceholder: 'Enter name of new field here',
      fileBehaviors: 'File Behaviors',
      fileObject: 'File Object',
      fileSpec: 'File Specification',
      fileState: 'File State',
      filename: 'Filename',
      filepath: 'Filepath',
      filepathTooltip:
        'A filepath must include the full path to the file, consisting of both the directory and file name',
      filespecTooltip: `Specify a file by using either:
        <ul>
        <li>The <b>filepath</b>, which is the path from root to the file and includes the file name and directory</li>
        <li>A combination of the <b>path</b> and <b>filename</b>, which is the path from root to the file and does not include the filename.</li>
        </ul>`,
      fsType: 'Filesystem Type',
      groupId: 'Group ID',
      hive: 'Hive',
      host: 'Host',
      ignoreCase: 'Ignore Case',
      include: 'Include',
      includeField:
        'true means include the following account(s), false means exclude the following account(s)',
      ipv4Acl: 'IPv4 ACL',
      ipv6Acl: 'IPv6 ACL',
      key: 'Key',
      maxDepth: 'Max Depth',
      messages: {
        cpeString: 'Must be valid CPE string',
        customAccount: 'SID(s) must begin with S- (e.g., S-1-5-11)',
        fileObject:
          'Must specify either a complete filepath or a path and a filename',
        hexadecimal: 'Must be valid hexadecimal string',
        integer: 'Must be an integer',
        maxDepthRange: 'Must be greater than or equal to -1',
        minAccounts: 'At least one account is required',
        number: 'Must be a number',
        validDate: 'Must be a valid date',
        validDateAfter: 'Must be valid date on or after 1/1/1970',
      },
      mountOptions: 'Mount Options',
      mountPoint: 'Mount Point',
      multiline: 'Multiline',
      name: 'Name',
      noneExist: 'None Exist',
      noneSatisfy: 'None Satisfy',
      onlyOne: 'Only One',
      onlyOneExists: 'Only One Exists',
      operator: 'Operator',
      pass: 'Pass',
      passwordType: 'Password Type',
      path: 'Path',
      permissionType: 'Permission Type',
      permissionValue: 'Permission Value',
      policyTestList: {
        accesstoken_test: {
          description:
            'The accesstoken_test is used to check the properties of a Windows access token as well as individual privileges and rights associated with it. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an accesstoken_object and the optional state element specifies the data to check.',
          title: 'Accesstoken Test',
        },
        acl_test: {
          description:
            'The acl test is used to check the properties of specific output lines from an ACL configuration.',
          title: 'Acl Test',
        },
        activedirectory_test: {
          description:
            'The active directory test is used to check information about specific entries in active directory. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an activedirectory_object and the optional state element specifies the metadata to check.',
          title: 'Active Directory Test',
        },
        activedirectory57_test: {
          description:
            'The active directory test is used to check information about specific entries in active directory. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an activedirectory57_object and the optional state element specifies the metadata to check.',
          title: 'Active Directory Test',
        },
        auditeventpolicy_test: {
          description:
            'The auditeventpolicy_test is used to check different types of events the system should audit. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a auditeventpolicy_object and the optional state element specifies the metadata to check.',
          title: 'Audit Event Policy Test',
        },
        auditeventpolicysubcategories_test: {
          description:
            'The auditeventpolicysubcategories_test is used to check the audit event policy settings on a Windows system. These settings are used to specify which system and network events are monitored.  For example, if the credential_validation element has a value of AUDIT_FAILURE, it means that the system is configured to log all unsuccessful attempts to validate a user account on a system. It is important to note that these audit event policy settings are specific to certain versions of Windows. As a result, the documentation for that version of Windows should be consulted for more information on each setting. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a auditeventpolicy_object and the optional state element specifies the metadata to check.',
          title: 'Audit Event Policy Subcategories Test',
        },
        benchmark_test: {
          description:
            'The benchmark test is used to check information by running cis test.',
          title: 'Benchmark Test',
        },
        bgpneighbor_test: {
          description:
            'The bgpneighbor test is used to check the BGP neighbor properties of BGP instances instances in IOS.',
          title: 'BGP Neighbor Test',
        },
        cmdlet_test: {
          description:
            'The cmdlet_test is used to leverage a PowerShell cmdlet to check a Windows system. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a cmdlet_object and the optional state element specifies the metadata to check.',
          title: 'Cmdlet Test',
        },
        dnscache_test: {
          description:
            "The dnscache_test is used to check the time to live and IP addresses associated with a domain name.  The time to live and IP addresses for a particular domain name are retrieved from the DNS cache on the local system. The entries in the DNS cache can be collected using Microsoft's DnsGetCacheDataTable() and DnsQuery() API calls. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a dnscache_object and the optional state element specifies the metadata to check.",
          title: 'DNS Cache Test',
        },
        dpkginfo_test: {
          description:
            'The dpkginfo test is used to check information for a given DPKG package. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a dpkginfo_object and the optional state element specifies the data to check.',
          title: 'Dpkginfo Test',
        },
        environmentvariable_test: {
          description:
            'The environmentvariable_test element is used to check an environment variable found on the system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a environmentvariable_object and the optional state element specifies the metadata to check.',
          title: 'Environment Variable Test',
        },
        environmentvariable58_test: {
          description:
            'The environmentvariable58_test element is used to check an environment variable for the specified process, which is identified by its process ID, on the system . It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a environmentvariable_object and the optional state element specifies the metadata to check.',
          title: 'Environment Variable Test',
        },
        facet_test: {
          description:
            'The facet_test is used to check the facets associated with the specified Image Packaging System image. Facets are properties that control whether or not optional components from a package are installed on a system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an facet_object and the optional state elements reference a facet_state and specifies the data to check.',
          title: 'Facet Test',
        },
        family_test: {
          description:
            'The family_test element is used to check the family a certain system belongs to. This test basically allows the high level system types (window, unix, ios, etc.) to be tested. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a family_object and the optional state element specifies the metadata to check.',
          title: 'Family Test',
        },
        fileauditedpermissions_test: {
          description:
            "The file audited permissions test is used to check the audit permissions associated with Windows files. Note that the trustee's audited permissions are the audit permissons that the SACL grants to the trustee or to any groups of which the trustee is a member. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a fileauditedpermissions_object, and the optional state element references a fileauditedpermissions_state that specifies the metadata to check.",
          title: 'File Audited Permissions Test',
        },
        fileauditedpermissions53_test: {
          description:
            "The file audit permissions test is used to check the audit permissions associated with Windows files. Note that the trustee's audited permissions are the audit permissons that the SACL grants to the trustee or to any groups of which the trustee is a member. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a fileauditedpermissions_object and the optional state element specifies the metadata to check.",
          title: 'File Audited Permissions Test',
        },
        fileeffectiverights_test: {
          description:
            "The file effective rights test is used to check the effective rights associated with Windows files. Note that the trustee's effective access rights are the access rights that the DACL grants to the trustee or to any groups of which the trustee is a member. The fileeffectiverights_test element extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a fileeffectiverights_object and the optional state element specifies the metadata to check.",
          title: 'File Effective Rights Test',
        },
        fileeffectiverights53_test: {
          description:
            "The file effective rights test is used to check the effective rights associated with Windows files. Note that the trustee's effective access rights are the access rights that the DACL grants to the trustee or to any groups of which the trustee is a member. The fileeffectiverights53_test element extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a fileeffectiverights53_object and the optional state element specifies the metadata to check.",
          title: 'File Effective Rights Test',
        },
        fileextendedattribute_test: {
          description:
            'The file extended attribute test is used to check extended attribute values associated with UNIX files, of the sort returned by the getfattr command or getxattr() system call. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a fileextendedattribute_object and the optional state element specifies the extended attributes to check.',
          title: 'File Extended Attribute Test',
        },
        filehash_test: {
          description:
            'The file hash test is used to check the hashes associated with a specified file. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a filehash_object and the optional state element specifies the different hashes to check.',
          title: 'File Hash Test',
        },
        filehash58_test: {
          description:
            'The file hash test is used to check a specific hash type associated with a specified file. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a filehash58_object and the optional state element specifies an expected hash value.',
          title: 'File Hash Test',
        },
        fileset_test: {
          description:
            'The fileset_test is used to check information associated with different filesets installed on the system. The information used by this test is modeled after the /usr/bin/lslpp -l command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an inetd_object and the optional state element specifies the information to check.',
          title: 'File Set Test',
        },
        fix_test: {
          description:
            'The fix test is used to check information associated with different fixes installed on the system. The information being tested is based off the /usr/sbin/instfix -iavk command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an fix_object and the optional state element specifies the information to check.',
          title: 'Fix Test',
        },
        gconf_test: {
          description:
            'The gconf_test is used to check the attributes and value(s) associated with GConf preference keys. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a gconf_object and the optional gconf_state element specifies the data to check.',
          title: 'Gconf Test',
        },
        getconf_test: {
          description:
            'From /usr/bin/getconf. See getconf manpage for specific fields',
          title: 'Get Configuration Test',
        },
        global_test: {
          description:
            'The global test is used to check for the existence of a particular line in the ios config file under the global context. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a global_object and the optional state element specifies the data to check.',
          title: 'Global Test',
        },
        group_sid_test: {
          description:
            'The group_sid_test allows the different users and subgroups, that directly belong to specific groups (identified by SID), to be tested. When the group_sid_test collects the group SIDs on the system, it should only include the local and built-in group SIDs and not domain group SIDs.  However, it is important to note that domain group SIDs can still be looked up. Also, note that the subgroups of the group will not be resolved to find indirect user and group members. If the subgroups need to be resolved, it should be done using the sid_sid_object. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a group_sid_object and the optional state element specifies the metadata to check.',
          title: 'Group Test',
        },
        group_test: {
          description:
            'The group_test allows the different users and subgroups, that directly belong to specific groups (identified by name), to be tested.  When the group_test collects the groups on the system, it should only include the local and built-in group accounts and not domain group accounts.  However, it is important to note that domain group accounts can still be looked up. Also, note that the subgroups of the group will not be resolved to find indirect user and group members. If the subgroups need to be resolved, it should be done using the sid_object. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a group_object and the optional state element specifies the metadata to check.',
          title: 'Group Test',
        },
        iflisteners_test: {
          description:
            'The iflisteners_test is used to check what applications such as packet sniffers that are bound to an interface on the system. This is limited to applications that are listening on AF_PACKET sockets. Furthermore, only applications bound to an ethernet interface should be collected. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an iflisteners_object and the optional iflisteners_state element specifies the data to check.',
          title: 'Interface Listeners Test',
        },
        image_test: {
          description:
            'The image_test provides support for checking the metadata of IPS images on Solaris systems. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a image_object and the optional state elements reference image_states that specify the metadata to check about a set of images.',
          title: 'Image Test',
        },
        inetd_test: {
          description:
            'The inetd test is used to check information associated with different Internet services. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an inetd_object and the optional state element specifies the information to check.',
          title: 'Inetd Test',
        },
        inetlisteningservers_test: {
          description:
            'The inet listening servers test is used to check what applications are listening on the network.  This is limited to applications that are listening for connections that use the TCP or UDP protocols and have addresses represented as IPv4 or IPv6 addresses (AF_INET or AF_INET6). It is generally using the parsed output of running the command netstat -tuwlnpe with root privilege. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an inetlisteningservers_object and the optional state element specifies the data to check.',
          title: 'Inet Listening Servers Test',
        },
        interface_test: {
          description:
            'The interface test enumerate various attributes about the interfaces on a system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an interface_object and the optional state element specifies the interface information to check.',
          title: 'Interface Test',
        },
        interim_fix_test: {
          description:
            'The interim fix test is used to check information associated with different interim or emergency fixes installed on the system. The information being tested is based off the emgr -l -u VUID command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an interim_fix_object and the optional state element specifies the information to check.',
          title: 'Interim Test',
        },
        isainfo_test: {
          description:
            'The isainfo test reveals information about the instruction set architectures. This information can be retrieved by the isainfo command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an isainfo_object and the optional state element specifies the metadata to check.',
          title: 'Isainfo Test',
        },
        ldap_test: {
          description:
            'The LDAP test is used to check information about specific entries in an LDAP directory. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an ldap_object and the optional state element, ldap_state, specifies the metadata to check.',
          title: 'Ldap Test',
        },
        ldap57_test: {
          description:
            'The LDAP test is used to check information about specific entries in an LDAP directory. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an ldap57_object and the optional state element, ldap57_state, specifies the metadata to check.',
          title: 'Ldap Test',
        },
        license_test: {
          description:
            'The license_test is used to check the content of a particular entry in the Windows registry HKLMSYSTEMCurrentControlSetControlProductOptions key, ProductPolicy value. Access to this data is exposed by the functions NtQueryLicenseValue (and also, in version 6.0 and higher, ZwQueryLicenseValue) in NTDLL.DLL.',
          title: 'License Test',
        },
        line_test: {
          description:
            'The line test is used to check the properties of specific output lines from a SHOW command, such as show running-config. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a line_object and the optional state element specifies the data to check.',
          title: 'Line Test',
        },
        lockoutpolicy_test: {
          description:
            'The lockout policy test enumerates various attributes associated with lockout information for users and global groups in the security database. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a lockoutpolicy_object and the optional state element specifies the metadata to check.',
          title: 'Lockout Policy Test',
        },
        metabase_test: {
          description:
            'The metabase test is used to check information found in the Windows metabase. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a metabase_object and the optional state element specifies the metadata to check.',
          title: 'Metabase Test',
        },
        ndd_test: {
          description: 'From /usr/bin/ndd. See ndd manpage for specific fields',
          title: 'Ndd Test',
        },
        no_test: {
          description:
            'The no test is used to check information related to the /usr/sbin/no command and the parameters it manages. The no command sets or displays current or next boot values for network tuning parameters.  The information being tested is based off the /usr/sbin/no -o command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a no_object and the optional state element specifies the value to check for.',
          title: 'No Test',
        },
        ntuser_test: {
          description:
            'The ntuser test is used to check metadata associated with Windows ntuser.dat files. It extends the standard TestType as defined in the oval-definitions-schema and                        one should refer to the TestType description for more information. The required object element references a ntuser_object and the optional state element specifies the ntuser                        data to check.',
          title: 'Ntuser Test',
        },
        oslevel_test: {
          description:
            'The oslevel test reveals information about the release and maintenance level of AIX operating system. This information can be retrieved by the /usr/bin/oslevel -r command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an oslevel_object and the optional state element specifies the metadata to check.',
          title: 'Oslevel Test',
        },
        package_test: {
          description:
            'The package test is used to check information associated with different SVR4 packages installed on the system. Image Packaging System (IPS) packages are not supported by this test. The information used by this test is modeled after the /usr/bin/pkginfo command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an package_object and the optional state element specifies the information to check.',
          title: 'Package Test',
        },
        package511_test: {
          description:
            'The package511_test provides support for checking the metadata of packages installed using the Solaris Image Packaging System. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a package511_object and the optional state elements reference package511_states that specify the metadata to check about a set of packages.',
          title: 'Package Test',
        },
        packageavoidlist_test: {
          description:
            'The packageavoidlist_test provides support for checking the metadata of IPS packages that have been flagged as needing to avoid from installation on a Solaris system. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a packageavoidlist_object and the optional state elements reference packageavoidlist_states that specify the metadata to check about a set of packages that have been flagged as to be avoided on a Solaris system.',
          title: 'Package Avoid List Test',
        },
        packagecheck_test: {
          description:
            'The packagecheck_test is used to verify the integrity of an installed Solaris SVR4 package. Image Packaging System (IPS) packages are not supported by this test. The information used by this test is modeled after the pkgchk command. For more information, see pkgchk(1M). It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a packagecheck_object and the optional packagecheck_state element specifies the data to check.',
          title: 'Package Check Test',
        },
        packagefreezelist_test: {
          description:
            'The packagefreezelist_test provides support for checking the metadata of IPS packages that have been frozen at a particular version. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a packagefreezelist_object and the optional state elements reference packagefreezelist_states that specify the metadata to check about a set of packages.',
          title: 'Package Freeze List Test',
        },
        packagepublisher_test: {
          description:
            'The packagepublisher_test provides support for checking the metadata of package publishers on a Solaris system. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a packagepublisher_object and the optional state elements reference packagepublisher_states that specify the metadata to check about a set of package publishers on a Solaris system.',
          title: 'Package Publisher Test',
        },
        partition_test: {
          description:
            'The partition_test is used to check the information associated with partitions on the local system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a partition_object and the optional state element references a partition_state that specifies the information to check.',
          title: 'Partition Test',
        },
        password_test: {
          description:
            'The password test is used to check metadata associated with the UNIX password file, of the sort returned by the passwd command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a password_object and the optional state element specifies the metadata to check.',
          title: 'Password Test',
        },
        passwordpolicy_test: {
          description:
            'The password policy test is used to check specific policy associated with passwords. It is important to note that these policies are specific to certain versions of Windows.  As a result, the documentation for that version of Windows should be consulted for more information. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a passwordpolicy_object and the optional state element specifies the metadata to check.',
          title: 'Password Policy Test',
        },
        patch_test: {
          description:
            'The patch test is used to check information associated with different patches installed on the system. The information being tested is based off the /usr/bin/showrev -p command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an inetd_object and the optional state element specifies the information to check.',
          title: 'Patch Test',
        },
        patch53_test: {
          description:
            'From /usr/sbin/swlist -l patch PHxx_yyyyy. See swlist manpage for specific fields',
          title: 'Patch Test',
        },
        patch54_test: {
          description:
            'The patch test is used to check information associated with different patches for SVR4 packages installed on the system. Image Packaging System (IPS) packages do not support patches and are not supported by this test. The information being tested is based off the /usr/bin/showrev -p command. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an inetd_object and the optional state element specifies the information to check.',
          title: 'Patch Test',
        },
        peheader_test: {
          description:
            'The peheader_test is used to check data from a Portable Executable file header. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a peheader_object and the optional state element specifies the metadata to check.',
          title: 'Peheader Test',
        },
        plist511_test: {
          description:
            'The plist511_test is used to check the value(s) associated with property list preference keys. It can be used to represent any plist file in XML form (whether its native format is ASCII text, binary, or XML), permitting the use of the XPATH query language to explore its contents. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a plist511_object and the optional plist511_state element specifies the data to check.',
          title: 'Plist Test',
        },
        port_test: {
          description:
            'The port test is used to check information about the available ports on a Windows system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a port_object and the optional state element specifies the port information to check.',
          title: 'Port Test',
        },
        printereffectiverights_test: {
          description:
            'The printer effective rights test is used to check the effective rights associated with Windows printers. The printereffectiverights_test element extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a printereffectiverights_object and the optional state element specifies the metadata to check.',
          title: 'Printer Effective Rights Test',
        },
        process_test: {
          description:
            'The process_test is used to check information found in the Windows processes. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a process_object and the optional state element references a process_state element that specifies the process information to check.',
          title: 'Process Test',
        },
        process58_test: {
          description:
            'The process58_test is used to check information found in the Windows processes. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a process58_object and the optional state element references a process58_state element that specifies the process information to check.',
          title: 'Process Test',
        },
        registry_test: {
          description:
            'The registry test is used to check metadata associated with Windows registry key. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a registry_object and the optional state element specifies the registry data to check.',
          title: 'Registry Test',
        },
        regkeyauditedpermissions_test: {
          description:
            "The registry key audited permissions test is used to check the audit permissions associated with Windows registry keys. Note that the trustee's audited permissions are the audit permissons that the SACL grants to the trustee or to any groups of which the trustee is a member. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a regkeyauditedpermissions_object and the optional state element specifies the metadata to check.",
          title: 'Regkey Audited Permissions Test',
        },
        regkeyauditedpermissions53_test: {
          description:
            "The registry key audited permissions test is used to check the audit permissions associated with Windows registry keys. Note that the trustee's audited permissions are the audit permissons that the SACL grants to the trustee or to any groups of which the trustee is a member. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a regkeyauditedpermissions53_object and the optional state element specifies the metadata to check.",
          title: 'Regkey Audited Permissions Test',
        },
        regkeyeffectiverights_test: {
          description:
            "The registry key effective rights test is used to check the effective rights associated with Windows files. Note that the trustee's effective access rights are the access rights that the DACL grants to the trustee or to any groups of which the trustee is a member. The regkeyeffectiverights_test element extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a regkeyeffectiverights_object and the optional state element specifies the metadata to check.",
          title: 'Regkey Effective Rights Test',
        },
        regkeyeffectiverights53_test: {
          description:
            "The registry key effective rights test is used to check the effective rights associated with Windows files. Note that the trustee's effective access rights are the access rights that the DACL grants to the trustee or to any groups of which the trustee is a member. The regkeyeffectiverights53_test element extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a regkeyeffectiverights53_object and the optional state element specifies the metadata to check.",
          title: 'Regkey Effective Rights Test',
        },
        router_test: {
          description:
            'The router test is used to check the properties of specific output lines from a router configurated instance in IOS.',
          title: 'Router Test',
        },
        routingprotocolauthintf_test: {
          description:
            'The routing protocol authentication interface test is used to check the properties of routing protocol authentication configured under interfaces in IOS.',
          title: 'Routing Protocol Auth Interface Test',
        },
        routingtable_test: {
          description:
            "The routingtable_test is used to check information about the IPv4 and IPv6 routing table entries found in a system's primary routing table. It is important to note that only numerical addresses will be collected and that their symbolic representations will not be resolved. This equivalent to using the '-n' option with route(8) or netstat(8). It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a routingtable_object and the optional routingtable_state element specifies the data to check.",
          title: 'Routing Table Test',
        },
        rpminfo_test: {
          description:
            'The rpminfo_test is used to check the RPM header information for a given RPM package. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a rpminfo_object and the optional state element specifies the data to check.',
          title: 'Rpm Info Test',
        },
        rpmverify_test: {
          description:
            'The rpmverify_test is used to verify the integrity of installed RPMs. This test aligns with the rpm -V command for verifying RPMs. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a rpmverify_object and the optional state element specifies the data to check.',
          title: 'Rpm Verify Test',
        },
        rpmverifyfile_test: {
          description:
            'The rpmverifyfile_test is used to verify the integrity of the individual files in installed RPMs. This test aligns with the rpm -V command for verifying RPMs. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a rpmverifyfile_object and the optional state element specifies the data to check.',
          title: 'Rpm Verify File Test',
        },
        rpmverifypackage_test: {
          description:
            'The rpmverifypackage_test is used to verify the integrity of installed RPMs. This test aligns with the rpm -V command for verifying RPMs. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a rpmverifypackage_object and the optional state element specifies the data to check.',
          title: 'Rpm Verify Package Test',
        },
        runlevel_test: {
          description:
            'The runlevel test is used to check information about which runlevel specified services are scheduled to exist at. For more information see the output generated by a chkconfig --list. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a runlevel_object and the optional state element specifies the data to check.',
          title: 'Runlevel Test',
        },
        sccs_test: {
          description:
            'The set of files to be evaluated may be identified with either a complete filepath or a path and filename. Only one of these options may be selected.',
          title: 'Sccs Test',
        },
        section_test: {
          description:
            'The section test is used to check the properties of specific output lines from a configuration section.',
          title: 'Section Test',
        },
        selinuxboolean_test: {
          description:
            'The selinuxboolean_test is used to check the current and pending status of a SELinux boolean. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a selinuxboolean_object and the optional state element references a selinuxboolean_state that specifies the metadata to check.',
          title: 'Selinux Boolean Test',
        },
        selinuxsecuritycontext_test: {
          description:
            'The selinuxsecuritycontext_test is used to check the security context of a file or process on the local system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a selinuxsecuritycontext_object and the optional state element references a selinuxsecuritycontext_state that specifies the metadata to check.',
          title: 'Selinux Security Context Test',
        },
        service_test: {
          description:
            'The service_test is used to check metadata associated with Windows services. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a service_object and the optional state elements specify the metadata to check.',
          title: 'Service Test',
        },
        serviceeffectiverights_test: {
          description:
            "The service effective rights test is used to check the effective rights associated with Windows services. Note that the trustee's effective access rights are the access rights that the DACL grants to the trustee or to any groups of which the trustee is a member. The serviceeffectiverights_test element extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a serviceeffectiverights_object and the optional state element specifies the metadata to check.",
          title: 'Service Effective Rights Test',
        },
        shadow_test: {
          description:
            "The shadow test is used to check information from the /etc/shadow file for a specific user. This file contains a user's password, but also their password aging and lockout information. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an shadow_object and the optional state element specifies the information to check.",
          title: 'Shadow Test',
        },
        sharedresource_test: {
          description:
            'The shared resource test is used to check properties associated with any shared resource on the system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a sharedresource_object and the optional state element specifies the metadata to check.',
          title: 'Shared Resource Test',
        },
        sharedresourceauditedpermissions_test: {
          description:
            "The shared resource audited permissions test is used to check the audit permissions associated with any shared resource on the system. Note that the trustee's audited permissions are the audit permissons that the SACL grants to the trustee or to any groups of which the trustee is a member. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a sharedresourceauditedpermissions_object and the optional state element specifies the metadata to check.",
          title: 'Shared Resource Audited Permissions Test',
        },
        sharedresourceeffectiverights_test: {
          description:
            "The shared resource effective rights test is used to check the effective rights associated with any shared resource on the system. Note that the trustee's effective access rights are the access rights that the DACL grants to the trustee or to any groups of which the trustee is a member. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a sharedresourceeffectiverights_object and the optional state element specifies the metadata to check.",
          title: 'Shared Resource Effective Rights Test',
        },
        shell_test: {
          description:
            'The shell test is used to check information by running specific commands.',
          title: 'Shell Test',
        },
        shellcommand_test: {
          description:
            'The shell command test is used to check system and configuration by running specific commands and matching the output against certain patterns.',
          title: 'Shell Command Test',
        },
        sid_sid_test: {
          description:
            'The sid_sid_test is used to check properties associated with the specified SID. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a sid_sid_object and the optional state element specifies the metadata to check.',
          title: 'Sid Test',
        },
        sid_test: {
          description:
            'The SID test is used to check properties associated with the specified SID. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a sid_object and the optional state element specifies the metadata to check.',
          title: 'Sid Test',
        },
        slackwarepkginfo_test: {
          description:
            'The slackware package info test is used to check information associated with a given Slackware package. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a slackwarepkginfo_object and the optional state element specifies the data to check.',
          title: 'Slackware Pkginfo Test',
        },
        smf_test: {
          description:
            'The smf_test is used to check service management facility controlled services including traditional unix rc level start/kill scrips and inetd daemon services. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a smf_object and the optional state element specifies the information to check.',
          title: 'Smf Test',
        },
        smfproperty_test: {
          description:
            'The smfproperty_test is used to check the value of properties associated with SMF services. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an smfproperty_object and the optional state elements reference a smfproperty_state and specifies the data to check.',
          title: 'SMF Property Test',
        },
        snmp_test: {
          description:
            'Tests if lines under the global context associated with snmp that have a specifiec access list or community name.',
          title: 'Snmp Test',
        },
        snmpcommunity_test: {
          description:
            'The snmpcommunity test is used to check the properties of specific output lines from an SNMP configuration.',
          title: 'Snmp Community Test',
        },
        snmpgroup_test: {
          description:
            'The snmpgroup test is used to check the properties of specific output lines from an SNMP group configuration.',
          title: 'Snmp Group Test',
        },
        snmphost_test: {
          description:
            'The snmphost test is used to check the properties of specific output lines from an SNMP configuration.',
          title: 'Snmp Host Test',
        },
        snmpuser_test: {
          description:
            'The snmpuser test is used to check the properties of specific output lines from an SNMP user configuration.',
          title: 'Snmp User Test',
        },
        snmpview_test: {
          description:
            'The snmpview test is used to check the properties of specific output lines from an SNMP view configuration.',
          title: 'Snmp View Test',
        },
        sql_test: {
          description:
            'The sql test is used to check information stored in a database. It is often the case that applications store configuration settings in a database as opposed to a file. This test has been designed to enable those settings to be tested. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a wmi_object and the optional state element specifies the metadata to check.',
          title: 'Sql Test',
        },
        sql57_test: {
          description:
            'The sql test is used to check information stored in a database. It is often the case that applications store configuration settings in a database as opposed to a file. This test has been designed to enable those settings to be tested. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a wmi_object and the optional state element specifies the metadata to check.',
          title: 'Sql Test',
        },
        swlist_test: {
          description:
            "Output of /usr/sbin/swlist command. Note: A quick way to check for the installation of a specific fileset is to use the command 'swlist -a version -l fileset filesetname'. See manpage for swlist for explanation of additional command options.",
          title: 'Swlist Test',
        },
        symlink_test: {
          description:
            'The symlink_test is used to obtain canonical path information for symbolic links.',
          title: 'Symlink Test',
        },
        sysctl_test: {
          description:
            'The sysctl_test is used to check the values associated with the kernel parameters that are used by the local system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a sysctl_object and the optional state element references a sysctl_state that specifies the information to check.',
          title: 'Sysctl Test',
        },
        systemdunitdependency_test: {
          description:
            'The systemdunitdependency_test is used to retrieve information about dependencies of a single systemd unit in the form of a list. This list contains all dependencies, including transitive dependencies. For more information see the output generated by systemctl list-dependencies --plain $unit. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a systemdunitdependency_object and the optional state element specifies the data to check.',
          title: 'Systemd Unit Dependency Test',
        },
        systemdunitproperty_test: {
          description:
            'The systemdunitproperty_test is used to retrieve information about systemd units in form of properties. For more information see the output generated by systemctl show $unit. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a systemdunitproperty_object and the optional state element specifies the data to check.',
          title: 'Systemd Unit Property Test',
        },
        systemmetric_test: {
          description:
            'The system metric test is used to check the value of a particular Windows system metric. Access to this information is exposed by the GetSystemMetrics function in User32.dll.',
          title: 'System Metric Test',
        },
        tclsh_test: {
          description:
            'The tclsh test is used to check tclsh information of the IOS operating system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a tclsh_object and the optional state element specifies the data to check.',
          title: 'Tclsh Test',
        },
        textfilecontent_test: {
          description:
            'The textfilecontent_test element is used to check the contents of a text file (aka a configuration file) by looking at individual lines. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a textfilecontent_object and the optional state element specifies the metadata to check.',
          title: 'Text File Content Test',
        },
        textfilecontent54_test: {
          description:
            'The textfilecontent54_test element is used to check the contents of a text file (aka a configuration file) by looking at individual blocks of text. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a textfilecontent54_object and the optional state element specifies the metadata to check.',
          title: 'Text File Content Test',
        },
        trusted_test: {
          description:
            'This test allows for analysis of account settings in trusted HP-UX installations',
          title: 'Trusted Test',
        },
        uac_test: {
          description:
            'The user access control test is used to check setting related to User Access Control within Windows. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a uaac_object and the optional state element specifies the metadata to check.',
          title: 'Uac Test',
        },
        uname_test: {
          description:
            'The uname test reveals information about the hardware the machine is running on. This information is the parsed equivalent of uname -a. For example: "Linux quark 2.6.5-7.108-default #1 Wed Aug 25 13:34:40 UTC 2004 i686 i686 i386 GNU/Linux" or "Darwin TestHost 7.7.0 Darwin Kernel Version 7.7.0: Sun Nov 7 16:06:51 PST 2004; root:xnu/xnu-517.9.5.obj~1/RELEASE_PPC Power Macintosh powerpc". It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a uname_object and the optional state element specifies the metadata to check.',
          title: 'Uname Test',
        },
        unix_file_test: {
          description:
            'The file test is used to check metadata associated with Unix files. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a file_object and the optional state element specifies the metadata to check.',
          title: 'Unix File Test',
        },
        unknown_test: {
          description:
            "An unknown_test acts as a placeholder for tests whose implementation is unknown. This test always evaluates to a result of 'unknown'. Any information that is known about the test should be held in the notes child element that is available through the extension of the abstract test element. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. Note that for an unknown_test, the required check attribute that is part of the extended TestType should be ignored during evaluation and hence can be set to any valid value.",
          title: 'Manual test, please consult benchmark',
        },
        user_sid_test: {
          description:
            'The user_sid_test is used to check information about Windows users. When the user_sid_test collects the user SIDs on the system, it should only include the local and built-in user SIDs and not domain user SIDs.  However, it is important to note that domain user SIDs can still be looked up. Also, note that the collection of groups, for which a user is a member, is not recursive. The only groups that will be collected are those for which the user is a direct member. For example, if a user is a member of group A, and group A is a member of group B, the only group that will be collected is group A. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a user_sid_object and the optional state element specifies the metadata to check.',
          title: 'User SID Test(deprecated)',
        },
        user_sid55_test: {
          description:
            'The user_sid55_test is used to check information about Windows users. When the user_sid55_test collects the user SIDs on the system, it should only include the local and built-in user SIDs and not domain user SIDs.  However, it is important to note that domain user SIDs can still be looked up. Also, note that the collection of groups, for which a user is a member, is not recursive. The only groups that will be collected are those for which the user is a direct member. For example, if a user is a member of group A, and group A is a member of group B, the only group that will be collected is group A. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a user_sid55_object and the optional state element specifies the metadata to check.',
          title: 'User SID55 Test',
        },
        user_test: {
          description:
            'The user_test is used to check information about Windows users. When the user_test collects the users on the system, it should only include the local and built-in user accounts and not domain user accounts.  However, it is important to note that domain user accounts can still be looked up. Also, note that the collection of groups, for which a user is a member, is not recursive. The only groups that will be collected are those for which the user is a direct member. For example, if a user is a member of group A, and group A is a member of group B, the only group that will be collected is group A. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a user_object and the optional state element specifies the metadata to check.',
          title: 'User Test',
        },
        userright_test: {
          description:
            'The userright_test is used to enumerate all of the SIDs that have been granted a specific user right/privilege.',
          title: 'User Right Test',
        },
        variable_test: {
          description:
            'The variable test allows the value of a variable to be compared to a defined value. As an example one might use this test to validate that a variable being passed in from an external source falls within a specified range. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a variable_object and the optional state element specifies the value to check.',
          title: 'Variable Test',
        },
        variant_test: {
          description:
            'The variant_test is used to check the variants associated with the current Image Packaging System image. Variants are properties that control whether or not mutually exclusive components from a package are installed on a system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an variant_object and the optional state elements reference a variant_state and specifies the data to check.',
          title: 'Variant Test',
        },
        version_test: {
          description:
            'The version test is used to check the version of the IOS operating system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a version_object and the optional state element specifies the data to check.',
          title: 'Version Test',
        },
        version55_test: {
          description:
            'The version55_test is used to check the version of the IOS operating system. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a version_object and the optional state element specifies the data to check.',
          title: 'Version Test',
        },
        virtualizationinfo_test: {
          description:
            'The virtualizationinfo_test provides support for checking the metadata associated with the current virtualization environment this instance of Solaris is running on. The test extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a virtualizationinfo_object and the optional state elements reference virtualizationinfo_states that specify the metadata to check the current virtualization environment.',
          title: 'Virtualization Info Test',
        },
        vm_advancedsetting_test: {
          description:
            'The vm_advancedsetting_test is used to collect information about various virtual machine settings on an ESXi host.',
          title: 'VM Advanced Setting Test',
        },
        vmhost_advancedsetting_test: {
          description:
            'The vmhost_advancedsetting_test is used to collect advanced configuration information from an ESXi host.',
          title: 'VMHOST Advanced Setting Test',
        },
        vmhost_module_test: {
          description:
            "The vmhost_modules_test is used to determine if any ESXi host's loaded kernel modules lack valid digital signatures.",
          title: 'VMHOST Module Test',
        },
        volume_test: {
          description:
            'The volume_test is used to check information about different storage volumes found on a Windows system. This includes the various system flags returned by GetVolumeInformation(). It is important to note that these system flags are specific to certain versions of Windows.  As a result, the documentation for that version of Windows should be consulted for more information. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a volume_object and the optional state element specifies the metadata to check.',
          title: 'Volume Test',
        },
        windows_file_test: {
          description:
            'The file test is used to check metadata associated with Windows files. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a file_object and the optional state element specifies the metadata to check.',
          title: 'Windows File Test',
        },
        wmi_test: {
          description:
            'The wmi test is used to check information accessed by WMI. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a wmi_object and the optional state element specifies the metadata to check.',
          title: 'Wmi Test',
        },
        wmi57_test: {
          description:
            'The wmi57 test is used to check information accessed by WMI. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a wmi57_object and the optional state element specifies the metadata to check.',
          title: 'Wmi Test',
        },
        wuaupdatesearcher_test: {
          description:
            'The wuaupdatesearcher_test is used to evaluate patch level in a Windows environment utilizing the WUA (Windows Update Agent) interface.  It is based on the Search method of the IUpdateSearcher interface found in the WUA API.  It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a wuaupdatesearcher_object and the optional state element specifies the metadata to check.',
          title: 'wuaupdatesearcher Test',
        },
        xinetd_test: {
          description:
            'The xinetd test is used to check information associated with different Internet services. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references an inetd_object and the optional state element specifies the information to check.',
          title: 'Xinetd Test',
        },
        xmlfilecontent_test: {
          description:
            'The xmlfilecontent_test element is used to explore the contents of an xml file. This test allows specific pieces of an xml document specified using xpath to be tested. It extends the standard TestType as defined in the oval-definitions-schema and one should refer to the TestType description for more information. The required object element references a xmlfilecontent_object and the optional state element specifies the metadata to check.',
          title: 'Xml File Content Test',
        },
      },
      recurse: 'Recurse',
      recurseDirection: 'Recurse Direction',
      recurseFileSystem: 'Recurse File System',
      release: 'Release',
      rpmPackageName: 'RPM Package Name',
      searchLocation: 'Search Location',
      singleline: 'Singleline',
      spaceLeft: 'Space Left',
      spaceUsed: 'Space Used',
      systemdUnit: 'Systemd Unit',
      testObject: '__test__ Object',
      testState: '__test__ State',
      testTitle: 'Test Title',
      tests: {
        auditeventpolicysubcategories: {
          accountLockout: 'Account Lockout',
          applicationGenerated: 'Application Generated',
          applicationGroupManagement: 'Application Group Management',
          auditPolicyChange: 'Audit Policy Change',
          authenticationPolicyChange: 'Authentication Policy Change',
          authorizationPolicyChange: 'Authorization Policy Change',
          centralAccessPolicyStaging: 'Central Access Policy Staging',
          certificationServices: 'Certification Services',
          computerAccountManagement: 'Computer Account Management',
          credentialValidation: 'Credential Validation',
          detailedDirectoryServiceReplication:
            'Detailed Directory Service Replication',
          detailedFileShare: 'Detailed File Share',
          directoryServiceAccess: 'Directory Service Access',
          directoryServiceChanges: 'Directory Service Changes',
          directoryServiceReplication: 'Directory Service Access',
          distributionGroupManagement: 'Distribution Account Management',
          dpapiActivity: 'DPAPI Activity',
          fileShare: 'File Share',
          fileSystem: 'File System',
          filteringPlatformConnection: 'Filtering Platform Connection',
          filteringPlatformPacketDrop: 'Filtering Platform Packet Drop',
          filteringPlatformPolicyChange: 'Filtering Platform Policy Change',
          groupMembership: 'Group Membership',
          handleManipulation: 'Handle Manipulation',
          ipsecDriver: 'IPsec Driver',
          ipsecExtendedMode: 'IPsec Extended Mode',
          ipsecMainMode: 'IPsec Main Mode',
          ipsecQuickMode: 'IPsec Quick Mode',
          kerberosAuthenticationService: 'Kerboros Authentication Service',
          kerberosServiceTicketOperations: 'Kerberos Service Ticket Operations',
          kerberosTicketEvents: 'Kerberos Ticket Events',
          kernelObject: 'Kernel Object',
          logoff: 'Logoff',
          logon: 'Logon',
          logonClaims: 'User / Device Claims',
          mpssvcRuleLevelPolicyChange: 'MPSSVC Rule-Level Policy Change',
          networkPolicyServer: 'Network Policy Server',
          nonSensitivePrivilegeUse: 'Non Sensitive Privilege Use',
          otherAccountLogonEvents: 'Other Account Logon Events',
          otherAccountManagementEvents: 'Other Account Management Events',
          otherLogonLogoffEvents: 'Other Logon/Logoff Events',
          otherObjectAccessEvents: 'Other Object Access Events',
          otherPolicyChangeEvents: 'Other Policy Change Events',
          otherPrivilegeUseEvents: 'Other Privilege Use Events',
          otherSystemEvents: 'Other System Events',
          plugAndPlayEvents: 'Plug and Play Events',
          processCreation: 'Process Creation',
          processTermination: 'Process Termination',
          registry: 'Registry',
          removableStorage: 'Removable Storage',
          rpcEvents: 'RPC Events',
          sam: 'SAM',
          securityGroupManagement: 'Security Group Management',
          securityStateChange: 'Security State Change',
          securitySystemExtension: 'Security System Extension',
          sensitivePrivilegeUse: 'Sensitive Privilege Use',
          specialLogon: 'Special Logon',
          stateTooltip: `__description__ <p>Category: __category__</p>`,
          systemIntegrity: 'System Integrity',
          userAccountManagement: 'User Account Management',
        },
        bgpneighbor: {
          neighbor: 'BGP Neighbor',
          password: '$t(password)',
        },
        cmdlet: {
          moduleId: 'Module ID',
          moduleName: 'Module Name',
          moduleVersion: 'Module Version',
          noun: 'Noun',
          parameters: 'Parameters',
          select: 'Select',
          value: 'Value',
          verb: 'Verb',
        },
        dpkginfo: {
          arch: '$t(customPolicy.checkDetails.arch)',
          epoch: '$t(customPolicy.checkDetails.epoch)',
          evr: '$t(customPolicy.checkDetails.evr)',
          name: 'DPKG Package Name',
          release: '$t(customPolicy.checkDetails.release)',
          version: '$t(customPolicy.checkDetails.version)',
        },
        family: {
          family: 'Family',
        },
        file: {
          _stateBanner: 'Expected File Configuration',
          aTime: 'A_time',
          cTime: 'C_time',
          fileSpec: '$t(customPolicy.checkDetails.fileSpec)',
          filename: '$t(customPolicy.checkDetails.filename)',
          filepath: '$t(customPolicy.checkDetails.filepath)',
          gexec: 'Gexec',
          gread: 'Gread',
          groupId: '$t(customPolicy.checkDetails.groupId)',
          gwrite: 'Gwrite',
          hasExtendedAcl: 'Has Extended ACL',
          mTime: 'M_time',
          oexec: 'Oexec',
          oread: 'Oread',
          owrite: 'Owrite',
          path: '$t(customPolicy.checkDetails.path)',
          sgid: 'Sgid',
          size: 'Size',
          sticky: 'Sticky',
          suid: 'Suid',
          type: '$t(customPolicy.checkDetails.type)',
          uexec: 'Uexec',
          uread: 'Uread',
          userId: '$t(customPolicy.checkDetails.userId)',
          uwrite: 'Uwrite',
        },
        fileeffectiverights53: {
          _stateBanner: 'Expected File Effective Rights Configuration',
          accessSystemSecurity: 'Access System Security',
          fileAppendData: 'File Append Data',
          fileDeleteChild: 'File Delete Child',
          fileExecute: 'File Execute',
          fileReadAttributes: 'File Read Attributes',
          fileReadData: 'File Read Data',
          fileReadEa: 'File Read EA',
          fileSpec: '$t(customPolicy.checkDetails.fileSpec)',
          fileWriteAttributes: 'File Write Attributes',
          fileWriteData: 'File Write Data',
          fileWriteEa: 'File Write EA',
          filename: '$t(customPolicy.checkDetails.filename)',
          filepath: '$t(customPolicy.checkDetails.filepath)',
          genericAll: 'Generic All',
          genericExecute: 'Generic Execute',
          genericRead: 'Generic Read',
          genericWrite: 'Generic Write',
          includeGroup: 'Include Group',
          path: '$t(customPolicy.checkDetails.path)',
          resolveGroup: 'Resolve Group',
          standardDelete: 'Standard Delete',
          standardReadControl: 'Standard Read Control',
          standardSynchronize: 'Standard Synchronize',
          standardWriteDac: 'Standard Write DAC',
          standardWriteOwner: 'Standard Write Owner',
          trusteeSid: 'Trustee SID',
          windowsView: '$t(customPolicy.checkDetails.windowsView)',
        },
        global: {
          expectedConfig: 'Expected global setting',
          globalCommand: 'Global Command',
        },
        inetlisteningservers: {
          foreignAddress: 'Foreign Address',
          foreignFullAddress: 'Foreign Full Address',
          foreignPort: 'Foreign Port',
          localAddress: 'Local Address',
          localFullAddress: 'Local Full Address',
          localPort: 'Local Port',
          pid: 'PID',
          programName: 'Program Name',
          protocol: 'Protocol',
          userId: '$t(customPolicy.checkDetails.userId)',
        },
        interface: {
          cryptoMap: 'Crypto Map',
          hardwareAddr: 'Hardware Address',
          ipDirectedBroadcastCommand: 'Directed Broadcast Command',
          ipv4AccessList: '$t(customPolicy.checkDetails.ipv4Acl)',
          ipv4Address: 'IPv4 Address',
          ipv6AccessList: '$t(customPolicy.checkDetails.ipv6Acl)',
          ipv6Address: 'IPv6 Address',
          name: '$t(customPolicy.checkDetails.name)',
          proxyArpCommand: 'Proxy ARP Command',
          shutdownCommand: 'Shutdown Command',
          switchportAccessVlan: 'Access VLAN',
          switchportMode: 'Switchport Mode',
          switchportNativeVlan: 'Trunk Native VLAN',
          switchportPortSecurity: 'Switchport Port-Security Commands',
          switchportPrunedVlans: 'Pruned VLANs',
          switchportTrunkEncapsulation: 'Switchport Trunk Encapsulation',
          switchportTrunkedVlans: 'Trunked VLANs',
          urpfCommand: 'uRPF Command',
        },
        line: {
          configLine: 'Config Line',
          showSubcommand: 'Sub-Command',
        },
        lockoutpolicy: {
          forceLogoff: 'Force Logoff',
          lockoutDuration: 'Lockout Duration',
          lockoutObservationWindow: 'Lockout Observation Window',
          lockoutThreshold: 'Lockout Threshold',
        },
        partition: {
          device: 'Device',
          fsType: 'Filesystem Type',
          mountOptions: 'Mount Options',
          mountPoint: 'Mount Point',
          spaceLeft: 'Space Left',
          spaceUsed: 'Space Used',
          totalSpace: 'Total Space',
          uuid: 'Uuid',
        },
        password: {
          gcos: 'GECOS',
          groupId: '$t(customPolicy.checkDetails.groupId)',
          homeDir: 'Home Directory',
          lastLogin: 'Last Login',
          loginShell: 'Login Shell',
          password: '$t(password)',
          userId: '$t(customPolicy.checkDetails.userId)',
          username: 'User Name',
        },
        passwordpolicy: {
          maxPasswdAge: 'Max Password Age',
          minPasswdAge: 'Min Password Age',
          minPasswdLen: 'Min Password Length',
          passwordComplexity: 'Password Complexity',
          passwordHistLen: 'Password History Length',
          reversibleEncryption: 'Reversible Encryption',
        },
        plist511: {
          appId: 'App ID',
          appSpec: 'App Specification',
          filepath: 'File Path',
          filter: 'Filter',
          valueOf: 'Value',
          xpath: 'XPath',
        },
        registry: {
          hive: 'Hive',
          key: 'Key',
          name: 'Name',
          type: 'Data Type',
          value: 'Value',
        },
        routingprotocolauthintf: {
          authType: 'Authentication Type',
          id: 'Protocol ID',
          interface: 'Interface',
          keyChain: 'Key Chain',
          ospfArea: 'OSPF Area',
          protocol: 'Protocol',
        },
        rpminfo: {
          arch: '$t(customPolicy.checkDetails.arch)',
          epoch: '$t(customPolicy.checkDetails.epoch)',
          evr: '$t(customPolicy.checkDetails.evr)',
          extendedName: 'Extended Name',
          filepath: '$t(customPolicy.checkDetails.filepath)',
          name: 'RPM Name',
          release: 'RPM Release',
          signatureKeyid: 'Signature Key ID',
          version: 'RPM Version',
        },
        runlevel: {
          kill: 'To be killed',
          runlevel: 'Runlevel',
          serviceName: 'Service Name',
          start: 'To be started',
        },
        section: {
          _stateBanner: 'Expected Value(s)',
          configLine: 'Config Line',
          sectionCommand: 'Section Command',
          sectionConfigLines: 'Section Config Lines',
        },
        service: {
          controlsAccepted: 'Controls Accepted',
          currentState: 'Current State',
          dependencies: 'Dependencies',
          description: 'Description',
          displayName: 'Display Name',
          path: '$t(customPolicy.checkDetails.path)',
          pid: 'Process ID',
          serviceFlag: 'Service Flag',
          serviceName: 'Service Name',
          serviceType: 'Service Type',
          startName: 'Start Name',
          startType: 'Start Type',
        },
        shadow: {
          chgAllow: 'Mininum password age',
          chgLst: 'Last password change',
          chgReq: 'Maximum password age',
          encryptMethod: 'Encryption Method',
          expDate: 'Expiration Date',
          expInact: 'Additional days allowed to change passwd',
          expWarn: 'Time before expiration warning',
          flag: 'Flag',
          password: '$t(password)',
          username: 'User Name',
        },
        shellcommand: {
          command: 'Command',
          exitStatus: 'Exit Status',
          lineSelection: 'Line Selection',
          stderrLine: 'Stderr Line',
          stdoutLine: 'Stdout Line',
        },
        sid_sid: {
          behaviors: 'Behaviors',
          trusteeDomain: 'Trustee Domain',
          trusteeName: 'Trustee Name',
          trusteeSid: 'Trustee SID',
        },
        smf: {
          execAsUser: 'Exec As User',
          fmri: 'FMRI',
          fmriTooltip: 'Fault Managed Resource Identifier for system objects',
          placeholder: 'svc://localhost/system/system-log:default',
          protocol: 'Protocol',
          serverArguements: 'Server Arguments',
          serverExecutable: 'Server Executables',
          serviceState: 'SMF Service State',
        },
        snmp: {
          _stateBanner: 'Expected SNMP Configuration',
          accessList: 'Access List',
          communityName: 'Community Name',
        },
        snmpcommunity: {
          _stateBanner: 'Expected SNMP Community Configuration',
          ipv4Acl: '$t(customPolicy.checkDetails.ipv4Acl)',
          ipv6Acl: '$t(customPolicy.checkDetails.ipv6Acl)',
          mode: 'Mode',
          name: '$t(customPolicy.checkDetails.name)',
          view: 'View',
        },
        snmpgroup: {
          _stateBanner: 'Expected SNMP Group Configuration',
          ipv4Acl: '$t(customPolicy.checkDetails.ipv4Acl)',
          ipv6Acl: '$t(customPolicy.checkDetails.ipv6Acl)',
          name: '$t(customPolicy.checkDetails.name)',
          notifyView: 'Notify View',
          readView: 'Read View',
          snmpv3SecLevel: 'SNMPv3 Security',
          version: '$t(customPolicy.checkDetails.version)',
          writeView: 'Write View',
        },
        snmphost: {
          _stateBanner: 'Expected SNMP Host Configuration',
          communityOrUser: 'Community or SNMPv3 User',
          host: '$t(customPolicy.checkDetails.host)',
          snmpv3SecLevel: 'SNMPv3 Security',
          traps: 'Traps',
          version: '$t(customPolicy.checkDetails.version)',
        },
        snmpuser: {
          _stateBanner: 'Expected SNMP User Configuration',
          auth: 'SNMPv3 Authentication',
          group: 'Group',
          ipv4Acl: '$t(customPolicy.checkDetails.ipv4Acl)',
          ipv6Acl: '$t(customPolicy.checkDetails.ipv6Acl)',
          name: '$t(customPolicy.checkDetails.name)',
          priv: 'SNMPv3 Encryption',
          version: '$t(customPolicy.checkDetails.version)',
        },
        sql57: {
          connectionString: 'Connection String',
          engine: 'Engine',
          result: 'Result',
          sql: 'Sql',
          version: '$t(customPolicy.checkDetails.version)',
        },
        sysctl: {
          name: 'Name',
          value: 'Value',
        },
        systemdunitproperty: {
          property: 'Property',
          unit: 'Systemd Unit',
          value: 'Value',
        },
        textfilecontent54: {
          _stateBanner: 'Expected Text File Configuration',
          fileSpec: '$t(customPolicy.checkDetails.fileSpec)',
          filename: '$t(customPolicy.checkDetails.filename)',
          filepath: '$t(customPolicy.checkDetails.filepath)',
          instance: 'Instance',
          path: '$t(customPolicy.checkDetails.path)',
          pattern: 'Pattern',
          subexpression: 'Subexpression',
          text: 'Text',
          windowsView: '$t(customPolicy.checkDetails.windowsView)',
        },
        uname: {
          machineClass: 'Machine Class',
          nodeName: 'Node Name',
          osName: 'OS Name',
          osRelease: 'OS Release',
          osVersion: 'OS Version',
          processorType: 'Processor Type',
        },
        user: {
          enabled: 'Enabled',
          group: 'Group SID',
          lastLogon: 'Last LOGON',
          user: 'User SID',
        },
        user_sid: {
          enabled: 'Enabled',
          group: 'Group SID',
          user: 'User SID',
        },
        user_sid55: {
          enabled: 'Enabled',
          groupSid: 'Group SID',
          lastLogon: 'Last LOGON',
          userSid: 'User SID',
        },
        vm_advancedsetting: {
          advancedSettingName: 'Advanced Setting Name',
          advancedSettingValue: 'Advanced Setting Value',
          vmName: 'VM Name',
        },
        vmhost_advancedsetting: {
          advancedSettingName: 'Advanced Setting Name',
          advancedSettingValue: 'Advanced Setting Value',
        },
        vmhost_module: {
          license: 'License',
          moduleFile: 'Module File',
          moduleName: 'Module Name',
          placeholder: 'Empty means all modules',
          signatureDigest: 'Signature Digest',
          signatureFingerprint: 'Signature Fingerprint',
          signedStatus: 'Signed Status',
          version: 'Version',
        },
        wmi57: {
          namespace: 'WMI Namespace',
          operator: 'Operator',
          result: 'Result',
          wql: 'WQL Query',
        },
        xinetd: {
          disabled: 'Disabled',
          flags: 'Flags',
          noAccess: 'No Access',
          onlyFrom: 'Only From',
          port: 'Port',
          protocol: 'Protocol',
          server: 'Server',
          serverArguments: 'Server Arguments',
          serviceName: 'Service Name',
          socketType: 'Socket Type',
          type: '$t(customPolicy.checkDetails.type)',
          user: 'User',
          wait: 'Wait',
        },
        xmlfilecontent: {
          _stateBanner: 'Expected XML File Configuration',
          fileSpec: '$t(customPolicy.checkDetails.fileSpec)',
          filename: '$t(customPolicy.checkDetails.filename)',
          filepath: '$t(customPolicy.checkDetails.filepath)',
          path: '$t(customPolicy.checkDetails.path)',
          valueOf: 'Value of',
          windowsView: '$t(customPolicy.checkDetails.windowsView)',
          xpath: 'XPath',
        },
      },
      toBeSupported: '<TO BE SUPPORTED IN THE FUTURE>',
      totalSpace: 'Total Space',
      type: 'Type',
      userId: 'User ID',
      uuid: 'Uuid',
      value: 'Value',
      valueDetail: 'Value Detail',
      valueDetailTooltip:
        'This summarizes how the “Value” field was used in a test. Any modifications here will change the rule details in the Security Console.',
      valueType: 'Value Type',
      version: 'Version',
      windowsView: 'Windows View',
    },
    cleanUpPolicy: 'Clean Up Policy',
    cleanUpPolicyModal: {
      content:
        "(Experimental feature) This will clean up the policy by removing data in the policy that's related to deleted rules and tests. It will not only slim down the policy, it could also prevent some errors.",
      title: 'Clean Up Policy',
    },
    cleanUpPolicyTooltip:
      'Remove unused data from policy to reduce the size of policy and prevent errors',
    clone: 'Copy',
    components: {
      InvalidPolicyErrorFallback: {
        content: 'TODO: update content copy',
        ctaButtonText: 'Delete Policy',
        docsButtonText: 'Visit Documentation',
        title: 'TODO: update title copy',
      },
    },
    copyPolicyTooltip:
      'This action creates a custom policy that can only be used for agent-based assessments. If you want to assess using the Scan Engine, you must do so from the Engine-Based Assessment page.',
    cpeEditNote:
      'Making edits on CPE will change any relevant applicability checks, just like editing tests in rules.',
    cpeEditNoteTitle: 'Common Platform Enumeration (CPE) Edits',
    cpeViewNote:
      'You will be redirected to view CPE details including relevant applicability checks.',
    cpeViewNoteTitle: 'Common Platform Enumeration (CPE) Details',
    createNewModal: {
      addNewTest: 'Add A New Test',
      addTestGroup: 'Add Test Group',
      createNewRule: 'Create A New Rule',
      ruleDetails: 'Rule Details',
      saveRule: 'Save New Rule',
      saveTest: 'Add Test',
      selectTestType: 'Select Test Type',
      testDetails: 'Test Details',
    },
    createPolicyError: 'An error occurred while creating your policy',
    createRule: 'Create Rule',
    deletePolicy: 'Delete Policy',
    deletePolicyModal: {
      deleteConfirm:
        'This action is permanent. You will not be able to recover your policy.',
      deleteConsolePolicyConfirm:
        'Deleting will not affect this policy on the Engine-Based Assessment page. To delete the policy permanently, you must delete from the Engine-Based Assessment page.',
      deleteError: 'An error occurred while deleting the policy.',
      successMessage: 'The policy was deleted successfully.',
      title: 'Delete the selected policy?',
      warningBanner:
        'Policy is currently enabled for agent assessment. Making changes will force the assessment to stop.',
    },
    deleteRemoveRef: 'Delete & Remove references',
    deleteRule: 'Delete Rules',
    deleteRuleModal: {
      deleteConfirm:
        'Are you sure you want to delete the selected rule(s)? This action cannot be undone.',
      deleteError: 'An error occurred while deleting the selected rule(s).',
      successMessage: 'The selected rules were deleted successfully.',
      successMessageSingleRule: 'The selected rule was deleted successfully.',
    },
    deleteTestGroupModal: {
      deleteConfirm: 'Are you sure you want to delete the selected test group?',
      deleteError: 'An error occurred while deleting the test group.',
      successMessage: 'The test group was deleted successfully.',
    },
    deleteTestModal: {
      deleteConfirm: 'Are you sure you want to delete the selected test?',
      deleteError: 'An error occurred while deleting the test.',
      successMessage: 'The test was deleted successfully.',
    },
    deprecated: 'Deprecated',
    disableRule: 'Disable Rules',
    editConsolePolicyButtonTooltip:
      'To edit this policy, go to the Engine-Based Assessment page.',
    editRule: 'Edit Rule',
    editTest: 'Edit Test',
    enableRule: 'Enable Rules',
    enabledPolicyWarning:
      'This custom policy is currently enabled for agent-based assessment. Only Global Administrators can edit custom policies that are enabled for agent-based assessment.',
    export: 'Export Policy',
    exportDashboardName: '$t(dashboardLabel) __dashboardName__',
    families: {
      android: 'Android',
      apple_ios: 'Apple iOS',
      asa: 'Cisco ASA',
      catos: 'Cisco CatOS',
      cisco_ios: 'Cisco IOS',
      ios: 'iOS',
      iosxe: 'Cisco IOS XE',
      junos: 'Junos',
      macos: 'MacOS',
      pixos: 'Cisco PIX',
      undefined: 'Not Available',
      unix: 'Unix',
      vmware_infrastructure: 'VMWare Infrastructure',
      windows: 'Windows',
    },
    filterLabels: {
      example: 'Example',
      includeDeprecatedPolicies: 'Include Deprecated Policies',
    },
    hideDetails: 'Hide Details',
    import: 'Import',
    importRuleModal: {
      title: 'Import Rules',
    },
    importRules: 'Import Rule(s)',
    includeCustomPolicyCheckboxLabel: 'Include Custom Policies',
    msgTestListNotShown:
      "The policy test list can't be shown at this time, please contact Rapid7 support, thank you.",
    myPolicy: 'My Policy',
    noRulesEnabledWarning: {
      content: 'Enable at least one rule before saving your policy.',
      title: 'Enable a Rule Before Saving',
    },
    none: 'None',
    notSupported: 'Not Yet Supported',
    other: 'Other',
    policyBuilder: 'Policy Builder',
    policyList: 'Policy List',
    policyListTooltips: {
      agentCompatible: `
      <div>
          <span>
            <checkIcon /> - The custom policy can be enabled for agent-based assessment.
          </span>
          <span>
            <xIcon /> - The custom policy did not meet the necessary requirements for agent-based assessment.
          </span>
      </div>`,
      status:
        'Invalid -  An issue was identified with the policy. Ensure that the policy adheres to the SCAP standard.',
    },
    policyProperties: {
      agentCompatible: 'Agent Compatible',
      lastModified: 'Last Modified',
      platform: 'Platform',
      publisher: 'Publisher',
      validationStatus: 'Status',
      version: 'Version',
    },
    processingPolicy:
      'Your policy is currently being processed, check back later.',
    promptToMoveon: 'Would you like to continue?',
    removeRuleRef: 'Remove references to rule in policy',
    requestTest: 'Request test type',
    ruleDetails: {
      class: 'Class',
      cpe: 'CPE String',
      cpeTooltip:
        'CPE string must match regex /[c][pP][eE]:\\/[AHOaho]?(:[A-Za-z0-9\\._\\-~%]*){0,6}$/',
      description: 'Description',
      emptyValue: 'Not specified',
      fixtext: 'Fixtext',
      headerTitle: 'Rule Details',
      operators: {
        and: {
          description: 'Immediate tests and test groups must pass',
          title: 'AND',
        },
        one: {
          description: 'Only one test or test group must pass',
          title: 'ONE',
        },
        or: {
          description: 'At least one test or test group must pass',
          title: 'OR',
        },
        xor: {
          description: 'An odd number of tests or test groups must pass',
          title: 'XOR',
        },
      },
      rationale: 'Rationale',
      role: 'Role',
      ruleTitle: 'Rule Title',
      selectPlaceholder: 'Select __term__',
      severity: 'Severity',
      tests: 'Tests',
      title: 'Title',
      value: 'Value',
      weight: 'Weight',
    },
    ruleGroup: {
      addRuleGroup: 'Add Rule Group',
      deleteConfirm:
        'Are you sure you want to delete this rule group and all the rules contained in it?',
      deleteRuleGroup: 'Delete Rule Group',
      saveButton: 'Save',
      updateButton: 'Update',
      updateRuleGroup: 'Update Rule Group Name',
    },
    ruleList: {
      emptySearch:
        'No rules matched your search. Check the spelling of your search terms or use more general ones.',
      emptyState:
        'The __title__ rule group helps keep your other rule groups organized. Open the rule subgroups below to view rules in this category.',
      emptyStateNoChildren:
        'This __title__ rule group does not have any rules associated with it.',
    },
    ruleTests: {
      subtitle: 'Customize your tests by adding more test groups or tests.',
      title: 'Rule Tests',
    },
    saveAndSyncTooltip:
      'Save and sync your updated policy to the Insight Platform and the Security Console.',
    saveTooltip:
      'Save your updated policy to the Insight Platform (cloud) only.',
    searchFilterDropdownLabel: 'Filter',
    searchFilterDropdownTitle: 'REPLACE THESE',
    searchTitle: 'Search Policies',
    seeDetails: 'See Details',
    selectAllRules: 'Select All Rules',
    skipRules: 'Skip Rule in assessment',
    skipped: 'Skipped',
    syncAndClose: 'Sync and Close',
    syncing: 'Syncing',
    syncingPolicy: 'Syncing Policy... Please do not navigate away.',
    testConfiguration: {
      subtitle: 'Edit your details to configure your test to meet your needs.',
      title: 'Test Configuration',
    },
    testNotEnabled:
      'This test type is not available for editing, but you can make a request for our team to build it.',
    testTypeRequestModal: {
      body:
        'Thank you! Your request has been sent to our team. We’ll notify you when it is available. <a target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/for-customers/"><strong><u>Contact support</u></strong></a> if you need anything else.',
      title: 'Success! Request Confirmed',
    },
    title: 'Policies',
    undoRedo: {
      addPolicyTest: 'Add new policy test',
      deletePolicyTest: 'Delete policy test',
      deleteTestGroup: 'Delete policy test group',
      dragAndDropRule: 'Drag and Drop Rule',
      policyDescUpdate: 'Policy description update',
      policyRuleUpdate: 'Policy rule update on field',
      policyTestOperatorUpdate: 'Update policy test operator',
      policyTestTitleUpdate: 'Policy policy test with title',
      policyTitleUpdate: 'Policy title update',
      policyVersionUpdate: 'Update policy version',
    },
    updatePolicyError: 'An error occurred while updating your policy',
    upload: 'Upload',
    uploadModal: {
      dropzone: 'Click here or drag a file to upload',
      error: 'An error occurred while uploading your policy.',
      rejected:
        'The selected file failed to upload. Verify that your file type meets the requirements.',
      success: 'Your policy was uploaded successfully.',
      summary: `
        <p>Please upload a .zip file with the following file names and formats:</p>
        <ul>
          <li>*-cpe-dictionary.xml</li>
          <li>*-cpe-oval.xml</li>
          <li>*-oval.xml</li>
          <li>*-xccdf.xml</li>
        </ul>
      `,
      title: 'Upload a Policy',
    },
    uploadPolicy: 'Upload Policy',
    viewAuditLogs: 'View Audit Logs',
    warning: {
      missingPolicyDefinitions: 'missing some policy definitions',
    },
  },
};

/* eslint-disable sort-keys/sort-keys-fix */
export default {
  translation: {
    components,
    enums,
    helpfulQueries,
    module: {
      asset,
      vulnerability,
    },
    ...customPolicyModule,
    ...policyModule,
    ...infrastructure,
    RECON: 'RECON CVE-2020-6287',
    access: 'Access',
    accessKey: 'Access Key',
    accessToken: 'Access Token',
    accountId: 'Account ID',
    acronym: {
      vulnerabilities: 'Vulns',
    },
    dashboardQuery: 'Dashboard query',
    rapid7Query: 'Rapid7 query',
    customQuery: 'Custom query',
    customQueryCount: 'Custom query (__count__ card)',
    customQueryCount_plural: 'Custom query (__count__ cards)',
    applyCustomQuery: 'Apply Custom Query',
    customQueryApplied: 'Custom query applied: __query__',
    clickToOpenQueryLogic:
      'Click to open in Query Builder.\nQuery Logic: __query__',
    queryLogic: 'Query Logic: __query__',
    loadDashboardQuery: 'Load Dashboard Query',
    actions: 'Actions',
    activate: 'Activate',
    active: 'Active',
    activelyExploited: {
      entity: {
        cve: 'CVE',
        dateAdded: 'Date Added',
        description: 'Description',
        rationale: 'Rationale',
        source: 'Source',
        target: 'Target',
      },
      title: 'Rapid7 Threat Feed Details',
    },
    add: 'Add',
    addCard: 'Add Card',
    addCardTitle: 'Add a Card',
    addDashboard: 'Add Dashboard',
    addRegistryConnection: 'Add a Registry Connection',
    added: 'Added',
    address: 'Address',
    addresses: 'Addresses',
    admin: {
      manageFilters: '$t(manage) $t(filters)',
      title: '$t(manage) Elements',
    },
    affects: 'Assets Affected',
    agent: 'Agent',
    agentConnectionDetail: 'Agent Connection Detail',
    agentDownload: {
      azure: 'Azure',
      azureTitle: 'Deploy Insight Agent from Microsoft Azure',
      downloading: 'Downloading:',
      error: 'There was an error downloading the agent software.',
      getPackage: 'Get Package',
      installationHelp:
        'See our <a href="https://insightagent.help.rapid7.com/docs/install" target="_blank" rel="noopener noreferrer">Help pages</a> for installation instructions.',
      installationHelpContact:
        'Let us know if we can be of assistance with installing the agent package. If you are not seeing the expected results, please <a target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/contact">contact us</a> for further help.',
      linuxAgent: 'Linux',
      macAgent: 'Mac',
      title: 'Download, Install, &amp; Deploy the Insight Agent',
      windowsAgent: 'Windows',
    },
    agentDownloadBenefits: {
      container: {
        answer:
          'Integrate with Docker registries and Jenkins to automatically assess your container-based deployments in production and pre-production.',
        title: 'Modern network scanning (Container Security)',
      },
      dashboard: {
        answer:
          'Easily create custom cards and full dashboards for anyone and query each card with simple language to track progress of your security program.',
        title: 'Live customizable dashboards',
      },
      integrations: {
        answer:
          "In-product integrations with JIRA and ServiceNow are easy to set up, and let you automatically fold remediation into IT's existing workflows.",
        title: 'JIRA and ServiceNow integrations',
      },
      remediation: {
        answer:
          'Assign and track remediation duties in real time, providing continuous visiblity into how well issues are being fixed.',
        title: 'Remediation Workflow',
      },
    },
    agentDownloadTitle: 'Download the Rapid7 Agent',
    agentID: 'Agent ID',
    agentInstructions: `<p>Copy the zip file to the systems. Unzip the file. You will have two MSI installers, one for 32-bit and one for 64-bit Windows systems, as well as config.json, three security files: client.key, client.crt, and cafile.pem.</p>
        <p>Install the agent by executing the relevant MSI installer. By default, the agent will be installed in c:\\Program Files (x86)\\Rapid7\\Endpoint Agent, and the security files will also be automatically installed. The agent will be installed as service that you will be able to see as the "Rapid7 Endpoint Agent".</p>
        <p>As part of the operation of the agent, information is logged to a “agent.log” file. This file also gets rolled into agent.log.1 and agent.log.2. Should there be an issue, we might ask you for these log files so that we can troubleshoot.</p>`,
    agentManagement: 'Manage Agents',

    agentManagerTitle: 'Agents',
    agentStatus: 'Agent Status',
    agentVersion: 'Version',
    agents: 'Agents',
    agentsCount: '__count__ Agents',
    agentsOldVersion: 'Using old version',
    agentsOnlyDownload: {
      content: {
        description:
          'Download and install the Rapid7 Insight Agent for better visibility and prioritized, actionable views of your data:',
      },
      header: {
        tagline:
          "Modern networks require a modern vulnerability management program. InsightVM uses the analytics of Rapid7's Insight platform to turn your vulnerability data into actionable answers across cloud, virtual, and application infrastructure. <b>Let's get started!</b>",
        title: 'Welcome to the InsightVM Free Trial',
      },
      notification:
        'Have questions, feedback, or need help? <a  target="_blank" rel="noopener noreferrer" class="btn btn--small" href="https://www.rapid7.com/contact">Contact Us</a>',
      onPrem: {
        description:
          'Combine platform features with core on-premises capabilities for comprehensive vulnerability management?',
        download: 'Download our Security Console',
        title: 'Try our on-premises vulnerability scanner',
      },
      or: 'OR',
      questions:
        'Have questions or need help? <a class="btn btn--secondary btn--small" target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/contact">Contact Us</a>',
    },
    agentsPage: {
      chartTitle: 'Assets with Agents by Operating System',
      deployingToAzureDescription:
        'Deploy the Insight Agent in your Azure environment by downloading the Insight Agent for Azure package (this is the only package needed) and following the instructions <a href="https://insightagent.help.rapid7.com/docs/integrations#section-microsoft-azure" target="_blank" rel="noopener noreferrer">here</a>.',
      deployingToAzurePublicKeyLink: 'Get the Public Key',
      deployingToAzurePublicKeyPopupDescription:
        'Copy and paste this key into your Azure Security Center interface to allow Azure to encrypt data.',
      deployingToAzurePublicKeyPopupHeader:
        'Azure Security Center Encoded Public Key',
      summary:
        '__percent__ of my assets with agents are running the __os__ Operating System',
    },
    agentsRecentlyAssessed: 'Recently Assessed',
    alert: 'Alert',
    all: 'All',
    allCards: 'All $t(cardsLabel)',
    allJobs: 'All Jobs',
    allRepositories: 'All Repositories',
    ampersandSymbol: '&&',
    analyze: 'Analyze',
    and: 'and',
    and_upper: 'AND',
    applicationId: 'Application ID',
    apply: 'Apply',
    approvedBy: 'Approved By',
    approve: 'Approve',
    architecture: 'Architecture',
    areYouSure: 'Are you sure?',
    areYouSureDelete: 'Are you sure you want to delete __registry__?',
    areYouSureDeleteConnection:
      'Are you sure you want to delete the connection?',
    areYouSureDeleteRepository:
      'Are you sure you want to delete the repository <strong>__repository__</strong>?',
    assess: 'Assess',
    assessImage: 'Assess Image',
    assessed: 'Assessed',
    unassessed: 'Unassessed',
    assessedAssets: 'Assessed Assets',
    assessment: 'Assessment',
    asset: 'Asset',
    asset_groups: 'Asset Groups',
    asset_indefinite: 'Asset',
    asset_plural: '__count__ Assets',
    asset_plural_indefinite: 'Assets',
    assetCount: 'Asset Count',
    assetNotFound: 'This asset could not be found',
    assetDetails: {
      contextDrivenRiskScore: 'Context-driven Risk Score',
      fetchFailed: 'Failed to retrieve asset, please try later.',
      kpi: {
        malware: 'Malware Kits',
        operatingSystemFingerprints: 'Fingerprints',
      },
      rotatedTitle: 'View Asset Overview',
      save: 'Save',
      title: 'Asset Details',
    },
    assetDetailsLabel: 'Asset Details',
    assetGroup: 'Asset Group',
    assetGroups: {
      donutTitle: 'Riskiest Groups',
      labels: {
        assets: 'Assets',
      },
      title: 'Top Riskiest Asset Groups',
    },
    assetGroupsLabel: 'Asset Groups',
    assetLabel: 'Asset',
    assetName: 'Asset Name',
    assets: 'Assets',
    assetsAffected: 'Number of Affected Assets',
    assetsByComponent: {
      OS: {
        title: 'Assets by OS',
      },
      assessmentStatus: {
        title: 'Assets by Assessment Status',
      },
    },
    assetsWithUnknownOS:
      'of my $t(assets) are running an unidentified $t(operatingSystem)',
    assignTo: 'Assign To',
    assignees: 'Assignees',
    at: 'at',
    autoAssessOnSync: 'Automatically assess new pushed images',
    automation: {
      [EventType.NOTIFICATION]: 'Notification',
      [EventType.WORKFLOW]: 'Workflow',
      advancedFilters: {
        fields: {
          timeCreated: 'Trigger Created',
          timeTriggered: 'Last Triggered',
          timeframe: 'Timeframe',
        },
        search: {
          history: 'Search history',
          [EventType.ACTION]: 'Search actions',
          [EventType.NOTIFICATION]: 'Search notifications',
          [EventType.WORKFLOW]: 'Search workflows',
        },
        searchHeader: {
          history: 'Filter History List',
          [EventType.ACTION]: '$t(actions)',
          [EventType.NOTIFICATION]: '$t(notifications)',
          [EventType.WORKFLOW]: '$t(workflows)',
        },
        triggerTypesHeader: {
          [EventType.ACTION]: 'Action Trigger Types',
          [EventType.NOTIFICATION]: 'Notification Trigger Types',
          [EventType.WORKFLOW]: 'Workflow Trigger Types',
        },
      },
      advancedFiltersTitle: 'Filters',
      areYouSureDelete:
        'Are you sure you want to delete <strong>__name__</strong>?',
      configureAutomation: 'New Automation',
      configuredCount: '__count__ $t(automation.configured.__eventType__)',
      create: {
        [EventType.NOTIFICATION]: 'Create a Notification',
        [EventType.WORKFLOW]: 'Create a Workflow',
        newConnection: {
          step: 'Configure your Work',
          successfulTest: 'The connection test was a success',
          testInProgress: 'This may take a little while...',
          testingConnection: 'Testing __name__ Connection',
        },
        notificationStepThree: {
          allRecipients:
            'Send to all users with access to assets included in trigger scope',
          configureEmail: 'Configure Email',
          configureInProductNotificaton: 'Configure In-Product Notification',
          configureSMS: 'Configure SMS',
          customRecipients: 'Send to custom user list',
          enterCommaEmailAddresses:
            'Enter email addresses, separated by commas',
          enterCommaMobileNumbers: 'Enter mobile numbers, separated by commas',
          notifyMe: 'Notify Me...',
          recipientsEmailAddresses: "Recipients' Email Addresses",
          recipientsMobileNumbers:
            "Recipients' Mobile Numbers (ex, +12345678901)",
          selectNotificationType: 'Select Notification Type',
          step: 'Configure your Notification',
          template: {
            header: {
              [NotificationType.EMAIL]: 'Email Preview',
              [NotificationType.SMS]: 'SMS Preview',
            },
            summary: {
              ASSET_FOUND: '__assetCount__ assets found',
              ASSET_UPDATED: '__assetCount__ assets re-assessed',
              VULNERABILITY_FOUND:
                '__vulnerabilityCount__ vulnerabilities found on __assetCount__ assets',
              VULNERABILITY_REASSESSED:
                '__vulnerabilityCount__ vulnerabilities reassessed on __assetCount__ assets',
            },
          },
        },
        stepOne: {
          header: 'Select an Automation',
          step: 'Select Automation',
        },
        stepThree: {
          confirmTriggerScope: 'Confirm Trigger Scope',
          headerNotification: 'Configure your Notification',
          headerWorkflow: 'Configure your Workflow',
          step: '$t(automation.configureAutomation)',
          uniqueName: 'The $t(workflow) name should be unique.',
          workflowName: 'Name your $t(automation.WORKFLOW)',
        },
        stepTwo: {
          assetDescription: {
            [EventType.WORKFLOW]:
              'Trigger Scope (Run this workflow when an asset meets the following criteria)',
            [EventType.NOTIFICATION]:
              'Trigger Scope (Notify me when an asset meets the following criteria)',
          },
          assetHeader: 'Specify Trigger Scope',
          assetTriggerEvent: 'Trigger when an asset is found',
          description: {
            [EventTriggerStatus.FOUND]: '$t(__trigger__) is found',
            [EventTriggerStatus.REASSESSED]: '$t(__trigger__) is re-assessed',
            [EventTriggerStatus.REMEDIATED]: '$t(__trigger__) is remediated',
            [EventTriggerStatus.UPDATED]: '$t(__trigger__) is updated',
          },
          header: 'Select a Trigger for your $t(automation.__automationType__)',
          headerEdit: 'Trigger for your $t(automation.__automationType__)',
          scope: {
            triggerScope: 'Trigger Scope',
            triggerScopeAsset: {
              notification:
                'Notify me when an asset meets the following criteria',
              workflow:
                'Run this workflow when an asset meets the following criteria',
            },
            triggerScopeVuln: {
              notification:
                'Notify me when a vulnerability meets the following criteria',
              workflow:
                'Run this workflow when a vulnerability meets the following criteria',
            },
          },
          step: 'Select Trigger',
          triggerType: 'Trigger Type',
          vulnDescription: {
            [EventType.WORKFLOW]:
              'Trigger Scope (Run this workflow when a vulnerability meets the following criteria)',
            [EventType.NOTIFICATION]:
              'Trigger Scope (Notify me when a vulnerability meets the following criteria)',
          },
          vulnHeader: 'Specify Trigger Scope',
          vulnTriggerEvent: 'Trigger when a vulnerability is found',
        },
        success: {
          [EventType.NOTIFICATION]:
            'The notification <strong>__name__</strong> has been successfully saved.',
          [EventType.WORKFLOW]:
            'The workflow <strong>__name__</strong> has been successfully configured.',
        },
        title: '$t(automation.configureAutomation)',
        validation: {
          allEmailValid: 'Please make sure all email addresses are valid',
          allSMSValid: 'Please make sure all phone numbers are valid',
          intervalTimeRange: 'The number must be between 1 and __max__',
          oneEmailRecipient: 'There should be at least 1 email recipient',
          oneSMSRecipient: 'There should be at least 1 sms recipient',
          selectRecipientGroup: 'At least one recipient group must be selected',
          smsFormat:
            'Number must be formatted as [+] [country code] [subscriber number including area code]',
          workflowUnsupported:
            'The __trigger__ trigger type does not support this workflow',
        },
        workflowConfiguration: {
          nameWorkflow: 'Name the workflow',
          nameWorkflowDescription: `$t(automation.${EventType.WORKFLOW}): __workflowName__`,
          newConnection: 'New Connection',
          selectConnection: 'Select a Connection',
          selectOrchestrator: 'Select an Orchestrator',
          selectWorkflow: 'Select a Workflow',
          stepCount: 'Steps (__count__)',
          trigger: 'Trigger:',
        },
      },
      deleteError: {
        ACTION: 'An error occurred while deleting the action.',
        ALERT: 'An error occurred while deleting the notification.',
        WORKFLOW: 'An error occurred while deleting the workflow.',
      },
      details: {
        [EventType.ACTION]: 'Action Details',
        [EventType.NOTIFICATION]: 'Notification Details',
        [EventType.WORKFLOW]: 'Workflow Details',
      },
      empty: {
        [EventType.NOTIFICATION]: "You don't have any Notifications yet",
        [EventType.ACTION]: "You don't have any Actions yet",
        [EventType.WORKFLOW]: "You don't have any Workflows yet",
      },
      entity: {
        assetScope: 'Asset Scope',
        automationName: 'Automation Name',
        created: 'Trigger Created',
        events: 'Events',
        initiations: 'Initiations',
        jobs: 'Jobs',
        lastTriggered: 'Last Triggered',
        name: '$t(name)',
        notificationConfiguration: 'Notification Configuration',
        notificationType: 'Notification Type',
        savedConnections: 'Saved Connections',
        status: 'Status',
        summary: 'Summary',
        trigger: 'Trigger',
        triggerType: 'Trigger Type',
        vulnerabilityScope: 'Vulnerability Scope',
        workflowTemplate: '$t(workflow)',
      },
      eventType: {
        [EventType.ACTION]: 'Action',
        [EventType.NOTIFICATION]: 'Notification',
        [EventType.WORKFLOW]: 'Workflow',
      },
      eventTypeBenefits: {
        [EventType.NOTIFICATION]: 'NOTIFICATION',
        [EventType.WORKFLOW]: 'WORKFLOW',
        description: {
          [EventType.NOTIFICATION]:
            'Receive notifications prompted <br/>by InsightVM events',
          [EventType.WORKFLOW]:
            'Orchestrate workflows<br />between InsightVM and other tools',
        },
        notificationAdvantages: {
          email:
            '<strong>Email distribution</strong> to both internal and external addresses',
          sms: '<strong>Text messaging</strong> to mobile numbers via SMS',
        },
        workflowAdvantages: {
          firewall:
            '<strong>Network containment</strong> with Palo Alto PAN-OS and Cisco ISE',
          nac: '<strong>Endpoint containment</strong> with Carbon Black',
          patch:
            '<strong>Automation-assisted patching</strong> with IBM BigFix and Microsoft SCCM',
        },
      },
      installKomandOrchestrator: 'Install Insight Orchestrator',
      noOrchestrator: {
        description:
          'You need to install and activate an Insight<br/> Orchestrator to power your Workflows',
        header: 'No Orchestrator found',
      },
      notificationTriggerTypes: {
        [NotificationType.EMAIL]: '$t(email)',
        [NotificationType.SMS]: '$t(sms)',
      },
      nullStateText:
        'Add an <a href="https://insightvm.help.rapid7.com/docs/automation" target="_blank" rel="noopener noreferrer">automation</a> to get started.',
      receiveNotifications:
        'Receive notifications prompted by InsightVM events',
      status: {
        off: '$t(off)',
        on: '$t(on)',
      },
      title: 'Automation',
      triggerSupportWorkflowTemplates: 'Trigger supports workflow types:',
      triggerType: {
        [EventTriggerTypes.ASSET]: 'Asset',
        [EventTriggerTypes.VULNERABILITY]: 'Vulnerability',
        ASSET_FOUND: 'Asset Found',
        ASSET_UPDATED: 'Asset Updated',
        VULNERABILITY_FOUND: 'Vulnerability Found',
        VULNERABILITY_REASSESSED: 'Vulnerability Updated',
      },
      workflowJobDetails: 'Workflow Job Details',
      workflowTemplates: {
        [WorkflowTemplateIds.CISCO_ISE_QUARANTINE_HOST]: 'Cisco ISE',
        [WorkflowTemplateIds.MICROSOFT_SCCM_PATCH]: 'Microsoft SCCM',
        [WorkflowTemplateIds.CB_RESPONSE_ISOLATE]: 'Carbon Black',
        [WorkflowTemplateIds.IBM_BIGFIX_PATCH]: 'IBM BigFix',
        [WorkflowTemplateIds.PALO_ALTO_PAN_OS]: 'Palo Alto PAN-OS',
      },
      [EventType.ACTION]: 'Action',
      [`${EventType.ACTION}_plural`]: 'Action',
      [EventType.NOTIFICATION]: 'Notification',
      [`${EventType.NOTIFICATION}_plural`]: 'Notifications',
      [EventType.WORKFLOW]: 'Workflow',
      [`${EventType.WORKFLOW}_plural`]: 'Workflows',
    },
    automation_plural: 'Automations',
    automationConfigurations: 'Automation Configurations',
    automationDetails: {
      configurationValue: {
        [NotificationType.SMS]: 'Send SMS notification to __count__ numbers',
        [NotificationType.EMAIL]: 'Send email notification to __count__ emails',
      },
      notificationTypes: {
        [NotificationType.SMS]: 'SMS Notification',
        [NotificationType.EMAIL]: 'Email Notification',
      },
      query: 'Query',
      summaryValue: '__vuln__ new vulnerabilities found on __asset__ assets',
    },
    automationEvent: {
      advancedFilters: {
        fields: {
          finishedAt: 'Time Triggered',
          startedAt: 'Started at',
        },
        search: {
          history: 'Search history',
        },
        searchHeader: {
          history: 'Filter History List',
        },
      },
      automatedEventsCount: '__count__ Automation Events',
      empty: 'No Automation events yet',
      entity: {
        eventType: 'Event Type',
        events: 'Events',
        finishTime: 'Finished At',
        name: 'Automation Name',
        reason: 'Status Reason',
        startTime: 'Started At',
        status: 'Status',
      },
      status: {
        [EventStatus.FAILED]: 'Failed',
        [EventStatus.SUCCEEDED]: 'Succeeded',
        [EventStatus.PAUSED]: 'Paused',
        [EventStatus.RUNNING]: 'Running',
      },
      tableTitle: {
        [EventType.ACTION]: 'Action History',
        [EventType.NOTIFICATION]: 'Notification History',
        [EventType.WORKFLOW]: 'Workflow History',
      },
    },
    automationLandingPage: {
      capabilities: {
        first:
          'Connect your tools and people to get complete visibility across your security program',
        second:
          'Save time, reduce costs, and get more value from your current suite of IT and security tools',
        third:
          'Respond to threats faster, reduce time to remediation, and amplify your security posture',
      },
      capabilitiesHeader: "With automation in InsightVM, you'll...",
      cta: {
        description:
          "This is a component that's installed in your environment to access on-prem tools.",
        download: 'Get Started',
        email:
          'You can also <a>send an email directly to your IT team</a> with installation instructions',
        header: 'Install and Activate the Insight Orchestrator',
        install: 'Get Installation Instructions',
      },
      header: 'Accelerate Vulnerability Management',
      headerDescription:
        'Run automated workflows to assist with the orchestration of patching systems and containing vulnerable assets directly within InsightVM.',
      integrations: {
        firewall: 'Firewall (PaloAlto, Cisco, CheckPoint, and more)\n',
        nac: 'NAC (Cisco ISE, ForeScout, CounterACT, and more)\n',
        patch: 'Patch (IBM BigFix, Microsoft SCCM, and more)\n',
      },
      integrationsHeader: 'Supported Out-of-the-Box Integrations',
      orchestrator: {
        featureProvisioning: {
          failed:
            'Orchestrator feature provision failed, please <a target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/contact"><strong><u>contact support.</u></strong></a>',
        },
      },
      slides: {
        first: {
          description:
            'Integrate with leading solutions like IBM BigFix and Microsoft SCCM to aggregate key information, retrieve fixes for identified vulnerabilities, and (when approved) apply the patches.',
          header: 'Accelerate remediation with automation-assisted patching',
        },
        second: {
          description:
            'Implement temporary (or permanent) compensating controls via integrations with Firewall, NAC solutions, and EDR solutions by limiting network traffic to vulnerable assets.',
          header: 'Restrict traffic to risky assets on your network',
        },
      },
    },
    availableDashboard: 'Available Dashboards',
    awsExpiredPage:
      'Hi! It looks like your subscription to InsightVM has ended.<br/> To continue your experience, please contact our sales team at<br/> __phoneNumber__',
    awsLandingPage: {
      header: 'Welcome to InsightVM agent-powered AWS Edition',
      tagline:
        "Utilizing the power of AWS and Rapid7's Insight platform, this agent-powered experience provides a streamlined way for AWS Marketplace customers like you to scale your collection of vulnerability data, turn it into answers, and minimize your risk in modern environments. <b>Let's get started!</b>",
    },
    azureRegistryNameRequirements:
      'Resource names may contain alpha numeric characters only and must be between 5 and 50 characters',
    back: 'Back',
    backToAll: 'Clear Selection',
    backToProducts: 'Back to Products: __products__',
    backToVendors: 'Back to Vendors',
    basicInfo: 'Basic Information',
    blockDevice: 'Block Device',
    buildAssessed: 'Build $t(assessed)',
    buildCompliance: 'Build Compliance',
    buildDate: 'Build Date',
    buildHistoryFor: 'Build History For',
    buildId: 'Build ID',
    buildJob: 'Build Job',
    buildJob_plural: 'Build Jobs',
    buildJobDetails: '$t(buildJob) Details',
    buildStatus: 'Build Status',
    buildSystem: 'Build System',
    builds: 'Builds',
    builtOn: 'Built On',
    byVendor: 'by __vendor__',
    cancel: 'Cancel',
    cancelAndSkip: 'Cancel and skip',
    cardActions: {
      close: 'Close',
      noChanges: 'The card has no changes to save',
      save: 'Save Card',
      saveAs: 'Save as New Card',
      saveAsNewQuery: 'Save as New Query',
      saveError:
        'There was an error updating the card. Please try again later.',
      saveQuery: 'Save Query',
      savedCard: 'Your card was updated.',
      savedExistingQuery: 'Your query was updated.',
      savedExistingQueryToExistingCard:
        'Your existing query was saved and your card was updated.',
      savedExistingQueryToNewCard:
        'Your existing query was saved and applied to your new card.',
      savedNewCard: 'Your new card was created.',
      savedNewQuery: 'Your new query was saved.',
      savedNewQueryToExistingCard:
        'Your new query was saved and your card was updated.',
      savedNewQueryToNewCard: 'Your new query was saved to your new card.',
      unsavedChanges: 'The card has unsaved changes. Do you want to continue?',
    },
    cardEditModal: {
      errors: {
        queryInUse: 'This name is already in use',
        queryRequired: 'Query name cannot be empty',
        titleInUse: 'This name is already in use',
        titleRequired: 'Name cannot be empty',
      },
      placeholder: {
        cardName: 'Name',
        cardHelp:
          'Enter a new name to display on this dashboard card.  Updating the card name does not affect the underlying Rapid7 query.',
        queryName: 'Query Name',
      },
      title: {
        save: 'Save',
        saveAs: 'Save as',
      },
    },
    cardLibrary: {
      card: {
        'riskiest-sites': {
          description: 'Riskiest sites, assets and their vulnerabilities',
          name: 'Riskiest Sites',
        },
      },
      category: {
        agents: '$t(agents)',
        assets: '$t(assets)',
        exploitableVulns: '$t(exploitableVulns)',
        goals: 'Goals',
        kpi: '$t(kpi)',
        remediations: '$t(remediations)',
        risk: '$t(risk)',
        sites: '$t(sites)',
        vulns: '$t(vulns)',
      },
    },
    cardScope: 'Card Scope',
    cardSearch: 'Search for cards',
    cardTemplates: {
      AssessedPolicies: {
        description: 'Shows the count of policies with findings stored',
        label: {cntAssessedPolicies: 'Total Policies'},
        name: 'Total Assessed Policies',
        summary: {
          cntAssessedPolicies: 'Assessed',
        },
      },
      AgentsByOS: {
        description:
          'A card with a breakdown of assets with connected agents by operating systems.',
        name: 'Assets with Agents by Operating System',
        summary:
          '__percent__ of my assets with agents are running the __os__ Operating System',
        value:
          '__percent__ (__value__) of my assets are running the __os__ Operating System',
      },
      AssessedAssets: {
        description:
          'Shows the total number of assets assessed for Policy Compliance.',
        label: {cntAssessedAssets: 'Total Assets'},
        name: 'Total Assets Assessed for Policy Compliance',
        summary: {cntAssessedAssets: 'Assessed for Policy Compliance'},
      },
      AssessedVsRiskOverTime: {
        description:
          'Displays a graph of assessed assets compared to the amount of risk.',
        name: 'Assessed Assets vs Risk Score',
      },
      AssetAssessment: {
        description:
          'This card compares the percentage of assets assessed and not assessed in your environment.',
        label: {
          assetCount: 'Assessed Assets',
          otherCount: 'Not Assessed Assets',
        },
        name: 'Asset Assessment',
        summary: {
          assetCount: '__percent__ of assets are assessed',
          otherCount: '__percent__ of assets are not assessed',
        },
        value: {
          assetCount: '__percent__ (__value__) of assets are assessed',
          otherCount: '__percent__ (__value__) of assets are not assessed',
        },
      },
      AssetComplianceByPolicy: {
        description: 'Shows the Asset compliance by policy.',
        name: 'Asset Compliance by Policy',
        summary: {
          cntAssetsFailingPolicy: '__percent__ of assets have failed',
          cntAssetsPassingPolicy: '__percent__ of assets have passed',
        },
        value: {
          cntAssetsFailingPolicy:
            '__percent__ (__value__) of assets have failed',
          cntAssetsPassingPolicy:
            '__percent__ (__value__) of assets have passed',
        },
      },
      AssetCompliancePolicySelector: {
        description: '$t(cardTemplates.AssetComplianceByPolicy.description)',
        name: '$t(cardTemplates.AssetComplianceByPolicy.name)',
      },
      AssetCriticality: {
        description:
          'This card shows the number of assets grouped by their assigned criticality tags.',
        label: {
          high: '__percent__ (__value__) high criticality',
          low: '__percent__ (__value__) low criticality',
          medium: '__percent__ (__value__) medium criticality',
          veryHigh: '__percent__ (__value__) very high criticality',
          veryLow: '__percent__ (__value__) very low criticality',
        },
        name: 'Asset Criticality',
        summary: {
          high: '__percent__ of my tagged assets have high criticality',
          low: '__percent__ of my tagged assets have low criticality',
          medium: '__percent__ of my tagged assets have medium criticality',
          veryHigh:
            '__percent__ of my tagged assets have very high criticality',
          veryLow: '__percent__ of my tagged assets have very low criticality',
        },
        value: {
          high:
            '__percent__ (__value__) of my tagged assets have high criticality',
          low:
            '__percent__ (__value__) of my tagged assets have low criticality',
          medium:
            '__percent__ (__value__) of my tagged assets have medium criticality',
          veryHigh:
            '__percent__ (__value__) of my tagged assets have very high criticality',
          veryLow:
            '__percent__ (__value__) of my tagged assets have very low criticality',
        },
      },
      Assets: {
        description:
          'This card shows the number of assets in your environment. If you apply a query, this card will display the number of assets within scope.',
        label: {
          count: '$t(assets)',
        },
        name: 'Assets',
      },
      AssetsByLastScanDate: {
        description:
          'Displays a graph of assets grouped by how recently they were scanned by an engine.',
        label: {
          cntMaxScan30To60: '30-60 days',
          cntMaxScan60To90: '60-90 days',
          cntMaxScanGt90: '>90 days',
          cntMaxScanLt30: '0-30 days',
        },
        name: 'Assets by Last Engine Scan Date',
        summary: {
          cntMaxScan30To60:
            '__percent__ of my assets were scanned by an engine 30-60 days ago',
          cntMaxScan60To90:
            '__percent__ of my assets were scanned by an engine 60-90 days ago',
          cntMaxScanGt90:
            '__percent__ of my assets were scanned by an engine more than 90 days ago',
          cntMaxScanLt30:
            '__percent__ of my assets were scanned by an engine in the last 30 days',
        },
        value: {
          cntMaxScan30To60:
            '__percent__ (__value__) of my assets were scanned by an engine in 30-60 days',
          cntMaxScan60To90:
            '__percent__ (__value__) of my assets were scanned by an engine in 60-90 days',
          cntMaxScanGt90:
            '__percent__ (__value__) of my assets were scanned by an engine more than 90 days ago',
          cntMaxScanLt30:
            '__percent__ (__value__) of my assets were scanned by an engine in 30 days',
        },
      },
      AssetsByNumberOfRunningContainers: {
        description:
          'Shows a list of assets that have containers running, ordered by highest to lowest number of running containers.',
        name: 'Assets By Number Of Running Containers',
      },
      AssetsByOS: {
        description: 'A card with a breakdown of assets by operating systems.',
        name: 'Assets by Operating System',
        summary:
          '__percent__ of my assets are running the __os__ Operating System',
        value:
          '__percent__ (__value__) of my assets are running the __os__ Operating System',
      },
      AssetsByTotalScanTime: {
        description: 'Displays a graph of assets grouped by engine scan time.',
        label: {
          cntScanTime10To20: '10-20 minutes',
          cntScanTime20To30: '20-30 minutes',
          cntScanTime30To40: '30-40 minutes',
          cntScanTimeGt40: '> 40 minutes',
          cntScanTimeLt10: '0-10 minutes',
        },
        name: 'Assets by Total Engine Scan Time',
        summary: {
          cntScanTime10To20:
            '__percent__ of my assets take 10-20 minutes to scan with an engine',
          cntScanTime20To30:
            '__percent__ of my assets take 20-30 minutes to scan with an engine',
          cntScanTime30To40:
            '__percent__ of my assets take 30-40 minutes to scan with an engine',
          cntScanTimeGt40:
            '__percent__ of my assets take > 40 minutes to scan with an engine',
          cntScanTimeLt10:
            '__percent__ of my assets take 0-10 minutes to scan with an engine',
        },
        value: {
          cntScanTime10To20:
            '__percent__ (__value__) of my assets take 10-20 minutes to scan with an engine',
          cntScanTime20To30:
            '__percent__ (__value__) of my assets take 20-30 minutes to scan with an engine',
          cntScanTime30To40:
            '__percent__ (__value__) of my assets take 30-40 minutes to scan with an engine',
          cntScanTimeGt40:
            '__percent__ (__value__) of my assets take > 40 minutes to scan with an engine',
          cntScanTimeLt10:
            '__percent__ (__value__) of my assets take 0-10 minutes to scan with an engine',
        },
      },
      AssetsByType: {
        description: 'Shows my assets grouped by operating system type',
        name: 'Assets by Type',
        summary:
          '__percent__ of my assets are running the __os__ Operating System type',
        value: '__percent__ (__value__)',
      },
      AssetsByVulnSeverity: {
        description:
          'Shows my assets that have vulnerabilities grouped by greatest severity',
        label: {
          critical:
            '__percent__ (__value__) Critical or Lower Severity Vulnerabilities',
          moderate: '__percent__ (__value__) Moderate Vulnerabilities',
          severe:
            '__percent__ (__value__) Severe or Lower Severity Vulnerabilities',
          unknown: '__percent__ (__value__) Unknown Vulnerabilities',
        },
        name: 'Assets by Maximum Vulnerability Severity',
        summary: {
          critical:
            '__percent__ of my assets have critical or lower severity vulnerabilities',
          moderate: '__percent__ of my assets have moderate vulnerabilities',
          severe:
            '__percent__ of my assets have severe or lower severity vulnerabilities',
          unknown:
            '__percent__ of my assets have no known vulnerabilities detected',
        },
        value: {
          critical:
            '__percent__ (__value__) of my assets have critical or lower severity vulnerabilities',
          moderate:
            '__percent__ (__value__) of my assets have moderate vulnerabilities',
          severe:
            '__percent__ (__value__) of my assets have severe or lower severity vulnerabilities',
          unknown:
            '__percent__ (__value__) of my assets have no known vulnerabilities detected',
        },
      },
      AssetsCriticalVuln: {
        description:
          'Shows the number of assets with critical risk vulnerabilities (CVSS score over 8) that are not excepted.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Assets with Critical Risk Vulnerabilities',
        summary: {
          assetCount: 'With Critical Vulnerabilities',
        },
      },
      AssetsNewlyDiscoveredXDays: {
        description:
          'Shows the number of assets that were newly discovered within the environment within a specified time frame.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Newly Discovered Assets',
        summary: {
          assetCount: 'Newly Discovered',
        },
      },
      AssetsRunningContainers: {
        description: 'Shows the number of assets with deployed containers.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Assets With Deployed Containers',
        summary: {
          assetCount: 'With Deployed Containers',
        },
      },
      AssetsWithActivelyExploitedVulnerabilities: {
        description:
          "Rapid7 has curated a threat feed of the most dangerous actively exploited vulnerabilities using information from Rapid7 Lab's Heisenberg honeypots, incident response activity, and trusted third parties. This card shows the number of assets that are affected by these vulnerabilities.",
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Assets With Actively Targeted Vulnerabilities',
        summary: {
          assetCount: 'with Actively Targeted Vulnerabilities',
        },
      },
      AssetsWithDefaultAccountVulns: {
        description:
          'Shows the number of assets with vulnerabilities related to the use of default accounts.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Assets with Default Account Vulnerabilities',
        summary: {
          assetCount: 'With Default Account Vulnerabilities',
        },
      },
      AssetsWithKnownObsoleteOS: {
        description:
          'The percentage of assets that are currently running on known obsolete operating systems.',
        label: {
          assetCount: 'Assets with Obsolete Operating System',
          otherCount: 'Remaining Assets',
        },
        name: 'Assets Running Obsolete Operating Systems',
        summary: {
          assetCount: '__percent__ of assets running obsolete OS',
          otherCount: '__percent__ of assets not running obsolete OS',
        },
        value: {
          assetCount:
            '__percent__ (__value__) Assets with Obsolete Operating System',
          otherCount: '__percent__ (__value__) Remaining Assets',
        },
      },
      AverageDaysToRemediateBySeverity: {
        description:
          'This card shows the average number of days needed to remediate a given vulnerability for all the vulnerabilities remediated per week within the time frame.',
        name: 'Average Days to Remediate By Severity',
        vulnerabilitySeverity: 'Vulnerability Severity',
        average: 'Vulnerabilities were remediated in __days__ days on average',
        label: {
          CRITICAL: 'Critical',
          MODERATE: 'Moderate',
          SEVERE: 'Severe',
        },
        value: {
          CRITICAL: '__value__ days',
          MODERATE: '__value__ days',
          SEVERE: '__value__ days',
        },
      },
      AverageDaysToRemediateByCvssV3Severity: {
        description:
          'This card shows the average number of days needed to remediate a given vulnerability for all the vulnerabilities remediated per week within the time frame.',
        name: 'Average Days to Remediate By CVSSv3 Severity Ratings',
        vulnerabilitySeverity: 'Vulnerability Severity',
        average: 'Vulnerabilities were remediated in __days__ days on average',
        label: {
          NONE: 'None',
          LOW: 'Low',
          MEDIUM: 'Medium',
          HIGH: 'High',
          CRITICAL: 'Critical',
        },
        value: {
          NONE: '__value__ days',
          LOW: '__value__ days',
          MEDIUM: '__value__ days',
          HIGH: '__value__ days',
          CRITICAL: '__value__ days',
        },
      },
      ChangeInNumberOfAssets: {
        description:
          'Shows the total number of assets changed in a specified time period and the total number of assets in your environment, so you can monitor Key Performance Indicators.',
        label: {
          totalAssets: 'Total Assets',
        },
        name: 'Asset Trends',
        summary: {
          assetChange: '__percent__ increase',
          totalAsset: '__value__ total assets',
        },
      },
      ContainerCountsCard: {
        description:
          'A card that displays the total number of assets, sites, asset groups, tags, and vulnerabilities.',
        name: 'Counts',
      },
      ContainerHosts: {
        description: 'Shows the number of assets that are container hosts.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Container Hosts',
        summary: {
          assetCount: 'Are Container Hosts',
        },
      },
      ContinuousGoal: {
        description: 'Displays assets in compliance with a continous goal',
        label: {
          assetsInComplianceCount: '$t(goal.charts.assetsCompliant)',
          assetsNotInComplianceCount: '$t(goal.charts.assetsNotCompliant)',
        },
        name: 'Continous Goal',
        summary: {
          assetsInComplianceCount:
            '__percent__ $t(goal.charts.assetsCompliant)',
          assetsNotInComplianceCount:
            '__percent__ $t(goal.charts.assetsNotCompliant)',
        },
        value: {
          assetsInComplianceCount: '$t(goal.charts.legend.assetsCompliant)',
          assetsNotInComplianceCount:
            '$t(goal.charts.legend.assetsNotCompliant)',
        },
      },
      ContinuousGoalSelector: {
        description: 'Monitor progress or conditions without a time limit.',
        name: 'Continuous Goal',
      },
      CredentialSuccessCard: {
        description:
          'Shows the Assets that have had successful authentication.',
        label: {
          cntCredentialFailure: '__percent__ (__value__) Failed logins',
          cntCredentialSuccess: '__percent__ (__value__) Successful logins',
          cntCredentialUnknown: '__percent__ (__value__) No logins attempted',
        },
        name: 'Assets by Authentication Status',
        summary: {
          cntCredentialFailure: '__percent__ of my assets had failed logins',
          cntCredentialSuccess:
            '__percent__ of my assets have had successful logins',
          cntCredentialUnknown:
            '__percent__ of my assets had no logins attempted',
        },
        value: {
          cntCredentialFailure:
            '__percent__ (__value__) of my assets had failed logins',
          cntCredentialSuccess:
            '__percent__ (__value__) of my assets have had successful logins',
          cntCredentialUnknown:
            '__percent__ (__value__) of my assets had no logins attempted',
        },
      },
      CredentialSuccessOverTime: {
        description:
          'A stacked area chart representing the success and failure of credentials on an asset over a period of time. Use this card to monitor the credential success rate on an asset.',
        label: {
          cntCredentialFailure: '$t(failed)',
          cntCredentialSuccess: '$t(successful)',
          cntCredentialUnknown: '$t(unknown)',
        },
        name: 'Credential Success Trends on an Asset: Stacked Area Chart',
        value: {
          cntCredentialFailure:
            '$t(weekOf)<br/>$t(failed) __percent__ (__value__)',
          cntCredentialSuccess:
            '$t(weekOf)<br/>$t(successful) __percent__ (__value__)',
          cntCredentialUnknown:
            '$t(weekOf)<br/>$t(unknown) __percent__ (__value__)',
        },
        yAxisLabel: '$t(assets)',
      },
      CredentialSuccessOverTimeLine: {
        description:
          'A line chart representing the success and failure of credentials on an asset over a period of time. Use this card to monitor the credential success rate on an asset.',
        label: {
          cntCredentialFailure: '$t(failed)',
          cntCredentialSuccess: '$t(successful)',
          cntCredentialUnknown: '$t(unknown)',
        },
        name: 'Credential Success Trends on an Asset: Line Chart',
        value: {
          cntCredentialFailure:
            '$t(weekOf)<br/>$t(failed) __percent__ (__value__)',
          cntCredentialSuccess:
            '$t(weekOf)<br/>$t(successful) __percent__ (__value__)',
          cntCredentialUnknown:
            '$t(weekOf)<br/>$t(unknown) __percent__ (__value__)',
        },
        yAxisLabel: '$t(assets)',
      },
      ExpiredSSLCertificatesCard: {
        description:
          'Shows the number of assets in your environment that currently have expired SSL certificates.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Assets with Expired SSL Certificates',
        summary: {
          assetCount: 'With Expired SSL Certificates',
        },
      },
      ExpiredVulnExceptionTableComponent: {
        description:
          'A card that shows the vulnerability exceptions in your environment that have recently expired.',
        name: 'Recently Expired Vulnerability Exceptions',
      },
      ExpiringSSLCertificatesCard: {
        description:
          'Shows the number of assets in your environment with SSL certificates set to expire within a specified time frame.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Assets with Expiring SSL Certificates',
        summary: {
          assetCount: 'With Expiring SSL Certificates',
        },
      },
      ExpiringVulnExceptionTableComponent: {
        description:
          'A card that shows vulnerability exceptions in your environment that are about to expire.',
        name: 'Upcoming Vulnerability Exception Expirations',
      },
      ExploitableAssetsBySkillLevel: {
        description:
          'Shows a breakdown of assets in your environment by the level of skill needed to exploit the vulnerabilities on them.',
        label: {
          cntMinSkillLevelRankExpert: '__percent__ Expert',
          cntMinSkillLevelRankIntermediate: '__percent__ Intermediate',
          cntMinSkillLevelRankNovice: '__percent__ (__value__) Novice',
        },
        name: 'Exploitable Assets by Skill Level',
        summary: {
          cntMinSkillLevelRankExpert:
            '__percent__ of exploitable assets can be exploited by an expert',
          cntMinSkillLevelRankIntermediate:
            '__percent__ of exploitable assets can be exploited with intermediate skill',
          cntMinSkillLevelRankNovice:
            '__percent__ of exploitable assets can be exploited by a novice',
        },
        value: {
          cntMinSkillLevelRankExpert:
            '__percent__ (__value__) of exploitable assets can be exploited by an expert',
          cntMinSkillLevelRankIntermediate:
            '__percent__ (__value__) of exploitable assets can be exploited with intermediate skill',
          cntMinSkillLevelRankNovice:
            '__percent__ (__value__) of exploitable assets can be exploited by a novice',
        },
      },
      ExploitableVulnByCVSSscore: {
        description:
          'Breaks down the percentages of exploitable vulnerabilities in your environment by CVSSv2 score so you can see what proportion of your environment has high risk according to general standards.',
        name: 'Exploitable Vulnerabilities by CVSSv2 Score',
        ...vulnerabilitiesByCvssScoreLabeling,
      },
      ExploitableVulnByCVSSv3score: {
        description:
          'Breaks down the percentages of exploitable vulnerabilities in your environment by CVSS V3 score so you can see what proportion of your environment has high risk according to general standards.',
        name:
          'Exploitable Vulnerabilities by CVSS Score (Grouped by V3 Category)',
        ...vulnerabilitiesByCvssV3ScoreLabeling,
      },
      ExploitableVulnDiscoverByTotalRiskScore: {
        description:
          'Shows exploitable vulnerabilities discovered in the past 90 days, sorted by total risk score from highest to lowest.',
        name:
          'Newly Discovered Exploitable Vulnerabilities by Total Risk Score',
      },
      ExploitableVulnerabilities: {
        description:
          'A card showing the total number of vulnerabilities in your environment and how that number is trending in a specified time period.',
        label: {
          count: '$t(vulnerabilityFindings)',
        },
        name: 'Exploitable Vulnerabilities Count',
        summary: {
          count: 'Exploitable Vulnerabilities',
        },
      },
      ExploitableVulnerabilitiesBySeverity: {
        description:
          'Shows the percentage breakdown of the exploitable vulnerabilities in your environment by their severity levels.',
        name: 'Exploitable Vulnerabilities by Severity',
        ...vulnerabilitiesBySeverityLabeling,
      },
      ExploitableVulnerabilityAgeByCVSS: {
        description:
          'A card highlighting when exploitable vulnerability findings in your environment were discovered ranked by CVSSv2 score',
        name:
          'Exploitable Vulnerability Findings Discovery Date by CVSSv2 Score',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      ExploitableVulnerabilityAgeByCVSSv3: {
        description:
          'A card highlighting when exploitable vulnerability findings in your environment were discovered ranked by CVSS V3 score',
        name:
          'Exploitable Vulnerability Findings Discovery Date by CVSS Score (Grouped by V3 Category)',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssV3ScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      VulnerabilityAgeByRiskScore: {
        description:
          'A card highlighting when vulnerability findings in your environment were published ranked by risk score',
        name:
          'Vulnerability Findings by Active Risk Score Severity and Publish Age',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: riskScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      ExploitableVulnerabilityDiscoveryDateBySeverity: {
        description:
          'A card highlighting when exploitable vulnerability findings in your environment were discovered ranked by severity',
        name: 'Exploitable Vulnerability Findings Discovery Date By Severity',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: severityLabels,
        yAxisLabel: dayRangeLabels,
      },
      ExploitableVulnerabilityPublishAgeBySeverity: {
        description:
          'A card that details the published age of exploitable vulnerability findings in your environment by severity',
        name: 'Exploitable Vulnerability Findings Publish Age by Severity',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: severityLabels,
        yAxisLabel: dayRangeLabels,
      },
      ExploitableVulnerabilityPublishDateByCVSS: {
        description:
          'A card that lists publish date of exploitable vulnerability findings by CVSSv2 score',
        name: 'Exploitable Vulnerability Findings Publish Age by CVSSv2 Score',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      ExploitableVulnerabilityPublishDateByCVSSv3: {
        description:
          'A card that lists publish date of exploitable vulnerability findings by CVSS V3 score',
        name:
          'Exploitable Vulnerability Findings Publish Age by CVSS Score (Grouped by V3 Category)',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssV3ScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      ExploitableVulnsBySkillLevel: {
        description:
          'Shows a breakdown of vulnerabilities in your environment by the level of skill needed to exploit them.',
        label: {
          cntExpert: '__percent__ Expert',
          cntIntermediate: '__percent__ Intermediate',
          cntNovice: '__percent__ (__value__) Novice',
        },
        name: 'Exploitable Vulnerabilities by Skill Level',
        summary: {
          cntExpert:
            '__percent__ of vulnerability findings can be exploited by an expert',
          cntIntermediate:
            '__percent__ of vulnerability findings can be exploited with intermediate skill',
          cntNovice:
            '__percent__ of vulnerability findings can be exploited by a novice',
        },
        value: {
          cntExpert:
            '__percent__ (__value__) of vulnerability findings can be exploited by an expert',
          cntIntermediate:
            '__percent__ (__value__) of vulnerability findings can be exploited with intermediate skill',
          cntNovice:
            '__percent__ (__value__) of vulnerability findings can be exploited by a novice',
        },
      },
      ExternalRemoteWorkforceBanner: {
        name: '$t(dashboardLibrary.templateName.externalRemoteWorkforce)',
      },
      GoalSelector: {
        description:
          'Select any of your goals or SLAs to display data in an appropriate format',
        name: 'Goals and SLAs',
      },
      ImageAssessment: {
        description:
          'A card detailing the proportion of images assessed and not assessed in your environment.',
        label: {
          assessed: 'Assets with assessed images',
          otherCount: 'Remaining Assets',
        },
        name: 'Image Assessment',
        summary: {
          assessed: '__percent__ Of Images Are Assessed',
          otherCount: '__percent__ of images are not assessed',
        },
        value: {
          assessed: '__percent__ (__value__) of images are assessed',
          otherCount: '__percent__ (__value__) of images are not assessed',
        },
      },
      InfrastructureAssessmentFindingsBySeverity: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card shows the percentage breakdown of the vulnerabilities in your environment by their severity levels.',
        label: {
          critical: '__percent__ (__value__) Critical Vulnerabilities',
          informational: '__percent__ (__value__) Informational',
          low: '__percent__ (__value__) Low Vulnerabilities',
          moderate: '__percent__ (__value__) Moderate Vulnerabilities',
          severe: '__percent__ (__value__) Severe Vulnerabilities',
        },
        name: 'Cloud Findings Percentage By Severity',
        summary: {
          critical: '__percent__ of my findings have critical vulnerabilities',
          low: '__percent__ of my findings have low vulnerabilities',
          moderate: '__percent__ of my findings have moderate vulnerabilities',
          severe: '__percent__ of my findings have severe vulnerabilities',
        },
        value: {
          critical:
            '__percent__ (__value__) of my findings have critical vulnerabilities',
          informational:
            '__percent__ (__value__) of my findings are informational',
          low:
            '__percent__ (__value__) of my findings have low vulnerabilities',
          moderate:
            '__percent__ (__value__) of my findings have moderate vulnerabilities',
          severe:
            '__percent__ (__value__) of my findings have severe vulnerabilities',
        },
      },
      InfrastructureAssessmentFindingsByStatus: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card shows the percentage breakdown of the vulnerabilities in your environment by their assessment statuses.',
        label: {
          fail: '__percent__ (__value__) Failed Findings',
          pass: '__percent__ (__value__) Passed Findings',
        },
        name: 'Cloud Findings Percentage By Status',
        summary: {
          fail: '__percent__ of my findings have failed',
          pass: '__percent__ of my findings have passed',
        },
        value: {
          fail: '__percent__ (__value__) of my findings have failed',
          pass: '__percent__ (__value__) of my findings have passed',
        },
      },
      InfrastructureAssessmentFindingsCountBySeverity: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays the number of failed findings by severity.',
        name: 'Cloud Findings By Severity',
      },
      InfrastructureAssessmentLeastCompliantRules: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays the five rules with the lowest compliance percentage according to the assessment results of your cloud resources.  Rules are listed by severity in descending order.  Each rule appears alongside a stacked bar chart detailing the compliance breakdown of the passed and failed findings that calculate the compliance percentage shown.',
        name: 'Cloud Rules With Lowest Compliance Percentage',
        value: {
          failed: '__percent__ (__value__) of my findings have failed',
          passed: '__percent__ (__value__) of my findings have passed',
        },
      },
      InfrastructureAssessmentMostExceptedRules: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays the rules with the highest number of excepted findings.',
        name: 'Most Excepted Cloud Rules',
        value: {
          excepted: '__percent__ (__value__) of my findings have been excepted',
        },
      },
      InfrastructureAssessmentRiskiestAccounts: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays up to five of your riskiest cloud accounts.  Accounts are listed by total failed findings in descending order.  Each account name appears alongside a stacked bar chart detailing the severity breakdown of the failed findings associated with the account.',
        name: 'Cloud Accounts With Highest Risk',
        value: {
          critical:
            '__percent__ (__value__) of my failed findings have critical vulnerabilities',
          informational:
            '__percent__ (__value__) of my failed findings are informational',
          low:
            '__percent__ (__value__) of my failed findings have low vulnerabilities',
          moderate:
            '__percent__ (__value__) of my failed findings have moderate vulnerabilities',
          severe:
            '__percent__ (__value__) of my failed findings have severe vulnerabilities',
        },
      },
      InfrastructureAssessmentRuleCompliance: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays the current state of a selected rule.',
        label: {
          excepted: '__percent__ (__value__) Excepted Findings',
          failed: '__percent__ (__value__) Failed Findings',
          passed: '__percent__ (__value__) Passed Findings',
        },
        name: 'Cloud Compliance By Rule',
        summary: {
          excepted: '__percent__ of my findings have been excepted',
          failed: '__percent__ of my findings have failed',
          passed: '__percent__ of my findings have passed',
        },
        value: {
          excepted: '__percent__ (__value__) of my findings have excepted',
          failed: '__percent__ (__value__) of my findings have failed',
          passed: '__percent__ (__value__) of my findings have passed',
        },
      },
      InfrastructureAssessmentRuleSelector: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays the current state of a selected rule.',
        name: '$t(cardTemplates.InfrastructureAssessmentRuleCompliance.name)',
      },
      InfrastructureAssessmentTopFailedRules: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays the rules with the highest number of failed findings.',
        name: 'Most Failed Cloud Rules',
      },
      InfrastructureAssessmentTotalFindings: {
        description:
          'Specific to the Cloud Configuration Assessment feature, this card displays an aggregated number of all your individual findings.',
        label: {
          total: 'Findings',
        },
        name: 'Total Cloud Findings',
        summary: {
          total: 'With Failed Rules',
        },
      },
      LeastCompliantAssets: {
        description: 'Shows the least compliant assets by policy.',
        name: 'Least Compliant Assets By Policy',
        table: {
          ruleCompliance: '__percentage__% (__passing__ of __total__)',
        },
      },
      LeastCompliantAssetsPolicySelector: {
        description: '$t(cardTemplates.LeastCompliantAssets.description)',
        name: '$t(cardTemplates.LeastCompliantAssets.name)',
      },
      MeltdownBanner: {
        name: 'Meltdown',
      },
      MostCommonExploitableVulnComponent: {
        description:
          'A card that shows the most common vulnerabilities in your environment based on number of affected assets.',
        name: 'Most Common Exploitable Vulnerabilities',
      },
      MostCommonExploitedVulnComponent: {
        description:
          "Rapid7 has curated a threat feed of the most dangerous actively exploited vulnerabilities using information from Rapid7 Lab''s Heisenberg honeypots, incident response activity, and trusted third parties. This card shows details about these vulnerabilities in your environment ranked by the number of assets affected.",
        name: 'Most Common Actively Targeted Vulnerabilities',
      },
      MostCommonExploitsComponent: {
        description:
          'Shows the most common exploits in your environment based on number of affected assets.',
        name: 'Most Common Exploits',
      },
      MostCommonMalwareKitsComponent: {
        description:
          'Shows the most common malware kits in your environment based on number of affected assets.',
        name: 'Most Common Malware Kits',
      },
      MostCommonServices: {
        description:
          'Shows the most common services in your environment based on the number of assets on which this service ran.',
        name: 'Most Common Services',
      },
      MostCommonSoftware: {
        description:
          "Shows the most common software in your environment based on the number of assets on which it's installed.",
        name: 'Most Common Software',
      },
      MostCommonSolutions: {
        description:
          'Shows the remediations or solutions that are the most frequently prescribed in your environment.',
        name: 'Most Common Solutions',
      },
      MostCommonVulnComponent: {
        description:
          'Shows the most common vulnerabilities in your environment based on number of affected assets.',
        name: 'Most Common Vulnerabilities',
      },
      MostCommonlyDeployedImages: {
        description:
          'Shows a list of images that are deployed to the highest number of assets (container hosts).',
        name: 'Most Commonly Deployed Images',
      },
      MostVulnerableImages: {
        description:
          'Shows a list of images that have the highest number of vulnerability instances.',
        name: 'Most Vulnerable Images',
      },
      NewVsRemediatedVulnerabilities: {
        description:
          'A card comparing counts of remediated vulnerability findings, new vulnerability findings, and unchanged vulnerability findings since a given date.',
        label: {
          newFindings: '__percent__ (__value__) New vulnerability findings',
          remediatedFindings:
            '__percent__ (__value__) Remediated vulnerability findings',
          unchangedFindings:
            '__percent__ (__value__) Unchanged vulnerability findings',
        },
        name: 'New vs. Remediated Vulnerabilities',
        summary: {
          newFindings: '__percent__ new vulnerability findings',
          remediatedFindings: '__percent__ remediated vulnerability findings',
          unchangedFindings: '__percent__ unchanged vulnerability findings',
        },
        value: {
          newFindings: '__percent__ (__value__) new vulnerability findings',
          remediatedFindings:
            '__percent__ (__value__) remediated vulnerability findings',
          unchangedFindings:
            '__percent__ (__value__) unchanged vulnerability findings',
        },
      },
      ...[
        'NewVsRemediatedVulnerabilitiesTrends',
        'NewVsRemediatedVulnerabilitiesTrendsMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A card detailing trends in remediated vulnerability findings, new vulnerability findings, and unchanged vulnerability findings.',
          label: {
            newFindings: 'New vulnerability findings',
            remediatedFindings: 'Remediated vulnerability findings',
            unchangedFindings: 'Unchanged vulnerability findings',
          },
          name: 'New vs. Remediated Vulnerabilities Over Time',
          value: {
            newFindings:
              '$t(monthOf)<br/>$t(cardTemplates.NewVsRemediatedVulnerabilitiesTrends.label.newFindings) __percent__ (__value__)',
            remediatedFindings:
              '$t(monthOf)<br/>$t(cardTemplates.NewVsRemediatedVulnerabilitiesTrends.label.remediatedFindings) __percent__ (__value__)',
            unchangedFindings:
              '$t(monthOf)<br/>$t(cardTemplates.NewVsRemediatedVulnerabilitiesTrends.label.unchangedFindings) __percent__ (__value__)',
          },
        };

        return acc;
      }, {}),
      ...[
        'NewVsRemediatedVulnerabilitiesComparisonOverTime',
        'NewVsRemediatedVulnerabilitiesComparisonOverTimeMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A card detailing trends in remediated vulnerability findings for the specified date range.',
          label: {
            newFindings: 'New vulnerability findings',
            remediatedFindings: 'Remediated vulnerability findings',
            totalFindings: 'Total vulnerability findings',
          },
          name: 'New vs. Remediated Vulnerability Comparison Over Time',
          value: {
            newFindings:
              '$t(monthOf)<br/>$t(cardTemplates.NewVsRemediatedVulnerabilitiesComparisonOverTime.label.newFindings) __value__',
            remediatedFindings:
              '$t(monthOf)<br/>$t(cardTemplates.NewVsRemediatedVulnerabilitiesComparisonOverTime.label.remediatedFindings) __value__',
            totalFindings:
              '$t(monthOf)<br/>$t(cardTemplates.NewVsRemediatedVulnerabilitiesComparisonOverTime.label.totalFindings) __value__',
          },
          y: 'New & Remediated Vuln Findings',
          y1: 'Total Vuln Findings',
        };

        return acc;
      }, {}),
      NewVulnerabilities: {
        description:
          'Shows the number of vulnerabilities that were newly discovered within the environment within a specified time frame.',
        label: {vulnerabilitiesCount: 'New Vulnerabilities'},
        name: 'New Vulnerabilities',
      },
      NumberOfConnectedAgentsComponent: {
        description:
          'A card showing the number of assets in your environment with connected agents.',
        label: {
          assetCount: '$t(assets)',
        },
        name: 'Number of Assets with an Agent',
        summary: {
          assetCount: 'With Connected Agents',
        },
      },
      NumberOfUniqueVulnerabilities: {
        description:
          'Show the number of unique vulnerabilities in your environment.',
        label: {
          vulnerabilitiesCount: 'Vulnerabilities in your environmnent',
          otherCount: 'Remaining vulnerabilities',
        },
        name: 'Number of Unique Vulnerabilities',
        summary: {
          vulnerabilitiesCount:
            '__percent__ of vulnerabilities are in your environment',
          otherCount:
            '__percent__ of vulnerabilities are not in your environment',
        },
        value: {
          vulnerabilitiesCount:
            '__percent__ (__value__) of vulnerabilities are in your environment',
          otherCount:
            '__percent__ (__value__) of vulnerabilities are not in your environment',
        },
      },
      OTIoTAssetsBanner: {
        name: '$t(dashboardLibrary.templateName.OTIoTAssets)',
      },
      ObsoleteSoftwareCard: {
        description:
          'A card detailing the obsolete/non obsolete assets on the network.',
        label: {
          assetCount: 'Assets with Obsolete Software',
          otherCount: 'Remaining Assets',
        },
        name: 'Assets Running Obsolete Software',
        summary: {
          assetCount:
            '__percent__ of assets have known obsolete software installed',
          otherCount:
            '__percent__ of assets do not have known obsolete software installed',
        },
        value: {
          assetCount:
            '__percent__ (__value__) of assets have known obsolete <br/> software installed',
          otherCount:
            '__percent__ (__value__) of assets do not have known <br/> obsolete software installed',
        },
      },
      PoliciesWithMostAssets: {
        description: 'Shows the policies with the most assets.',
        name: 'Top Policies with the Most Assets',
      },
      ProjectsAtRiskCard: {
        description:
          'Shows the number of projects nearing expiration that are still open.',
        label: {
          totalElements: 'Projects Closing',
        },
        name: 'Projects at Risk',
        summary: {
          totalElements: '',
        },
      },
      ProjectsByAcceptedRisk: {
        description:
          'Shows a list of projects whose combined solutions sum up to the highest amount of risk marked as Will Not Fix.  With this card, you can easily monitor remediations you do not want your teams to focus on.',
        name: 'Projects with Highest Amount of Risk in Will Not Fix',
      },
      ProjectsLeastRemainingWorkPercentCard: {
        description:
          'Lists projects with the lowest number of unresolved remediation items.',
        name: 'Projects with Least Remaining Work',
      },
      ProjectsMostRemainingWorkCard: {
        description:
          'Lists projects with the highest number of unresolved remediation items.',
        name: 'Remaining Work',
      },
      ProjectsMostRemainingWorkPercentCard: {
        description:
          'Lists projects with the highest number of unresolved remediation items.',
        name: 'Remaining Work %',
      },
      ProjectsPastDueCard: {
        description: 'Shows the number of projects that are past due.',
        label: {
          totalElements: '$t(projects)',
        },
        name: 'Past Due Projects',
        summary: {
          totalElements: 'Are Past Due',
        },
      },
      ProjectsRecentlyClosedCard: {
        description:
          'Shows the number of projects that have been recently closed.',
        label: {
          totalElements: 'Projects Closed',
        },
        name: 'Recently Closed Projects',
        summary: {
          totalElements: '',
        },
      },
      ReconBanner: {
        name: '$t(dashboardLibrary.templateName.RECON)',
      },
      RemediatedVulnerabilities: {
        description:
          'Lists vulnerabilities whose presence has been reduced in your environment within the last 30 days.',
        name: 'Top Remediated Vulnerabilities',
      },
      RemediatedVulnerabilityFindings: {
        description:
          'Shows the number of remediated vulnerabilities that were newly discovered within the environment within a specified time frame.',
        label: {vulnerabilitiesCount: 'Remediated Vulnerability'},
        name: 'Remediated Vulnerability',
      },
      RemediatorsRequiringAssistance: {
        description:
          'Lists the project owners with the most projects still open and past due.',
        name: 'Remediators Requiring Assistance',
      },
      RiskVulnsCard: {
        description:
          'Shows a scatter plot chart with assets ordered by risk and number of vulnerabilities',
        name: 'Assets by Risk and Vulnerabilities',
        summary: 'Hostname: __value__',
        value: {
          bubble: `
              <b>$t(assetCount)</b>: __assetCount__<br/>
              <b>$t(riskScoreLabel)</b>: __minRiskScore__ - __maxRiskScore__<br/>
              <b>$t(vulns)</b>: __minVulnCount__ - __maxVulnCount__
            `,
          scatter: `
              <b>$t(hostName)</b>: __hostName__<br/>
              <b>$t(riskScoreLabel)</b>: __riskScore__<br/>
              <b>$t(vulns)</b>: __vulns__
            `,
        },
      },
      RuleComplianceByPolicy: {
        description: 'Shows the Rule compliance by policy.',
        name: 'Rule Compliance by Policy',
        summary: {
          cntPolicyFindingsFailing: '__percent__ of rules have failed',
          cntPolicyFindingsPassing: '__percent__ of rules have passed',
        },
        value: {
          cntPolicyFindingsFailing:
            '__percent__ (__value__) of rules have failed',
          cntPolicyFindingsPassing:
            '__percent__ (__value__) of rules have passed',
        },
      },
      RuleCompliancePolicySelector: {
        description: '$t(cardTemplates.RuleComplianceByPolicy.description)',
        name: '$t(cardTemplates.RuleComplianceByPolicy.name)',
      },
      PatchTuesdayBanner: {
        description: `
          <p>
            Use this dashboard to gain visibility into the latest
            vulnerabilities from Microsoft Patch Tuesday, is when Microsoft
            releases fixes for security flaws in their software. This occurs the
            second Tuesday of every month.
          </p>
          <p>
            After these patches are available, attackers immediately start
            analyzing them to develop exploits. Because of this, it’s critical
            to track your exposure and monitor your remediation efforts on an
            ongoing basis to minimize the window of opportunity for
            exploitation.
          </p>
        `,
        name: 'Microsoft Patch Tuesday',
        goalButton:
          'Create a goal to track remediation of the latest Microsoft Patch Tuesday vulnerabilities.',
        remediationButton:
          'Create a remediation project to fix the most recent Microsoft Patch Tuesday vulnerabilities.',
      },
      SignificantChangesInLast30DaysBanner: {
        description:
          'This dashboard gives you visibility on any significant asset and vulnerability changes in your environment over the last 30 days. Use our pre-populated queries to create a goal or remediation project to track your progress.',
        goalButton: `Create a goal to make sure all new assets discovered in the  last 30 days have been assessed with credentials`,
        name: 'Significant Changes In Last 30 Days',
        remediationButton: `Create a project to remediate critical vulnerabilities on all assets discovered in the last 30 days`,
      },
      SlaGoalSelector: {
        description:
          'Define conditions and set the number of days to track remediation over time.',
        name: 'SLA',
      },
      TimeBoundGoal: {
        description: 'Displays assets in compliance with a time bound goal',
        name: 'Time Bound Goal',
      },
      TimeBoundGoalSelector: {
        description:
          'Specify metrics based on a set deadline and data scopes that do not change.',
        name: 'Time Bound Goal',
      },
      TopRemediators: {
        description:
          'Lists the project owners with the most projects successfully closed and on time.',
        name: 'Top Remediators',
      },
      TopRiskiestAssetGroupsComponent: {
        description:
          'Lists the asset groups with the highest risk scores in your environment.',
        name: 'Top Riskiest Asset Groups',
      },
      TopRiskiestAssetLocationsComponent: {
        description:
          'Lists the locations with the highest total risk in your environment.',
        name: 'Top Riskiest Asset Locations',
      },
      TopRiskiestAssetOwnersComponent: {
        description:
          'Lists asset owners whose assets have the highest total risk.',
        name: 'Top Riskiest Asset Owners',
      },
      TopRiskiestAssetsComponent: {
        description:
          'Lists the assets with the highest risk in your environment.',
        name: 'Top Riskiest Assets',
      },
      TopRiskiestSitesComponent: {
        description:
          'Lists the sites with the highest total risk in your environment.',
        name: 'Top Riskiest Sites',
      },
      TotalVulnerabilities: {
        description:
          'A card showing the number of vulnerabilities found in your environment.',
        label: {
          count: '$t(vulnerabilityFindings)',
        },
        name: 'Number of Vulnerabilities',
        summary: {
          count: 'Total Vulnerabilities',
        },
      },
      TrendingVulnerabilities: {
        description:
          'Lists vulnerabilities becoming more prevalent in your environment within the last 30 days.',
        name: 'Top Trending Vulnerabilities',
      },
      VulnByCVSSscore: {
        description:
          'Breaks down the percentages of vulnerabilities in your environment by CVSSv2 score so you can see what proportion of your environment has high risk according to general standards.',
        name: 'Vulnerabilities by CVSSv2 Score',
        ...vulnerabilitiesByCvssScoreLabeling,
      },
      VulnByCVSSv3Score: {
        description:
          'Breaks down the percentages of vulnerabilities in your environment by CVSS V3 score so you can see what proportion of your environment has high risk according to general standards.',
        name: 'Vulnerabilities by CVSS Score (Grouped by V3 Category)',
        ...vulnerabilitiesByCvssV3ScoreLabeling,
      },
      VulnDiscoverByTotalRiskScore: {
        description:
          'Shows vulnerabilities discovered in the past 90 days, sorted by total risk score, from highest to lowest.',
        name: 'Newly Discovered Vulnerabilities by Total Risk Score',
      },
      ...['VulnRiskOverTime', 'VulnRiskOverTimeMultiProviderType'].reduce(
        (acc, key) => {
          acc[key] = {
            description:
              'Show the trend of vulnerability risk in the environment, so you can see whether the total risk has increased or decreased over time.',
            name: 'Vulnerability Risk Score Over Time',
            value: {
              riskScore: '$t(weekOf)<br/>$t(riskScoreLabel) __value__',
            },
            yAxisLabel: '$t(riskScoreLabel)',
          };

          return acc;
        },
        {}
      ),
      ...['VulnTrends', 'VulnTrendsMultiProviderType'].reduce((acc, key) => {
        acc[key] = {
          description:
            'A stacked area chart that shows vulnerability severity trends in your environment over time. Use this card to monitor the level of severe vulnerabilities over time.',
          label: {
            cntCritical: '$t(critical)',
            cntModerate: '$t(moderate)',
            cntSevere: '$t(severe)',
          },
          name:
            'Vulnerability Severity Trends by Asset Count: Stacked Area Chart',
          value: {
            cntCritical: '$t(weekOf)<br/>$t(critical) __percent__ (__value__)',
            cntModerate: '$t(weekOf)<br/>$t(moderate) __percent__ (__value__)',
            cntSevere: '$t(weekOf)<br/>$t(severe) __percent__ (__value__)',
          },
        };

        return acc;
      }, {}),
      ...['VulnTrendsLine', 'VulnTrendsLineMultiProviderType'].reduce(
        (acc, key) => {
          acc[key] = {
            description:
              'A line chart that shows vulnerability severity trends in your environment over time. Use this card to monitor the level of severe vulnerabilities over time.',
            label: {
              cntCritical: '$t(critical)',
              cntModerate: '$t(moderate)',
              cntSevere: '$t(severe)',
            },
            name: 'Vulnerability Severity Trends by Asset Count: Line Chart',
            value: {
              cntCritical:
                '$t(weekOf)<br/>$t(critical) __percent__ (__value__)',
              cntModerate:
                '$t(weekOf)<br/>$t(moderate) __percent__ (__value__)',
              cntSevere: '$t(weekOf)<br/>$t(severe) __percent__ (__value__)',
            },
          };

          return acc;
        },
        {}
      ),
      VulnerabilitiesBySeverity: {
        description:
          'Shows the percentage breakdown of the vulnerabilities in your environment by their severity levels.',
        name: 'Vulnerabilities by Severity',
        ...vulnerabilitiesBySeverityLabeling,
      },
      VulnerabilityAgeByCVSS: {
        description:
          'A card highlighting when vulnerability findings in your environment were discovered ranked by CVSSv2 Score',
        name: 'Vulnerability Findings Discovery Date by CVSSv2 Score',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      VulnerabilityAgeByCVSSv3: {
        description:
          'A card highlighting when vulnerability findings in your environment were discovered ranked by CVSS V3 Score',
        name:
          'Vulnerability Findings Discovery Date by CVSS Score (Grouped by V3 Category)',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssV3ScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      ...[
        'VulnerabilityCountByCvssScoreOverTime',
        'VulnerabilityCountByCvssScoreOverTimeMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A stacked area chart representing the number and score of vulnerability findings in your environment over time, grouped by CVSS v2 severity ratings.',
          name:
            'Vulnerability Count Trends by CVSSv2 Severity: Stacked Area Chart',
          ...vulnerabilityCountByCvssKeyValues,
        };

        return acc;
      }, {}),
      ...[
        'VulnerabilityCountByCvssScoreOverTimeLine',
        'VulnerabilityCountByCvssScoreOverTimeLineMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A line chart representing the number of vulnerability findings in your environment over time, grouped by CVSS v2 severity ratings.',
          name: 'Vulnerability Count Trends by CVSSv2 Severity: Line Chart',
          ...vulnerabilityCountByCvssKeyValues,
        };

        return acc;
      }, {}),
      ...[
        'VulnerabilityCountByCvssV3ScoreOverTime',
        'VulnerabilityCountByCvssV3ScoreOverTimeMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A stacked area chart representing the number and score of vulnerability findings in your environment over time, grouped by CVSS v3 severity ratings.',
          name:
            'Vulnerability Count Trends by CVSSv3 Severity: Stacked Area Chart',
          ...vulnerabilityCountByCvssV3KeyValues,
        };

        return acc;
      }, {}),
      ...[
        'VulnerabilityCountByCvssV3ScoreOverTimeLine',
        'VulnerabilityCountByCvssV3ScoreOverTimeLineMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A line chart representing the number of vulnerability findings in your environment over time, grouped by CVSS v3 severity ratings.',
          name: 'Vulnerability Count Trends by CVSSv3 Severity: Line Chart',
          ...vulnerabilityCountByCvssV3KeyValues,
        };

        return acc;
      }, {}),
      VulnerabilityDiscoveryDateBySeverity: {
        description:
          'A card highlighting when vulnerability findings in your environment were discovered ranked by severity',
        name: 'Vulnerability Findings Discovery Date By Severity',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: severityLabels,
        yAxisLabel: dayRangeLabels,
      },
      VulnerabilityPublishAgeBySeverity: {
        description:
          'A card that details the published age of vulnerability findings in your environment by severity',
        name: 'Vulnerability Findings Publish Age by Severity',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: severityLabels,
        yAxisLabel: dayRangeLabels,
      },
      VulnerabilityPublishDateByCVSS: {
        description:
          'A card that lists publish date of vulnerability findings by CVSSv2 score',
        name: 'Vulnerability Findings Publish Age by CVSSv2 Score',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      VulnerabilityPublishDateByCVSSv3: {
        description:
          'A card that lists publish date of vulnerability findings by CVSS V3 score',
        name:
          'Vulnerability Findings Publish Age by CVSS Score (Grouped by V3 Category)',
        value: '__value__ $t(vulnerabilityFindings)',
        xAxisLabel: cvssV3ScoreLabels,
        yAxisLabel: dayRangeLabels,
      },
      VulnerabilityByRiskScore: {
        description:
          'A card that lists the vulnerability findings by risk score severity',
        name: 'Vulnerability Findings by Active Risk Score Severity',
        label: {
          critical: '__percent__ (__value__) critical risk score',
          high: '__percent__ (__value__) high risk score',
          low: '__percent__ (__value__) low risk score',
          medium: '__percent__ (__value__) medium risk score',
          none: '__percent__ (__value__) none risk score',
        },
        summary: {
          critical:
            '__percent__ of my vulnerability findings have critical risk score',
          high: '__percent__ of my vulnerability findings have high risk score',
          low: '__percent__ of my vulnerability findings have low risk score',
          medium:
            '__percent__ of my vulnerability findings have medium risk score',
          none: '__percent__ of my vulnerability findings have none risk score',
        },
        value: {
          critical:
            '__percent__ (__value__) of my vulnerability findings have a critical risk score',
          high:
            '__percent__ (__value__) of my vulnerability findings have a high risk score',
          low:
            '__percent__ (__value__) of my vulnerability findings have a low risk score',
          medium:
            '__percent__ (__value__) of my vulnerability findings have a medium risk score',
          none:
            '__percent__ (__value__) of my vulnerability findings have a none risk score',
        },
      },
      ...[
        'VulnerabilitySeverityOverTime',
        'VulnerabilitySeverityOverTimeMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A stacked area chart showing the vulnerability trends over time.',
          label: {
            cntCritical: '$t(critical)',
            cntModerate: '$t(moderate)',
            cntSevere: '$t(severe)',
          },
          name: 'Vulnerability Trends by Severity: Stacked Area Chart',
          value: {
            cntCritical: '$t(weekOf)<br/>$t(critical) __percent__ (__value__)',
            cntModerate: '$t(weekOf)<br/>$t(moderate) __percent__ (__value__)',
            cntSevere: '$t(weekOf)<br/>$t(severe) __percent__ (__value__)',
          },
          yAxisLabel: '$t(vulns)',
        };

        return acc;
      }, {}),
      ...[
        'VulnerabilitySeverityOverTimeLine',
        'VulnerabilitySeverityOverTimeLineMultiProviderType',
      ].reduce((acc, key) => {
        acc[key] = {
          description:
            'A line chart showing the vulnerability trends over time.',
          label: {
            cntCritical: '$t(critical)',
            cntModerate: '$t(moderate)',
            cntSevere: '$t(severe)',
          },
          name: 'Vulnerability Trends by Severity: Line Chart',
          value: {
            cntCritical: '$t(weekOf)<br/>$t(critical) __percent__ (__value__)',
            cntModerate: '$t(weekOf)<br/>$t(moderate) __percent__ (__value__)',
            cntSevere: '$t(weekOf)<br/>$t(severe) __percent__ (__value__)',
          },
          yAxisLabel: '$t(vulns)',
        };

        return acc;
      }, {}),
      'meltdown-AssetsCriticalVuln': {
        description:
          'The number of assets in your environment with Meltdown vulnerabilities',
        label: {assetCount: '$t(assets)'},
        name: 'Assets with Meltdown Vulnerabilities',
        summary: {assetCount: 'With Meltdown Vulnerabilities'},
      },
      'meltdown-TotalVulnerabilities': {
        description:
          'The number of Meltdown vulnerability findings in your environment.',
        label: {count: 'Meltdown Vulnerability Findings'},
        name: 'Meltdown Vulnerability Count',
        summary: {count: ''},
      },
      'petya-AssetsByOS': {
        description: '$t(assetsByComponent.OS.title) vulnerable to Petya',
        name: 'Petya Vulnerable $t(assetsByComponent.OS.title)',
        summary:
          '__percent__ of my assets are running the __os__ Operating System',
        value:
          '__percent__ (__value__) of my assets are running the __os__ Operating System',
      },
      'petya-AssetsCriticalVuln': {
        description:
          'The number of assets in your environment with Petya vulnerabilities',
        label: {assetCount: '$t(assets)'},
        name: 'Assets with Petya Vulnerabilities',
        summary: {assetCount: 'With Petya Vulnerabilities'},
      },
      'petya-MostCommonExploitsComponent': {
        description:
          '$t(cards.mostCommonExploits.title) for Petya in your environment',
        name: '$t(cards.mostCommonExploits.title) for Petya',
      },
      'petya-MostCommonVulnComponent': {
        description:
          'The most common Petya vulnerabilities in your environment.',
        name: 'Most Common Petya Vulnerabilities',
      },
      'petya-TotalVulnerabilities': {
        description:
          'The number of Petya vulnerability findings in your environment.',
        label: {count: 'Petya Vulnerability Findings'},
        name: 'Petya Vulnerability Count',
        summary: {count: ''},
      },
      'samba-AssetsByOS': {
        description:
          '$t(cards.assets.card.byOS.title) $t(sambaWithCve) found in your environment',
        name: '$t(cards.assets.card.byOS.title) $t(sambaWithCve)',
        summary:
          '__percent__ of my assets are running the __os__ Operating System',
        value:
          '__percent__ (__value__) of my assets are running the __os__ Operating System',
      },
      'samba-ChangeInNumberOfAssets': {
        description: 'Assets vulnerable to $t(samba)',
        label: {
          totalAssets: 'Assets Vulnerable to Samba',
        },
        name: 'Assets $t(sambaWithCve)',
        summary: {
          assetCount: '__value__ (__percent__) increase',
        },
      },
      'samba-MostCommonExploitsComponent': {
        description: '$t(cards.mostCommonExploits.title) for $t(sambaWithCve)',
        name: '$t(cards.mostCommonExploits.title) for $t(samba)',
      },
      'samba-TopRiskiestAssetsComponent': {
        description:
          'A list of assets vulnerable to $t(samba), sorted by risk.',
        name:
          '$t(cardTemplates.TopRiskiestAssetsComponent.name) $t(sambaWithCve)',
      },
      'samba-TotalVulnerabilities': {
        description: 'Number of $t(samba) findings in your environment',
        label: {count: '$t(samba) $t(vulnerabilities)'},
        name: 'Number of $t(samba) findings',
        summary: {count: ''},
      },
      'spectre-AssetsCriticalVuln': {
        description:
          'The number of assets in your environment with Meltdown vulnerabilities',
        label: {assetCount: '$t(assets)'},
        name: 'Assets with Spectre Vulnerabilities',
        summary: {assetCount: 'With Spectre Vulnerabilities'},
      },
      'spectre-TotalVulnerabilities': {
        description:
          'The number of Spectre vulnerability findings in your environment.',
        label: {count: 'Spectre Vulnerability Findings'},
        name: 'Spectre Vulnerability Count',
        summary: {count: ''},
      },
      'wannacry-AssetsByOS': {
        description: '$t(assetsByComponent.OS.title) vulnerable to WannaCry',
        name: 'WannaCry vulnerable $t(assetsByComponent.OS.title)',
        summary:
          '__percent__ of my assets are running the __os__ Operating System',
        value:
          '__percent__ (__value__) of my assets are running the __os__ Operating System',
      },
      'wannacry-AssetsCriticalVuln': {
        description:
          'The number of assets in your environment with WannaCry vulnerabilities',
        label: {assetCount: '$t(assets)'},
        name: 'Assets with WannaCry Vulnerabilities',
        summary: {assetCount: 'With WannaCry Vulnerabilities'},
      },
      'wannacry-MostCommonExploitsComponent': {
        description:
          '$t(cards.mostCommonExploits.title) for WannaCry in your environment',
        name: '$t(cards.mostCommonExploits.title) for WannaCry',
      },
      'wannacry-MostCommonVulnComponent': {
        description:
          'The most common WannaCry vulnerabilities in your environment.',
        name: 'Most Common WannaCry Vulnerabilities',
      },
      'wannacry-TotalVulnerabilities': {
        description:
          'The number of WannaCry vulnerability findings in your environment.',
        label: {count: 'WannaCry Vulnerability Findings'},
        name: 'WannaCry Vulnerability Count',
        summary: {count: ''},
      },
    },
    cards: {
      assets: {
        card: {
          agents: {
            byOS: {
              description:
                'A card with a breakdown of assets with connected agents by operating systems.',
              summary:
                'of my assets with agents are running the __os__ Operating System',
              title: 'Assets with Agents by Operating System',
            },
            comingSoon: 'Coming Soon',
            downloadPageCategory: 'Agent Management',
            downloadPageTitle: 'Agent Download and Administration Page',
            featureOne: {
              desc: 'The Rapid7 Agent integrates with other Rapid7 products.',
              number: '1',
              title: 'ONE AGENT TO RULE THEM ALL',
            },
            featureThree: {
              desc:
                'Designed to be light and nimble, your users will never notice the agent.',
              number: '3',
              title: 'LIMITED FOOTPRINT',
            },
            featureTwo: {
              desc:
                'Get meaningful data right out of the box that syncs whenever your users are online.',
              number: '2',
              title: 'DATA WHEN YOU NEED IT',
            },
            installInstructions: {
              installation: 'For Installation',
              installsTo: 'The agent installs to:',
              linux: {
                install: './agent_installer.sh install_start',
                installsTo1: '/opt/rapid7/ir_agent/',
                installsTo2:
                  "The agent can be controlled either through the platform's SCM (systemctl, systemd, etc.), or through the installer:",
                installsTo3: './agent_installer.sh status',
                uninstall: './agent_installer.sh uninstall',
              },
              localInstallation: 'For Local Installation',
              mac: {
                install: 'Double click the .pkg to go through a UI installer.',
                installsTo: '/opt/rapid7/ir_agent/',
                installsTo2:
                  'The agent can be controlled through launchctl as com.rapid7.ir_agent.',
                silentInstall: 'sudo installer -pkg <.pkg> -target /',
                uninstall: 'sudo /opt/rapid7/ir_agent/uninstall.sh -f',
              },
              multipleInstallations: 'For Multiple Installations',
              silentInstall: 'For Silent Installation',
              silentLocalInstall: 'For Silent Local Installation',
              uninstall: 'For Uninstall',
              win: {
                installsTo: 'C:\\Program Files\\Rapid7\\Endpoint Agent\\',
                installsTo2:
                  'The agent can be controlled using the ‘Rapid7 Endpoint Agent’ service.',
                local: 'Double click the .msi to go through a UI installer.',
                multiple:
                  'To install the agent to multiple assets, use an endpoint management tool such as System Center Configuration Manager (SCM). To use Group Policy Object (GPO), place the MSI installer in the Network Logon Share (Active Directory). Create a new GPO.',
                silentLocal: 'msiexec /i agentinstaller-x86_x64.msi /quiet /qn',
              },
            },
            linuxAgent: 'Linux Agent',
            macAgent: 'Mac Agent',
            numberConnected: {
              description:
                'A card showing the number of assets in your environment with connected agents.',
              subTitle: 'Assets',
              summary: 'With Connected Agents',
              title: 'Number of Assets with an Agent',
            },
            supportText:
              'Need help? Contact <a class="support-link">customer support</a>.',
            winAgent: 'Windows Agent',
          },
          byLastScanDate: {
            description:
              'Displays a graph of assets grouped by how recently they were scanned by an engine.',
            scanDateIn: "$t(timeUnits.__timeUnit__, {'count': __time__})",
            summary:
              'of my assets have not been scanned by an engine in __days__ days.',
            title: 'Assets by Last Engine Scan Date',
            totalAssets: 'Scanned assets',
          },
          byOS: {
            description:
              'A card with a breakdown of assets by operating systems.',
            summary: 'of my assets are running the __os__ Operating System',
            title: 'Assets by Operating System',
            unknownOS: 'Unknown OS',
          },
          byRiskDelta: {
            description:
              'A card that highlights the assets in your environment whose risk score increased the most.',
            title: 'Assets by 30-day Risk Increase',
          },
          bySkillLevel: {
            description:
              'Shows a breakdown of assets in your environment by the level of skill needed to exploit the vulnerabilities on them.',
            expertSummary:
              'of exploitable assets in your environment can be exploited by an expert',
            intermediateSummary:
              'of exploitable assets in your environment can be exploited with intermediate',
            legend: {
              expert: 'Expert',
              intermediate: 'Intermediate',
              novice: 'Novice',
              unknown: 'No known exploit',
            },
            noviceSummary:
              'of exploitable assets in your environment can be exploited by a novice',
            title: 'Exploitable Assets by Skill Level',
            unknownSummary:
              'of exploitable assets in your environment have no known exploit',
          },
          byTotalScanTime: {
            description:
              'Displays a graph of assets grouped by engine scan time.',
            scannedIn: "$t(timeUnits.__timeUnit__, {'count': __time__})",
            summary:
              'of my assets take __mins__ minutes to scan with an engine.',
            title: 'Assets by Total Engine Scan Time',
            totalAssets: 'Scanned assets',
            totalScanTime: 'Total Engine Scan Time',
          },
          byVulnSeverity: {
            criticalSummary: 'of my assets have critical vulnerabilities',
            description:
              'Shows my assets that have vulnerabilities grouped by severity',
            legend: {
              critical: 'Critical Vulnerabilities',
              moderate: 'Moderate Vulnerabilities',
              severe: 'Severe Vulnerabilities',
              unknown: 'Unknown Vulnerabilities',
            },
            moderateSummary: 'of my assets have moderate vulnerabilities',
            severeSummary: 'of my assets have severe vulnerabilities',
            title: 'Assets by Vulnerability Severity',
            unknownSummary:
              'of my assets have no known vulnerabilities detected',
          },
          changeInNumber: {
            description:
              'Shows the total number of assets and how that number has changed in a specified time period, so you can monitor Key Performance Indicators.',
            subTitle: 'Total Assets',
            summary: ' __difference__ (__percentDifference__% change)',
            title: 'Total Asset Trends',
          },
          defaultAccountVulns: {
            description:
              'Shows the number of assets with vulnerabilities related to the use of default accounts.',
            subTitle: 'Assets',
            summary: 'With Default Account Vulnerabilities',
            title: 'Assets with Default Account Vulnerabilities',
          },
          knownObsoleteOS: {
            description:
              'The percentage of assets that are currently running on known obsolete operating systems.',
            subTitle: 'Assets Running Obsolete OS',
            title: 'Assets Running Obsolete Operating Systems',
          },
          notScanned: {
            description:
              'Shows the number of assets that have not been scanned in a specified time period.',
            subTitle: 'Assets',
            summary: 'Not Scanned',
            title: 'Assets Not Recently Scanned',
          },
          owners: {
            violatingPolicy: {
              description:
                'Owners associated with the highest number of assets that violate company policy.',
              title: 'Top Asset Owners Violating Company Policy',
            },
          },
          violatingPolicy: {
            description:
              'Locations have the highest number of assets that violate company policy.',
            title: 'Top Asset Locations Violating Company Policy',
          },
        },
        description:
          'Lists the assets with the highest risk in your environment.',
        donutTitle: 'Riskiest Assets',
        drilldown: {
          remediation: {
            assigneesPlaceholder: 'Assign the project to...',
            clickHereToViewNewProject: 'Click here to view your new project.',
            createStaticProject: 'Create a Static Remediation Project',
            createStaticProjectHint:
              'Make a selection to create a static project. Only supports up to 10,000 items.',
            descriptionPlaceholder: 'A description for the project',
            errorNotification:
              'There was an error saving your remediation project, try again later.',
            manageProjects: 'Manage projects',
            remediate: 'Remediate',
            selectedCount: '__count__ record(s) selected.',
            successNotification:
              'Remediation project __title__ was created successfully. __link__',
            titlePlaceholder: 'A name for the project',
            titleRequired: 'Title cannot be empty',
          },
        },
        newlyDiscovered: {
          description:
            'Shows the number of assets that were newly discovered within the environment within a specified time frame.',
          subTitle: 'Assets',
          summary: 'Newly Discovered',
          title: 'Newly Discovered Assets',
        },
        title: 'Top Riskiest Assets',
        vulnerabilities: {
          critical: {
            description:
              'Shows the number of assets with critical risk vulnerabilities (CVSS score over 8) that are not excepted.',
            subTitle: 'Assets',
            summary: 'With Critical Vulnerabilities',
            title: 'Assets with Critical Risk Vulnerabilities',
          },
          dataFetchFailed:
            'Failed to retrieve vulnerabilities, please try later.',
          nonCritical: {
            summary: 'of my assets have non critical risk vulnerabilities',
          },
        },
      },
      counts: {
        assetGroupsCount: 'Asset Groups',
        assetSitesCount: 'Sites',
        assetTagsCount: 'Tags',
        assetsCount: 'Assets',
        vulnerabilitiesCount: 'Vulnerabilities',
      },
      credential: {
        'success-over-time': 'Credential Success Over Time',
        successCard: {
          chart: {
            credentialFailedAssetCount: 'of my assets had failed logins',
            credentialSuccessAssetCount: 'of my assets had successful logins',
            credentialUnknownAssetCount: 'of my assets had no logins attempted',
            legend: {
              credentialFailedAssetCount: 'Failed logins',
              credentialSuccessAssetCount: 'Successful logins',
              credentialUnknownAssetCount: 'No logins attempted',
            },
          },
          description:
            'A card detailing the success/fail ratio of credentials on the network.',
          summary: {
            credentialFailedAssetCount: 'of my assets had failed logins',
            credentialSuccessAssetCount:
              'of my assets have had successful logins',
            credentialUnknownAssetCount: 'of my assets had no logins attempted',
          },
          title: 'Authentication Status for Assets',
        },
      },
      'credential-success': {
        description:
          'Shows the Assets that have had successful authentication.',
        name: 'Assets by Authentication Status',
      },
      'credential-success-over-time': {
        description:
          'A card that shows information and graph about the success or failure of credentials on asset over time',
        name: 'Credential Success Over Time',
      },
      expired: {
        ssl: {
          certificatesCard: {
            description:
              'Shows the number of assets in your environment that currently have expired SSL certificates.',
            subTitle: 'Assets',
            summary: 'With Expired SSL certificates',
            title: 'Assets with Expired SSL Certificates',
          },
        },
      },
      expiredVulnException: {
        description:
          'A card that shows the vulnerability exceptions in your environment that have recently expired.',
        title: 'Recently Expired Vulnerability Exceptions',
      },
      expiring: {
        ssl: {
          certificatesCard: {
            description:
              'Shows the number of assets in your environment with SSL certificates set to expire within a specified time frame.',
            subTitle: 'Assets',
            summary: 'With Expiring SSL certificates',
            title: 'Assets with Expiring SSL Certificates',
          },
        },
      },
      expiringVulnException: {
        description:
          'A card that shows vulnerability exceptions in your environment that are about to expire.',
        title: 'Upcoming Vulnerability Exception Expirations',
      },
      exploitableVulnByCVSSScore: {
        description:
          'Breaks down the percentages of exploitable vulnerabilities in your environment by CVSS score so you can see what proportion of your environment has high risk according to general standards.',
        legend: '__from__ - __to__',
        summary:
          'of exploitable vulnerabilities have a CVSS score between __from__ and __to__',
        title: 'Exploitable Vulnerabilities by CVSS Score',
      },
      location: {
        description:
          'Lists the locations with the highest total risk in your environment.',
        title: 'Top Riskiest Asset Locations',
      },
      mostCommonExploitableVuln: {
        description:
          'A card that shows the most common vulnerabilities in your environment based on number of affected assets.',
        title: 'Most Common Exploitable Vulnerabilities',
      },
      mostCommonExploits: {
        description:
          'Shows the most common exploits in your environment based on number of affected assets.',
        title: 'Most Common Exploits',
      },
      mostCommonMalwareKits: {
        description:
          'Shows the most common malware kits in your environment based on number of affected assets.',
        title: 'Most Common Malware Kits',
      },
      mostCommonVuln: {
        description:
          'Shows the most common vulnerabilities in your environment based on number of affected assets.',
        title: 'Most Common Vulnerabilities',
      },
      obsolete: {
        osCard: {
          chart: {
            assetCount: 'Obsolete OS Assets',
            totalAssets: 'Remaining Assets',
          },
          description:
            'The percentage of assets that are currently running on known obsolete operating systems.',
          summary: {
            nonObsolete: 'of assets running non-obsolete OS',
            obsolete: 'of assets running obsolete OS',
          },
          title: 'Assets Running Obsolete Operating Systems',
        },
        softwareCard: {
          chart: {
            assetCount: 'Obsolete Software Assets',
            totalAssets: 'Remaining Assets',
          },
          description:
            'A card detailing the obsolete/non obsolete assets on the network.',
          summary: {
            nonObsolete:
              'of assets do not have known obsolete software installed',
            obsolete: 'of assets have known obsolete software installed',
          },
          title: 'Assets Running Obsolete Software',
        },
      },
      owner: {
        description:
          'Lists asset owners whose assets have the highest total risk.',
        title: 'Top Riskiest Asset Owners',
      },
      projectsAtRisk: {
        description:
          'Shows the number of projects nearing expiration that are still open.',
        subTitle: 'Projects Closing',
        summary: '',
        title: 'Projects at Risk',
      },
      projectsMostRemainingWork: {
        description:
          'Lists projects with the highest number of unresolved remediation items.',
        title: 'Remaining Work',
      },
      projectsMostRemainingWorkPercent: {
        description:
          'Lists projects with the highest number of unresolved remediation items.',
        title: 'Remaining Work %',
      },
      projectsPastDue: {
        description: 'Shows the number of projects that are past due.',
        subTitle: 'Projects',
        summary: 'Are Past Due',
        title: 'Past Due Projects',
      },
      projectsRecentlyClosed: {
        description:
          'Shows the number of projects that have been recently closed.',
        subTitle: 'Projects Closed',
        summary: '',
        title: 'Recently Closed Projects',
      },
      remediatedVulnerabilities: {
        description:
          'Lists vulnerabilities whose presence has been reduced in your environment within the last 30 days.',
        title: 'Top Remediated Vulnerabilities',
      },
      remediatorsRequiringAssistance: {
        description:
          'Lists the project owners with the most projects still open and past due.',
        title: 'Remediators Requiring Assistance',
      },
      'risk-vulns-card': {
        description:
          'Shows a scatter plot chart with assets ordered by risk and number of vulnerabilities',
        name: 'Assets by Risk and Vulnerabilities',
      },
      riskOverTimeCard: {
        allTime: 'All Time',
        custom: 'Custom',
        riskScore: 'Risk Score',
        thisMonth: 'This Month',
        thisWeek: 'This Week',
        time: 'Time',
        title: 'Risk Over Time',
      },
      'scan-card': {
        description:
          'Shows a summary of all scans in your environment, including the number of failed, paused and completed scans',
        name: 'Scan',
      },
      scanCard: {
        complete: 'Complete',
        drilldownTitle: 'Scans',
        failed: 'Failed',
        inProgress: 'In Progress',
        paused: 'Paused',
        subTitle: 'Past 30 Days',
        table: {
          assets: 'Assets',
          siteName: 'Site Name',
          startDate: 'Start Date',
          status: 'Status',
          vulnerabilities: 'Vulnerabilities',
        },
        title: 'Scan',
      },
      sites: {
        riskiest: 'Riskiest Sites',
        title: 'Top Riskiest Sites',
      },
      'top-riskiest-asset-groups': {
        description:
          'Lists the asset groups with the highest risk scores in your environment.',
        name: 'Top Riskiest Asset Groups',
      },
      'top-riskiest-assets': {
        description:
          'Lists the assets with the highest risk in your environment.',
        name: 'Top Riskiest Assets',
      },
      'top-riskiest-sites': {
        description:
          'Lists the sites with the highest total risk in your environment.',
        name: 'Top Riskiest Sites',
      },
      topRemediators: {
        description:
          'Lists the project owners with the most projects successfully closed and on time.',
        title: 'Top Remediators',
      },
      trendingVulnerabilities: {
        description:
          'Lists vulnerabilities becoming more prevalent in your environment within the last 30 days.',
        title: 'Top Trending Vulnerabilities',
      },
      vulnByCVSSScore: {
        description:
          'Breaks down the percentages of vulnerabilities in your environment by CVSS score so you can see what proportion of your environment has high risk according to general standards.',
        legend: '__from__ - __to__',
        summary:
          'of vulnerabilities have a CVSS score between __from__ and __to__',
        title: 'Vulnerabilities by CVSS Score',
      },
      vulnByCVSSv3Score: {
        description:
          'Breaks down the percentages of vulnerabilities in your environment by CVSS V3 score so you can see what proportion of your environment has high risk according to general standards.',
        legend: '__from__ - __to__',
        summary:
          'of vulnerabilities have a CVSS V3 score between __from__ and __to__',
        title: 'Vulnerabilities by CVSS Score (Grouped by V3 Category)',
      },
      vulnByRiskScore: {
        description:
          'A card that shows percentage of all vulnerabilities in my environment by risk score.',
        legend: '__from__ - __to__',
        summary:
          'of vulnerabilities have a risk score between __from__ and __to__',
        summaryOver900: 'of vulnerabilities have a risk score over 900',
        title: 'Vulnerabilities by Risk Score',
      },
      vulnerabilities: {
        ageByCVSS: {
          categories: {
            'age-range-1': '0-29 days',
            'age-range-2': '30-59 days',
            'age-range-3': '60-90 days',
            'age-range-4': '> 90 days',
            'cvss-range-1': '0.1-3.9',
            'cvss-range-2': '4.0-6.9',
            'cvss-range-3': '7.0-10.0',
          },
          description:
            'A card highlighting when vulnerabilities in your environment were discovered ranked by CVSS Score',
          title: 'Vulnerability Discovery Date by CVSS Score',
        },
        ageBySeverity: {
          categories: {
            'age-range-1': '0-29 days',
            'age-range-2': '30-59 days',
            'age-range-3': '60-90 days',
            'age-range-4': '> 90 days',
            critical: 'Critical',
            moderate: 'Moderate',
            severe: 'Severe',
          },
          description:
            'A card showing the average life of the vulnerabilities in your environment by severity',
          title: 'Average Vulnerability Age by Severity',
        },
        bySkillLevel: {
          description:
            'Shows a breakdown of vulnerabilities in your environment by the level of skill needed to exploit them.',
          expertSummary: 'of vulnerabilities can be exploited by an expert',
          intermediateSummary:
            'of vulnerabilities can be exploited by an intermediate',
          legend: {
            expert: 'Expert',
            intermediate: 'Intermediate',
            novice: 'Novice',
            unknown: 'No known exploit',
          },
          noviceSummary: 'of vulnerabilities can be exploited by a novice',
          title: 'Exploitable Vulnerabilities by Skill Level',
          unknownSummary:
            'of vulnerabilities in your environment have no known exploit',
        },
        bySkillLevelOverTime: {
          description:
            'A card that shows all vulnerabilities in your environment by severity, over time.',
          title: 'Exploitable Vulnerabilities by Skill Level Over Time',
        },
        byVulnSeverity: {
          criticalSummary: 'of your vulnerabilities are Critical',
          description:
            'Shows the percentage breakdown of the vulnerabilities in your environment by their severity levels.',
          legend: {
            critical: 'Critical Vulnerabilities',
            moderate: 'Moderate Vulnerabilities',
            severe: 'Severe Vulnerabilities',
            unknown: 'Unknown',
          },
          moderateSummary: 'of your vulnerabilities are Moderate',
          severeSummary: 'of your vulnerabilities are Severe',
          title: 'Vulnerabilities by Severity',
        },
        card: {
          exploitable: {
            description:
              'A card showing the total number of vulnerabilities in your environment and how that number is trending in a specified time period.',
            subTitle: 'Exploitable Vulnerabilities',
            title: 'Exploitable Vulnerabilities Count',
          },
          totalNumber: {
            description:
              'A card showing the change in the number of vulnerabilities in your environment in a chosen time.',
            subTitle: 'Total Vulnerabilities',
            summary: ' __difference__ (__percentDifference__% change)',
            title: 'Number of Vulnerabilities',
          },
        },
        detail: {
          entity: {
            severity: 'Severity',
            title: 'Title',
            vulnId: 'Vuln ID',
          },
          title: 'Vulnerability Details',
          vulnInstances: {
            dataFetchFailed: 'Failed to retrieve vulnerability instances.',
          },
        },
        discoveryByTotalRiskScore: {
          description:
            'Shows vulnerabilities discovered in the past 90 days, sorted by total risk score, from highest to lowest.',
          title: 'Newly Discovered Vulnerabilities by Total Risk Score',
        },
        discoveryDateBySeverity: {
          categories: {
            'age-range-1': '0-29 days',
            'age-range-2': '30-59 days',
            'age-range-3': '60-90 days',
            'age-range-4': '> 90 days',
            critical: 'Critical',
            moderate: 'Moderate',
            severe: 'Severe',
          },
          description:
            'A card highlighting when vulnerabilities in your environment where discovered ranked by severity',
          title: 'Vulnerability Discovery Date By Severity',
        },
        exploitable: {
          ageByCVSS: {
            description:
              'A card highlighting when exploitable vulnerabilities in your environment were discovered ranked by CVSS score',
            title: 'Exploitable Vulnerability Discovery Date by CVSS Score',
            xAxisLabel: cvssScoreLabels,
            yAxisLabel: dayRangeLabels,
          },
          ageByCVSSv3: {
            description:
              'A card highlighting when exploitable vulnerabilities in your environment were discovered ranked by CVSS V3 score',
            title:
              'Exploitable Vulnerability Discovery Date by CVSS Score (Grouped by V3 Category)',
            xAxisLabel: cvssV3ScoreLabels,
            yAxisLabel: dayRangeLabels,
          },
          ageBySeverity: {
            description:
              'A card showing the average life of the exploitable vulnerabilities in your environment by severity',
            title: 'Average Exploitable Vulnerability Age by Severity',
          },
          byVulnSeverity: {
            criticalSummary: 'of your exploitable vulnerabilities are critical',
            description:
              'Shows the percentage breakdown of the exploitable vulnerabilities in your environment by their severity levels.',
            legend: {
              critical: 'Critical Exploitable Vulnerabilities',
              moderate: 'Moderate Exploitable Vulnerabilities',
              severe: 'Severe Exploitable Vulnerabilities',
              unknown: 'Unknown',
            },
            moderateSummary: 'of your exploitable vulnerabilities are Moderate',
            severeSummary: 'of your exploitable vulnerabilities are Severe',
            title: 'Exploitable Vulnerabilities by Severity',
          },
          discoveryByTotalRiskScore: {
            description:
              'Shows exploitable vulnerabilities discovered in the past 90 days, sorted by total risk score, from highest to lowest.',
            title:
              'Newly Discovered Exploitable Vulnerabilities by Total Risk Score',
          },
          discoveryDateBySeverity: {
            description:
              'A card highlighting when exploitable vulnerabilities in your environment were discovered ranked by severity',
            title: 'Exploitable Vulnerability Discovery Date By Severity',
            xAxisLabel: severityLabels,
            yAxisLabel: dayRangeLabels,
          },
          publishAgeBySeverity: {
            description:
              'A card that details the published age of exploitable vulnerabilities in your environment by severity',
            title: 'Exploitable Vulnerability Publish Age by Severity',
            xAxisLabel: severityLabels,
            yAxisLabel: dayRangeLabels,
          },
          publishDateByCVSS: {
            description:
              'A card that lists publish date of exploitable vulnerabilities by CVSS score',
            title: 'Exploitable Vulnerability Publish Age by CVSS Score',
            xAxisLabel: cvssScoreLabels,
            yAxisLabel: dayRangeLabels,
          },
          publishDateByCVSSv3: {
            description:
              'A card that lists publish date of exploitable vulnerabilities by CVSS V3 score',
            title:
              'Exploitable Vulnerability Publish Age by CVSS Score (Grouped by V3 Category)',
            xAxisLabel: cvssV3ScoreLabels,
            yAxisLabel: dayRangeLabels,
          },
        },
        introducedLastWeek: {
          description:
            'A card showing new vulnerabilities introduced to your network in the last week by risk score.',
          title: 'New Vulnerabilities By Risk Score',
        },
        publishAgeBySeverity: {
          categories: {
            'age-range-1': '0-29 days',
            'age-range-2': '30-59 days',
            'age-range-3': '60-90 days',
            'age-range-4': '> 90 days',
            critical: 'Critical',
            moderate: 'Moderate',
            severe: 'Severe',
          },
          description:
            'A card that details the published age of vulnerabilities in your environment by severity',
          title: 'Vulnerability Publish Age by Severity',
        },
        publishDateByCVSS: {
          categories: {
            'age-range-1': '0-29 days',
            'age-range-2': '30-59 days',
            'age-range-3': '60-90 days',
            'age-range-4': '> 90 days',
            'cvss-range-1': '0.1-3.9',
            'cvss-range-2': '4.0-6.9',
            'cvss-range-3': '7.0-10.0',
          },
          description:
            'A card that lists publish date of vulnerabilities by CVSS score',
          title: 'Vulnerability Publish Age by CVSS Score',
        },
        publishDateByCVSSv3: {
          categories: {
            'age-range-1': '0-29 days',
            'age-range-2': '30-59 days',
            'age-range-3': '60-90 days',
            'age-range-4': '> 90 days',
            'cvss-range-1': '0-0',
            'cvss-range-2': '0.1-3.9',
            'cvss-range-3': '4.0-6.9',
            'cvss-range-4': '7.0-8.9',
            'cvss-range-5': '9.0-10.0',
          },
          description:
            'A card that lists publish date of vulnerabilities by CVSS V3 score',
          title:
            'Vulnerability Publish Age by CVSS Score (Grouped by V3 Category)',
        },
        remediated: {
          description:
            'A card showing showing a list of remediations on your network that had the highest impact on risk score.',
          title: 'Recent Remediations Completed By Risk Score',
        },
        riskScoreOverTime: {
          cardTitle: 'Vulnerability Risk Score Over Time',
          description:
            'Show the trend of vulnerability risk in the environment, so you can see whether the total risk has increased or decreased over time.',
          name: 'Vulnerability Risk Score Over Time',
        },
      },
    },
    cardsLabel: 'Cards',
    category: 'Category',
    certainity: 'Certainty',
    certainty: 'Certainty',
    characterDevice: 'Character Device',
    characterLimit: '__characterCount__ Character Limit',
    chart: {
      loading: 'The chart is loading.',
      noData: 'There is not enough data to display this chart.',
    },
    checksum: 'Checksum',
    class: 'Class',
    clipboard,
    close: 'Close',
    closed: 'Closed',
    clusters: 'Clusters',
    collector: 'Collector',
    collector_plural: 'Collectors',
    collectorManagement: {
      collectNetworkData: 'Transmit Vulnerability Data',
      description:
        'InsightVM can now utilize the Collector to transmit vulnerability data to <a href="https://insight.rapid7.com" target="_blank" rel="noopener noreferrer"> insight.rapid7.com</a>. If you are also an InsightIDR or InsightOps subscriber, you can use any Collectors you have already deployed for this InsightVM functionality.',
      download: '$t(download) $t(collector)',
      downloadPlatform: '$t(download) __platform__ $t(collector)',
      getStarted:
        'To get started, download the Collector for your OS and follow the guided instructions.',
    },
    comingSoon: 'Coming Soon',
    comparison: {
      cancelText: 'Cancel Comparison',
      compare: 'Compare',
      errorMessage: 'Please select two valid date for comparison.',
      failedDataFetch: 'Failed to get comparison data, please try later.',
      fromDate: 'From Date',
      metricAdded: 'Added __name__',
      metricChange: '__name__ Change',
      metricPrimarySubTitle: 'Total __name__ in two selected scans',
      metricRemediated: 'Remediated __name__',
      metricUnchanged: 'Unchanged __name__',
      text:
        'COMPARISON - Compare two assessment datasets to see what has changed',
      toDate: 'To Date',
    },
    comparisonLabel: 'Comparison',
    completed: 'Completed',
    compliance: 'Compliance',
    complianceStatus: 'Compliance Status',
    configure: 'Configure',
    configuredCount: '__count__ Configured',
    confirm: 'OK',
    confirmEmailAddress: 'Re-enter email address',
    confirmLabel: 'Confirm',
    connected: 'Connected',
    connectionError: {
      authorization: 'Authorization failed',
      connect_timeout: 'Connection timed out',
      not_found: 'Host not found',
      ssl_handshake: 'SSL handshake failed',
    },
    connectionStatus: 'Connection Status',
    connectionType: 'Connection Type',
    connections: 'Connections',
    consoleDisabled: {
      body:
        'Contact your Security Console Global Administrator to re-enable your user account.',
      title: 'Your Security Console Account Is Disabled',
      goBack: 'Go Back',
    },
    consolePairing: {
      content:
        "Use this pairing key within $t(timeUnits.hoursLower, {'count': __hours__}) to complete your console activation.",
      contentHeader:
        'Activate your Security Console on the Rapid7 Insight platform',
      header: 'Welcome to InsightVM',
      instructionFirst: 'Log in to your on-prem Security Console.',
      instructionSecond:
        'Navigate to the Insight platform tab by clicking the <strong>Enter pairing key</strong> button as prompted at the top of the screen, or through <strong>Administration</strong> > "Global and Console Settings" > <strong>Administer</strong> > <strong>Insight platform</strong> tab.',
      instructionThird:
        'Enter the pairing key and click <strong>Activate Console.</strong> A notification will display when the console has been activated successfully.',
    },
    consoleUnreachable: {
      buttonText: 'Got it',
      contentHtml:
        "<paragraph><listItem>Make sure that you are on the same network as your Security Console host and verify that it's online. After doing so, navigate to <settings>Org Settings</settings> and check that your console URL is correct. You may need to change your console URL configuration to reestablish the connection.</listItem></paragraph>\
        <paragraph><listItem>If your Security Console is still using a self-signed certificate, this error is likely due to a certificate warning produced by your browser. To resolve this, <consoleUrl>navigate to your Security Console URL directly</consoleUrl> and dismiss the certificate warning to restore connectivity to InsightVM Platform Login. Once you have dismissed the certificate warning, perform a <refresh>refresh</refresh> to continue using this session.</listItem></paragraph>",
      restrictedContentHtml: '$t(orgSettings.error.body) <0/>',
      title: "Your Security Console Didn't Respond",
    },
    container: 'Container',
    'container-counts': {
      description: 'A card that displays asset container counts',
      name: 'Counts',
    },
    containerBuildPolicyCriterion: {
      critical_vuln_count: 'Critical Vulnerabilities',
      exploitable_vuln_count: 'Exploitable Vulnerabilities',
      max_cvss_score: 'CVSS Score',
      moderate_vuln_count: 'Moderate Vulnerabilities',
      package_name: 'Package Name',
      package_risk_score: 'Packages Risk Score',
      risk_score: 'Risk Score',
      severe_vuln_count: 'Severe Vulnerabilities',
      total_vuln_count: 'Total Vulnerabilities',
      vuln_category: 'Vulnerability Category',
      vuln_name: 'Vulnerability Name',
      vuln_package_name: 'Vulnerable Package Name',
      vulns_with_malware_count: 'Vulnerabilities with Malware',
    },
    containerBuildStatus: {
      failed: {
        critical_vuln_count:
          '$t(critical) $t(vulnerabilities) count was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
        exploitable_vuln_count:
          '$t(exploitable) $t(vulnerabilities) count was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
        max_cvss_score:
          'Maximum $t(cvssScore) was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
        moderate_vuln_count:
          '$t(moderate) $t(vulnerabilities) count was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
        package_name:
          'One or more packages containing "__configuredValue__" were found.',
        package_risk_score:
          'One or more packages with $t(riskScoreLabel) above __configuredValue__ were found.',
        risk_score:
          'Total $t(riskScoreLabel) was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
        severe_vuln_count:
          '$t(severe) $t(vulnerabilities) count was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
        total_vuln_count:
          '$t(totalVulns) count was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
        vuln_category:
          'One or more vulnerabilities with vulnerability category containing "__configuredValue__" were found.',
        vuln_name:
          'One or more vulnerabilities with vulnerability title containing "__configuredValue__" were found',
        vuln_package_name:
          'One or more vulnerable packages containing "__configuredValue__" were found.',
        vulns_with_malware_count:
          '$t(vulnerabilities) with $t(malwareCount) was __actualValue__, which exceeded the configured threshold of __configuredValue__.',
      },
      passed: {
        critical_vuln_count:
          '$t(critical) $t(vulnerabilities) count was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
        exploitable_vuln_count:
          '$t(exploitable) $t(vulnerabilities) count was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
        max_cvss_score:
          'Maximum $t(cvssScore) was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
        moderate_vuln_count:
          '$t(moderate) $t(vulnerabilities) count was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
        package_name:
          'No packages were found with name containing "__configuredValue__".',
        package_risk_score:
          'No packages were found with $t(riskScoreLabel) above __configuredValue__.',
        risk_score:
          '$t(riskScoreLabel) was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
        severe_vuln_count:
          '$t(severe) $t(vulnerabilities) count was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
        total_vuln_count:
          '$t(totalVulns) count was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
        vuln_category:
          'No vulnerabilities with vulnerability category containing "__configuredValue__" were found.',
        vuln_name:
          'No vulnerabilities with vulnerability title containing "__configuredValue__" were found.',
        vuln_package_name:
          'No vulnerable packages were found with name containing "__configuredValue__".',
        vulns_with_malware_count:
          '$t(vulnerabilities) with $t(malwareCount) was __actualValue__, which did not exceed the configured threshold of __configuredValue__.',
      },
    },
    containerComponents: {
      age: 'Age',
      assessedJobBuilds: '$t(assessed) Build Jobs',
      assessment: 'Assessment',
      assessmentStatus: {
        ASSESSED: 'Assessed',
        NOT_ASSESSED: 'Not Assessed',
      },
      buildPolicy: 'Build Policy',
      buildPolicyRules: '$t(containerComponents.buildPolicy) Rules',
      emptyState:
        "To add containers, run the Kubernetes Monitor in your Kubernetes clusters. <br/><a href='https://docs.rapid7.com/insightvm/manage-kubernetes-integration/' target='_blank' rel='noopener noreferrer'>See The Documentation</a>",
      imageSource: {
        asset: 'Docker',
        cluster: 'Kubernetes',
      },
      latestBuildPolicy: 'Latest $t(containerComponents.buildPolicy)',
      management: 'Management',
      managementStatus: {
        KUBERNETES: 'Kubernetes',
        STAND_ALONE: 'Stand Alone',
      },
      podName: 'Pod Name',
      search: {
        images: 'Search images',
      },
    },
    containerCount: 'Container Count',
    containerId: 'Container ID',
    containers: 'Container Security',
    containersBuildAboveThresholdItemLabel: 'above threshold',
    containersBuildBelowThresholdItemLabel: 'below threshold',
    containersBuildThresholdItemLabel: 'threshold',
    containersBuildThresholdLabel: 'Acceptable Threshold',
    containersImageAssessError:
      'Image __imageId__ could not be assessed, please try again later.',
    containersImageUploadError:
      'The image could not be uploaded, please try again later.',
    containersImageUploadSuccess:
      'The image was successfully uploaded, and will be queued for processing.',
    containersModule: {
      selectedOfTotalRecords: '(__selected__ of __total__ records selected)',
    },
    before: 'Before',
    after: 'After',
    containersText: 'Containers',
    context: 'Context',
    continue: 'Continue',
    copied: 'Copied',
    copy: 'Copy',
    copyOf: 'Copy of',
    count: 'Count',
    cpe: 'CPE',
    create: 'Create',
    createdOn: 'Created On',
    creator: 'Creator',
    critical: 'Critical',
    csvExportError:
      'There was a problem exporting to csv. Please try again later.',
    customDates: 'custom date range',
    cvss: 'CVSS',
    cvssScore: 'CVSS Score',
    cvssV2: 'CVSS v2',
    cvssV3: 'CVSS v3',
    dashboard: {
      cannotBeEmpty: 'Dashboard name cannot be empty',
      nameInUse: 'This name is already in use',
      shareWithUsersAutomaticallyInfo:
        'All associated filters will automatically be shared. Private filters you do not own will not be shared.',
      sharingLimitExceeded:
        'You have exceeded the number of users (100) the dashboard can be shared with.',
      descriptionPlaceholder: 'A description for the project',
      renameCard: 'Rename Dashboard Card',
    },
    dashboardDescription: '$t(dashboardLabel) Description',
    dashboardDropdown: {
      allMyDashboards: 'All Dashboards',
      allTemplates: 'See More in the R7 Library',
      createDashboard: 'Create a New Dashboard',
      dashboardTemplates: 'Rapid7 Recommends These Dashboard Templates',
      myDashboards: 'My Dashboards',
    },
    exportReportNotification:
      "Your report is being generated. If you stay on this page, it will download automatically when it's ready. Otherwise, you can find it in the <manageLink><manageButton>Report Archive</manageButton></manageLink>",
    dashboardLabel: 'Dashboard',
    dashboardLibrary: {
      categoryName: {
        allMyDashboards: '$t(dashboardDropdown.allMyDashboards)',
        allTemplates: 'All Dashboard Templates',
        containers: 'Containers',
        asset: 'Assets Templates',
        changed: "What's Changed Templates",
        executive: 'Executive Templates',
        ownedDashboards: 'Owned by me',
        scans: 'Scans & Operations Templates',
        sharedDashboards: 'Owned by others',
        vulnerability: 'Vulnerabilities Templates',
      },
      dashboardTemplates: 'Rapid7 Dashboard Templates',
      myDashboards: 'My Dashboards',
      templateDescription: {
        OTIoTAssets:
          'Gain visibility to all the connected devices in your networks, which includes Operational Technology (OT), Industrial Control Systems (ICS), and Internet of Things (IoT), as discovered by SCADAfence.',
        PatchTuesday:
          "Get an overview of the changes introduced to your environment by Microsoft's Patch Tuesday.",
        RECON:
          'View all your occurrences of RECON, a critical vulnerability that affects the SAP NetWeaver Application Server (AS) Java component LM Configuration Wizard. An unauthenticated attacker can exploit this vulnerability through the Hypertext Transfer Protocol (HTTP) to take control of trusted SAP applications.',
        SIGNIFICANT_CHANGES:
          'Get an overview of significant asset and vulnerability changes in your environment over the last 30 days.',
        assets:
          'Discover and understand the most important assets and their threat exposure in your environment by seeing their attributes and vulnerabilities in a single place.',
        changed:
          "The What's Changed dashboard provides an efficient way to determine what has changed, newly discovered assets and baseline metrics to help establish context.",
        containers:
          'See data about vulnerable container images and container usage in your environment.',
        executive:
          'Understand if threat exposure is getting better or worse. Gain insight into team performance and key metrics to extracting return on investment.',
        exploitableVulns:
          'See vulnerability data that is associated with known exploit kits or malware.',
        externalRemoteWorkforce:
          'Assess your risk exposure by identifying potential external and remote workforce assets, as well as vulnerabilities and trends across these assets.',
        meltdown:
          "See data related to Meltdown (CVE-2017-5754), a specific high profile vulnerability. These cards are custom filtered to report on your organization's security posture pertaining to this vulnerability.",
        petya:
          "See data related to Petya-like Ransomware, which spreads, in part, by exploiting vulnerabilities (MS17-010). These cards are custom filtered to report on your organization's security posture pertaining to this vulnerability.",
        remediations:
          'See your remediation efforts to help you focus on what matters: at-risk projects, remediation teams in need, or even your best remediation teams.',
        samba:
          "See data related to Samba (CVE-2017-7494), a specific high profile vulnerability. These cards are custom filtered to report on your organization's security posture pertaining to this vulnerability.",
        specificVulnerability:
          'See the existence of specific vulnerabilities according to a query you apply so you can understand the most important actions to take next in your environment.',
        spectre:
          "This dashboard contains a set of cards that provide you instant visibility to a specific high profile vulnerability: Spectre (CVE-2017-5715 and CVE-2017-5753). These cards are custom filtered to report on your organization's security posture pertaining to this vulnerability.",
        threatFeed:
          'See vulnerability data that is highly exploitable and being actively tracked and exploited through Rapid7 Threat Feed sources.',
        vulnerabilities:
          'See the existence and nature of vulnerabilities so you can understand the most important actions to take next in your environment.',
        wannaCry:
          "See data related to WannaCry Ransomware, a worm which spreads by exploiting a vulnerability (CVE-2017-0144). These cards are custom filtered to report on your organization's security posture pertaining to this vulnerability.",
      },
      templateName: {
        specificVulnerability: 'Specific Vulnerability Dashboard',
        OTIoTAssets: 'OT/IoT Assets Dashboard',
        OTIoTAssetsCard: {
          AssetAssessment: 'OT/IoT Asset Assessment',
          AssetCriticality: 'OT/IoT Asset Criticality',
          Assets: {
            general: 'All Assets in My Environment',
            scoped: 'OT/IoT Assets in My Environment',
          },
          AssetsByOS: 'OT/IoT Assets by Vendor & Operating System',
          AssetsByType: 'OT/IoT Assets by Type',
        },
        PatchTuesday: 'Microsoft Patch Tuesday Vulnerabilities',
        RECON: 'RECON Dashboard for SAP NetWeaver',
        SIGNIFICANT_CHANGES: 'Significant Changes in the Last 30 Days',
        assets: 'Assets Dashboard',
        changed: "What's Changed Dashboard",
        containers: 'Containers Dashboard',
        executive: 'Executive Dashboard',
        executiveReport: 'Executive Report',
        exploitableVulns: 'Exploitable Vulnerability Dashboard',
        externalRemoteWorkforce:
          'External and Remote Workforce Assets Dashboard',
        PatchTuesdayQuery: {
          vulnerabilities: 'Patch Tuesday Vulnerabilities (__date__)',
          vendor: 'Microsoft assets',
        },
        OTIoTAssetsQuery: 'OT/IoT Assets',
        externalRemoteWorkforceCard: {
          assessedAssetsVsRiskScoreForExternalAssets:
            'Assessed Assets vs Risk Score for External Assets',
          assessedAssetsVsRiskScoreForRemoteWorkforceAssets:
            'Assessed Assets vs Risk Score for Remote Workforce Assets',
          assetsByOperatingSystem: 'Assets by Operating System',
          externalAssetTrends: 'External Asset Trends',
          externalAssetsByOperatingSystem: 'External Assets Assets by OS',
          externalAssetsByRiskAndVulnerabilities:
            'External Assets by Risk and Vulnerabilities',
          externalAssetsWithExpiredSslTlsCertificates:
            'External Assets with Expired SSL/TLS Certificates',
          externalAssetsWithExpiringSslTlsCertificates:
            'External Assets with Expiring SSL/TLS Certificates',
          externalAssetsWithSslTlsVulnerabilitiesPki:
            'External Assets with SSL/TLS Vulnerabilities, PKI',
          mostCommonSslTlsSolutionsForExternalAssets:
            'Most Common SSL/TLS Solutions for External Assets',
          mostCommonSslTlsVulnerabilitiesOnExternalAssets:
            'Most Common SSL/TLS Vulnerabilities on External Assets',
          newVsRemediatedExternalAssetVulnerabilities:
            'New vs Remediated External Asset Vulnerabilities',
          newVsRemediatedWorkforceAssetVulnerabilities:
            'New vs Remediated Workforce Asset Vulnerabilities',
          remoteWorkforceAssetTrends: 'Remote Workforce Asset Trends',
          remoteWorkforceAssetsByOperatingSystem:
            'Remote Workforce Assets by OS',
          remoteWorkforceAssetsByRiskAndVulnerabilities:
            'Remote Workforce Assets by Risk and Vulnerabilities',
          sslTlsVulnerabilitiesOnExternalAssetsBySeverity:
            'SSL/TLS Vulnerabilities on External Assets by Severity',
          vulnerabilityCountForExternalAssets:
            'Vulnerability Count for External Assets',
          vulnerabilityCountForRemoteWorkforceAssets:
            'Vulnerability Count for Remote Workforce Assets',
        },
        externalRemoteWorkforceQuery: {
          external: 'External Assets',
          remote: 'Remote Workforce Assets',
          sslTlsVulnerabilities: 'SSL/TLS Vulnerabilities, PKI',
        },
        meltdown: 'Meltdown',
        petya: 'Petya-like Ransomware',
        recentCriticalVulnerabilitiesQuery:
          'Critical Vulnerabilities Found in the Last 30 Days',
        recentlyDiscoveredAssetsQuery: 'Assets Discovered in the Last 30 Days',
        recentlyFoundVulnerabilitiesQuery:
          'Vulnerabilities Found in the Last 30 Days',
        reconCard: {
          AssetsByOS: '$t(cards.assets.card.byOS.title) $t(reconWithCve)',
          ChangeInNumberOfAssets: 'Assets $t(reconWithCve)',
          MostCommonExploitsComponent:
            '$t(cards.mostCommonExploits.title) for $t(RECON)',
          NewVsRemediatedVulnerabilities:
            'New vs. Remediated $t(RECON) Vulnerabilities',
          TopRiskiestAssetsComponent:
            '$t(cardTemplates.TopRiskiestAssetsComponent.name) $t(reconWithCve)',
          TotalVulnerabilities: 'Number of $t(RECON) findings',
        },
        reconQuery: '$t(RECON) query',
        remediations: 'Remediations Dashboard',
        samba: 'Samba',
        significantChangesCard: {
          ExploitableVulnerabilities:
            'Number of Exploitable Critical Vulnerabilities Found in the Last 30 Days',
          RiskVulnsCard:
            'Assets by Risk and Vulnerabilities Found in the Last 30 Days',
          TotalVulnerabilities:
            'Number of Critical Vulnerabilities Found in the Last 30 Days',
        },
        spectre: 'Spectre',
        threatFeed: 'Threat Feed Dashboard',
        vulnerabilities: 'Vulnerability Dashboard',
        wannaCry: 'WannaCry',
      },
      dashboardName: {
        PatchTuesday: 'Patch Tuesday Dashboard (__date__)',
      },
    },
    dashboardLibraryLabel: '$t(dashboardLabel) Library',
    dashboardName: '$t(dashboardLabel) Name',
    dashboardOwner: {
      deleted: '__id__ (deleted user)',
      me: 'Owned by me',
      other: 'Owned by __dashboardOwner__',
    },
    dashboardsFailed:
      'Failed to retrieve your dashboards, please refresh and try again.',
    dashboardsSaveFailed: 'Warning: Failed to save your dashboards',
    databaseName: 'Database Name',
    databases: 'Databases',
    date: 'Date',
    dateModified: 'Modified',
    datePublished: 'Published',
    days: 'Days',
    defaultDashboard: 'Default Dashboard',
    defaultDelete: 'You cannot delete your default dashboard',
    defaultNameChangeAlert:
      'You cannot change the name of your default dashboard!',
    delete: 'Delete',
    deleteDashboard:
      'Are you sure you want to delete dashboard __dashboardName__?',
    deleteDashboardError: 'An error occurred while deleting the dashboard.',
    deleteDashboardListItem:
      'Are you sure you want to delete dashboard <strong>__dashboardName__<strong>?',
    deleteIconToolTip: 'Delete Card',
    deleteModal: {
      title: 'This action is permanent',
    },
    description: 'Description',
    details: 'Details',
    deviceClass: 'Device Class',
    digest: 'Digest',
    directory: 'Directory',
    disabled: 'Disabled',
    disable: 'Disable',
    discoveredByConnection: 'Discovered by connection',
    discoveryDate: 'Discovery Date',
    dismiss: 'Dismiss',
    documentation: 'Documentation',
    done: 'Done',
    dontShowAgain: "Don't Show Again",
    download: 'Download',
    downloadAgent: 'Download Agent',
    downloadAgents: 'Download Agents',
    downloadError: 'There was an error with your download.',
    downloadReport: 'Download Report',
    downloadReportImage: 'Report Download Image',
    downloading: 'Downloading',
    drilldown: {
      cantSharePrivateFilterInfo:
        "You cannot share the associated filter. Please contact the filter's owner __owner__ and request they share this filter with the users you want to share it with.",
      continueWithoutSharing: 'Continue without sharing the filter.',
      shareFilterWithDashboardUsersInfo:
        'Users with whom you have shared the dashboard will now be able to view this filter.',
    },
    dualComparisonTooltip:
      'Week of __dayOfWeek__, __date__ <br/> __label__ __value__',
    dueOn: 'Due On',
    duplicate: 'Duplicate',
    duplicateDashboard: '$t(duplicate) $t(dashboardLabel)',
    duplicateWidgetTitle: 'Duplicate Card',
    dynamic: 'Dynamic',
    earlyAccess: 'Early Access',
    earlyAccessHeader: 'Welcome to Rapid7 Early Access!',
    earlyAccessHero: `
    <p>
      We're continually developing and improving InsightVM in pursuit of your security goals,
      and we need your expert feedback to help shape the look, feel, and function of new
      features before they become generally available. To facilitate your contributions,
      we designed this Early Access program as a means to partner with a select group of
      customers who show interest in upcoming features. As an Early Access program
      participant, you'll have a direct line of communication to key Rapid7 stakeholders as
      you experience features in active development. If you feel like engaging with the larger
      Early Access community, you'll also have the opportunity to discuss your ideas with
      other program participants.
    </p>
    <p>
      The features shown on this page are currently eligible for Early Access participation.
      Check back here often for new items!
    </p>
    `,
    edit: 'Edit',
    edited: 'edited',
    eligibleFeatures: 'Eligible Features',
    ellipses: '__phrase__...',
    email: 'Email',
    emailAddress: 'Email Address',
    emailAddressModal: {
      descriptionAlreadyHaveAccount:
        "If you already have an <a href='https://insight.rapid7.com' target='_blank' rel='noopener noreferrer'>insight.rapid7.com</a> account, make sure you provide the same email address associated with that account.",
      descriptionMustHaveAccount:
        'As of October 1st, 2018, you must have a valid email address to access cloud capabilities in InsightVM. Please confirm your email address to avoid feature disruption.',
      emailAddressLabel: 'Email (Please take a moment to verify)',
      header: 'Confirm your email address',
      help:
        "<a href='https://insightvm.help.rapid7.com/docs/email-confirmation-for-insight-platform-account-mapping' target='_blank' rel='noopener noreferrer'>Learn more about insight.rapid7.com account mapping</a>",
      resendActivationDescription:
        "<strong>Looks like you still need to activate your Insight account.</strong> If you're having trouble finding your activation email, <1>click here</1> to request another one. If you need further assistance, contact your Customer Success Manager.",
      resendActivationEmail: 'Resend Activation Email',
      successDescription:
        "Check your email to complete your insight.rapid7.com account registration. Note that some of InsightVM's cloud capabilities will be inaccessible until you complete this process.",
      successHeader: 'Almost there!',
      thanksForUpdatingYourInformation:
        'Thanks for updating your user information',
      validation: {
        emailAssociatedAdmin:
          'This email address is already claimed by another Security Console user in your organization. <a target="_blank" rel="noopener noreferrer" href="__consoleUrl__">Modify your existing user accounts</a> to resolve this issue.',
        emailFormat: 'Emails must be formatted as xxx@xxx.xxx',
        emailRequired: 'Email is required',
        emailWrongCustomer:
          'This email address is already claimed. Contact <a target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/for-customers/">Support</a> to resolve this issue.',
        firstNameRequired: 'First name is required',
        generic: 'There was an error saving your email address.',
        lastNameRequired: 'Last name is required',
        unique:
          'That email is currently in use. Please enter a unique email address.',
      },
    },
    enableLiveMonitoring: 'Enable Live Monitoring',
    enabled: 'Enabled',
    enable: 'Enable',
    endDate: 'End Date',
    epoch: 'Epoch',
    error: 'Error',
    events: 'Events',
    every: 'Every',
    exceptionScope: 'Exception Scope',
    exceptions: 'Exceptions',
    expandCard: 'Expand Card',
    expirationTime: 'Expire On',
    expired: 'Expired',
    expiresOn: 'Expires On',
    expiringOn: 'Expiring On',
    exploit: 'Exploit',
    exploitCount: 'Exploit Count',
    exploitDB:
      'One or more exploits have been published for this vulnerability in the $t(exploit) Database.',
    exploitRank: {
      expert: 'Expert',
      intermediate: 'Intermediate',
      novice: 'Novice',
      title: 'Rank',
    },
    exploitRanks: {
      average: 'Average',
      excellent: 'Excellent',
      good: 'Good',
      great: 'Great',
      low: 'Low',
      manual: 'Manual',
      normal: 'Normal',
    },
    exploitable: 'Exploitable',
    exploitableVulnerabilities: 'Exploitable Vulnerabilities',
    exploitableVulns: '$t(exploitableVulnerabilities)',
    exploits: 'Exploits',
    explorerUi: {
      columnLabels: {
        address: 'Address',
        assetCount: 'Asset Count',
        assetInstancesCount: 'Instances',
        cvss: 'CVSS',
        dateAdded: 'Added On',
        dateModified: 'Modified',
        datePublished: 'Published',
        exploits: 'Exploits',
        hasExploits: 'Is Exploitable',
        hasMalware: 'Has Malware',
        instances: 'Instances',
        lastScanTime: 'Last Scan Time',
        malware: 'Malware',
        minDateFound: 'First Found',
        name: 'Name',
        operatingSystem: 'Operating System',
        operatingSystemArchitecture: 'OS Architecture',
        operatingSystemProduct: 'OS Product',
        operatingSystemVendor: 'OS Vendor',
        operatingSystemVersion: 'OS Version',
        risk: 'Risk',
        severity: 'Severity',
        sites: 'Sites',
        tags: 'Tags',
        title: 'Title',
        totalRisk: 'Total Risk',
        vulnId: 'ID',
        solutionId: 'ID',
        type: 'Type',
        solutionFix: 'Details',
        vulnerabilities: 'Vulnerabilities',
        reintroduced: 'Reintroduced',
      },
      expertModal: {
        body: 'Continue',
        title: 'Continue',
      },
      featureLabel: 'Query Builder',
      fields: {
        asset: {
          _altips: 'A collection of alternate IPs',
          _altnames: 'A collection of alternate names',
          _containers: 'A collection of containers',
          _endpoints: 'A collection of endpoints',
          _os: 'The OS object of the primary asset',
          _software: 'A collection of software objects',
          _tags: 'The tag name of the asset',
          agentKey: 'The key of the agent',
          altips: {
            size: 'The total number of alternate IPs',
          },
          altmacs: {
            name: 'A collection of alternate MAC addresses',
            size: 'The total number of alternate MAC addresses',
          },
          altnames: {
            size: 'The total number of alternate names',
          },
          assetKey: 'The key of the asset',
          averageScanDuration: 'The average time it takes to scan an asset',
          console: 'The primary console',
          containers: {
            created: 'The number of containers that have been created',
            finished: 'The number of containers that have finished',
            id: 'The container ID',
            imageDigest:
              'The digest, or identifier, of an image in a repository',
            imageId: 'The image ID',
            name: 'The name of the container',
            repository: 'The repository of the container',
            size: 'The number of containers on an asset',
            started:
              'The start date of the last assessment of a container image',
            status: 'The status of the container',
          },
          credentialSuccessStatus:
            'This indicates if credential authentication was successful',
          endpoints: {
            name: 'The name of the endpoint service',
            port: 'The port of the endpoint',
            protocol: 'The protocol of the endpoint',
            size: 'The total number of endpoints',
          },
          firstScanTime: 'The first scan date of the asset',
          groups: 'A collection of groups',
          ipv4: 'The primary IPv4 of the asset',
          ipv6: 'The primary IPv6 of the asset',
          lastAgentConnectionTime:
            'The date and time of the last connection made by the agent',
          lastScanDuration: 'The duration of the last scan',
          lastScanTime: 'The last date a scan occurred',
          mac: 'The primary MAC address of the asset',
          name: 'The host name of the asset',
          os: {
            arch: 'The OS architecture of the asset',
            description: 'The OS description of the asset',
            family: 'The OS family of the asset',
            product: 'The OS product of the asset',
            type: 'The OS type of the asset',
            vendor: 'The OS vendor of the asset',
            version: 'The OS version of the asset',
          },
          riskScore: 'The risk score of the asset',
          sites: 'A collection of sites',
          software: {
            description: 'The description of the software',
            family: 'The family of the software',
            product: 'The product name of the software found on the asset',
            program: 'The type of software found on the asset',
            size: 'The total number of installed software programs',
            vendor: 'The name of the software vendor',
            version: 'The software version number',
          },
          sslCertificateMinimumExpirationDate:
            'The expiration date of the SSL certificate',
          tags: {
            type: 'The tag type of the asset',
          },
          type: 'The host type of the asset',
        },
        finding: {
          firstFound:
            'The first date any instance of the vulnerability was found',
          reintroduced:
            'The first date any instance of the vulnerability was reintroduced',
        },
        vulnerability: {
          _alternateIds: 'The collection of alternate IDs for a vulnerability',
          _categories:
            'The categories, or groups, that the vulnerability belongs to',
          _exploits:
            'The collection of exploits associated with the vulnerability',
          _malwareKits: 'The malware kits associated with the vulnerability',
          alternateIds: {
            altId: 'The identifier of an alternate ID',
            size: 'The total number of alternate IDs',
            source: 'The source of the alternate ID',
          },
          categories: {
            size: 'The total number of categories',
          },
          cveIds: 'A list of CVE IDs for the vulnerability',
          cvssScore: 'The CVSS score of the vulnerability',
          dateModified: 'The date the vulnerability was modified',
          datePublished: 'The date the vulnerability was published',
          description: 'The description of the vulnerability',
          exploits: {
            description: 'The description of the exploit',
            exploitRank: 'The rank of the exploit',
            id: 'The ID of the exploit',
            name: 'The name of the exploit',
            rank: 'The rank of an exploit',
            size: 'The total number of exploits for the vulnerability',
          },
          malwareKits: {
            size: 'The total number of malware kits',
          },
          riskScoreReal: 'The risk score of the vulnerability',
          severity: 'The severity of the vulnerability',
          severityScore: 'The numeric severity score of the vulnerability',
          skillLevel: 'The skill level needed to exploit the vulnerability',
          title: 'The title of the vulnerability',
          vulnKey: 'The unique identifier of the vulnerability',
        },
      },
      helpfulQueryModal: {
        body:
          'This will reset the page with the selected query and any unsaved work will be lost. Are you sure you want to continue?',
        title: 'Replace query?',
      },
      modeSwitchModal: {
        body:
          "You will lose the query you are currently building because you haven't saved it. Do you want to continue?",
        title: 'Do you want to switch to standard mode?',
      },
      newQueryModal: {
        body:
          'This will reset the page and any unsaved work will be lost. Are you sure you want to continue?',
        button: 'New',
        buttonTitle: 'Resets the page',
        title: 'New Query?',
      },
      query: {
        add: 'Add',
        apply: 'Apply',
        applyCriteria: 'Apply Criteria',
        maxReachedLabel: 'Max Reached',
        cannotDelete:
          'Your query is being used by one or more of the following: Remediation Projects, Goals and SLAs, or Automation. To make changes, save this query as a new one.',
        cannotSave:
          'Your query is being used by one or more of the following: Static Remediation Projects, Goals and SLAs, or Automation. To make changes, save this query as a new one.',
        createdOn: 'Created on __date__',
        deleteConfirmation: 'Are you sure you want to delete __name__?',
        deleteError:
          'There was a problem deleting your query. Please try again later.',
        edited: 'Edited',
        incompatibleWithStandard:
          'This query is incompatible with standard mode.',
        lastUpdated: 'Last updated on __date__',
        load: 'Load',
        loadError:
          'There was a problem loading your query. Please try again later.',
        loadQuery: 'Load Query',
        nameError:
          'A query with this name already exists. Please enter another name.',
        ownedBy: 'Owned by __user__',
        ownedByMe: 'Owned by me',
        referencesCount: 'Used in __count__ place(s)',
        save: 'Save',
        saveAsNew: 'Save as New',
        saveError:
          'There was a problem saving your query. Please try again later.',
        sharedWithCount: 'Shared with __count__ users',
        switchToExpert: 'Switch to Expert',
        switchToStandard: 'Switch to Standard',
      },
      serviceColumnLabels: {
        assetCount: 'Asset Count',
        port: 'Port',
        product: 'Product',
        protocol: 'Protocol',
        serviceName: 'Service Name',
        vendor: 'Vendor',
        version: 'Version',
        nic: 'Network Interface',
      },
      softwareColumnLabels: {
        assetCount: 'Asset Count',
        product: 'Product',
        vendor: 'Vendor',
        version: 'Version',
      },
      tabTooltip:
        'Displays all results on assets with at least one __attribute__ that matches your criteria.',
      wizardExplorerUi: {
        addCriteria: 'Add Criteria',
        asset: 'asset',
        asset_plural: 'assets',
        editScope: 'Edit scope',
        emptyMessage:
          'No scope has been added yet. Click the Add Scope button to get started.',
        errorMessage: {
          createQuery: 'An error occurred while creating your query',
          loadQuery:
            'An error occurred while loading your scope query. Please try again later or select new scope.',
        },
        load: 'Load',
        scope: 'Scope',
        scopeMessage:
          'Your __fieldType__ scope will be linked to the <span>__queryName__</span> query.<br /> The __fieldType__ will not be tracking any future changes to your scope and any edits to <span>__queryName__</span>.',
        vuln: 'vulnerability',
        vuln_plural: 'vulnerabilities',
        with: ' with ',
      },
    },
    exportErrorMessage:
      'Your csv export could not be generated, please try again later.',
    exportToCSV: 'Export to CSV',
    exportToPDF: 'Export to PDF',
    exportVulnInstances: 'Export Vulnerability Instances',
    exportVulnerabilities: 'Export Vulnerabilities',
    exposure: 'Exposure',
    exposureValues: {
      common: 'Common',
      favored: 'Favored',
      occasional: 'Occasional',
      popular: 'Popular',
      rare: 'Rare',
      uncommon: 'Uncommon',
      undefined: 'Not Defined',
      unknown: 'Unknown',
    },
    externalRemoteWorkforceHtml: `
      <p>This dashboard:</p>
      <list>
        <listItem>Identifies potential external and remote workforce assets</listItem>
        <listItem>Shows vulnerabilities and trends across these assets to assess your risk exposure</listItem>
        <listItem>Displays the status of your external-facing and remote workforce assets as data is updated</listItem>
      </list>
      <p>Upon creation, this dashboard uses queries based on RFC1918 IP ranges to identify external assets and non-server operating systems information to identify remote workforce assets. These queries can be used as-is, modified, or added to other dashboard cards. For example, if you already tag your external-facing assets with a tag called "external," you can modify the query with the following criteria: <b>asset.tags IN ['external']</b>. When you update these queries, your dashboard will automatically update to reflect the updated criteria.</p>
      <p>In addition, at the time of setup, you will see another query called "SSL/TLS Vulnerabilities, PKI" to filter out encryption related vulnerabilities on external facing assets.</p>
      <p>The list of queries reflects what is currently applied to the data in this dashboard.</p>
    `,
    failed: 'Failed',
    false: 'False',
    family: 'Family',
    features: 'Features',
    file: 'File',
    fileName: 'File Name',
    files: 'Files',
    filesAndDirectories: 'Files and Directories',
    FilterPanel: {
      showLess: 'Show -Less',
      showLessOptions: 'Show Less __name__',
      showMore: 'Show +More',
      showMoreOptions: 'Show More __name__',
    },
    filter: {
      asset: {
        addressType: 'Address Type',
        assetId: 'Asset ID',
        assetKey: 'Asset Unique Identifier',
        assetType: 'Asset Type',
        exploitCount: 'Exploit Count',
        hostName: '$t(hostName)',
        ipAddress: 'IP Address',
        mac: 'MAC Address',
        malwareCount: 'Malware Count',
        operatingSystem: 'OS',
        riskScore: 'Risk Score',
        vulnerabilityCount: 'Vulnerability Count',
      },
      'asset-group': {
        assets: 'Assets',
        groupId: 'Group ID',
        groupName: 'Group Name',
        isDynamic: 'is Dynamic',
        riskScore: 'Risk Score',
        vulnerabilities: 'Vulnerabilities',
      },
      cardError: {
        actionableCardError:
          'There was an error loading the card. Please <a class="action reload-card">reload</a> the card or <a class="action delete-card">delete</a> the card.',
        actionableFilterError:
          'The card cannot load because the associated filter has an error. <a class="action reset-card">Reset</a> to default or <a class="action delete-card">delete</a> this card',
        servicesProvisioningError:
          'Please allow a few minutes while we set up the services for this card. Please contact support if the issue still persists.',
        cardError:
          'There was error loading the card please reload the card, or contact the dashboard owner to reset or delete the card.',
        filterError:
          'The card cannot load because the associated filter has an error. Contact the dashboard owner to reset or remove the filter.',
        goalsError: {
          403: 'The goal’s owner did not share this goal with you. Ask them to access it.',
          404: 'This goal was deleted.',
          500: 'There was an error loading this goal. Try again later.',
          default: 'There was an error loading this goal.',
        },
      },
      filterName: 'Query Name',
      filterText: 'Query Logic',
      management: {
        allFilter: 'All $t(filters)',
        createError:
          'An error was encountered while creating $t(filterLabel) "__filterName__"',
        createFilter: 'Create $t(filterLabel)',
        createSuccess:
          '$t(filterLabel) "__filterName__" was successfully created',
        deleteConfirmation:
          'Are you sure you want to delete the selected filter(s)?',
        deleteError: 'An error occurred while deleting the selected filter(s).',
        deleteError_plural:
          'There was an error deleting the selected filters. Please try again later.',
        deleteSavedFilter: 'Delete Saved Filter',
        deleteSharedOnly:
          'No filters were deleted because they are all shared.',
        deleteSuccess:
          'The $t(filterLabel) was deleted and will be deleted from the cards it was in use.',
        deleteSuccess_plural:
          '__count__ $t(filters) were deleted and will be deleted from the cards they were in use.',
        duplicateFilter: 'Duplicate $t(filterLabel)',
        editFilter: 'Edit $t(filterLabel)',
        saveError:
          'There was an error updating filter "__filterName__". Please try again later.',
        saveSuccess:
          '$t(filterLabel) "__filterName__", was successfully updated.',
      },
      notFound: {
        actionableDescription:
          'The filter applied to this card has been deleted by its owner. <a class="action reset-card">Reset</a> to default or <a class="action delete-card">delete</a> this card',
        description:
          'The filter applied to this card has been deleted by its owner. Contact the dashboard owner to update or remove the filter.',
      },
      os: {
        'attribute.deviceType': 'Device Type',
        'attribute.operatingSystemDescription': 'OS Description',
        'attribute.product': 'Product',
        'attribute.productFamily': 'Product Family',
        'attribute.productVendor': 'Product Vendor',
        'attribute.systemArchitecture': 'System Architecture',
        'attribute.version': 'Version',
        count: 'Count',
      },
      site: {
        assetCount: 'Asset Count',
        autoEngineSelectionEnabled: 'Engine Selection Enabled',
        criticalVulnerabilityCount: 'Critical Vulnerability Count',
        dynamic: 'Dynamic',
        key: 'Key',
        moderateVulnerabilityCount: 'Moderate Vulnerability Count',
        name: 'Name',
        riskFactor: 'Risk Factor',
        riskScore: 'Risk Score',
        riskScoreTimestamp: 'Risk Score Timestamp',
        severeVulnerabilityCount: 'Severe Vulnerability Count',
        siteID: 'Site ID',
      },
    },
    filterLabel: 'Filter',
    filterNameAlreadyEntered:
      'This filter name has already been entered, please change it to a unique value',
    filterNameInUse: 'This filter name is already in use',
    queryNameInUse: 'This query name is already in use',
    filters: 'Filters',
    filterValues: 'Filter values',
    filtersComponent: {
      applyMessage:
        'Must apply, revert, or delete this filter in order to continue',
      assetFilter: 'Asset Filter',
      filterPlaceholder: 'Click to add a filter',
      vulnerabilityFilter: 'Vulnerability Filter',
    },
    find: 'Find',
    fingerprints: 'Fingerprints',
    firstFound: 'Found',
    firstName: 'First Name',
    fix: 'Fix',
    fixableAssessmentModalDescription:
      "Image __image__ couldn't be assessed - Connection to the repository __repository__ has failed",
    fixableAssessmentModalDescriptionMultiple:
      "Image __image__ couldn't be assessed - Connection to the repositories __repository__ has failed",
    fixableAssessmentModalTitle: "Image couldn't be assessed",
    format: 'Format',
    found: 'Found',
    goal: {
      addRecommendedGoal: 'Add to My Goals',
      assetFilter: 'Asset filter',
      assets: 'assets',
      buildGoal: {
        allTheTime: 'All the time',
        assetConfiguration: {
          howToConfigure: 'How do you want these assets to be configured? *',
          howToConfigureQuery:
            " You'll <0>build a query</0> here to describe the ideal state for your assets.",
          whatAssets: 'What assets do you want to configure? *',
          whenToConfigure: 'When do you want these assets to be configured? *',
        },
        atLeast: 'At least',
        by: 'By',
        completeBy: 'Complete By',
        criteriaType: 'Criteria Type',
        days: 'days',
        daysOfDiscovery: 'days of discovery',
        endDate: 'Pick date',
        mustHave: 'must have',
        of: 'of',
        ofAssetsToAchieve: 'of $t(assets) to achieve',
        remediate: 'Remediate',
        remediateAll: 'Remediate all',
        remediationEfforts: {
          tooltipExample:
            'Example: If you want to remediate all critical vulnerabilities on Windows assets, build: vulnerability.severity=critical AND asset.os like “windows"',
          whatAssets: 'What assets should be included in this effort? *',
          whatVulnQuery:
            " You'll <0>build a query</0> to narrow down your vulnerabilities.",
          whatVulnerabilities:
            'What vulnerabilities do you want to remediate? *',
          whenToRemediate:
            'When do you want to remediate your vulnerabilities by? *',
          whenToRemediateTooltip: 'Not available for vulnerability based goals',
        },
        setGoal: 'Set a goal for ',
        targetConditions: 'Target Conditions',
        threshold: 'Threshold',
        title: 'Specify the Criteria of Your Goal',
        toAchieve: 'to achieve',
        whatAssetsQuery:
          " You'll load or <0>build a query</0> to narrow down your assets.",
        where: 'where',
        willAchieve: 'will achieve',
        within: 'Within',
      },
      cards: {
        continuous: {
          selectPlaceholder: 'Select a continuous goal',
        },
        createGoal: 'Create a Goal',
        nullStateText: 'Create a goal to populate this card with its data.',
        selectGoalText: 'Select a goal to populate this card with its data.',
        sla: {
          selectPlaceholder: 'Select an SLA goal',
        },
        timebound: {
          selectPlaceholder: 'Select a time bound goal',
        },
      },
      charts: {
        assets: 'of $t(assets)',
        assetsCompliant: 'of $t(assets) in Compliance',
        assetsGracePeriod: 'of $t(assets) Currently in Grace Period',
        assetsNotCompliant: '$t(assets) not in Compliance',
        assetsRemediated: 'of $t(assets) Currently Remediated',
        legend: {
          assetsCompliant:
            '__percent__$t(percentageSymbol) (__value__) $t(goal.charts.assetsCompliant)',
          assetsNotCompliant:
            '__percent__$t(percentageSymbol) (__value__) $t(goal.charts.assetsNotCompliant)',
        },
        today: 'Today',
        totalNumber: 'Total #',
        vulnerabilitiesCompliant: 'of $t(vulnerabilities) in Compliance',
        vulnerabilitiesGracePeriod:
          'of $t(vulnerabilities) Currently in Grace Period',
        vulnerabilitiesRemediated:
          'of $t(vulnerabilities) Currently Remediated',
        vulnerabilityInstances: 'of $t(vulnerability) Instances',
      },
      conditions: 'Conditions',
      createError: 'An error occurred while creating your goal',
      createdBy: 'Created By',
      createdOn: 'Created On',
      criteriaForm: {
        remediate: 'Remediate __threshold__$t(percentageSymbol) of',
        toAchieve: 'to achieve',
      },
      criteriaInteger: 'Must be integer',
      dashboardSaveFailed:
        'Goal Deleted. This goal was used in at least one dashboard card. Contact your admin to delete.',
      days: '__count__ day',
      days_plural: '__count__ days',
      daysAgo: '__count__ day ago',
      daysAgo_plural: '__count__ days ago',
      daysOfDiscovery: 'days of discovery',
      daysPastDue: '__count__ day past due',
      daysPastDue_plural: '__count__ days past due',
      daysRemaining: '__count__ day remaining',
      daysRemaining_plural: '__count__ days remaining',
      deadline: 'Deadline',
      deleteConfirm: 'Are you sure you want to delete the selected goal?',
      deleteError: 'An error occurred while deleting the goal.',
      deleteModalTitle: 'Confirm Deletion',
      drilldownText: 'View Goal Details',
      errors: {
        loadGoal: 'There was an error loading the goal',
        loadQuery: 'The associated query was not found',
        updateGoal: 'There was a problem updating the goal',
      },
      examples: {
        continuous: [
          'All external facing assets must have a closed SSH port',
          'The Insight Agent is installed on at least 90% of my Windows servers',
        ],
        sla: [
          'Remediate all critical vulnerabilities in production environments within three days of discovery',
          'Remediate all assets which have port 22 open within three days of discovery',
        ],
        timebound: [
          'Upgrade 100% of Windows 7 desktops by 1/14/2021',
          'Reduce the number of exploitable vulnerabilties in Boston by 50% by 12/15/2020',
        ],
      },
      filterPlaceholder: 'Click to add a filter',
      goal: 'Goal',
      goal_plural: 'Goals',
      goalCriteria: 'Criteria',
      goalOverview: 'Goal Overview',
      goalSharingHelp:
        'Have a user with the <a href="__link__">goal creation permission</a> share a goal with you.',
      goalStatus: 'Status',
      goalType: 'Goal Type',
      goalTypes: {
        continuous: 'Continuous',
        continuousDescription:
          'Monitor progress or conditions without a time limit. <a href="https://insightvm.help.rapid7.com/docs/goals-and-slas/" target="_blank" rel="noopener noreferrer">Learn More.</a>',
        header: 'Examples',
        newGoal: 'New Goal',
        newGoal_upper: 'NEW GOAL',
        newGoalDescription: 'Create your new goal',
        sla: 'SLA',
        slaDescription:
          'Define conditions and set the number of days to track remediation over time. <a href="https://insightvm.help.rapid7.com/docs/goals-and-slas/" target="_blank" rel="noopener noreferrer">Learn More.</a>',
        timebound: 'Time Bound',
        timeboundDescription:
          'Specify metrics based on a set deadline and data scopes that do not change. <a href="https://insightvm.help.rapid7.com/docs/goals-and-slas/" target="_blank" rel="noopener noreferrer">Learn More.</a>',
      },
      goalWithCount: '__count__ Goal',
      goalWithCount_plural: '__count__ Goals',
      goalsNullStateHeader: "It's lonely here.",
      goalsNullStateText:
        "Set a goal to define security metrics, track your team's work, and get alerts on goal progression.",
      gracePeriodRange: 'Must be 1-365',
      help: {
        newStatus:
          'Your goal has been created. It may take several minutes for the initial status to be calculated.',
      },
      labels: {
        assetsInScope: '$t(assets) Affected',
        compliant: 'Compliant',
        compliantAssets: '$t(assets) in compliance',
        compliantVulns: '$t(vulnerabilities) remediated',
        discoveryDate: 'First Found',
        discoveryDateAssets: 'Discovery Date',
        dueDate: 'Due Date',
        dueOn: 'Due On',
        goalOwner: 'Goal Owner',
        inGracePeriod: 'In Grace Period',
        lastScanned: 'Last Scanned',
        noncompliant: 'Not Compliant',
        noncompliantAssets: '$t(assets) not in compliance',
        noncompliantVulns: '$t(vulnerabilities) not remediated',
        nonremediated: 'Not Remediated',
        remainingTime: 'Remaining Time',
        remediated: 'Remediated',
        compliantDisclaimer:
          'This includes all compliant assets including ones that have been removed, offline, or not discoverable',
      },
      lastUpdated: 'Last updated',
      modalTitle: {
        copy: 'Copy Goal',
        create: 'Create Goal',
        edit: 'Edit Goal',
      },
      noGoalsShared: 'No goals are shared with you.',
      noPermissions:
        " You don't have the right <0>permissions</0> to create a goal.",
      ongoing: 'Ongoing',
      optionalFilter: 'Click to add an optional filter',
      recommendedGoals: 'Recommended Goals',
      recommendedGoalsDescriptions: {
        continuous:
          'A continuous goal lets you monitor progress or conditions without a time limit, such as a rule or a key performance indicator.',
        sla:
          'An SLA (service level agreement) lets you define conditions and set the number of days to track remediation over time.',
        timebound:
          'A time bound goal lets you specify metrics based on a set deadline and data scopes that do not change.',
      },
      recommendedGoal: 'Recommended',
      scope: 'Scope',
      searchPlaceholder: 'Search Goals and SLAs',
      statuses: {
        atRisk: 'At Risk',
        completed: 'Met',
        error: 'Error',
        failed: 'Not Met',
        inCompliance: 'Compliant',
        inProgress: '$t(goal.statuses.onTrack)',
        new: 'Initializing',
        notInCompliance: 'Not Compliant',
        pendingCompletion: 'Pending Completion',
        onTrack: 'On Track',
        ownedByMe: 'Owned By Me',
        ownedByOthers: 'Owned By Others',
        retry: 'Retry',
      },
      successMessage:
        'Goal <0/> was created successfully. <1>Click here to view your new goal</1>',
      tableRowActions: 'Action',
      thresholdInteger: 'Must be integer',
      thresholdRange: 'Enter 1-100',
      timeRemaining: 'Time Remaining',
      trackingForm: {
        allTheTime: 'all the time',
        appAlertsLabel: 'Enable app notifications for goal status changes',
        byEndDate: 'by __endDate__',
        configure: 'Configure __percentage__ of my assets',
        dashboardsLabel: 'Assign to Dashboard',
        dashboardsPlaceholder: 'Select dashboard(s) for your goal',
        description: 'Description',
        descriptionPlaceholder: 'Write a description of your goal',
        emailAlertsLabel: 'Send email alerts for goal status changes',
        name: 'Goal Name *',
        notificationDescription:
          'Be notified of when your goal status changes by enabling alerts and notifications',
        otherTrackingTitle: 'You are building a ',
        presentIn: 'present in',
        remediate: 'Remediate __percentage__ of my vulnerabilities',
        shareGoalDescription:
          'Select users that you want to share the goal with',
        shareGoalLabel: 'Share goal with',
        slaTrackingTitle: 'You are building an ',
        thatAre: 'that are ',
        toAchieve: 'to achieve',
        tooltipNoPermission: "You don't have permission to edit this goal",
        tooltipSharedGoal: 'You cannot edit a shared goal',
        withinDaysOfDiscovery: 'within __gracePeriod__ day(s) of discovery',
      },
      type: 'Type',
      typeForm: {
        goalType: 'Goal Type',
        title: 'Select Goal Type',
      },
      vulnerabilityFilter: 'Vulnerability Filter',
      vulns: 'vulns',
      withinDaysOfDiscovery: 'within __gracePeriod__ days of discovery',
      wizard: {
        buttons: {
          next: 'Continue',
          prev: 'Back',
          save: 'Finish',
        },
        completedStepDescriptions: {
          goalBuilt: 'Goal Built',
          type: 'Goal Selected',
        },
        header: 'Create a Goal',
      },
      wizardSteps: {
        buildGoal: 'Build Goal',
        tracking: 'Review',
        type: 'Select Goal',
      },
    },
    goals: 'Goals and SLAs',
    groups: 'Groups',
    hardware: 'Hardware',
    hasMalware: 'Malware Kits',
    help: 'Help',
    history: 'History',
    host: 'Host',
    hostName: 'Host Name',
    hostNames: 'Host Name(s)',
    hostType: 'Host Type',
    hosts: 'Hosts',
    id: 'ID',
    image: {
      image: 'Image',
      imageDetails: 'Image Details',
      imageId: 'Image ID',
      images: 'Images',
      imagesNullStateText:
        "To add and assess images, <a href='https://insightvm.help.rapid7.com/docs/configuring-scan-credentials' target='_blank' rel='noopener noreferrer'>run authenticated scans</a> or <a href='http://insightagent.help.rapid7.com' target='_blank' rel='noopener noreferrer'>download and install the Insight Agent</a> to detect containers on your assets.",
    },
    in: 'in',
    inProgress: 'In Progress',
    insightOrchestrator: 'Insight Orchestrator',
    installedSoftware: 'Installed Software',
    instanceCount: 'Instances',
    instances: 'Instances',
    inventory: 'Inventory',
    ipAddress: 'IP Address',
    ipAddresses: 'IP Address(es)',
    jenkins: 'Jenkins',
    job: 'Job',
    jobId: 'Job ID',
    json: 'JSON',
    key: 'Key',
    kpi: 'KPI',
    kubernetesContainers: 'Kubernetes Containers',
    labels: {
      assetGroups: 'Asset Groups',
    },
    lastJobs: 'Last __count__ jobs',
    lastModified: 'Last Modified',
    lastName: 'Last Name',
    lastOnline: 'Last online',
    lastScan: 'Last Scan',
    lastSync: 'Last Sync',
    latestBuildCompliance: 'Last Build Compliance',
    latestBuildDate: 'Latest $t(buildDate)',
    latestBuildStatus: 'Last Build Status',
    latestComplianceStatus: 'Latest Compliance Status',
    latestImage: 'Latest Image',
    latestVulnerabilities: 'Latest Vulnerabilities',
    launch: 'Launch',
    layers: 'Layers',
    learnMore: 'Learn More',
    licenseExpiration: {
      body:
        "Contact our <a href='https://docs.rapid7.com/nexpose/support-technical-support-and-customer-care/' target='_blank' rel='noopener noreferrer'>Support Team</a> to request a new product key or to reactivate your original license.",
      renew: 'Renew your license',
      title: 'Your Product License Has Expired',
    },
    list: {
      loading: 'The list data is loading.',
    },
    liveAssessmentDescription: `Agents are a key component of your live assessment
    and monitoring<br>program. Agents can gather information on remote assets,
    enabling you to<br>track more of your assets without having to add scans.`,
    loadingIssueTypes: 'Loading issue types',
    location: 'Location',
    lonelyHere: "It's lonely here.",
    macAddress: 'MAC Address',
    macAddresses: 'MAC Address(es)',
    malware: 'Malware',
    malwareCount: 'Malware Count',
    malwareKit: 'Malware Kit',
    malwareKits: 'Malware Kits',
    manage: 'Manage',
    manageConnections: 'Manage Connections',
    manageFields: 'Manage Fields...',
    me: 'Me',
    meltdown: 'Meltdown',
    meltdownHtml: `
        <header>About this Meltdown dashboard</header>
        <p>This dashboard provides visibility to your threat exposure to the Meltdown attack.  The potential surface area for Meltdown is expansive - most Intel processors manufactured as far back as 1995 are affected. Many technology vendors, such as Microsoft, VMware, Google Chrome and Mozilla Firefox, have released or are working on updates and patches for their products, with many aiming for delivery January 9th, 2018. As Rapid7 works in concert with vendors to provide checks, you can stay up to date by not only referencing our <researchLink>research blogs</researchLink> but also using this dashboard to view the status of your assets as our data is updated.</p>
        <header>What is Meltdown?</header>
        <p>Meltdown allows attackers to access memory and therefore secrets of applications and OS’s by exploiting a vulnerability in the processor.</p>
        <header>What Steps Can I Take to Protect Against Meltdown?</header>
        <p>You can expand the cards and create a Remediation Workflow project to manage and monitor installing the appropriate patches to mitigate exposure to Meltdown.</p>
        <p>See also: <meltdownLink>https://meltdownattack.com</meltdownLink></p>
      `,
    metasploit: 'Metasploit',
    metasploitExploit:
      'This vulnerability can be exploited by one or more $t(metasploit) modules.',
    minutes: 'Minutes',
    moderate: 'Moderate',
    modifiedDate: 'Modified On',
    modifiedOn: 'Modified On',
    more: 'More',
    moreDetails: 'More Details',
    remediationDetails: 'Remediation Details',
    myRepositories: 'My Private Repos',
    name: 'Name',
    nameIsRequired: 'Name cannot be empty',
    namespace: 'Namespace',
    namespaces: 'Namespaces',
    needConsoleForCard: 'You need a console to properly view this card.',
    neverExpire: 'Never Expire',
    new: 'New',
    newAssets: 'New Assets',
    newInPastNDays: 'new in the past __days__ days',
    newInPastNMonths: 'new in the past __months__ months',
    newInPastYear: 'new in the past year',
    newInstances: 'New Instances',
    next: 'Next',
    next6months: 'in the next 6 months',
    next7days: 'in the next 7 days',
    next30days: 'in the next 30 days',
    next60days: 'in the next 60 days',
    next90days: 'in the next 90 days',
    nextScan: 'Next Scan',
    nextYear: 'in the next year',
    no: 'No',
    noActiveExploitsAvailable:
      'This vulnerability is not currently tracked as actively targeted by Rapid7.',
    noBuildRulesForJob:
      'There were no build policy rules applied to this build job.',
    noContent: 'No content is available for this card.',
    noContentChart: 'No content is available for this chart.',
    noOfAssets: 'No of Assets',
    noOfAssetsAffected: 'No of Assets Affected',
    noOfInstances: 'No of instances',
    noRecords: 'No records found.',
    noWidgets: 'No cards selected.',
    nonFixableAssessmentModalDescription:
      'Image __image__ could not be assessed. The image could not be found in any available registry. You can configure a new registry connection (recommended) or manually upload the image using the output of docker save command.',
    nonFixableAssessmentModalTitle: 'Image could not be assessed',
    notAssessed: 'Not Assessed',
    notAvailable: 'Not Available',
    notification: 'Notification',
    notifications: 'Notifications',
    nullstate: {
      204: {
        description: 'No content available.',
        title: "It's lonely here.",
      },
      403: {
        description:
          "Sorry, we can't show you this page. You may not have the authorization to view this content. Check your credentials.",
      },
      404: {
        description: "Sorry, we can't seem to find what you're looking for.",
      },
      429: {
        description:
          'There are too many requests right now. Wait for a few minutes and try again.',
      },
      500: {
        description:
          'Sorry, it\'s not you, it\'s us. There\'s an internal server error. Please try again later. If the problem persists, please <a target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/contact"><strong><u>contact support.</u></strong></a>.',
      },
      502: {
        description:
          "Sorry it's not you, it's us. Service is temporarily unavailable. Try refreshing the page. If that doesn't work, wait a bit and try again.",
      },
      503: {
        description:
          "Sorry it's not you, it's us. Service is temporarily unavailable. Try refreshing the page. If that doesn't work, wait a bit and try again.",
      },
      generic: {
        description: 'It looks like there was an unexpected error.',
      },
    },
    numImages: '__num__ Images',
    numJobs: '__num__ Jobs',
    numRepositories: '__num__ Repositories',
    numberOfNumber: '__dividend__ of __divisor__',
    off: 'Off',
    offline: 'Offline',
    on: 'On',
    online: 'Online',
    onlineSince: 'Online since',
    open: 'Open',
    operatingSystem: 'Operating System',
    operatingSystems: 'Operating Systems',
    optional: 'Optional',
    or_upper: 'OR',
    orchestrationConnections: 'Orchestration Connections',
    orchestratorOptions: {
      activateOrchestrator: 'Activate Orchestrator',
      installOrchestrator: 'Install Orchestrator',
    },
    orgSettings: {
      confirm: 'Confirm',
      consoleUrl: 'Security Console URL',
      content:
        "Lorem ipsum dolor amet before they sold out mlkshk hot chicken YOLO ethical, wolf freegan. Paleo tacos asymmetrical whatever kitsch vape, semiotics kogi scenester wolf blog marfa. Williamsburg artisan twee, art party 90s literally etsy godard subway tile snackwave forage tumeric. You probably haven't heard of them readymade whatever, flannel keffiyeh kale chips letterpress chambray stumptown meggings jianbing austin enamel pin pour-over bespoke.",
      error: {
        body:
          "We couldn't get the response we needed from your Security Console through the provided URL. Make sure that you are on the same network as your Security Console host and verify that it's online.",
        cantReachConsole: "Your Security Console didn't respond.",
        detail:
          '$t(orgSettings.error.body) $t(orgSettings.error.furtherAction)',
        furtherAction:
          'If your existing console URL is not correct, you can change it here.',
        headline: 'Connection Failed',
        validation: {
          ip:
            'Your console URL needs to be a valid URL with http(s) or IP address',
          nonEmpty: 'Security Console URL required',
        },
      },
      success: {
        detail:
          'Your Security Console is connected for use with the InsightVM Platform Login feature.',
        headline: 'Connected',
      },
      tabHeader: 'Org Settings',
    },
    originalRiskScore: 'Original Risk Score',
    os: {
      products: '__vendor__ products',
      versions: '__vendor__ __product__ versions',
    },
    osLabel: 'OS',
    osName: 'OS Name',
    otIOTAssetsHtml:
      '<p>This dashboard gives you visibility to Operational Technology (OT), Industrial Control Systems (ICS), and Internet of Things (IoT) assets in your environment, as discovered by SCADAfence. To maintain a healthy and secure infrastructure, it is crucial to know about all the connected devices in your networks beyond the traditional IT systems. These assets need to be assessed appropriately, without causing downtime or negatively impacting safety and productivity.</p>',
    other: 'Other',
    overview: 'Overview',
    owner: 'Owner',
    package: 'Package',
    package_plural: 'Packages',
    partnerConnections: 'Partner Connections',
    passed: 'Passed',
    password: 'Password',
    'password/token': 'Password / Token',
    past2weeks: 'in the past 2 weeks',
    past6months: 'in the past 6 months',
    past7days: 'in the past 7 days',
    past30days: 'in the past 30 days',
    past60days: 'in the past 60 days',
    past90days: 'in the past 90 days',
    pastDue: 'Past Due',
    pastMonth: 'in the past month',
    pastWeek: 'in the past week',
    pastYear: 'in the past year',
    percent: '__percent__$t(percentageSymbol)',
    percentLabel: 'Percent',
    percentOfAssets: '$t(percentageSymbol) of Assets',
    percentageSymbol: '%',
    permissions: 'Permissions',
    petya: 'Petya-like',
    platformAccount: {
      creationProgress: {
        banner:
          "<strong>Looks like you don't have an Insight account yet.</strong> We'll create one for you now.",
        headline: "We're creating an account for you...",
        paragraph: 'Give us a few seconds.',
      },
      creationSuccess: {
        banner:
          "<strong>Done!</strong> Your new insight.rapid7.com account is based on the email address attached to your Security Console user. Check your email for activation instructions and get access to all of InsightVM's cloud features!",
        headline: 'Your account has been created!',
        paragraph:
          "This page will automatically refresh momentarily. If it doesn't, you can do so manually.",
      },
      error: {
        emailExists: {
          banner:
            '$t(platformAccount.error.general.detail) Your Security Console user email address is already being used by another account on the Insight Platform. <0><0><strong>Edit your console user</strong></0></0> with a different email address and try again.',
        },
        general: {
          banner:
            '$t(platformAccount.error.general.detail) $t(platformAccount.error.general.paragraph)',
          detail:
            '<strong>There was an error creating your Insight account.</strong>',
          headline: 'Something went wrong!',
          paragraph:
            '<a target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/contact"><strong><u>Contact Support</u></strong></a> for assistance.',
        },
      },
      refresh: 'Refresh',
    },
    platformActivationModal: {
      accountCreatedWithDate:
        'You successfully created your <a href="https://insight.rapid7.com" target="_blank" rel="noopener noreferrer">insight.rapid7.com</a> account <strong> on __date__</strong>. An email with account activation instructions was sent to this email address:',
      accountCreatedWithoutDate:
        'You successfully created your <a href="https://insight.rapid7.com" target="_blank" rel="noopener noreferrer">insight.rapid7.com</a> account. An email with account activation instructions was sent to this email address:',
      avoidFeatureDisruption:
        'To avoid feature disruption, activate your Insight platform account now. Click <b>Resend Activation Email</b> to send the activation email again.',
      learnMoreAutomation: 'Learn more about Automation',
      learnMorePlatform: 'Learn more about Insight platform',
      snoozeForDays: 'Snooze for __number__ days',
      title: 'Activate your Insight Platform Account',
      viewDetails: 'View Details',
    },
    pods: 'Pods',
    policies: 'Policies',
    policy: 'Policy',
    policyElement: 'Policy Element',
    policyName: 'Policy Name',
    policyRuleStatus: 'Policy Rule Status',
    policyTitle: 'Policy Title',
    port: 'Port',
    preview: 'Preview',
    previewFeatures: {
      [PreviewFeature.INFRASTRUCTURE]: {
        description: `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec vulputate metus nec massa
          varius vulputate.`,
        title: 'Cloud Configuration',
      },
    },
    previous: 'Previous',
    private: 'Private',
    product: 'Product',
    program: 'Program',
    project: 'project',
    projects: 'Projects',
    proof: 'Proof',
    protocol: 'Protocol',
    nic: 'Network Interface',
    public: 'Public',
    publishDate: 'Published On',
    publishedOn: 'Published On',
    pullCommand: 'Pull Command',
    quayOrg: 'Quay Organization',
    queries: 'Queries',
    queryBuilder: {
      alternateIds: {
        altId: 'The ID of the alternate ID',
        size: 'The number of alternate IDs',
        source: 'The alternate ID source',
        title: 'A collection of alternate IDs for vulnerability',
      },
      asset: {
        agentKey: 'The key of the agent',
        altips: {
          name: 'A collection of alternate IPs',
          size: 'The number of alternate IPs',
        },
        altmacs: {
          name: 'A collection of alternate MACs',
          size: 'The number of alternate MACs',
        },
        altnames: {
          name: 'A collection of alternate names',
          size: 'The number of alternate names',
        },
        assetKey: 'The key of the asset',
        averageScanDuration: 'The average scan duration',
        console: 'The primary console',
        container: {
          _label: 'A collection of containers',
          created: 'Created',
          finished: 'Finished',
          id: 'The container ID',
          imageDigest: 'Image Digest',
          imageId: 'Image ID',
          name: 'The container name',
          repository: 'Repository',
          size: 'Size',
          started: 'Started',
          status: 'The container status',
        },
        credentialSuccessStatus: 'Indicates succeeded or failed status',
        endpoints: {
          name: 'The endpoint service name',
          port: 'The endpoint port',
          protocol: 'The endpoint protocol',
          size: 'The number of endpoints',
          title: 'A collection of endpoints',
        },
        firstScanTime: 'The first asset scan date',
        groups: 'A collection of group names',
        ipv4: 'The primary IPv4 of the asset',
        ipv6: 'The primary IPv6 of the asset',
        lastAgentConnectionTime:
          'The date and time of the last connection made by the agent.',
        lastScanDuration: 'The most recent scan duration',
        lastScanTime: 'The most recent asset scan date',
        mac: 'The primary mac of the asset',
        name: 'The host name of the asset',
        os: {
          arch: 'The asset OS architecture',
          description: 'The asset OS description',
          family: 'The asset OS family',
          product: 'The asset OS product',
          title: 'The primary asset OS object',
          type: 'The asset OS type',
          vendor: 'The asset OS vendor',
          version: 'The asset OS version',
        },
        riskScore: 'The asset risk score',
        sites: 'A collection of site names',
        software: {
          description: 'The software description',
          family: 'The software family',
          product: 'The software product',
          program: 'The software program',
          size: 'The number of installed software',
          title: 'A collection of software objects',
          vendor: 'The software vendor',
          version: 'The software version',
        },
        sslCertificateMinimumExpirationDate: 'The ssl certificate expiration',
        tags: {
          name: 'The tag name',
          type: 'The tag type',
        },
        type: 'The host type of the asset',
      },
      assetgroup: {
        assets: {
          description: 'The assets in the group',
          size: 'Number of assets in the group',
        },
        dynamic: 'Indicates static or dynamic',
        groupKey: 'The unique ID of the group',
        name: 'The name of the asset group',
        riskScore: 'The riskscore of the group',
        vulnCount: 'The number of vulnerabilities',
      },
      aws: {
        account: 'Account',
        alb: {
          _label: 'ALB',
          addressType: 'Address Type',
          availabilityZones: 'Availability Zones',
          crossZoneSettings: 'Cross Zone Settings',
          dns: 'DNS',
          hostedZoneId: 'Hosted Zone ID',
          scheme: 'Scheme',
          securityGroups: 'Security Groups',
          state: 'State',
          subnets: 'Subnets',
          type: 'Type',
          vpc: 'VPC',
        },
        arn: 'ARN',
        created: 'Created',
        description: 'Description',
        ec2: {
          _label: 'EC2',
          architecture: 'Architecture',
          availabilityZone: 'Availability Zone',
          imageId: 'Image ID',
          instanceProfile: 'Instance Profile',
          kernelId: 'Kernel ID',
          launched: 'Launched',
          privateDns: 'Private DNS',
          privateIp: 'Private IP',
          publicDns: 'Public DNS',
          publicIp: 'Public IP',
          rootDeviceName: 'Root Device Name',
          rootDeviceType: 'Root Device Type',
          state: 'State',
          stateReason: 'State Reason',
          stateReasonCode: 'State Reason Code',
          subnet: 'Subnet',
          vpc: 'VPC',
        },
        elb: {
          availabilityZones: 'Availability Zones',
          crossZoneSettings: 'Cross Zone Settings',
          dns: 'DNS',
          scheme: 'Scheme',
          securityGroups: 'Security Groups',
          subnets: 'Subnets',
          type: 'Type',
          vpc: 'VPC',
        },
        iam: {
          identifier: 'Identifier',
          name: 'Name',
          path: 'Path',
        },
        kms: {
          enabled: 'Enabled',
          manager: 'Manager',
          origin: 'Origin',
          rotationEnabled: 'Rotation Enabled',
          state: 'State',
        },
        name: 'Name',
        r53: {
          _label: 'Route 53',
          callerReference: 'Caller Reference',
          config: {
            _label: 'Config',
            comment: 'Comment',
            privateZone: 'Private Zone',
          },
          delegationSet: {
            _label: 'Delegation Set',
            callerReference: 'Caller Reference',
            nameServers: 'Name Servers',
            setId: 'Set ID',
          },
          domainName: 'Domain Name',
          linkedService: {
            _label: 'Linked Service',
            description: 'Description',
            principal: 'Principal',
          },
          recordSetCount: 'Record Set Count',
          resourceRecordSet: {
            _label: 'Resource Record Set',
            aliasTarget: {
              _label: 'Alias Target',
              dnsName: 'DNS Name',
              evaluateTargetHealth: 'Evaluate Target Health',
            },
            domainName: 'Domain Name',
            failover: 'Failover',
            geoLocation: {
              _label: 'Geo Location',
              continent: 'Continent',
              country: 'Country',
              subdivision: 'Subdivision',
            },
            healthCheckId: 'Health Check ID',
            multivalueAnswer: 'Multivalue Answer',
            region: 'Region',
            resourceRecordList: 'Resource Record List',
            setIdentifier: 'Set Identifier',
            trafficPolicyId: 'Traffic Policy ID',
            ttl: 'TTL',
            type: 'Type',
            weight: 'Weight',
          },
          trafficPolicy: {
            _label: 'Traffic Policy',
            name: 'Name',
            policyId: 'Policy ID',
            state: 'State',
            trafficPolicyId: 'Traffic Policy ID',
            type: 'Type',
            version: 'Version',
          },
          vpc: {
            _label: 'VPC',
            arn: 'ARN',
            region: 'Region',
          },
        },
        rds: {
          _label: 'RDS',
          dbName: 'DB Name',
          engineName: 'Engine Name',
          engineVersion: 'Engine Version',
        },
        region: 'Region',
        ruleFinding: {
          _label: 'Rule Finding',
          description: 'Description',
          entityKey: 'Entity Key',
          firstDiscovered: 'First Discovered',
          lastConfirmed: 'Last Confirmed',
          result: 'Result',
          ruleKey: 'Rule Key',
          severity: 'Severity',
          size: 'Size',
          title: 'Title',
        },
        s3: {
          _label: 'S3',
          acl: {
            grant: {
              granteeId: 'Grantee Id',
              granteeType: 'Grantee Type',
              permission: 'Permission',
            },
            isRequesterCharged: 'Is Requester Charged',
            ownerId: 'Owner Id',
            ownerName: 'Owner Name',
          },
          cors: {
            allowedHeaders: 'Allowed Headers',
            allowedMethods: 'Allowed Methods',
            allowedOrigins: 'Allowed Origins',
            exposedHeaders: 'Exposed Headers',
            id: 'Id',
            maxAgeSeconds: 'Max Age Seconds',
          },
          ownerId: 'Owner Id',
          ownerName: 'Owner Name',
        },
        service: 'Service',
        sns: {
          _label: 'SNS',
          displayName: 'Display Name',
          owner: 'Owner',
          subscription: {
            arn: 'Arn',
            endpoint: 'Endpoint',
            owner: 'Owner',
            protocol: 'Protocol',
          },
          subscriptionsConfirmed: 'Subscriptions Confirmed',
          subscriptionsDeleted: 'Subscriptions Deleted',
          subscriptionsPending: 'Subscriptions Pending',
        },
        sqs: {
          _label: 'SQS',
          deadLetterTarget: 'Dead Letter Target',
          kmsKey: 'Kms Key',
          kmsReuse: 'Kms Reuse',
          maximumMessageSize: 'Maximum Message Size',
          maximumReceiveCount: 'Maximum Receive Count',
          modified: 'Modified',
          receiveWaitTime: 'Receive Wait Time',
          retention: 'Retention',
          url: 'Url',
          visibilityTimeout: 'Visibility Timeout',
        },
        tag: {
          _label: 'Tag',
          name: 'Name',
          value: 'Value',
        },
      },
      cloud: {
        entity: {
          account: 'Account',
          entityKey: 'Entity Key',
          partition: 'Partition',
          region: 'Region',
          resourceId: 'Resource ID',
          service: 'Service',
          type: 'Type',
        },
        findingKey: 'Finding Key',
        rule: {
          failedSince: 'Rule Failed Since',
          firstEvaluated: 'Rule First Evaluated',
          result: {
            status: 'Rule Result Status',
            variable: {
              key: 'Rule Result Variable Key',
              value: 'Rule Result Variable Value',
            },
          },
          ruleKey: 'Rule Key',
          severity: 'Rule Severity',
          status: 'Rule Status',
          title: 'Rule Title',
        },
      },
      exploits: {
        description: 'The exploit description',
        exploitRank: 'The exploit rank',
        id: 'The exploit id',
        name: 'The exploit name',
        rank: 'The exploit rank',
        size: 'The number of exploits for the vuln',
        source: 'The exploit source',
        title: 'The collection of exploits the vuln belongs to',
      },
      filter: {
        enterCustom: 'Enter custom query',
        selectExisting: 'Select an existing query',
      },
      help: {
        title: 'Syntax Help / Query Dictionary',
      },
      nameIsRequired: '$t(nameIsRequired)',
      placeholder: 'click to create a new filter',
      saveFilter: {
        placeholder: 'Save filter',
        title: 'Save Filter',
      },
      selectSavedQuery: 'Select a Saved Query',
      site: {
        assets: {
          description: 'The assets in the site',
          size: 'Number of assets in the site',
        },
        autoEngineSelected: 'Indicates auto-engine status',
        dynamic: 'Indicates static or dynamic',
        name: 'The name of the site',
        riskFactor: 'The risk factor of the site',
        riskScore: 'The riskscore of the site',
        siteKey: 'Unique identifier for a site',
        status: 'The site status',
        vulnCount: 'The number of vulnerabilities in the site',
      },
      solution: {
        fix: 'The fix of the solution for the given vuln',
        solutionKey: 'The key of the solution for the given vuln',
        summary: 'The summary of the solution for the given vuln',
      },
      tag: {
        assets: {
          description: 'The assets in the tag',
          size: 'Number of assets in the tag',
        },
        groups: {
          description: 'The groups in the tag',
          size: 'Number of groups in the tag',
        },
        name: 'The name of the tag',
        riskModifierAttribute: "The tag's risk modifier",
        sites: {
          description: 'The sites in the tag',
          size: 'Number of sites in the tag',
        },
        sourceAttribute: "The tag's source attribute",
        tagKey: 'Unique identifier for a tag',
        type: 'The type of the tag',
      },
      type: {
        IP: 'IP Address',
        boolean: 'Boolean',
        date: 'Date',
        duration: 'Duration',
        integer: 'Integer',
        object: 'Object',
        objectCollection: 'Object Collection',
        standardNumber: 'Number',
        standardString: 'String',
        stringCollection: 'String Collection',
      },
      vulnerability: {
        assetKey: 'The key of the asset',
        categories: {
          size: 'The number of categories',
          title: 'The categories that the vulnerability belongs to',
        },
        cveIds: 'A list of cve ids on the vuln',
        cvssScore: 'The vuln cvss score',
        dateModified: 'The date the vuln was last modified',
        datePublished: 'The date the vuln was published',
        description: 'The vulnerability description',
        firstFound: 'The date the vuln was found',
        malwareKits: {
          size: 'The number of malware kits',
          title: 'The malware kits associated with vuln',
        },
        object: 'Encapsulates facts about the vulnerability',
        pciCompliant: 'Indicates if the asset is PCI compliant',
        port: 'The vuln endpoint port',
        protocol: 'The vuln endpoint protocol',
        risk: 'The vuln risk (can be stale if not rescanned)',
        severity: 'The vulnerability severity',
        severityScore: 'The numeric vulnerability severity score',
        size: 'The number of vulnerabilities',
        skillLevel: 'The vulnerability skill level',
        software: 'A composite string id for the software',
        softwareFamily: 'The software family of the vuln',
        softwareProduct: 'The software product of the vuln',
        softwareVendor: 'The software vendor of the vuln',
        softwareVersion: 'The software version of the vuln',
        title: 'The vulnerability title',
        vulnKey: 'Vulnerability Unique Identifier',
      },
      vulnerabilityexception: {
        asset: {
          ipv4: 'The ipv4 of an asset with applied exceptions',
          ipv6: 'The ipv6 of an asset with applied exceptions',
          key: 'The key of the asset the exception was defined on',
          name: 'The name of an asset with applied exceptions',
          size: 'Number of assets in the tag',
        },
        expirationTime: 'The expiration time of the exception',
        port: 'The port the exception was defined on',
        reason: 'The reason for the exception',
        reasonDetails: 'The details of the reason of the exception',
        reviewerActionTime: 'The action time of the reviewer',
        reviewerKey: 'The key of the reviewer',
        reviewerReason: 'The reason for the reviewer action',
        scope: 'The scope of the exception',
        siteKey: 'The key of the site the exception was defined on',
        state: 'The state of the exception',
        submitterActionTime: 'The action time of the submitter',
        submitterKey: 'The key of the submitter',
        vulnExceptionKey: 'Vulnerability Exception Unique Identifier',
      },
    },
    queryBuilders: {
      addFilterLabels: {
        ASSET: 'Add an asset filter',
        VULNERABILITY: 'Add a vulnerability filter',
      },
      labels: {
        ASSET: 'Asset filter',
        ASSET_VULNERABILITY: 'Add an asset or vulnerability filter',
        VULNERABILITY: 'Vulnerability filter',
      },
    },
    ra: {
      affectedAssets: 'Affected Assets',
      assignees: 'Assignees',
      description:
        'A card that highlights remediations you can perform on your network that will have the biggest impact on reducing overall risk score.',
      project: {
        acceptedRiskValue:
          'The total risk of Will Not Fix solutions is __acceptedRisk__.',
        access: 'Access',
        accessDescription:
          'Permit users who do not have access to asset(s) associated with this project to see',
        allAssets: 'All',
        applyMessage:
          'Must apply, revert, or delete this filter in order to save this project',
        assetCountExceeded:
          'There is a limit of __maxAssets__ assets per project. Please refine your query.',
        assetFilter: 'Asset filter',
        assetSelectionExceeded:
          'There is a limit of __maxCount__ assets for static projects. Please deselect some assets and try again.',
        assetSelectionExceededWarning:
          'There is a limit of __maxCount__ assets for static projects, only the first __maxCount__ assets in this query will be considered.',
        assigneeRequired: 'At least one user or email is required',
        cardAssetFilter: 'Card asset filter',
        cardVulnerabilityFilter: 'Card vulnerability filter',
        chooseConnection: 'Choose a project connection',
        createProject: 'Create Remediation Project',
        deleteProjectConfirm:
          'Are you sure you want to delete the selected project(s)?',
        deleteProjectError: 'An error occurred while deleting the project(s).',
        deleteProjectTitle: 'Confirm project deletion',
        dueOnRequired: 'Due date is required',
        editProject: 'Edit Remediation Project',
        emailAssigneesWillBeEmailed:
          'Email assignees will be notified of this project',
        entity: {
          acceptedRisk: 'Accepted Risk',
          addedRemediations: 'Added Remediations',
          assignees: 'Assignees',
          completedAssetsPercent: 'Assets Completed',
          completedRemediations: 'Solutions Resolved',
          completedRemediationsPercent: 'Progress',
          createdOn: '$t(createdOn)',
          description: 'Description',
          dueOnTime: 'Due On',
          emptyRemediatedAssets:
            ' (__emptyRemediatedAssets__ asset(s) without solutions)',
          neverExpire: 'Never Expires',
          numAssets: 'Assets Affected',
          originalRemediations: 'Original Remediations',
          ownerName: 'Owner',
          progress: 'Progress',
          remainingRemediations: 'Remediations Remaining',
          remainingTime: 'Remaining Time',
          remainingWork: 'Work Remaining',
          removedRemediations: 'Removed Remediations',
          scope: 'Scope: __numAssets__',
          serverActive: 'Ticketing',
          status: 'Status',
          title: 'Project Name',
          tooltipForProgressBar:
            'This metric tracks project progress. Progress advances when a solution is applied to one asset.',
          totalRemediations: 'Total Remediations',
          totalRisk: 'Cumulative Risk',
          type: 'Type',
        },
        filterPlaceholder: 'click to add a filter',
        minimumDate: 'The due date must be on or after today.',
        nameInUse: 'This name is already in use',
        nameIsRequired: '$t(nameIsRequired)',
        overview: {
          title: 'Project Overview',
        },
        owner: {
          active: '$t(active)',
          email: 'Email',
          userFullName: 'Name',
          userKey: 'User ID',
          userName: 'Username',
        },
        ownerTitle: 'Project Owner',
        populatingNoRows:
          'No records found. If the project was recently created it might take a few minutes to be fully populated with solutions.',
        populatingWarning:
          'Your project has been created. It may take several minutes for your projects to be fully populated with solutions. It is currently __percent__ populated.',
        projectContent: 'Project Content',
        projectType: 'Project Type',
        projectedSolutions:
          'This project will have approximately __count__ solution(s).',
        remediated: 'Remediated',
        status: {
          CLOSED: '$t(closed)',
          EXPIRED: '$t(expired)',
          OPEN: '$t(open)',
        },
        ticketingConfigurationWarning:
          'The selection you make here can not be changed later.',
        type: {
          DYNAMIC: 'Dynamic',
          STATIC: 'Static',
        },
        unresolved: 'Unresolved',
        updateDueOnTimeNotification:
          'This project is expired. Please update its due date, and then update the project status to open.',
        updateError: '$t(cards.assets.drilldown.remediation.errorNotification)',
        updateStatusError:
          'An error occurred while updating your remediation project(s).',
        updateToClosedStatus: 'Close selected projects',
        updateToOpenStatus: 'Open selected projects',
        viewProject: 'View Project',
        vulnerabilityFilter: 'Vulnerability filter',
        vulnerabilitySelectionExceeded:
          'There is a limit of __maxCount__ vulnerabilities for static projects. Please deselect some assets and try again.',
        vulnerabilitySelectionExceededWarning:
          'There is a limit of __maxCount__ vulnerabilities for static projects, only the first __maxCount__ assets in this query will be considered.',
      },
      projectError: {
        entity: {
          description: 'Description',
          projectName: 'Project Name',
          timeOccurred: 'Date of Error',
        },
        errorsDescription:
          "The remediation project's ticketing has errors. See error details below.",
        errorsHeader:
          'Ticketing Errors in Remediation Workflow __projectName__',
        errorsResultsLimitDescription:
          "The remediation project's ticketing has errors. See error details below. Returning the 1000 most recent results.",
        message: {
          BAD_STATE:
            'Bad state - there are no mappings for remediation status __REMEDIATION_STATUS__ on server __SERVER_NAME__',
          FAIL_COMMENT_1:
            'Failed to add comments to issue __ISSUE_KEY__ - interrupted or timed out.',
          FAIL_COMMENT_2:
            'Failed to add comments to issue __ISSUE_KEY__ - execution error.',
          FAIL_ISSUE_1:
            'Failed to get issue for __ISSUE_KEY__ - interrupted or timed out.',
          FAIL_ISSUE_2:
            'Failed to get issue for __ISSUE_KEY__ - execution error.',
          FAIL_STATUS_1:
            'Failed to get issue for __ISSUE_KEY__ - interrupted or timed out. As a result, will not be able to sync the status for remediation: __REMEDIATION_SOLUTION_NAME__.',
          FAIL_STATUS_2:
            'Failed to get issue for __ISSUE_KEY__ - execution error. As a result, will not be able to sync the status for remediation: __REMEDIATION_SOLUTION_NAME__.',
          FAIL_TICKET_1:
            'Could not get the ticket server details for: __SERVER_NAME__ with URL: __URL__. Unable to create ticket for remediation __REMEDIATION_UUID__ for user __USER_NAME__ on assets __USER_REMEDIATION_ASSET_NAMES__.',
          FAIL_TICKET_2:
            'Failed to create __type__ issue - interrupted or timed out. Unable to create ticket for remediation __REMEDIATION_UUID__ for user __USER_NAME__ on assets __USER_REMEDIATION_ASSET_NAMES__.',
          FAIL_TICKET_3:
            "Failed to create __type__ issue - execution error. Unable to create ticket for remediation __REMEDIATION_UUID__ for user __USER_NAME__ on assets __USER_REMEDIATION_ASSET_NAMES__. __type__'s error collection returns: __ERROR_COLLECTION__.",
          FAIL_TICKET_4:
            'There was an error creating a ticket with the specified field mappings. Unable to create ticket for remediation __REMEDIATION_UUID__ for user __USER_NAME__ on assets __USER_REMEDIATION_ASSET_NAMES__.',
          MAX_TICKETS:
            'There have been __MAX_TICKET_NUMBER__ tickets created for this project. More tickets will not be created; please create a separate project if you would like more tickets.',
          SERVER_DETAILS: 'Unable to retrieve the ticket server details.',
          STATUS_DETAILS:
            "There are no status mappings for this server. Can't update remediation statuses.",
          USER_DETAILS: 'There are no assignment rules for this project.',
        },
      },
      projectForm: {
        accessDescription:
          'Select the level of detail that will be available to your assignee(s):',
        assetCountMin:
          'Remediation projects require a minimum of __min__ asset. Adjust your project to increase the current number of assets.',
        assetCountLimit:
          'Remediation projects are limited to __limit__ assets. Adjust your project to reduce the current number of assets.',
        assetCountLoading: 'Calculating the number of assets affected.',
        assetsSelected: '__count__ assets selected',
        assignStepDescription:
          'Assign this project to one or more InsightVM users.  If you need to assign it to a user outside of InsightVM, enable the following permission checkbox.  Outside users will be provided with a link to the project via email.',
        assignStepHeader: 'Project Assignment',
        assignStepTitle: 'Assign',
        basicInformationStepDescription:
          'Name and describe your project so it can be easily identified.',
        basicInformationStepHeader: 'Basic Information',
        basicInformationStepTitle: 'Basic Information',
        createHeader: 'Create a New Project',
        createProject: 'Create Project',
        createProjectAssetConstraintWarning:
          'Adjust your project scope to have less than <b>__assetCount__</b> assets',
        createProjectQueryConstraintWarning:
          'A query is required to create a project',
        createProjectVulnerabilityConstraintWarning:
          'Adjust your project scope to have less than <b>__vulnerabilityCount__</b> vulnerabilities',
        dueDateStepDescription:
          'Set a due date for this project.  You will be provided with alerts as the project approaches this date.  Expired projects will no longer update and cannot be accessed via any emailed links sent previously.',
        dueDateStepHeader: 'Specify Project Due Date',
        dueDateStepTitle: 'Due Date',
        dynamicDetail:
          '<a href="https://insightvm.help.rapid7.com/docs/remediation-workflow#section-create-a-remediation-project" target="_blank" rel="noopener noreferrer">Dynamic projects</a> automatically add solutions as conditions change in your network.',
        editHeader: 'Edit Project',
        emailAssigneesToggle: 'Permit users who do not have access to console',
        emailAssigneesToggleNote:
          '* Unchecking this option will remove the non-console users from this list automatically',
        nextAddUsers: 'Next: Add Users',
        nextComplete: 'Save and Complete',
        nextContinue: 'Save and Continue',
        projectTypeSubtext:
          '__assetCount__ assets | __vulnerabilityCount__ vulnerabilities | __solutionCount__ solutions',
        review: 'Review',
        scopeStepDescription:
          '<ol><li>Specify <b>static</b> or <b>dynamic</b> for your project type. This will determine if qualifying assets or vulnerabilities are automatically added to or removed from the project as they are detected.</li>' +
          '<li>Add scope to narrow your data down. An estimated number of solutions will be calculated based on your scope.</li></ol>',
        scopeStepDescriptionSelectionOnly:
          '<p>The project scope will be defined by the assets or vulnerabilities you have selected.</p>',
        scopeStepHeader: 'Scope',
        scopeStepTitle: '$t(scope)',
        staticDetail:
          '<a href="https://insightvm.help.rapid7.com/docs/remediation-workflow#section-create-a-remediation-project" target="_blank" rel="noopener noreferrer">Static projects</a> lock asset and solution membership after you initially create them.',
        staticVsDynamicDetail:
          '<p>Static projects lock asset and solution membership after you initially create them. Their scope cannot change unless you delete asset members from the Security Console. However, the solutions in a static project can add applicable assets as long as those assets were already project members. Static projects are commonly used for addressing specific asset and vulnerability groups.</p>' +
          '<p>Dynamic projects automatically add solutions as conditions change in your network. Unlike static projects, you can also modify the scope of a dynamic project after you create it. To help you visualize your progress, dynamic projects do not remove solutions as you remediate, but they will remove solutions if you elect to change the scope. Like static projects, dynamic project scope will also be affected if you delete asset members from the Security Console. Dynamic projects are commonly used for ongoing maintenance, such as the remediation of any assets that surpass a certain level of risk.</p>',
        staticVsDynamicNote:
          'What is the difference between static and dynamic?',
        ticketingStepDescription:
          'If applicable, specify a configured external ticketing system to automatically create, assign, and track remediation progress.',
        ticketingStepHeader: 'Specify Ticketing',
        ticketingStepTitle: 'Ticketing',
        typeStepDescription:
          'Specify static or dynamic for your project type. This will determine if qualifying assets or vulnerabilities are automatically added to or removed from the project as they are detected.',
        typeStepTitle: 'Project Type',
        vulnerabilitiesSelected: '__count__ vulnerabilities selected',
      },
      projects: {
        assetsCompletedTooltip:
          'This tracks how many assets have remediated all vulnerability findings addressed by a solution.',
        createProject: 'Create a Project',
        dueDateWillBeUpdated:
          'Existing projects without a due date have been updated with a due date set to 12 months in the future.',
        editProject: 'Edit Remediation Project',
        metrics: {
          numAssignedMeProjects: 'Assigned to Me',
          numClosedProjects: '$t(closed)',
          numExpiredProjects: '$t(expired)',
          numOpenProjects: '$t(open)',
          numOwnedMeProjects: 'Owned by Me',
          numProjects: '__numProjects__ Projects',
        },
        searchPlaceholder: 'Search Remediations',
        title: 'Remediation Projects',
        hrv: {
          description1:
            'Start protecting your cloud environment and make informed decisions to drive your security program with our new <bold>Remediation Hub</bold> Feature!',
          description2:
            'Drive accountability, enforce compliance, accelerate remediation and visualise risks to your on-premise and cloud infrastructure and workloads together, creating visibility of the Hybrid Attack Surface.',
          exploreMore: 'Explore more',
          heading:
            'Solutions for reducing risk across your hybrid environments',
          newFeature: 'New Feature',
          takeMeThere: 'Take me there',
        },
      },
      remediation: {
        entity: {
          assigneeName: 'Assignee',
          completedAssets: '$t(assets) Completed',
          numAssets: 'Assets Affected',
          riskReduction: 'Risk Reduction',
          status: 'Status',
          summary: 'Solutions',
          unresolvedVulns: '$t(vulnerabilities)',
        },
        metrics: {
          assigned: 'Assigned',
          assignedToMe: 'Assigned To Me',
          numRemediations: '__numRemediations__ Solutions',
          pendingVerification: 'Pending Verification',
          unassigned: 'Unassigned',
          unknownSolutions: 'Unknown Solutions',
        },
        noScanStartedWarning:
          'Based on selected remediations, no scans could be started.',
        runValidationScan: 'Run Validation Scan',
        scanConfirmationModal: {
          confirmation: 'Yes, Run Scan',
          description:
            'One or more selected solutions have the Status of "Will Not Fix" or "Closed" and will not be scanned.',
          title:
            'Would you like to run a validation scan on your affected assets?',
        },
        scanFailed: 'Unable to scan.',
        scanSolutionsTooltip:
          'Any selected solutions that have the "Open" status will move to the "Awaiting Verification" status. You can view the status of your validation scan on the Scan History page in your Security Console.',
        scanStarted: 'Scan successfully started!',
        startingScan: 'Starting Scan',
        status: {
          AWAITING_VERIFICATION: 'Awaiting Verification',
          CLOSED: '$t(closed)',
          OPEN: '$t(open)',
          OPEN_PENDING: 'Open - Pending',
          REOPEN: 'Reopen',
          WILL_NOT_FIX: 'Will Not Fix',
          WILL_NOT_FIX_PENDING: 'Will Not Fix - Pending',
        },
        tableTitle: 'Remediation Solutions',
        updateError:
          'An error occurred while updating your remediation solution.',
        updateStatus: 'Update Status',
      },
      riskScoreImageTitle: 'Risk score increased or decreased on Asset.',
      solution: {
        entity: {
          additionalInfo: 'Additional Information',
          fix: 'Fix',
          solutionId: 'Solution ID',
          summary: 'Summary',
          url: 'URL',
        },
      },
      solutionTitle: 'Solution',
      solutions: 'Solutions',
      summaryInformation: 'Summary Information',
      ticketing: {
        addConnection: 'Add a ticketing connection',
        addRuleInfo: 'Click New Rule to add a rule',
        addStatus: '+ Add a status',
        applyMessage:
          'Must apply, revert, or delete this filter in order to save this rule',
        assetFilterOrVulnerabilityFilterRequired:
          'An asset or vulnerability filter is required',
        assignmentRule: 'Assignment Rule',
        assignmentRuleDescription:
          'Create a rule for who a ticket will be assigned to, based on information from the remediation details. You will be able to create multiple rules, and set an order in which to apply them.',
        assignmentRules: 'Assignment Rules',
        assignmentRulesDescription:
          'Assignment rules allow you to automatically assign tickets to __type__ users. Add new rules and rearrange the rules below to prioritize your ruleset. Rules are applied in order of precedence from the top down.',
        assignmentRulesHeader:
          'Create a New Assignment Rule for __type__ Project __name__',
        automatedTicketing: 'Automated ticketing',
        automatedTickets: 'Automated Tickets',
        changeConnection: 'Change Connection',
        chooseConnection: 'Choose a project connection',
        closeWizardWarning:
          'Are you sure you want to cancel? Your mappings and templates will not be saved. Only the ticketing connection will be retained.',
        configurations: 'Configurations',
        configurationsDescription:
          'Edit existing configurations by clicking on the Ticketing Template Name. Add new configurations by clicking on the New Configuration button.',
        configure: 'Configure',
        configured: 'Configured for __fieldMappingName__',
        configuredWarning:
          '__type__ project connection can not be changed. If you wish to use a different ticketing configuration, please create a new project.',
        connectionSettings: 'Connection Settings',
        createServerWarning:
          'You must configure a connection before proceeding',
        defaultAssignee: 'Default Assignee',
        defaultAssigneeRequired: 'Default assignee (and/or) group is required.',
        defaultGroup: 'Default Group',
        defaultGroupRequired: 'Default group (and/or) assignee is required.',
        deleteConnection: 'Delete Connection',
        deleteConnectionDisabled:
          'You have remediation projects with active ticketing connections. You must delete these projects in order to delete the ticketing server connection.',
        deleteConnectionError:
          'An error occurred while deleting the connection.',
        deleteServerError: 'An error occurred while deleting the server.',
        deleteServerWarning: 'Are you sure you want to delete this server?',
        deleteTicketingServerConfigDisabled:
          'Before you can delete a configuration, you must remove any connected ticketing projects. Open the configuration to edit its connection settings',
        disabled: 'Configured, Disabled',
        enabled: 'Configured, Enabled',
        error: 'Configured, ERROR',
        errorsDescription:
          "The remediation's project ticketing has errors. See error details below.",
        errorsHeader:
          'Ticketing Errors in Remediation Workflow __projectName__',
        fieldDescription: 'Field Description',
        fieldMappingDescription:
          'Specify values to be set when tickets are created. The Syntax Help contains variables you can use to include remediation data on the tickets. Suggested default values have been provided.',
        fieldMappingError:
          'The field mapping could not be saved, try again later.',
        fieldMappingHeader: 'Field Mapping for __type__ Project __name__',
        fieldMappingNoValuesExist:
          'This value is required but there are no options for it.',
        fieldMappingRequired:
          '* At least one Ticketing System Status must be selected for each Remediation Solution Status.',
        fieldMappingSuccess: 'The field mapping was updated.',
        inactiveWarning: 'The server must be activated in order to edit it.',
        integration: 'Remediation Ticket Integration',
        issueKey: 'Issue Key',
        issueKeyFailedToDelete:
          'A test ticket "__issueKeyFailedToDelete__" was created to test these mappings. Please review it, ensure it looks correct, and delete manually. If you do not want to delete manually, update your user with delete permissions.',
        issueType: 'Issue Type',
        manageConnections: 'Manage Ticketing Connection',
        newRule: 'New Rule',
        noConnection: 'No project connection',
        noIssueTypeForProjectKey:
          'Please select another project key this one has no issue types',
        notConfigured: 'Not Configured',
        projectAndFieldMapping: 'Ticketing Project and Field Mapping',
        projectError:
          'There is __count__ ticketing error in your __projectName__ project.',
        projectErrorTitle:
          '__count__ Ticketing Errors in Remediation Workflow __projectName__',
        projectErrors:
          'There are __count__ ticketing errors in your __projectName__ project.',
        projectKey: 'Project Key',
        projectKeyDescription: 'Issues will be created in the selected project',
        projectName: 'Project Name',
        remediationStatusButtonWarning:
          'This button is only enabled when selected remediations are all of the same status (for states Open/Reopen/Awaiting Verification).',
        replacementString: {
          $ASSET_IP_LIST: 'A comma delimited list of asset ips.',
          $ASSET_NAME_LIST: 'A comma delimited list of asset names.',
          $ASSET_OS_LIST: "A comma delimited list of asset os's.",
          $DUE_ON: 'The due date for the remediation project.',
          $PROJ_NAME: 'The project name.',
          $PROJ_UUID: 'The project uuid.',
          $SOL_ADDL_INFO: 'Solution Additional Information.',
          $SOL_FIX: 'Solution Fix Steps',
          $SOL_NAME: 'The solution name.',
          $SOL_URL: 'Solution Reference URL',
          $VULN_ID_LIST: 'A comma delimited list of vulnerability ids.',
        },
        ruleSetName: 'Rule Set Name',
        schemaSettingsDescription:
          ' Create a connection to a specific project in your ticketing server. You can create connections to additional projects and issue types later.',
        schemaSettingsHeader:
          'Connect to a ticketing project and issue type in __name__',
        selectStatus: 'Select a status *',
        serverConnectionAuthenticationError:
          'Could not connect to the ticketing server. Please verify that the user name and password provided are correct.',
        serverConnectionAuthorizationError:
          'Could not connect to the ticketing server. Please verify that the account provided meets these <a href="https://insightvm.help.rapid7.com/docs/ticketing-integration-for-remediation-workflow-projects" target="_blank" rel="noopener noreferrer">requirements</a>.',
        serverConnectionDeleteError:
          'The connection could not be deleted, please try again.',
        serverConnectionDeleteSuccess:
          'The connection was deleted successfully.',
        serverConnectionError:
          'The server connection could not be created, please try again.',
        serverConnectionInactiveWarning:
          'The server connection was saved but must be activated to continue.',
        serverConnectionSuccess:
          'The server connection was created successfully.',
        serverCreated: 'A ticketing server was successfully created',
        serverName: 'Server Name',
        serverSettingsDescription:
          'Create a connection to your instance of a ticketing program such as __type__.',
        serverSettingsDescription2:
          'In these three steps, we will guide you through adding a connection to one __type__ project and issue type, as well as configuring rules.',
        serverSettingsHeader: 'Connect to a __type__ ticketing server',
        serverSettingsUserWarning:
          'This field requires a user with the following __type__ permissions:',
        serverSettingsWarning:
          'This will establish a connection to the __type__ server.',
        serverSettingsWarning2:
          'No tickets will be generated until you associate a ticketing connection to a Remediation Workflow project.',
        serviceNowStatusMappingWarning:
          'Create desired states in ServiceNow prior to mapping.',
        servicePickerDescription: 'Click on a ticketing service to configure',
        servicePickerHeader:
          'Choose a ticketing server to manage the connections',
        solutionStatus: 'Remediation Solution Status',
        solutionStatusMapping: 'Solution Status Mapping',
        statusMappingDescription:
          'Match each remediation status in InsightVM to a project status in your ticketing program.',
        statusMappingError:
          'The project remediation status mapping could not be saved, please try again.',
        statusMappingHeader: 'Set up Solution Status Mapping for __name__',
        statusMappingSuccess: 'Your status mappings were updated.',
        syntaxHelp: 'Syntax Help',
        templateName: 'Ticketing Template Name',
        ticketingConnection: 'Ticketing Connection',
        ticketingSystemStatus: 'Ticketing System Project Status',
        ticketsCreated: 'Tickets created: __count__',
        type: {
          JIRA: 'JIRA',
          SERVICENOW: 'ServiceNow',
        },
        userMappingDescription:
          'Create rules to determine ticket assignees based on filter criteria. The rules will be applied in order of priority. If a ticket does not meet any rule criteria, it will be assigned to the default assignee.',
        userMappingDescription2:
          'In Jira, confirm that the Assignee field is visible and assignable to ensure that tickets will be created properly.',
        userMappingError:
          'The user mapping could not be saved, try again later.',
        userMappingHeader: 'Assignment Ruleset for __type__ Project __name__',
        userMappingSuccess: 'The user mapping was updated.',
        vulnerabilityFilterRequired:
          'A project with ticketing enabled requires a vulnerability filter',
        wizardHeader: 'Ticketing Connection Settings',
      },
      title: 'Top Remediations To Impact Your Risk Score',
      user: {
        userName: 'Username',
      },
      vulnerabilities: 'Vulnerabilities',
    },
    range: '__from__ - __to__ __unit__',
    reason: 'Reason',
    reassessImage: 'Reassess Image',
    reassessed: 'Re-assessed',
    recipients: 'Recipients',
    reconHtml: `
      <p>RECON can lead to the compromise of vulnerable SAP installations, including the modification or extraction of highly sensitive information, as well as the disruption of critical business processes. A remote, unauthenticated attacker can exploit this vulnerability through an HTTP interface, which is typically exposed to end users and, in many cases, exposed to the internet.</p>
      <p>If successfully exploited, a remote, unauthenticated attacker can obtain unrestricted access to SAP systems through the creation of high-privileged users and the execution of arbitrary operating system commands with the privileges of the SAP service user account (adm), which has unrestricted access to the SAP database and is able to perform application maintenance activities, such as shutting down federated SAP applications.</p>
      Find out more at:
      <list>
        <listItem><attackerKbLink>AttackerKB</attackerKbLink></listItem>
        <listItem><sapBlogLink>Rapid7 blog - CVE-2020-6287: Critical Vulnerability in SAP NetWeaver Application Server (AS) Java</sapBlogLink></listItem>
        <listItem><cisaAdvisoryLink>CISA Advisory</cisaAdvisoryLink></listItem>
        <listItem><sapThreatsLink>SAP Top 10 Threats</sapThreatsLink></listItem>
      </list>
    `,
    reconWithCve: 'with $t(RECON)',
    redo: 'Redo',
    references: 'References',
    refresh: 'Refresh',
    region: 'Region',
    registries: 'Registries',
    registry: {
      connection: {
        nameLabel: 'Registry Name',
        registrySaveFail:
          'The registry could not be saved now, please try again.',
        registrySaveSuccess: 'The registry was created successfully.',
        testAuthFail:
          'Could not connect to __registry__ $t(registry.shortTitle.__vendor__) registry using the provided information',
        testFail: 'Test failed',
        testSuccess:
          'Successfully connected to __registry__ $t(registry.shortTitle.__vendor__) registry using the provided information',
      },
      create: {
        header: {
          [RegistryVendor.quay]: `$t(registry.title.${RegistryVendor.quay}) Registry Connection`,
          [RegistryVendor.dockerHub]: `$t(registry.title.${RegistryVendor.dockerHub}) Registry Connection`,
          [RegistryVendor.ecr]: `$t(registry.title.${RegistryVendor.ecr}) Connection`,
          [RegistryVendor.acs]: `$t(registry.title.${RegistryVendor.acs}) Connection`,
          [RegistryVendor.gcr]: `$t(registry.title.${RegistryVendor.gcr}) Connection`,
          [RegistryVendor.docker]: `$t(registry.title.${RegistryVendor.docker}) Registry Connection`,
        },
      },
      label: 'Registry',
      shortTitle: {
        [RegistryVendor.acs]: 'Azure',
        [RegistryVendor.quay]: 'Quay.io',
        [RegistryVendor.dockerHub]: 'Docker Hub',
        [RegistryVendor.ecr]: 'Amazon ECR',
        [RegistryVendor.gcr]: 'Google',
        [RegistryVendor.docker]: 'Private Docker',
        [RegistryScope.custom]: 'Private',
      },
      title: {
        [RegistryVendor.acs]: 'Azure Container Registry',
        [RegistryVendor.quay]: 'Quay.io',
        [RegistryVendor.dockerHub]: 'Docker Hub',
        [RegistryVendor.ecr]: 'Amazon EC2 Container Registry',
        [RegistryVendor.gcr]: 'Google Container Registry',
        [RegistryVendor.docker]: 'Private Docker',
      },
    },
    reintroducedDescription:
      'The first date any instance of the vulnerability was reintroduced',
    reload: 'Reload',
    remainingInstances: 'Remaining Instances',
    remediated: 'Remediated',
    remediatedAssets: 'Remediated Assets',
    remediatedInstances: 'Remediated Instances',
    remediatedVulnerabilities: 'Remediated Vulnerabilities',
    remediation: 'Remediation',
    remediationCreateSuccess:
      'Remediation project <title/> was created successfully. <projectLink>Click here to view your new project.</projectLink>',
    remediations: 'Remediations',
    removeWidget: 'Delete the "__title__" card?',
    removeWidgetTitle: 'Remove Card',
    renameDashboard: 'Rename $t(dashboardLabel)',
    editDashboard: 'Edit $t(dashboardLabel)',
    reportExportError:
      'There was a problem exporting the report. Please try again later.',
    reportModule,
    reportedBy: 'Reported By',
    reviewerComment: "Reviewer's Comment",
    reports: {
      appliedQueries: 'Applied Queries',
      applicableSolutions: 'Applicable Solutions',
      applyingSolution:
        'Applying 1 solution will remediate this vulnerability in your environment',
      applyingSolutions:
        'Applying __solutionCount__ different solutions will remediate this vulnerability in your environment',
      assessed: 'Assessed',
      assessedAssets: 'Assessed Assets',
      assessmentCount: 'Assessment Counts of Assets',
      assessmentRatio: 'Assessment Ratio',
      assetDetail: 'Asset Details Report',
      assetsTagged: 'Tagged Assets',
      assetsVsRiskComparison:
        '<0></0>__assetCount__ assessed assets / <4></4>__risk__ risk vs last 6 months',
      assetsWithAgents: 'Assets with Agents',
      assetsWithVuln: 'Assets with this Vulnerability',
      averageAssessedDays:
        'On average, assets were assessed within __averageDays__ days of discovery',
      averageAssessmentCount:
        'Assets were assessed an average of <b>__averageAssessmentCount__ times</b> for this period',
      averageRemediatedDays:
        'On average, assets were remediated within __averageDays__ days of discovery',
      avgRemediationByCriticality: 'Average Remediation by Criticality',
      avgRemediationDays: 'Avg. remediation (days)',
      baseScore: 'Base Score',
      bestSolutions: 'Best Solutions',
      comparison: {
        addedVulns: 'Added vulnerabilities',
        noChange: 'No change since last scan',
        remediatedVulns: 'Remediated vulnerabilities',
        totalVulns: 'Total vulnerabilities',
        unchangedVulns: 'Unchanged vulnerabilities',
      },
      criticalVulns: 'Critical Vulnerabilities',
      criticalVulnsAvgRemediated:
        'Critical vulnerabilities are remediated in an average of __days__ days',
      criticalityTags: 'Criticality Tags',
      cvssV2: 'CVSS V2',
      cvssV3: 'CVSS V3',
      dashboardReport: 'Dashboard Report',
      date: {
        fullScan: '__date__ - Full Scan',
        timeAgo: '__days__ days ago',
      },
      discoveredAssets: 'Discovered Assets',
      environmentByCriticality: 'of environment tagged by criticality',
      environmentByLocation: 'of environment tagged by location',
      environmentByOwner: 'of environment tagged by owner',
      environmentOverview: 'Environment Overview',
      executiveReport: 'Executive Report',
      exploitDatabase: 'Exploit Database',
      exploitsAvailable: 'Available Exploits',
      exploitsRemediated: 'Remediated Exploits',
      firstDiscovered: 'First Discovered',
      frequencyOfAssets: 'Frequency of Assessment for New Assets',
      from: 'from',
      locationTags: 'Location Tags',
      malwareKitsAvailable: 'Available Malware Kits',
      malwareKitsRemediated: 'Remediated Malware Kits',
      metasploitModule: 'Metasploit Module',
      moderateVulns: 'Moderate Vulnerabilities',
      modified: 'Modified',
      moreVulnerabilities: 'And __vulnerabilityCount__ more vulnerabilities',
      newAssets: 'New Assets',
      newRemediationProjects: 'New Remediation Projects',
      newRisk: 'New Risk',
      newRiskByCriticality: 'New Risk by Criticality',
      newRiskByLocation: 'New Risk by Location',
      newRiskByOwner: 'New Risk by Owner',
      newRiskVsRemediatedRisk: 'New Risk vs Remediated Risk',
      newServices: 'New Services',
      newSoftware: 'New Software',
      newTargetedVulns: 'New Targeted Vulnerabilities',
      newVulnerabilities: 'New Vulnerabilities',
      newVulnsByCrit: 'New Vulnerabilities by Criticality',
      noRecordsFound: 'No Records Found',
      notAssessed: 'Not Assessed',
      nothingRemediated: 'Nothing Remediated During This Period',
      numberOfAssets: 'Number of Assets',
      ownerName: 'by __ownerName__',
      ownerTags: 'Owner Tags',
      percentOfNonAssessedAssets:
        '<b>__percent__</b> of new assets are not assessed',
      percentOfVulnsCritical:
        '<b>__percent__</b> of your vulnerabilities are critical',
      programImprovements: 'Program Improvements',
      published: 'Published',
      remediatedRisk: 'Remediated Risk',
      remediatedRiskByCriticality: 'Remediated Risk by Criticality',
      remediatedRiskByLocation: 'Remediated Risk by Location',
      remediatedRiskByOwner: 'Remediated Risk by Owner',
      remediatedVulns: 'Remediated Vulnerabilities',
      remediationProjectsClosed: 'Closed Remediation Projects',
      reportPeriod: 'Report Period',
      riskDecrease: 'risk decrease',
      riskGrowth: 'risk growth',
      riskOverTime: 'Risk Over Time',
      severeVulns: 'Severe Vulnerabilities',
      solutionId: 'Solution ID',
      solutionRemediates:
        'This solution remediates __assetCount__ assets and will reduce risk score by __riskScore__',
      targetedVulns: 'Targeted Vulns',
      targetedVulnsRemediated: 'Remediated Targeted Vulnerabilities',
      topNCriticalities: 'Top __numItems__ Criticalities',
      topNLocations: 'Top __numItems__ Locations',
      topNOwners: 'Top __numItems__ Owners',
      total: 'Total',
      totalAssessedAssets: 'Total Assessed Assets',
      totalRisk: 'Total Risk',
      totalVulns: 'Total Vulnerabilities',
      vector: 'Vector',
      vs: 'vs',
      vulnCriticality: 'Vulnerabilities by Criticality',
      vulnDecrease: 'vulnerabilities decrease',
      vulnGrowth: 'vulnerabilities growth',
      vulnID: 'Vuln ID',
      vulnerabilitiesOverTime: 'Vulnerabilities Over Time',
      vulnerabilityDetail: 'Vulnerability Details Report',
      vulnerabilityInformation: 'Vulnerability Information',
      vulnerabilityNames: 'Vulnerability Names',
      vulnsAreCrit: 'Critical Vulnerabilities',
      vulnsRemediatedByCriticality: 'Remediations by Criticality',
    },
    repositories: 'Repositories',
    repository: 'Repository',
    repositoryConfigDescription:
      'These credentials are used in place of global credentials when synchronizing the specified repository.',
    repositoryDetails: 'Repository Details',
    requestFailed: 'An error occurred while retrieving or submitting data.',
    requestSucceeded: 'The request completed successfully.',
    required: 'Required',
    resendPlatformEmailModal: {
      accountMapped: {
        button: 'Close & Refresh',
        description:
          "It looks like <strong>__email__</strong> already matches an activated Insight account in your organization, so we've mapped this account to your Security Console user. You're all done!",
        title: 'Account Mapped Successfully!',
      },
      button: 'Resend Activation Email',
      description: 'If the email address is not correct, please correct it.',
      errorNotification:
        'We are unable to resend an activation email. Please <a target="_blank" rel="noopener noreferrer" href="https://www.rapid7.com/contact"><strong><u>contact support.</u></strong></a>',
      successNotification: 'Your activation email is on the way!',
      title: 'Verify Your Email Address',
    },
    reset: 'Reset',
    rename: 'Rename',
    reject: 'Reject',
    resetAll: 'Reset All',
    resetVersion: 'Reset Version',
    result: 'Result',
    returnToYourHomepage: 'Return To Your Homepage',
    review: 'Review',
    reviewStatus: 'Review Status',
    risk: 'Risk',
    riskOverTime: 'Risk Over Time',
    riskScore: '$t(riskScoreLabel): __riskScore__',
    riskScoreDecrease: 'Risk Score Decrease',
    riskScoreIncrease: 'Risk Score Increase',
    riskScoreLabel: 'Risk Score',
    riskScoreReduction: 'Risk Score Reduction',
    riskTrend: 'Risk Trend',
    ruleCompliance: 'Rule Compliance',
    runningServices: 'Running Services',
    samba: 'Samba CVE-2017-7494',
    sambaWithCve: 'with $t(samba)',
    save: 'Save',
    saveConnection: 'Save Connection',
    saveFilter: 'Save Filter',
    saveNewFilter: 'save new filter',
    saving: 'Saving',
    scan: 'Scan',
    scanDuration: 'Scan Duration',
    scanEngine: 'Scan Engine',
    scanHistory: 'Scan History',
    scanStatus: 'Stan Status',
    scope: 'Scope',
    score: 'Score',
    search: 'Search',
    secretKey: 'Secret Key',
    seeDetails: 'See Details',
    seeDocumentation: '<docLink>See documentation</docLink>',
    select: 'Select',
    selectAll: 'Select all',
    selectAnOption: 'Select an option',
    selectNone: 'Deselect all',
    selectTime: 'Select Time',
    selected: 'Selected',
    selectedCount: '__selected__ Selected',
    selectedTableText: '(__selected__ of __total__ selected)',
    selectionIsEmptyValidation: 'Please make a selection',
    serviceErrors: {
      DASHBOARD_FORBIDDEN:
        'Unable to complete the request. You must own the filter to update it.',
      DASHBOARD_SERVICE_DEFAULT:
        'Unable to save default dashboard. The dashboard you saved does not have a valid ID.',
      DASHBOARD_SERVICE_FILTER:
        'You have exceeded the available number of filters.',
      DASHBOARD_SERVICE_LARGE:
        'The dashboard is too large. Remove some cards and try again.',
      DASHBOARD_SERVICE_NUMBER:
        'You have exceeded the available number of dashboards.',
      DASHBOARD_SERVICE_PERM:
        'You do not have permissions to update this dashboard.',
      DASHBOARD_SERVICE_SHARE:
        'Unable to share filter. A filter can be shared with 100 users max.',
      DASHBOARD_SERVICE_VALID:
        'The list of filter UUIDs to delete cannot be null or empty.',
      FILTER_VALIDATION: 'Unable to create or update filter.',
    },
    serviceName: 'Service Name',
    serviceVendor: {
      docker: 'Docker',
      ecr: 'Amazon',
      gcr: 'Google',
      quay: 'CoreOS, Inc',
    },
    services: 'Services',
    sessionTokenErrorModal: {
      description: 'Try your action again.',
      dismiss: '$t(dismiss)',
      title: "Your Security Console Didn't Respond",
    },
    settingsModule,
    severe: 'Severe',
    severity: 'Severity: __severity__',
    severityLabel: 'Severity',
    shareDashboardWith: 'Give Access to',
    shared: 'Shared',
    sharedWith: 'Shared With (__count__ $t(user))',
    sharedWith_plural: 'Shared With (__count__ $t(users))',
    showing: 'Showing',
    showAllRecommendedActions: 'Show All Recommended Actions',
    site: 'Site',
    siteName: 'Site Name',
    siteTable: {
      inProgress: 'Scan in progress',
      notScanned: 'Not scanned',
      scanFinishedOn: 'Scan finished on',
      scanPausedOn: 'Scan paused on',
      scheduledScanFinishedOn: 'Scheduled scan finished on',
      scheduledScanInProgress: 'Scheduled scan in progress',
      scheduledScanPausedOn: 'Scheduled scan paused on',
    },
    sites: 'Sites',
    size: 'Size',
    sms: 'SMS',
    software: 'Software',
    solution: 'Solution',
    solutions: 'Solutions',
    source: 'Source',
    sourceLink: 'Source Link',
    sourceName: 'Source Name',
    sourceType: 'Source Type',
    spectre: 'Spectre',
    scopePreview: 'Scope Preview',
    spectreHtml: `
        <h3>About this Spectre dashboard</h3>
        <p>This dashboard provides visibility to your threat exposure to the Spectre attack. The surface area for Spectre is expansive - nearly all processors, not just Intel, are affected. Many technology vendors, such as Microsoft, VMware, Google Chrome and Mozilla Firefox, have released or are working on updates and patches for their products, with many aiming for delivery January 9th, 2018. As Rapid7 works in concert with vendors to provide checks, you can stay up to date by not only referencing our <a href="https://blog.rapid7.com/2018/01/04/meltdown-and-spectre-what-you-need-to-know-cve-2017-5715-cve-2017-5753-cve-2017-5754/" target="_blank" rel="noopener noreferrer">research blogs</a> but also using this dashboard to view the status of your assets as our data is updated.</p>
        <h3>What is Spectre?</h3>
        <p>Spectre allows attackers to trick applications into revealing secrets. It is harder to exploit than Meltdown, but also harder to mitigate. Comprehensive mitigations against Spectre will likely be a long term endeavor by many vendors.</p>
        <h3>What Steps Can I Take to Protect Against Meltdown?</h3>
        <p>You can expand the cards and create a Remediation Workflow project to manage and monitor installing the appropriate patches to mitigate exposure to Spectre.</p>
        <p>See also: <a href="https://meltdownattack.com/" target="_blank" rel="noopener noreferrer">https://meltdownattack.com</a> which covers both Meltdown and Spectre.</p>
      `,
    ssoEnableModal: {
      completedView: {
        done: 'Done',
        info:
          'All Security Console and cloud-based menu items are now available when opening InsightVM through insight.rapid7.com. This is also now your exclusive method for accessing InsightVM going forward.',
        logMeOut: 'Log me out',
        nextActionInfo:
          "You'll be logged out and redirected to insight.rapid7.com in <span>__timeLeft__ seconds</span>.",
        successHeader: 'InsightVM Platform Login is Now Active!',
      },
      enableView: {
        cantEnableSSO: 'Configuration has failed due to connectivity issues',
        cantReachConsole:
          "We didn't get a response from your Security Console. Verify that your console is online and confirm that your URL is correct before trying again.",
        confirmEmail:
          'I confirm the following: <ul><li><span>__email__</span> is the email address I currently use to access insight.rapid7.com.</li>',
        confirmRequired: 'Confirmation is required',
        details:
          "Bring your InsightVM experience one step closer to cloud-native with <a href='https://insightvm.help.rapid7.com/docs/enable-insightvm-platform-login' target='_blank' rel='noopener noreferrer'>InsightVM Platform Login</a>! This improved login method consolidates all on-premises Security Console tabs and cloud-based features for your user account in a single experience accessed exclusively through insight.rapid7.com.",
        enable: 'Enable InsightVM Platform Login',
        enterSecurityConsoleUrl: 'Enter Security Console URL',
        instructions: {
          enterConsoleUrl:
            "To enable <a href='https://insightvm.help.rapid7.com/docs/enable-insightvm-platform-login' target='_blank' rel='noopener noreferrer'>InsightVM Platform Login</a> for your user account, you'll need to do the following:<ul><li>Enter your full Security Console URL in the provided field.<li>Verify that the email address shown below is the same email address that you currently use to access insight.rapid7.com.</ul>",
          knownConsoleUrl:
            "To enable <a href='https://insightvm.help.rapid7.com/docs/enable-insightvm-platform-login' target='_blank' rel='noopener noreferrer'>InsightVM Platform Login</a> for your user account, verify that the email address shown below is the same email address that you currently use to access insight.rapid7.com:",
        },
        irreversibleDisclaimer:
          '<li>I understand that, as a Global Administrator, enabling InsightVM Platform Login for my user account also makes this enablement opportunity available for all other Security Console users in my organization.</li><li>I understand that I cannot turn off InsightVM Platform Login for my own user account after enablement, nor can any other user in my organization after they enable it themselves.</li>',
        reEnterPrompt:
          'You can reopen this window at any time by clicking <strong>Enable InsightVM Platform Login</strong> in your user profile menu.',
        snooze: 'Remind me later',
        thirdPartyAuthDetails:
          "InsightVM Platform Login is designed to leverage the <a href='https://docs.rapid7.com/insightvm/enable-insightvm-platform-login/#external-authentication-sources' target='_blank' rel='noopener noreferrer'>global authentication method</a> configured in your Insight Platform settings. Enabling this login method will deprecate the console-based external authentication source that your user account is already using, so we recommend that you contact your Platform Administrator to find out which global authentication method you will use with InsightVM Platform Login going forward.",
        title: 'You Are Eligible for InsightVM Platform Login',
      },
      learnMore: 'Learn more about InsightVM Platform Login.',
    },
    stale: 'Stale',
    standardPolicies: 'Standard Policies',
    startDate: 'Start Date',
    static: 'Static',
    status: 'Status',
    stockchart: {
      buttons: {
        all: 'All',
        monthText: '__text__m',
        weekText: '__text__w',
        yearText: '__text__y',
        ytd: 'YTD',
      },
    },
    submit: 'Submit',
    succeeded: 'Succeeded',
    success: 'Success',
    successful: 'Successful',
    summary: 'Summary',
    symbolicLink: 'Symbolic Link',
    syncRepo: 'Synchronize Repository',
    syncRepoInProgress: 'Synchronizing',
    syncRepoNotification:
      'Your request has been accepted. It may take several hours to finish synchronization.',
    table: {
      errorMessage: 'Failed to retrieve, please try later.',
      initialError:
        'There was an error loading the table. Please try and <a class="action refresh">reload</a> it at a later time.',
      loading: 'The table data is loading.',
    },
    tag: 'Tag',
    tagName: 'Tag Name',
    taggedAssets: 'Tagged Assets',
    tags: 'Tags',
    template: 'Template',
    temporalUnit: {
      hours: 'HOURS',
      minutes: 'MINUTES',
      seconds: 'SECONDS',
    },
    testAssumeRole: 'Test Assume Role',
    testConnection: 'Test Connection',
    testingConnection: 'Test Connection',
    threatFeed: 'Threat Feed',
    ticketing: {
      label: 'Ticketing',
      shortTitle: {
        [TicketServiceType.JIRA]: 'JIRA',
        [TicketServiceType.SERVICENOW]: 'ServiceNow',
      },
      title: {
        [TicketServiceType.JIRA]: 'JIRA',
        [TicketServiceType.SERVICENOW]: 'ServiceNow',
      },
    },
    time: 'Time',
    timeSinceLastAssessment: 'Time Since Last Assessment',
    timeUnits: {
      days: '__count__ Day',
      days_plural: '__count__ Days',
      hours: '__count__ Hour',
      hours_plural: '__count__ Hours',
      hoursLower: '__count__ hour',
      hoursLower_plural: '__count__ hours',
      minutes: '__count__ Minute',
      minutes_plural: '__count__ Minutes',
      months: '__count__ Month',
      months_plural: '__count__ Months',
      ongerThan: "> $t(timeUnits.__timeUnit__, {'count': __time__})",
      weeks: '__count__ Week',
      weeks_plural: '__count__ Weeks',
      years: '__count__ Year',
      years_plural: '__count__ Years',
    },
    title: 'Title',
    tooLong: 'You have exceeded the maximum length of __length__ characters',
    totalAssets: 'Total Assets',
    totalInstances: 'Total Instances',
    totalNewAssets: 'total new assets',
    totalRiskScore: 'Total Risk Score',
    totalRiskScoreReduction: 'Total Risk Score Reduction',
    totalVulns: 'Total Vulnerabilities',
    trendChartError: 'This chart cannot be displayed due to an error.',
    trendChartNotificationError:
      'An error occurred while loading the chart. Please try again.',
    trends: 'Trends',
    true: 'True',
    type: 'Type',
    typeYourCommentHere: 'Type your comment here...',
    typedFilterLabel: '$t(__type__) filter',
    unableToShare: 'unable to share this filter',
    unchanged: 'Unchanged',
    undo: 'Undo',
    unknown: 'Unknown',
    unknownOS: '$t(unknown) OS',
    unknownOSProduct: '$t(unknown) OS Product',
    unknownOSVendor: '$t(unknown) OS Vendor',
    unknownOSVersion: '$t(unknown) OS Version',
    unstable: 'Unstable',
    updateMessage: 'Your changes have been saved',
    updated: 'Updated',
    uploadImageFile: 'upload an image file',
    uploading: 'Uploading: __progress__',
    url: 'URL',
    user: 'User',
    username: 'Username',
    'username/email': 'Username / Email',
    users: 'Users',
    usersAndGroups: 'Users And Groups',
    validationErrorMessage: {
      isInUse: '__name__ is already in use',
      isInvalid: '__name__ is invalid',
      isNotHost: 'Invalid host address',
      isRequired: '__name__ is required',
      isURL: '__name__ cannot be a URL',
      selectEntityLimit: 'A maximum of __count__ __entity__ may be selected',
    },
    vendor: 'Vendor',
    vendorName: {
      [RegistryVendor.quay]: 'CoreOS, Inc.',
      [RegistryVendor.docker]: 'Docker Inc.',
      [RegistryVendor.ecr]: 'Amazon Web Services, Inc.',
      [RegistryVendor.gcr]: 'Google',
      [TicketServiceType.JIRA]: 'Atlassian, Inc.',
      [TicketServiceType.SERVICENOW]: 'ServiceNow',
    },
    vendors: 'Vendors',
    version: 'Version',
    versions: 'Versions',
    versus: 'VS',
    viewLog: 'View Log',
    visibility: 'Visibility',
    visitDocs: 'Visit docs to learn more',
    vuln: 'Vulnerabilities',
    vulnCount: '__vulns__ vulns',
    vulnTitle: 'Vulnerability Title',
    vulnerabilities: 'Vulnerabilities',
    vulnerabilitiesOverTime: 'Vulnerabilities Over Time',
    vulnerability: 'Vulnerability',
    vulnerabilityCount: '$t(vulnerabilities): __vulns__',
    vulnerabilityDetails: {
      title: 'Vulnerability Details',
    },
    vulnerabilityException: 'Vulnerability Exception',
    vulnerabilityExceptions: 'Vulnerability Exceptions',
    vulnerabilityFindings: 'Vulnerability Findings',
    vulnerabilityInstanceStatus: {
      EXCEPTION_VULN_EXPL: 'Exception Vulnerable Exploit',
      EXCEPTION_VULN_POTL: 'Exception Vulnerable POTL',
      EXCEPTION_VULN_VERS: 'Exception Vulnerable Version',
      NOT_VULN_DONT_STORE: "Not Vulnerable Don't Store",
      NOT_VULNERABLE: 'Not Vulnerable',
      OVERRIDDEN_VULN_VERS: 'Overriden Vulnerability Version',
      SKIPPED_DISABLED: 'Skipped Disabled',
      SKIPPED_DOS: 'Skipped Dos',
      SKIPPED_VERS: 'Skipped Version',
      SUPERSEDED: 'Superseded',
      UNEXPECTED_ERR: 'Unexpected Error',
      UNKNOWN: 'Unknown',
      VULNERABLE_EXPL: 'Vulnerable Exploited',
      VULNERABLE_POTENTIAL: 'Vulnerable Potential',
      VULNERABLE_VERS: 'Vulnerable Version',
    },
    vulnerabilityInstances: 'Vulnerability Instances',
    vulnerabilityLabel: 'Vulnerability',
    vulnerabilityLabels: {
      criticalVulnerability: 'Critical $t(vulnerabilities)',
      moderateVulnerability: 'Moderate $t(vulnerabilities)',
      severeVulnerability: 'Severe $t(vulnerabilities)',
      trendLine: '$t(assets) by $t(vulnerability) Severity Over Time',
      trendLineDescription: 'A card that shows vulnerability trends over time.',
    },
    vulnerabilitySeverityCount: '__vulns__ __severity__',
    vulnerabilityTitle: 'Vulnerability Name',
    vulnerableAssets: 'Vulnerable Assets',
    vulnexception: '$t(vulnerabilityException)',
    vulns: 'Vulnerabilities',
    vulnsInfo: 'Vulnerabilities Information',
    wannaCry: 'WannaCry',
    weekOf: 'Week of __week__',
    monthOf: 'Month of __week__',
    where: 'where',
    withoutAgents: 'Without Agents',
    workflow: 'Workflow',
    workflows: 'Workflows',
    yes: 'Yes',
    yourScope: 'Your Scope',
  },
};
/* eslint-enable sort-keys/sort-keys-fix */
